import { defineComponent, ref, provide, h as h$1, Transition, withDirectives, inject, watch, onBeforeMount, onMounted, onUpdated, toRefs, cloneVNode, onUnmounted, nextTick, computed, vShow, onBeforeUnmount, Teleport } from 'vue';

const CAccordion = defineComponent({
    name: 'CAccordion',
    props: {
        /**
         * The active item key.
         */
        activeItemKey: [Number, String],
        /**
         * Make accordion items stay open when another item is opened
         */
        alwaysOpen: Boolean,
        /**
         * Removes the default background-color, some borders, and some rounded corners to render accordions edge-to-edge with their parent container.
         */
        flush: Boolean,
    },
    setup(props, { slots }) {
        const activeItemKey = ref(props.activeItemKey);
        const setActiveItemKey = (key) => {
            activeItemKey.value = key;
        };
        provide('activeItemKey', activeItemKey);
        provide('alwaysOpen', props.alwaysOpen);
        provide('setActiveItemKey', setActiveItemKey);
        return () => h$1('div', { class: ['accordion', { ['accordion-flush']: props.flush }] }, slots.default && slots.default());
    },
});

const vVisible = {
    beforeMount(el, { value }, { transition }) {
        el._vod = el.style.display === 'none' ? '' : el.style.display;
        if (transition && value) {
            transition.beforeEnter(el);
        }
    },
    mounted(el, { value }, { transition }) {
        if (transition && value) {
            transition.enter(el);
        }
    },
    updated(el, { value, oldValue }, { transition }) {
        if (!value === !oldValue)
            return;
        if (transition) {
            if (value) {
                transition.beforeEnter(el);
                transition.enter(el);
            }
            else {
                transition.leave(el, () => {
                    // setDisplay(el, false)
                });
            }
        }
    },
};

const execute = (callback) => {
    if (typeof callback === 'function') {
        callback();
    }
};
const executeAfterTransition = (callback, transitionElement, waitForTransition = true) => {
    if (!waitForTransition) {
        execute(callback);
        return;
    }
    const durationPadding = 5;
    const emulatedDuration = getTransitionDurationFromElement(transitionElement) + durationPadding;
    let called = false;
    const handler = ({ target }) => {
        if (target !== transitionElement) {
            return;
        }
        called = true;
        transitionElement.removeEventListener('transitionend', handler);
        execute(callback);
    };
    transitionElement.addEventListener('transitionend', handler);
    setTimeout(() => {
        if (!called) {
            triggerTransitionEnd(transitionElement);
        }
    }, emulatedDuration);
};
const getTransitionDurationFromElement = (element) => {
    if (!element) {
        return 0;
    }
    // Get transition-duration of the element
    let { transitionDuration, transitionDelay } = window.getComputedStyle(element);
    const floatTransitionDuration = Number.parseFloat(transitionDuration);
    const floatTransitionDelay = Number.parseFloat(transitionDelay);
    // Return 0 if element or transition duration is not found
    if (!floatTransitionDuration && !floatTransitionDelay) {
        return 0;
    }
    // If multiple durations are defined, take the first
    transitionDuration = transitionDuration.split(',')[0];
    transitionDelay = transitionDelay.split(',')[0];
    return (Number.parseFloat(transitionDuration) + Number.parseFloat(transitionDelay)) * 1000;
};
const triggerTransitionEnd = (element) => {
    element.dispatchEvent(new Event('transitionend'));
};

const CCollapse = defineComponent({
    name: 'CCollapse',
    props: {
        /**
         * Set horizontal collapsing to transition the width instead of height.
         */
        horizontal: Boolean,
        /**
         * Toggle the visibility of component.
         */
        visible: Boolean,
    },
    emits: [
        /**
         * Callback fired when the component requests to be hidden.
         */
        'hide',
        /**
         * Callback fired when the component requests to be shown.
         */
        'show',
    ],
    setup(props, { slots, emit }) {
        const collapsing = ref(false);
        const show = ref(props.visible);
        const handleBeforeEnter = () => {
            collapsing.value = true;
        };
        const handleEnter = (el, done) => {
            emit('show');
            // collapsing.value = true
            setTimeout(() => {
                executeAfterTransition(() => done(), el);
                if (props.horizontal) {
                    el.style.width = `${el.scrollWidth}px`;
                    return;
                }
                el.style.height = `${el.scrollHeight}px`;
            }, 1);
        };
        const handleAfterEnter = (el) => {
            show.value = true;
            collapsing.value = false;
            props.horizontal ? el.style.removeProperty('width') : el.style.removeProperty('height');
        };
        const handleBeforeLeave = (el) => {
            collapsing.value = true;
            show.value = false;
            if (props.horizontal) {
                el.style.width = `${el.scrollWidth}px`;
                return;
            }
            el.style.height = `${el.scrollHeight}px`;
        };
        const handleLeave = (el, done) => {
            emit('hide');
            setTimeout(() => {
                executeAfterTransition(() => done(), el);
                if (props.horizontal) {
                    el.style.width = '0px';
                    return;
                }
                el.style.height = '0px';
            }, 1);
        };
        const handleAfterLeave = (el) => {
            collapsing.value = false;
            props.horizontal ? el.style.removeProperty('width') : el.style.removeProperty('height');
        };
        return () => h$1(Transition, {
            css: false,
            onBeforeEnter: () => handleBeforeEnter(),
            onEnter: (el, done) => handleEnter(el, done),
            onAfterEnter: (el) => handleAfterEnter(el),
            onBeforeLeave: (el) => handleBeforeLeave(el),
            onLeave: (el, done) => handleLeave(el, done),
            onAfterLeave: (el) => handleAfterLeave(el),
        }, () => withDirectives(h$1('div', {
            class: [
                collapsing.value ? 'collapsing' : 'collapse',
                { 'collapse-horizontal': props.horizontal, show: show.value },
            ],
        }, slots.default && slots.default()), [[vVisible, props.visible]]));
    },
});

const CAccordionBody = defineComponent({
    name: 'CAccordionBody',
    setup(_, { slots }) {
        const visible = inject('visible');
        return () => h$1(CCollapse, { class: 'accordion-collapse', visible: visible.value }, {
            default: () => h$1('div', { class: ['accordion-body'] }, slots.default && slots.default()),
        });
    },
});

const CAccordionButton = defineComponent({
    name: 'CAccordionButton',
    setup(_, { slots }) {
        const toggleVisibility = inject('toggleVisibility');
        const visible = inject('visible');
        return () => h$1('button', {
            type: 'button',
            'aria-expanded': !visible.value,
            class: ['accordion-button', { ['collapsed']: !visible.value }],
            onClick: () => toggleVisibility(),
        }, slots.default && slots.default());
    },
});

const CAccordionHeader = defineComponent({
    name: 'CAccordionHeader',
    setup(_, { slots }) {
        return () => h$1('div', { class: ['accordion-header'] }, h$1(CAccordionButton, {}, {
            default: () => slots.default && slots.default(),
        }));
    },
});

const CAccordionItem = defineComponent({
    name: 'CAccordionItem',
    props: {
        /**
         * The item key.
         */
        itemKey: [Number, String],
    },
    setup(props, { slots }) {
        const activeItemKey = inject('activeItemKey');
        const alwaysOpen = inject('alwaysOpen');
        const setActiveItemKey = inject('setActiveItemKey');
        const itemKey = ref(props.itemKey ?? Math.random().toString(36).slice(2, 11));
        const visible = ref(Boolean(activeItemKey.value === itemKey.value));
        watch(activeItemKey, () => (visible.value = Boolean(activeItemKey.value === itemKey.value)));
        const toggleVisibility = () => {
            visible.value = !visible.value;
            !alwaysOpen && visible && setActiveItemKey(itemKey.value);
        };
        provide('visible', visible);
        provide('toggleVisibility', toggleVisibility);
        return () => h$1('div', { class: ['accordion-item'] }, slots.default && slots.default());
    },
});

const CAccordionPlugin = {
    install: (app) => {
        app.component(CAccordion.name, CAccordion);
        app.component(CAccordionBody.name, CAccordionBody);
        app.component(CAccordionButton.name, CAccordionButton);
        app.component(CAccordionHeader.name, CAccordionHeader);
        app.component(CAccordionItem.name, CAccordionItem);
    },
};

const CCloseButton = defineComponent({
    name: 'CCloseButton',
    props: {
        /**
         * Toggle the disabled state for the component.
         */
        disabled: Boolean,
        /**
         * Change the default color to white.
         */
        white: Boolean,
    },
    emits: [
        /**
         * Event called when the user clicks on the component.
         */
        'click',
    ],
    setup(props, { emit }) {
        const handleClick = () => {
            if (props.disabled) {
                return;
            }
            emit('click');
        };
        return () => h$1('button', {
            type: 'button',
            class: [
                'btn',
                'btn-close',
                {
                    ['btn-close-white']: props.white,
                },
                props.disabled,
            ],
            'aria-label': 'Close',
            disabled: props.disabled,
            onClick: handleClick,
        });
    },
});

const CCloseButtonPlugin = {
    install: (app) => {
        app.component(CCloseButton.name, CCloseButton);
    },
};

const Shape = {
    type: String,
    validator: (value) => {
        // The value must match one of these strings
        return [
            'rounded',
            'rounded-top',
            'rounded-end',
            'rounded-bottom',
            'rounded-start',
            'rounded-circle',
            'rounded-pill',
            'rounded-0',
            'rounded-1',
            'rounded-2',
            'rounded-3',
        ].includes(value);
    },
};
const Color$1 = {
    type: String,
    validator: (value) => {
        // The value must match one of these strings
        return [
            'primary',
            'secondary',
            'success',
            'danger',
            'warning',
            'info',
            'dark',
            'light',
            'link',
            'transparent',
            'primary-gradient',
            'secondary-gradient',
            'success-gradient',
            'danger-gradient',
            'warning-gradient',
            'info-gradient',
            'dark-gradient',
            'light-gradient',
        ].includes(value);
    },
};
const TextColor = {
    type: String,
    validator: (value) => {
        return [
            'primary',
            'secondary',
            'success',
            'danger',
            'warning',
            'info',
            'dark',
            'light',
            'white',
            'muted',
            'high-emphasis',
            'medium-emphasis',
            'disabled',
            'high-emphasis-inverse',
            'medium-emphasis-inverse',
            'disabled-inverse',
        ].includes(value);
    },
};

const CAlert = defineComponent({
    name: 'CAlert',
    props: {
        /**
         * Sets the color context of the component to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
         */
        color: Color$1,
        /**
         * Optionally add a close button to alert and allow it to self dismisss.
         */
        dismissible: Boolean,
        /**
         * Set the alert variant to a solid.
         *
         * @values 'solid'
         */
        variant: {
            type: String,
            validator: (value) => {
                return value === 'solid';
            },
        },
        /**
         * Toggle the visibility of alert component.
         */
        visible: {
            type: Boolean,
            default: true,
        },
    },
    emits: [
        /**
         * Callback fired when the component requests to be closed.
         */
        'close',
    ],
    setup(props, { slots, emit }) {
        const visible = ref(props.visible);
        watch(() => props.visible, () => {
            visible.value = props.visible;
        });
        const handleDismiss = () => {
            visible.value = false;
            emit('close');
        };
        return () => h$1(Transition, {
            enterFromClass: '',
            enterActiveClass: 'fade',
            enterToClass: 'fade show',
            leaveActiveClass: 'fade',
        }, {
            default: () => visible.value &&
                h$1('div', {
                    class: [
                        'alert',
                        props.variant === 'solid'
                            ? `bg-${props.color} text-white border-0`
                            : `alert-${props.color}`,
                        {
                            [`alert-${props.color}`]: props.color,
                            'alert-dismissible': props.dismissible,
                        },
                    ],
                }, [
                    slots.default && slots.default(),
                    props.dismissible &&
                        h$1(CCloseButton, {
                            onClick: () => {
                                handleDismiss();
                            },
                        }),
                ]),
        });
    },
});

const CAlertHeading = defineComponent({
    name: 'CAlertHeading',
    props: {
        /**
         * 	Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'h4',
        },
    },
    setup(props, { slots }) {
        return () => h$1(props.component, {
            class: 'alert-heading',
        }, slots);
    },
});

const CAlertLink = defineComponent({
    name: 'CAlertLink',
    setup(_, { slots }) {
        return () => h$1('a', {
            class: 'alert-link',
        }, slots);
    },
});

const CAlertPlugin = {
    install: (app) => {
        app.component(CAlert.name, CAlert);
        app.component(CAlertHeading.name, CAlertHeading);
        app.component(CAlertLink.name, CAlertLink);
    },
};

const CAvatar = defineComponent({
    name: 'CAvatar',
    props: {
        /**
         * Sets the color context of the component to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
         */
        color: Color$1,
        /**
         * Select the shape of the component.
         *
         * @values 'rounded', 'rounded-top', 'rounded-end', 'rounded-bottom', 'rounded-start', 'rounded-circle', 'rounded-pill', 'rounded-0', 'rounded-1', 'rounded-2', 'rounded-3'
         */
        shape: Shape,
        /**
         * Size the component small, large, or extra large.
         *
         * @values 'sm', 'md', 'lg', 'xl'
         */
        size: {
            type: String,
            validator: (value) => {
                return ['sm', 'md', 'lg', 'xl'].includes(value);
            },
        },
        /**
         * The src attribute for the img element.
         */
        src: String,
        /**
         * Sets the color context of the status indicator to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
         */
        status: {
            type: String,
            validator: (value) => {
                return [
                    'primary',
                    'secondary',
                    'success',
                    'danger',
                    'warning',
                    'info',
                    'dark',
                    'light',
                ].includes(value);
            },
        },
        /**
         * Sets the text color of the component to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light', 'white', 'muted', 'high-emphasis', 'medium-emphasis', 'disabled', 'high-emphasis-inverse', 'medium-emphasis-inverse', 'disabled-inverse'
         */
        textColor: TextColor,
    },
    setup(props, { slots }) {
        return () => h$1('div', {
            class: [
                'avatar',
                {
                    [`bg-${props.color}`]: props.color,
                    [`avatar-${props.size}`]: props.size,
                    [`text-${props.textColor}`]: props.textColor,
                },
                `${props.shape}`,
            ],
        }, [
            props.src
                ? h$1('img', { src: props.src, class: 'avatar-img' })
                : slots.default && slots.default(),
            props.status && h$1('span', { class: ['avatar-status', `bg-${props.status}`] }),
        ]);
    },
});

const CAvatarPlugin = {
    install: (app) => {
        app.component(CAvatar.name, CAvatar);
    },
};

const CBackdrop = defineComponent({
    name: 'CBackdrop',
    props: {
        /**
         * Toggle the visibility of modal component.
         */
        visible: {
            type: Boolean,
            default: false,
        },
    },
    setup(props) {
        return () => h$1(Transition, {
            onEnter: (el) => {
                el.classList.add('show');
            },
            onLeave: (el) => {
                el.classList.remove('show');
            },
        }, () => props.visible &&
            h$1('div', {
                class: 'fade',
            }));
    },
});

const CBackdropPlugin = {
    install: (app) => {
        app.component(CBackdrop.name, CBackdrop);
    },
};

const CBadge = defineComponent({
    name: 'CBadge',
    props: {
        /**
         * Sets the color context of the component to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
         */
        color: Color$1,
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'span',
        },
        /**
         * Position badge in one of the corners of a link or button.
         *
         * @values 'top-start', 'top-end', 'bottom-end', 'bottom-start'
         */
        position: {
            type: String,
            validator: (value) => {
                return ['top-start', 'top-end', 'bottom-end', 'bottom-start'].includes(value);
            },
        },
        /**
         * Select the shape of the component.
         *
         * @values 'rounded', 'rounded-top', 'rounded-end', 'rounded-bottom', 'rounded-start', 'rounded-circle', 'rounded-pill', 'rounded-0', 'rounded-1', 'rounded-2', 'rounded-3'
         */
        shape: Shape,
        /**
         * Size the component small.
         *
         * @values 'sm'
         */
        size: {
            type: String,
            validator: (value) => {
                return value === 'sm';
            },
        },
        /**
         * Sets the text color of the component to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light', 'white', 'muted', 'high-emphasis', 'medium-emphasis', 'disabled', 'high-emphasis-inverse', 'medium-emphasis-inverse', 'disabled-inverse'
         */
        textColor: TextColor,
    },
    setup(props, { slots }) {
        return () => h$1(props.component, {
            class: [
                'badge',
                {
                    [`bg-${props.color}`]: props.color,
                    'position-absolute translate-middle': props.position,
                    'top-0': props.position && props.position.includes('top'),
                    'top-100': props.position && props.position.includes('bottom'),
                    'start-100': props.position && props.position.includes('end'),
                    'start-0': props.position && props.position.includes('start'),
                    [`badge-${props.size}`]: props.size,
                    [`text-${props.textColor}`]: props.textColor,
                },
                props.shape,
            ],
        }, slots.default && slots.default());
    },
});

const CBadgePlugin = {
    install: (app) => {
        app.component(CBadge.name, CBadge);
    },
};

const CBreadcrumbItem = defineComponent({
    name: 'CBreadcrumbItem',
    props: {
        /**
         * Toggle the active state for the component.
         */
        active: Boolean,
        /**
         * The `href` attribute for the inner link component.
         */
        href: String,
    },
    setup(props, { slots }) {
        return () => h$1('li', {
            class: [
                'breadcrumb-item',
                {
                    active: props.active,
                },
            ],
            ...(props.active && { 'aria-current': 'page' }),
        }, props.href
            ? h$1('a', { href: props.href }, slots.default && slots.default())
            : slots.default && slots.default());
    },
});

const CBreadcrumb = defineComponent({
    name: 'CBreadcrumb',
    setup(_, { slots, attrs }) {
        return () => h$1('nav', {
            'aria-label': 'breadcrumb',
        }, h$1('ol', { class: ['breadcrumb', attrs.class] }, slots.default && slots.default()));
    },
});

const CBreadcrumbPlugin = {
    install: (app) => {
        app.component(CBreadcrumb.name, CBreadcrumb);
        app.component(CBreadcrumbItem.name, CBreadcrumbItem);
    },
};

const CButton = defineComponent({
    name: 'CButton',
    props: {
        /**
         * Toggle the active state for the component.
         */
        active: Boolean,
        /**
         * Sets the color context of the component to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
         */
        color: Color$1,
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'button',
        },
        /**
         * Toggle the disabled state for the component.
         */
        disabled: Boolean,
        /**
         * The href attribute specifies the URL of the page the link goes to.
         */
        href: String,
        /**
         * Select the shape of the component.
         *
         * @values 'rounded', 'rounded-top', 'rounded-end', 'rounded-bottom', 'rounded-start', 'rounded-circle', 'rounded-pill', 'rounded-0', 'rounded-1', 'rounded-2', 'rounded-3'
         */
        shape: Shape,
        /**
         * Size the component small or large.
         *
         * @values 'sm', 'lg'
         */
        size: {
            type: String,
            validator: (value) => {
                return ['sm', 'lg'].includes(value);
            },
        },
        /**
         * Specifies the type of button. Always specify the type attribute for the `<button>` element.
         * Different browsers may use different default types for the `<button>` element.
         *
         * @values 'button', 'submit', 'reset'
         */
        type: {
            type: String,
            default: 'button',
            validator: (value) => {
                return ['button', 'submit', 'reset'].includes(value);
            },
        },
        /**
         * Set the button variant to an outlined button or a ghost button.
         *
         * @values 'ghost', 'outline'
         */
        variant: {
            type: String,
            validator: (value) => {
                return ['ghost', 'outline'].includes(value);
            },
        },
    },
    emits: [
        /**
         * Event called when the user clicks on the button.
         */
        'click',
    ],
    setup(props, { emit, slots }) {
        const handleClick = (event) => {
            if (props.disabled) {
                return;
            }
            emit('click', event);
        };
        return () => h$1(props.component, {
            class: [
                'btn',
                props.variant ? `btn-${props.variant}-${props.color}` : `btn-${props.color}`,
                {
                    [`btn-${props.size}`]: props.size,
                    active: props.active,
                    disabled: props.disabled,
                },
                props.shape,
            ],
            disabled: props.disabled && props.component !== 'a',
            ...(props.component === 'a' && props.disabled && { 'aria-disabled': true, tabIndex: -1 }),
            ...(props.component === 'a' && props.href && { href: props.href }),
            ...(props.component === 'button' && { type: props.type }),
            onClick: handleClick,
        }, slots.default && slots.default());
    },
});

const CButtonPlugin = {
    install: (app) => {
        app.component(CButton.name, CButton);
    },
};

const CButtonToolbar = defineComponent({
    name: 'CButtonToolbar',
    setup(_, { slots }) {
        return () => h$1('div', { class: 'btn-toolbar' }, slots.default && slots.default());
    },
});

const CButtonGroup = defineComponent({
    name: 'CButtonGroup',
    props: {
        /**
         * Size the component small or large.
         *
         * @values 'sm', 'lg'
         */
        size: {
            type: String,
            validator: (value) => {
                return ['sm', 'lg'].includes(value);
            },
        },
        /**
         * Create a set of buttons that appear vertically stacked rather than horizontally. Split button dropdowns are not supported here.
         */
        vertical: Boolean,
    },
    setup(props, { slots }) {
        return () => h$1('div', {
            class: [
                props.vertical ? 'btn-group-vertical' : 'btn-group',
                { [`btn-group-${props.size}`]: props.size },
            ],
        }, slots.default && slots.default());
    },
});

const CButtonGroupPlugin = {
    install: (app) => {
        app.component(CButtonToolbar.name, CButtonToolbar);
        app.component(CButtonGroup.name, CButtonGroup);
    },
};

const createGroupsInArray = (arr, numberOfGroups) => {
    const perGroup = Math.ceil(arr.length / numberOfGroups);
    return new Array(numberOfGroups)
        .fill('')
        .map((_, i) => arr.slice(i * perGroup, (i + 1) * perGroup));
};
const getMonthsNames = (locale) => {
    const months = [];
    const d = new Date();
    d.setDate(1);
    for (let i = 0; i < 12; i++) {
        d.setMonth(i);
        months.push(d.toLocaleString(locale, { month: 'short' }));
    }
    return months;
};
const getYears = (year) => {
    const years = [];
    for (let _year = year - 6; _year < year + 6; _year++) {
        years.push(_year);
    }
    return years;
};
const getLeadingDays = (year, month, firstDayOfWeek) => {
    // 0: sunday
    // 1: monday
    const dates = [];
    const d = new Date(year, month);
    const y = d.getFullYear();
    const m = d.getMonth();
    const firstWeekday = new Date(y, m, 1).getDay();
    let leadingDays = 6 - (6 - firstWeekday) - firstDayOfWeek;
    if (firstDayOfWeek) {
        leadingDays = leadingDays < 0 ? 7 + leadingDays : leadingDays;
    }
    for (let i = leadingDays * -1; i < 0; i++) {
        dates.push({
            date: new Date(y, m, i + 1),
            month: 'previous',
        });
    }
    return dates;
};
const getMonthDays = (year, month) => {
    const dates = [];
    const lastDay = new Date(year, month + 1, 0).getDate();
    for (let i = 1; i <= lastDay; i++) {
        dates.push({
            date: new Date(year, month, i),
            month: 'current',
        });
    }
    return dates;
};
const getTrailingDays = (year, month, leadingDays, monthDays) => {
    const dates = [];
    const days = 42 - (leadingDays.length + monthDays.length);
    for (let i = 1; i <= days; i++) {
        dates.push({
            date: new Date(year, month + 1, i),
            month: 'next',
        });
    }
    return dates;
};
const getMonthDetails = (year, month, firstDayOfWeek) => {
    const daysPrevMonth = getLeadingDays(year, month, firstDayOfWeek);
    const daysThisMonth = getMonthDays(year, month);
    const daysNextMonth = getTrailingDays(year, month, daysPrevMonth, daysThisMonth);
    const days = [...daysPrevMonth, ...daysThisMonth, ...daysNextMonth];
    const weeks = [];
    days.forEach((day, index) => {
        if (index % 7 === 0 || weeks.length === 0) {
            weeks.push([]);
        }
        weeks[weeks.length - 1].push(day);
    });
    return weeks;
};
const isDisableDateInRange = (startDate, endDate, dates) => {
    if (startDate && endDate) {
        const date = new Date(startDate);
        let disabled = false;
        while (date < endDate) {
            date.setDate(date.getDate() + 1);
            if (isDateDisabled(date, null, null, dates)) {
                disabled = true;
                break;
            }
        }
        return disabled;
    }
    return false;
};
const isDateDisabled = (date, min, max, dates) => {
    let disabled;
    if (dates) {
        dates.forEach((_date) => {
            if (Array.isArray(_date)) {
                if (isDateInRange(date, _date[0], _date[1])) {
                    disabled = true;
                }
            }
            if (_date instanceof Date) {
                if (isSameDateAs(date, _date)) {
                    disabled = true;
                }
            }
        });
    }
    if (min && date < min) {
        disabled = true;
    }
    if (max && date > max) {
        disabled = true;
    }
    return disabled;
};
const isDateInRange = (date, start, end) => {
    return start && end && start <= date && date <= end;
};
const isDateSelected = (date, start, end) => {
    return (start && isSameDateAs(start, date)) || (end && isSameDateAs(end, date));
};
const isEndDate = (date, start, end) => {
    return start && end && isSameDateAs(end, date) && start < end;
};
const isLastDayOfMonth = (date) => {
    const test = new Date(date.getTime());
    const month = test.getMonth();
    test.setDate(test.getDate() + 1);
    return test.getMonth() !== month;
};
const isSameDateAs = (date, date2) => {
    if (date instanceof Date && date2 instanceof Date) {
        return (date.getDate() === date2.getDate() &&
            date.getMonth() === date2.getMonth() &&
            date.getFullYear() === date2.getFullYear());
    }
    if (date === null && date2 === null) {
        return true;
    }
    return false;
};
const isStartDate = (date, start, end) => {
    return start && end && isSameDateAs(start, date) && start < end;
};
const isToday = (date) => {
    const today = new Date();
    return (date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear());
};

const CCalendar = defineComponent({
    name: 'CCalendar',
    props: {
        /**
         * Default date of the component
         */
        calendarDate: [Date, String],
        /**
         * The number of calendars that render on desktop devices.
         */
        calendars: {
            type: Number,
            default: 1,
        },
        /**
         * Set the format of day name.
         *
         * @default 'numeric'
         * @since 4.6.0
         */
        dayFormat: {
            type: [Function, String],
            default: 'numeric',
            required: false,
            validator: (value) => {
                if (typeof value === 'string') {
                    return ['numeric', '2-digit'].includes(value);
                }
                if (typeof value === 'function') {
                    return true;
                }
                if (typeof value === 'function') {
                    return true;
                }
                return false;
            },
        },
        /**
         * Specify the list of dates that cannot be selected.
         */
        disabledDates: {
            type: Array,
        },
        /**
         * Initial selected to date (range).
         */
        endDate: [Date, String],
        /**
         * Sets the day of start week.
         * - 0 - Sunday,
         * - 1 - Monday,
         * - 2 - Tuesday,
         * - 3 - Wednesday,
         * - 4 - Thursday,
         * - 5 - Friday,
         * - 6 - Saturday,
         */
        firstDayOfWeek: {
            type: Number,
            default: 1,
        },
        /**
         * Sets the default locale for components. If not set, it is inherited from the navigator.language.
         */
        locale: {
            type: String,
            default: 'default',
        },
        /**
         * Max selectable date.
         */
        maxDate: [Date, String],
        /**
         * Min selectable date.
         */
        minDate: [Date, String],
        /**
         * Show arrows navigation.
         */
        navigation: {
            type: Boolean,
            default: true,
        },
        /**
         * Reorder year-month navigation, and render year first.
         *
         * @since 4.6.0
         */
        navYearFirst: Boolean,
        /**
         * Allow range selection.
         */
        range: Boolean,
        /**
         * Toggle select mode between start and end date.
         */
        selectEndDate: Boolean,
        /**
         * Set whether days in adjacent months shown before or after the current month are selectable. This only applies if the `showAdjacementDays` option is set to true.
         *
         * @since 4.9.0
         */
        selectAdjacementDays: Boolean,
        /**
         * Set whether to display dates in adjacent months (non-selectable) at the start and end of the current month.
         *
         * @since 4.9.0
         */
        showAdjacementDays: {
            type: Boolean,
            default: true,
        },
        /**
         * Initial selected date.
         */
        startDate: [Date, String],
        /**
         * Set length or format of day name.
         *
         * @type  number | 'long' | 'narrow' | 'short'
         */
        weekdayFormat: {
            type: [Function, Number, String],
            default: 2,
            validator: (value) => {
                if (typeof value === 'string') {
                    return ['long', 'narrow', 'short'].includes(value);
                }
                if (typeof value === 'number') {
                    return true;
                }
                if (typeof value === 'function') {
                    return true;
                }
                return false;
            },
        },
    },
    emits: [
        /**
         * Callback fired when the user hovers over the calendar cell.
         *
         * @property {Date | null} date
         */
        'calendar-cell-hover',
        /**
         * Callback fired when the calendar date changed.
         *
         * @property {Date | null} date
         */
        'calendar-date-change',
        /**
         * Callback fired when the start date changed.
         *
         * @property {Date | null} date
         */
        'start-date-change',
        /**
         * Callback fired when the end date changed.
         *
         * @property {Date | null} date
         */
        'end-date-change',
    ],
    setup(props, { slots, emit }) {
        const calendarDate = ref(props.calendarDate
            ? new Date(props.calendarDate)
            : props.startDate
                ? new Date(props.startDate)
                : new Date());
        const startDate = ref(props.startDate ? new Date(props.startDate) : null);
        const endDate = ref(props.endDate ? new Date(props.endDate) : null);
        const hoverDate = ref(null);
        const maxDate = ref(props.maxDate ? new Date(props.maxDate) : null);
        const minDate = ref(props.minDate ? new Date(props.minDate) : null);
        const selectEndDate = ref(props.selectEndDate);
        const view = ref('days');
        watch(() => props.calendarDate, () => {
            if (props.calendarDate) {
                calendarDate.value = new Date(props.calendarDate);
            }
        });
        watch(() => props.startDate, () => {
            const date = props.startDate ? new Date(props.startDate) : null;
            if (!isSameDateAs(date, startDate.value)) {
                startDate.value = date;
            }
        });
        watch(() => props.endDate, () => {
            const date = props.endDate ? new Date(props.endDate) : null;
            if (!isSameDateAs(date, endDate.value)) {
                endDate.value = date;
            }
        });
        watch(() => props.maxDate, () => {
            const date = props.maxDate ? new Date(props.maxDate) : null;
            if (!isSameDateAs(date, maxDate.value)) {
                maxDate.value = date;
            }
        });
        watch(() => props.minDate, () => {
            const date = props.minDate ? new Date(props.minDate) : null;
            if (!isSameDateAs(date, minDate.value)) {
                minDate.value = date;
            }
        });
        watch(() => props.selectEndDate, () => {
            selectEndDate.value = props.selectEndDate;
        });
        watch(startDate, () => {
            emit('start-date-change', startDate.value);
        });
        watch(endDate, () => {
            emit('end-date-change', endDate.value);
        });
        const setCalendarPage = (years, months = 0, setMonth) => {
            const year = calendarDate.value.getFullYear();
            const month = calendarDate.value.getMonth();
            const d = new Date(year, month, 1);
            years && d.setFullYear(d.getFullYear() + years);
            months && d.setMonth(d.getMonth() + months);
            typeof setMonth === 'number' && d.setMonth(setMonth);
            calendarDate.value = d;
            emit('calendar-date-change', d);
        };
        const handleCellOnClick = (date) => {
            if (isDateDisabled(date, minDate.value, maxDate.value, props.disabledDates)) {
                return;
            }
            if (props.range) {
                if (selectEndDate.value) {
                    selectEndDate.value = false;
                    if (startDate.value && startDate.value > date) {
                        startDate.value = null;
                        endDate.value = null;
                        return;
                    }
                    if (isDisableDateInRange(startDate.value, date, props.disabledDates)) {
                        startDate.value = null;
                        endDate.value = null;
                        return;
                    }
                    endDate.value = date;
                    return;
                }
                if (endDate.value && endDate.value < date) {
                    startDate.value = null;
                    endDate.value = null;
                    return;
                }
                if (isDisableDateInRange(date, endDate.value, props.disabledDates)) {
                    startDate.value = null;
                    endDate.value = null;
                    return;
                }
                selectEndDate.value = true;
                startDate.value = date;
                return;
            }
            startDate.value = date;
        };
        const handleCellKeyDown = (event, date) => {
            if (event.code === 'Space' || event.key === 'Enter') {
                event.preventDefault();
                handleCellOnClick && handleCellOnClick(date);
                return;
            }
        };
        const handleCellMouseEnter = (date) => {
            if (isDateDisabled(date, minDate.value, maxDate.value, props.disabledDates)) {
                return;
            }
            hoverDate.value = date;
            emit('calendar-cell-hover', date);
        };
        const handleCellMouseLeave = () => {
            hoverDate.value = null;
            emit('calendar-cell-hover', null);
        };
        const handleMonthKeyDown = (event, month) => {
            if (event.code === 'Space' || event.key === 'Enter') {
                setCalendarPage(0, month);
                view.value = 'days';
            }
        };
        const handleYearKeyDown = (event, date) => {
            if (event.code === 'Space' || event.key === 'Enter') {
                calendarDate.value = date;
                view.value = 'months';
            }
        };
        const handleNavigationOnClick = (direction, double = false) => {
            if (direction === 'prev') {
                if (double) {
                    setCalendarPage(view.value === 'days' ? -1 : -10);
                    return;
                }
                if (view.value !== 'days') {
                    setCalendarPage(-1);
                    return;
                }
                setCalendarPage(0, -1);
                return;
            }
            if (direction === 'next') {
                if (double) {
                    setCalendarPage(view.value === 'days' ? 1 : 10);
                    return;
                }
                if (view.value !== 'days') {
                    setCalendarPage(1);
                    return;
                }
                setCalendarPage(0, 1);
                return;
            }
        };
        const Calendar = (addMonths) => {
            let date = calendarDate.value;
            if (addMonths !== 0) {
                date = new Date(calendarDate.value.getFullYear(), calendarDate.value.getMonth() + addMonths, 1);
            }
            const monthDetails = getMonthDetails(date.getFullYear(), date.getMonth(), props.firstDayOfWeek);
            const listOfMonths = createGroupsInArray(getMonthsNames(props.locale), 4);
            const listOfYears = createGroupsInArray(getYears(date.getFullYear()), 4);
            const weekDays = monthDetails[0];
            return h$1('table', {}, [
                view.value === 'days' &&
                    h$1('thead', {}, h$1('tr', {}, weekDays.map(({ date }) => {
                        return h$1('th', { class: 'calendar-cell' }, h$1('div', {
                            class: 'calendar-header-cell-inner',
                        }, typeof props.weekdayFormat === 'function'
                            ? props.weekdayFormat(date)
                            : typeof props.weekdayFormat === 'string'
                                ? date.toLocaleDateString(props.locale, {
                                    weekday: props.weekdayFormat,
                                })
                                : date
                                    .toLocaleDateString(props.locale, { weekday: 'long' })
                                    .slice(0, props.weekdayFormat)));
                    }))),
                h$1('tbody', {}, [
                    view.value === 'days' &&
                        monthDetails.map((week) => {
                            return h$1('tr', {}, week.map(({ date, month }) => {
                                return month === 'current' || props.showAdjacementDays
                                    ? h$1('td', {
                                        class: [
                                            'calendar-cell',
                                            {
                                                today: isToday(date),
                                                disabled: isDateDisabled(date, minDate.value, maxDate.value, props.disabledDates),
                                                [month]: true,
                                                clickable: month !== 'current' && props.selectAdjacementDays,
                                                last: isLastDayOfMonth(date),
                                                range: month === 'current' &&
                                                    isDateInRange(date, startDate.value, endDate.value),
                                                'range-hover': month === 'current' &&
                                                    (hoverDate.value && selectEndDate.value
                                                        ? isDateInRange(date, startDate.value, hoverDate.value)
                                                        : isDateInRange(date, hoverDate.value, endDate.value)),
                                                selected: isDateSelected(date, startDate.value, endDate.value),
                                                start: isStartDate(date, startDate.value, endDate.value),
                                                end: isEndDate(date, startDate.value, endDate.value),
                                            },
                                        ],
                                        tabindex: (month === 'current' || props.selectAdjacementDays) &&
                                            !isDateDisabled(date, minDate.value, maxDate.value, props.disabledDates)
                                            ? 0
                                            : -1,
                                        title: date.toLocaleDateString(props.locale),
                                        ...((month === 'current' || props.selectAdjacementDays) && {
                                            onBlur: () => handleCellMouseLeave(),
                                            onClick: () => handleCellOnClick(date),
                                            onFocus: () => handleCellMouseEnter(date),
                                            onKeydown: (event) => handleCellKeyDown(event, date),
                                            onmouseenter: () => handleCellMouseEnter(date),
                                            onmouseleave: () => handleCellMouseLeave(),
                                        }),
                                        ...(month !== 'current' &&
                                            !props.selectAdjacementDays && {
                                            onMouseEnter: () => handleCellMouseLeave(),
                                        }),
                                    }, h$1('div', {
                                        class: 'calendar-cell-inner',
                                    }, typeof props.dayFormat === 'function'
                                        ? props.dayFormat(date)
                                        : date.toLocaleDateString(props.locale, {
                                            day: props.dayFormat,
                                        })))
                                    : h$1('td');
                            }));
                        }),
                    view.value === 'months' &&
                        listOfMonths.map((row, index) => {
                            return h$1('tr', {}, row.map((month, idx) => {
                                return h$1('td', {
                                    class: 'calendar-cell month',
                                    onClick: () => {
                                        setCalendarPage(0, 0, index * 3 + idx - addMonths);
                                        view.value = 'days';
                                    },
                                    onKeydown: (event) => handleMonthKeyDown(event, index * 3 + idx - addMonths),
                                    tabindex: 0,
                                }, h$1('div', { class: 'calendar-cell-inner' }, month));
                            }));
                        }),
                    view.value === 'years' &&
                        listOfYears.map((row) => {
                            return h$1('tr', {}, row.map((year) => {
                                return h$1('td', {
                                    class: 'calendar-cell year',
                                    onClick: () => {
                                        calendarDate.value = new Date(year, date.getMonth() - addMonths, date.getDate());
                                        view.value = 'months';
                                    },
                                    onKeydown: (event) => handleYearKeyDown(event, new Date(year, date.getMonth() - addMonths, date.getDate())),
                                    tabindex: 0,
                                }, h$1('div', { class: 'calendar-cell-inner' }, year));
                            }));
                        }),
                ]),
            ]);
        };
        const Navigation = (addMonths) => {
            let date = calendarDate.value;
            if (addMonths !== 0) {
                date = new Date(calendarDate.value.getFullYear(), calendarDate.value.getMonth() + addMonths, 1);
            }
            return h$1('div', { class: 'calendar-nav' }, [
                props.navigation &&
                    h$1('div', {
                        class: 'calendar-nav-prev',
                    }, [
                        h$1(CButton, {
                            color: 'transparent',
                            size: 'sm',
                            onClick: () => handleNavigationOnClick('prev', true),
                        }, {
                            /**
                             * @slot Location for double previous icon.
                             */
                            default: () => slots.navPrevDoubleIcon
                                ? slots.navPrevDoubleIcon()
                                : h$1('span', { class: 'calendar-nav-icon calendar-nav-icon-double-prev' }),
                        }),
                        view.value === 'days' &&
                            h$1(CButton, {
                                color: 'transparent',
                                size: 'sm',
                                onClick: () => handleNavigationOnClick('prev'),
                            }, {
                                /**
                                 * @slot Location for previous icon.
                                 */
                                default: () => slots.navPrevIcon
                                    ? slots.navPrevIcon()
                                    : h$1('span', { class: 'calendar-nav-icon calendar-nav-icon-prev' }),
                            }),
                    ]),
                h$1('div', {
                    class: 'calendar-nav-date',
                    ...(props.navYearFirst && { style: { display: 'flex', justifyContent: 'center' } }),
                }, [
                    view.value === 'days' &&
                        h$1(CButton, {
                            color: 'transparent',
                            size: 'sm',
                            onClick: () => {
                                if (props.navigation)
                                    view.value = 'months';
                            },
                        }, () => date.toLocaleDateString(props.locale, { month: 'long' })),
                    h$1(CButton, {
                        color: 'transparent',
                        size: 'sm',
                        onClick: () => {
                            if (props.navigation)
                                view.value = 'years';
                        },
                        ...(props.navYearFirst && { style: { order: '-1' } }),
                    }, () => date.toLocaleDateString(props.locale, { year: 'numeric' })),
                ]),
                props.navigation &&
                    h$1('div', {
                        class: 'calendar-nav-next',
                    }, [
                        view.value === 'days' &&
                            h$1(CButton, {
                                color: 'transparent',
                                size: 'sm',
                                onClick: () => handleNavigationOnClick('next'),
                            }, {
                                /**
                                 * @slot Location for next icon.
                                 */
                                default: () => slots.navNextIcon
                                    ? slots.navNextIcon()
                                    : h$1('span', { class: 'calendar-nav-icon calendar-nav-icon-next' }),
                            }),
                        h$1(CButton, {
                            color: 'transparent',
                            size: 'sm',
                            onClick: () => handleNavigationOnClick('next', true),
                        }, {
                            /**
                             * @slot Location for double next icon.
                             */
                            default: () => slots.navNextDoubleIcon
                                ? slots.navNextDoubleIcon()
                                : h$1('span', { class: 'calendar-nav-icon calendar-nav-icon-double-next' }),
                        }),
                    ]),
            ]);
        };
        return () => h$1('div', { class: 'calendars' }, [
            Array.from({ length: props.calendars }, (_, index) => h$1('div', { class: ['calendar', view.value] }, [Navigation(index), Calendar(index)])),
        ]);
    },
});

const CCalendarPlugin = {
    install: (app) => {
        app.component(CCalendar.name, CCalendar);
    },
};

const CCallout = defineComponent({
    name: 'CCallout',
    props: {
        /**
         * Sets the color context of the component to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
         */
        color: Color$1,
    },
    setup(props, { slots }) {
        return () => h$1('div', {
            class: [
                'callout',
                {
                    [`callout-${props.color}`]: props.color,
                },
            ],
        }, slots.default && slots.default());
    },
});

const CCalloutPlugin = {
    install: (app) => {
        app.component(CCallout.name, CCallout);
    },
};

const CCard = defineComponent({
    name: 'CCard',
    props: {
        /**
         * Sets the color context of the component to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
         */
        color: Color$1,
        /**
         * Sets the text color context of the component to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light', 'white', 'muted', 'high-emphasis', 'medium-emphasis', 'disabled', 'high-emphasis-inverse', 'medium-emphasis-inverse', 'disabled-inverse'
         */
        textColor: TextColor,
    },
    setup(props, { slots }) {
        return () => h$1('div', {
            class: [
                'card',
                {
                    [`bg-${props.color}`]: props.color,
                    [`text-${props.textColor}`]: props.textColor,
                },
            ],
        }, slots.default && slots.default());
    },
});

const CCardBody = defineComponent({
    name: 'CCardBody',
    setup(_, { slots }) {
        return () => h$1('div', { class: 'card-body' }, slots.default && slots.default());
    },
});

const CCardFooter = defineComponent({
    name: 'CCardFooter',
    setup(_, { slots }) {
        return () => h$1('div', { class: 'card-footer' }, slots.default && slots.default());
    },
});

const CCardGroup = defineComponent({
    name: 'CCardGroup',
    setup(_, { slots }) {
        return () => h$1('div', { class: 'card-group' }, slots.default && slots.default());
    },
});

const CCardHeader = defineComponent({
    name: 'CCardHeader',
    props: {
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'div',
        },
    },
    setup(props, { slots }) {
        return () => h$1(props.component, { class: 'card-header' }, slots.default && slots.default());
    },
});

const CCardImage = defineComponent({
    name: 'CCardImage',
    props: {
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'img',
        },
        /**
         * Optionally orientate the image to the top, bottom.
         *
         * @values 'top', 'bottom'
         */
        orientation: {
            type: String,
            validator: (value) => {
                return ['top', 'bottom'].includes(value);
            },
        },
    },
    setup(props, { slots }) {
        return () => h$1(props.component, {
            class: `card-img${props.orientation ? `-${props.orientation}` : ''}`,
        }, slots.default && slots.default());
    },
});

const CCardImageOverlay = defineComponent({
    name: 'CCardImageOverlay',
    setup(_, { slots }) {
        return () => h$1('div', { class: 'card-img-overlay' }, slots.default && slots.default());
    },
});

const CLink = defineComponent({
    name: 'CLink',
    props: {
        /**
         * Toggle the active state for the component.
         */
        active: Boolean,
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'a',
        },
        /**
         * Toggle the disabled state for the component.
         */
        disabled: Boolean,
        /**
         * The href attribute specifies the URL of the page the link goes to.
         */
        href: String,
    },
    emits: [
        /**
         * Event called when the user clicks on the component.
         */
        'click',
    ],
    setup(props, { slots, emit }) {
        const handleClick = () => {
            emit('click', props.href);
        };
        return () => h$1(props.component, {
            class: [{ active: props.active, disabled: props.disabled }],
            ...(props.active && { 'aria-current': 'page' }),
            ...(props.component === 'a' && props.disabled && { 'aria-disabled': true, tabIndex: -1 }),
            ...((props.component === 'a' || props.component === 'button') && {
                onClick: handleClick,
            }),
            href: props.href,
        }, slots.default && slots.default());
    },
});

const CCLinkPlugin = {
    install: (app) => {
        app.component(CLink.name, CLink);
    },
};

const CCardLink = defineComponent({
    name: 'CCardLink',
    props: {
        /**
         * The href attribute specifies the URL of the page the link goes to.
         */
        href: {
            type: String,
            default: '#',
        },
    },
    setup(props, { slots }) {
        return () => h$1(CLink, { class: 'card-link', href: props.href }, { default: () => slots.default && slots.default() });
    },
});

const CCardSubtitle = defineComponent({
    name: 'CCardSubtitle',
    props: {
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'h6',
        },
    },
    setup(props, { slots }) {
        return () => h$1(props.component, { class: 'card-subtitle' }, slots.default && slots.default());
    },
});

const CCardText = defineComponent({
    name: 'CCardText',
    props: {
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'p',
        },
    },
    setup(props, { slots }) {
        return () => h$1(props.component, { class: 'card-text' }, slots.default && slots.default());
    },
});

const CCardTitle = defineComponent({
    name: 'CCardTitle',
    props: {
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'h5',
        },
    },
    setup(props, { slots }) {
        return () => h$1(props.component, { class: 'card-title' }, slots.default && slots.default());
    },
});

const CCardPlugin = {
    install: (app) => {
        app.component(CCard.name, CCard);
        app.component(CCardBody.name, CCardBody);
        app.component(CCardFooter.name, CCardFooter);
        app.component(CCardGroup.name, CCardGroup);
        app.component(CCardHeader.name, CCardHeader);
        app.component(CCardImage.name, CCardImage);
        app.component(CCardImageOverlay.name, CCardImageOverlay);
        app.component(CCardLink.name, CCardLink);
        app.component(CCardSubtitle.name, CCardSubtitle);
        app.component(CCardText.name, CCardText);
        app.component(CCardTitle.name, CCardTitle);
    },
};

const isRTL = (element) => {
    if (typeof document !== 'undefined' && document.documentElement.dir === 'rtl') {
        return true;
    }
    if (element) {
        return element.closest('[dir="rtl"]') !== null;
    }
    return false;
};

const getRTLPlacement = (placement, element) => {
    switch (placement) {
        case 'right': {
            return isRTL(element) ? 'left' : 'right';
        }
        case 'left': {
            return isRTL(element) ? 'right' : 'left';
        }
        default: {
            return placement;
        }
    }
};

const getUID = (prefix) => {
    do {
        prefix += Math.floor(Math.random() * 1_000_000);
    } while (document.getElementById(prefix));
    return prefix;
};

const isInViewport = (element) => {
    const rect = element.getBoundingClientRect();
    return (Math.floor(rect.top) >= 0 &&
        Math.floor(rect.left) >= 0 &&
        Math.floor(rect.bottom) <= (window.innerHeight || document.documentElement.clientHeight) &&
        Math.floor(rect.right) <= (window.innerWidth || document.documentElement.clientWidth));
};

const isObjectInArray = (array, item, ignore = []) => array.some((_item) => {
    let result = true;
    for (const key in item) {
        if (!ignore.includes(key) && item[key] !== _item[key]) {
            result = false;
            break;
        }
    }
    return result;
});

const CCarousel = defineComponent({
    name: 'CCarousel',
    props: {
        /**
         * Adding in the previous and next controls.
         */
        controls: Boolean,
        /**
         * Add darker controls, indicators, and captions.
         */
        dark: Boolean,
        /**
         * index of the active item.
         */
        index: {
            type: Number,
            default: 0,
        },
        /**
         * Adding indicators at the bottom of the carousel for each item.
         */
        indicators: Boolean,
        /**
         * The amount of time to delay between automatically cycling an item. If false, carousel will not automatically cycle.
         */
        interval: {
            type: [Boolean, Number],
            default: 5000,
        },
        /**
         * If set to 'hover', pauses the cycling of the carousel on mouseenter and resumes the cycling of the carousel on mouseleave. If set to false, hovering over the carousel won't pause it.
         */
        pause: {
            type: [Boolean, String],
            default: 'hover',
            validator: (value) => {
                return typeof value === 'boolean' || value === 'hover';
            },
        },
        /**
         * Set type of the transition.
         *
         * @values 'crossfade', 'slide'
         */
        transition: {
            type: String,
            default: 'slide',
            validator: (value) => {
                return ['crossfade', 'slide'].includes(value);
            },
        },
        /**
         * Set whether the carousel should cycle continuously or have hard stops.
         */
        wrap: {
            type: Boolean,
            default: true,
        },
    },
    setup(props, { slots }) {
        const carouselRef = ref();
        const active = ref(props.index);
        const animating = ref(false);
        const customInterval = ref(props.interval);
        const direction = ref('next');
        const items = ref([]);
        const timeout = ref();
        const visible = ref();
        const setAnimating = (value) => {
            animating.value = value;
        };
        const setCustomInterval = (value) => {
            customInterval.value = value;
        };
        provide('setAnimating', setAnimating);
        provide('setCustomInterval', setCustomInterval);
        const pause = () => timeout.value && clearInterval(timeout.value);
        const cycle = () => {
            pause();
            if (typeof props.interval === 'number') {
                timeout.value = setTimeout(() => nextItemWhenVisible(), typeof customInterval.value === 'number' ? customInterval.value : props.interval);
            }
        };
        const handleControlClick = (_direction) => {
            if (animating.value) {
                return;
            }
            direction.value = _direction;
            if (_direction === 'next') {
                active.value === items.value.length - 1 ? (active.value = 0) : active.value++;
            }
            else {
                active.value === 0 ? (active.value = items.value.length - 1) : active.value--;
            }
        };
        const nextItemWhenVisible = () => {
            // Don't call next when the page isn't visible
            // or the carousel or its parent isn't visible
            if (!document.hidden && carouselRef.value && isInViewport(carouselRef.value)) {
                handleControlClick('next');
            }
        };
        const handleIndicatorClick = (index) => {
            if (active.value === index) {
                return;
            }
            if (active.value < index) {
                direction.value = 'next';
                active.value = index;
                return;
            }
            if (active.value > index) {
                direction.value = 'prev';
                active.value = index;
            }
        };
        const handleScroll = () => {
            visible.value =
                !document.hidden && carouselRef.value && isInViewport(carouselRef.value) ? true : false;
        };
        onBeforeMount(() => {
            if (slots.default) {
                const children = typeof slots.default()[0].type === 'symbol'
                    ? slots.default()[0].children
                    : slots.default();
                if (children && Array.isArray(children)) {
                    // @ts-expect-error TODO: fix types
                    items.value = children.filter((child) => child.type.name === 'CCarouselItem');
                }
            }
        });
        onMounted(() => {
            window.addEventListener('scroll', handleScroll);
        });
        onUpdated(() => {
            watch(animating, () => {
                if (props.wrap) {
                    !animating.value && cycle();
                    return;
                }
                if (!props.wrap && active.value < items.value.length - 1) {
                    !animating.value && cycle();
                }
            });
        });
        watch(visible, () => {
            visible.value && cycle();
        });
        return () => h$1('div', {
            class: [
                'carousel slide',
                props.transition === 'crossfade' && 'carousel-fade',
                props.dark && 'carousel-dark',
            ],
            onmouseover: () => props.pause && pause(),
            onmouseleave: () => cycle(),
            ref: carouselRef,
        }, [
            props.indicators &&
                h$1('div', {
                    class: 'carousel-indicators',
                }, items.value.map((_, index) => {
                    return h$1('button', {
                        type: 'button',
                        id: index,
                        'data-coreui-target': '',
                        ...(active.value === index && { class: 'active' }),
                        onClick: () => handleIndicatorClick(index),
                    });
                })),
            h$1('div', { class: 'carousel-inner' }, items.value.map((item, index) => {
                return h$1(item, {
                    active: active.value === index ? true : false,
                    direction: direction.value,
                });
            })),
            props.controls && [
                h$1('button', {
                    type: 'button',
                    class: 'carousel-control-prev',
                    'data-coreui-target': '',
                    onClick: () => handleControlClick('prev'),
                }, [
                    h$1('span', { class: 'carousel-control-prev-icon', ariaHidden: 'true' }),
                    h$1('span', { class: 'visually-hidden' }, 'Previous'),
                ]),
                h$1('button', {
                    type: 'button',
                    class: 'carousel-control-next',
                    'data-coreui-target': '',
                    onClick: () => handleControlClick('next'),
                }, [
                    h$1('span', { class: 'carousel-control-next-icon', ariaHidden: 'true' }),
                    h$1('span', { class: 'visually-hidden' }, 'Next'),
                ]),
            ],
        ]);
    },
});

const CCarouselCaption = defineComponent({
    name: 'CCarouselCaption',
    setup(_, { slots }) {
        return () => h$1('div', {
            class: 'carousel-caption',
        }, slots.default && slots.default());
    },
});

const CCarouselItem = defineComponent({
    name: 'CCarouselItem',
    props: {
        /**
         * @ignore
         */
        active: {
            type: Boolean,
            default: false,
        },
        /**
         * @ignore
         */
        direction: {
            type: String,
            default: 'next',
        },
        /**
         * The amount of time to delay between automatically cycling an item.
         */
        interval: {
            type: [Boolean, Number],
            default: false,
        },
    },
    setup(props, { slots }) {
        const carouselItemRef = ref();
        const { active } = toRefs(props);
        const directionClassName = ref();
        const orderClassName = ref();
        const activeClassName = ref(active.value && 'active');
        // eslint-disable-next-line no-unused-vars
        const setAnimating = inject('setAnimating');
        // eslint-disable-next-line no-unused-vars
        const setCustomInterval = inject('setCustomInterval');
        watch(active, (active, prevActive) => {
            active && setCustomInterval(props.interval);
            if (!prevActive && active) {
                orderClassName.value = `carousel-item-${props.direction}`;
                setCustomInterval(props.interval);
            }
            setTimeout(() => {
                if (prevActive && !active) {
                    activeClassName.value = 'active';
                }
                directionClassName.value = `carousel-item-${props.direction === 'next' ? 'start' : 'end'}`;
            }, 0);
            carouselItemRef.value.addEventListener('transitionstart', () => {
                setAnimating(true);
            });
            carouselItemRef.value.addEventListener('transitionend', () => {
                setAnimating(false);
                if (active) {
                    directionClassName.value = '';
                    orderClassName.value = '';
                    activeClassName.value = 'active';
                }
                if (!active) {
                    directionClassName.value = '';
                    orderClassName.value = '';
                    activeClassName.value = '';
                }
            });
        });
        return () => h$1('div', {
            class: [
                'carousel-item',
                activeClassName.value,
                directionClassName.value,
                orderClassName.value,
            ],
            ref: carouselItemRef,
        }, slots.default && slots.default());
    },
});

const CCarouselPlugin = {
    install: (app) => {
        app.component(CCarousel.name, CCarousel);
        app.component(CCarouselCaption.name, CCarouselCaption);
        app.component(CCarouselItem.name, CCarouselItem);
    },
};

const CCollapsePlugin = {
    install: (app) => {
        app.component(CCollapse.name, CCollapse);
    },
};

function _typeof(obj) {
  "@babel/helpers - typeof";

  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) {
    return typeof obj;
  } : function (obj) {
    return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
  }, _typeof(obj);
}

function toInteger(dirtyNumber) {
  if (dirtyNumber === null || dirtyNumber === true || dirtyNumber === false) {
    return NaN;
  }
  var number = Number(dirtyNumber);
  if (isNaN(number)) {
    return number;
  }
  return number < 0 ? Math.ceil(number) : Math.floor(number);
}

function requiredArgs(required, args) {
  if (args.length < required) {
    throw new TypeError(required + ' argument' + (required > 1 ? 's' : '') + ' required, but only ' + args.length + ' present');
  }
}

/**
 * @name toDate
 * @category Common Helpers
 * @summary Convert the given argument to an instance of Date.
 *
 * @description
 * Convert the given argument to an instance of Date.
 *
 * If the argument is an instance of Date, the function returns its clone.
 *
 * If the argument is a number, it is treated as a timestamp.
 *
 * If the argument is none of the above, the function returns Invalid Date.
 *
 * **Note**: *all* Date arguments passed to any *date-fns* function is processed by `toDate`.
 *
 * @param {Date|Number} argument - the value to convert
 * @returns {Date} the parsed date in the local time zone
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // Clone the date:
 * const result = toDate(new Date(2014, 1, 11, 11, 30, 30))
 * //=> Tue Feb 11 2014 11:30:30
 *
 * @example
 * // Convert the timestamp to date:
 * const result = toDate(1392098430000)
 * //=> Tue Feb 11 2014 11:30:30
 */
function toDate(argument) {
  requiredArgs(1, arguments);
  var argStr = Object.prototype.toString.call(argument);

  // Clone the date
  if (argument instanceof Date || _typeof(argument) === 'object' && argStr === '[object Date]') {
    // Prevent the date to lose the milliseconds when passed to new Date() in IE10
    return new Date(argument.getTime());
  } else if (typeof argument === 'number' || argStr === '[object Number]') {
    return new Date(argument);
  } else {
    if ((typeof argument === 'string' || argStr === '[object String]') && typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn("Starting with v2.0.0-beta.1 date-fns doesn't accept strings as date arguments. Please use `parseISO` to parse strings. See: https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#string-arguments");
      // eslint-disable-next-line no-console
      console.warn(new Error().stack);
    }
    return new Date(NaN);
  }
}

/**
 * @name addMilliseconds
 * @category Millisecond Helpers
 * @summary Add the specified number of milliseconds to the given date.
 *
 * @description
 * Add the specified number of milliseconds to the given date.
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of milliseconds to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the milliseconds added
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Add 750 milliseconds to 10 July 2014 12:45:30.000:
 * const result = addMilliseconds(new Date(2014, 6, 10, 12, 45, 30, 0), 750)
 * //=> Thu Jul 10 2014 12:45:30.750
 */
function addMilliseconds(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var timestamp = toDate(dirtyDate).getTime();
  var amount = toInteger(dirtyAmount);
  return new Date(timestamp + amount);
}

var defaultOptions = {};
function getDefaultOptions() {
  return defaultOptions;
}

/**
 * Google Chrome as of 67.0.3396.87 introduced timezones with offset that includes seconds.
 * They usually appear for dates that denote time before the timezones were introduced
 * (e.g. for 'Europe/Prague' timezone the offset is GMT+00:57:44 before 1 October 1891
 * and GMT+01:00:00 after that date)
 *
 * Date#getTimezoneOffset returns the offset in minutes and would return 57 for the example above,
 * which would lead to incorrect calculations.
 *
 * This function returns the timezone offset in milliseconds that takes seconds in account.
 */
function getTimezoneOffsetInMilliseconds(date) {
  var utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds()));
  utcDate.setUTCFullYear(date.getFullYear());
  return date.getTime() - utcDate.getTime();
}

/**
 * @name isDate
 * @category Common Helpers
 * @summary Is the given value a date?
 *
 * @description
 * Returns true if the given value is an instance of Date. The function works for dates transferred across iframes.
 *
 * @param {*} value - the value to check
 * @returns {boolean} true if the given value is a date
 * @throws {TypeError} 1 arguments required
 *
 * @example
 * // For a valid date:
 * const result = isDate(new Date())
 * //=> true
 *
 * @example
 * // For an invalid date:
 * const result = isDate(new Date(NaN))
 * //=> true
 *
 * @example
 * // For some value:
 * const result = isDate('2014-02-31')
 * //=> false
 *
 * @example
 * // For an object:
 * const result = isDate({})
 * //=> false
 */
function isDate(value) {
  requiredArgs(1, arguments);
  return value instanceof Date || _typeof(value) === 'object' && Object.prototype.toString.call(value) === '[object Date]';
}

/**
 * @name isValid
 * @category Common Helpers
 * @summary Is the given date valid?
 *
 * @description
 * Returns false if argument is Invalid Date and true otherwise.
 * Argument is converted to Date using `toDate`. See [toDate]{@link https://date-fns.org/docs/toDate}
 * Invalid Date is a Date, whose time value is NaN.
 *
 * Time value of Date: http://es5.github.io/#x15.9.1.1
 *
 * @param {*} date - the date to check
 * @returns {Boolean} the date is valid
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // For the valid date:
 * const result = isValid(new Date(2014, 1, 31))
 * //=> true
 *
 * @example
 * // For the value, convertable into a date:
 * const result = isValid(1393804800000)
 * //=> true
 *
 * @example
 * // For the invalid date:
 * const result = isValid(new Date(''))
 * //=> false
 */
function isValid(dirtyDate) {
  requiredArgs(1, arguments);
  if (!isDate(dirtyDate) && typeof dirtyDate !== 'number') {
    return false;
  }
  var date = toDate(dirtyDate);
  return !isNaN(Number(date));
}

/**
 * @name subMilliseconds
 * @category Millisecond Helpers
 * @summary Subtract the specified number of milliseconds from the given date.
 *
 * @description
 * Subtract the specified number of milliseconds from the given date.
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of milliseconds to be subtracted. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the milliseconds subtracted
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Subtract 750 milliseconds from 10 July 2014 12:45:30.000:
 * const result = subMilliseconds(new Date(2014, 6, 10, 12, 45, 30, 0), 750)
 * //=> Thu Jul 10 2014 12:45:29.250
 */
function subMilliseconds(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var amount = toInteger(dirtyAmount);
  return addMilliseconds(dirtyDate, -amount);
}

var MILLISECONDS_IN_DAY = 86400000;
function getUTCDayOfYear(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var timestamp = date.getTime();
  date.setUTCMonth(0, 1);
  date.setUTCHours(0, 0, 0, 0);
  var startOfYearTimestamp = date.getTime();
  var difference = timestamp - startOfYearTimestamp;
  return Math.floor(difference / MILLISECONDS_IN_DAY) + 1;
}

function startOfUTCISOWeek(dirtyDate) {
  requiredArgs(1, arguments);
  var weekStartsOn = 1;
  var date = toDate(dirtyDate);
  var day = date.getUTCDay();
  var diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;
  date.setUTCDate(date.getUTCDate() - diff);
  date.setUTCHours(0, 0, 0, 0);
  return date;
}

function getUTCISOWeekYear(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var year = date.getUTCFullYear();
  var fourthOfJanuaryOfNextYear = new Date(0);
  fourthOfJanuaryOfNextYear.setUTCFullYear(year + 1, 0, 4);
  fourthOfJanuaryOfNextYear.setUTCHours(0, 0, 0, 0);
  var startOfNextYear = startOfUTCISOWeek(fourthOfJanuaryOfNextYear);
  var fourthOfJanuaryOfThisYear = new Date(0);
  fourthOfJanuaryOfThisYear.setUTCFullYear(year, 0, 4);
  fourthOfJanuaryOfThisYear.setUTCHours(0, 0, 0, 0);
  var startOfThisYear = startOfUTCISOWeek(fourthOfJanuaryOfThisYear);
  if (date.getTime() >= startOfNextYear.getTime()) {
    return year + 1;
  } else if (date.getTime() >= startOfThisYear.getTime()) {
    return year;
  } else {
    return year - 1;
  }
}

function startOfUTCISOWeekYear(dirtyDate) {
  requiredArgs(1, arguments);
  var year = getUTCISOWeekYear(dirtyDate);
  var fourthOfJanuary = new Date(0);
  fourthOfJanuary.setUTCFullYear(year, 0, 4);
  fourthOfJanuary.setUTCHours(0, 0, 0, 0);
  var date = startOfUTCISOWeek(fourthOfJanuary);
  return date;
}

var MILLISECONDS_IN_WEEK$1 = 604800000;
function getUTCISOWeek(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var diff = startOfUTCISOWeek(date).getTime() - startOfUTCISOWeekYear(date).getTime();

  // Round the number of days to the nearest integer
  // because the number of milliseconds in a week is not constant
  // (e.g. it's different in the week of the daylight saving time clock shift)
  return Math.round(diff / MILLISECONDS_IN_WEEK$1) + 1;
}

function startOfUTCWeek(dirtyDate, options) {
  var _ref, _ref2, _ref3, _options$weekStartsOn, _options$locale, _options$locale$optio, _defaultOptions$local, _defaultOptions$local2;
  requiredArgs(1, arguments);
  var defaultOptions = getDefaultOptions();
  var weekStartsOn = toInteger((_ref = (_ref2 = (_ref3 = (_options$weekStartsOn = options === null || options === void 0 ? void 0 : options.weekStartsOn) !== null && _options$weekStartsOn !== void 0 ? _options$weekStartsOn : options === null || options === void 0 ? void 0 : (_options$locale = options.locale) === null || _options$locale === void 0 ? void 0 : (_options$locale$optio = _options$locale.options) === null || _options$locale$optio === void 0 ? void 0 : _options$locale$optio.weekStartsOn) !== null && _ref3 !== void 0 ? _ref3 : defaultOptions.weekStartsOn) !== null && _ref2 !== void 0 ? _ref2 : (_defaultOptions$local = defaultOptions.locale) === null || _defaultOptions$local === void 0 ? void 0 : (_defaultOptions$local2 = _defaultOptions$local.options) === null || _defaultOptions$local2 === void 0 ? void 0 : _defaultOptions$local2.weekStartsOn) !== null && _ref !== void 0 ? _ref : 0);

  // Test if weekStartsOn is between 0 and 6 _and_ is not NaN
  if (!(weekStartsOn >= 0 && weekStartsOn <= 6)) {
    throw new RangeError('weekStartsOn must be between 0 and 6 inclusively');
  }
  var date = toDate(dirtyDate);
  var day = date.getUTCDay();
  var diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;
  date.setUTCDate(date.getUTCDate() - diff);
  date.setUTCHours(0, 0, 0, 0);
  return date;
}

function getUTCWeekYear(dirtyDate, options) {
  var _ref, _ref2, _ref3, _options$firstWeekCon, _options$locale, _options$locale$optio, _defaultOptions$local, _defaultOptions$local2;
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var year = date.getUTCFullYear();
  var defaultOptions = getDefaultOptions();
  var firstWeekContainsDate = toInteger((_ref = (_ref2 = (_ref3 = (_options$firstWeekCon = options === null || options === void 0 ? void 0 : options.firstWeekContainsDate) !== null && _options$firstWeekCon !== void 0 ? _options$firstWeekCon : options === null || options === void 0 ? void 0 : (_options$locale = options.locale) === null || _options$locale === void 0 ? void 0 : (_options$locale$optio = _options$locale.options) === null || _options$locale$optio === void 0 ? void 0 : _options$locale$optio.firstWeekContainsDate) !== null && _ref3 !== void 0 ? _ref3 : defaultOptions.firstWeekContainsDate) !== null && _ref2 !== void 0 ? _ref2 : (_defaultOptions$local = defaultOptions.locale) === null || _defaultOptions$local === void 0 ? void 0 : (_defaultOptions$local2 = _defaultOptions$local.options) === null || _defaultOptions$local2 === void 0 ? void 0 : _defaultOptions$local2.firstWeekContainsDate) !== null && _ref !== void 0 ? _ref : 1);

  // Test if weekStartsOn is between 1 and 7 _and_ is not NaN
  if (!(firstWeekContainsDate >= 1 && firstWeekContainsDate <= 7)) {
    throw new RangeError('firstWeekContainsDate must be between 1 and 7 inclusively');
  }
  var firstWeekOfNextYear = new Date(0);
  firstWeekOfNextYear.setUTCFullYear(year + 1, 0, firstWeekContainsDate);
  firstWeekOfNextYear.setUTCHours(0, 0, 0, 0);
  var startOfNextYear = startOfUTCWeek(firstWeekOfNextYear, options);
  var firstWeekOfThisYear = new Date(0);
  firstWeekOfThisYear.setUTCFullYear(year, 0, firstWeekContainsDate);
  firstWeekOfThisYear.setUTCHours(0, 0, 0, 0);
  var startOfThisYear = startOfUTCWeek(firstWeekOfThisYear, options);
  if (date.getTime() >= startOfNextYear.getTime()) {
    return year + 1;
  } else if (date.getTime() >= startOfThisYear.getTime()) {
    return year;
  } else {
    return year - 1;
  }
}

function startOfUTCWeekYear(dirtyDate, options) {
  var _ref, _ref2, _ref3, _options$firstWeekCon, _options$locale, _options$locale$optio, _defaultOptions$local, _defaultOptions$local2;
  requiredArgs(1, arguments);
  var defaultOptions = getDefaultOptions();
  var firstWeekContainsDate = toInteger((_ref = (_ref2 = (_ref3 = (_options$firstWeekCon = options === null || options === void 0 ? void 0 : options.firstWeekContainsDate) !== null && _options$firstWeekCon !== void 0 ? _options$firstWeekCon : options === null || options === void 0 ? void 0 : (_options$locale = options.locale) === null || _options$locale === void 0 ? void 0 : (_options$locale$optio = _options$locale.options) === null || _options$locale$optio === void 0 ? void 0 : _options$locale$optio.firstWeekContainsDate) !== null && _ref3 !== void 0 ? _ref3 : defaultOptions.firstWeekContainsDate) !== null && _ref2 !== void 0 ? _ref2 : (_defaultOptions$local = defaultOptions.locale) === null || _defaultOptions$local === void 0 ? void 0 : (_defaultOptions$local2 = _defaultOptions$local.options) === null || _defaultOptions$local2 === void 0 ? void 0 : _defaultOptions$local2.firstWeekContainsDate) !== null && _ref !== void 0 ? _ref : 1);
  var year = getUTCWeekYear(dirtyDate, options);
  var firstWeek = new Date(0);
  firstWeek.setUTCFullYear(year, 0, firstWeekContainsDate);
  firstWeek.setUTCHours(0, 0, 0, 0);
  var date = startOfUTCWeek(firstWeek, options);
  return date;
}

var MILLISECONDS_IN_WEEK = 604800000;
function getUTCWeek(dirtyDate, options) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var diff = startOfUTCWeek(date, options).getTime() - startOfUTCWeekYear(date, options).getTime();

  // Round the number of days to the nearest integer
  // because the number of milliseconds in a week is not constant
  // (e.g. it's different in the week of the daylight saving time clock shift)
  return Math.round(diff / MILLISECONDS_IN_WEEK) + 1;
}

function addLeadingZeros(number, targetLength) {
  var sign = number < 0 ? '-' : '';
  var output = Math.abs(number).toString();
  while (output.length < targetLength) {
    output = '0' + output;
  }
  return sign + output;
}

/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* |                                |
 * |  d  | Day of month                   |  D  |                                |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  m  | Minute                         |  M  | Month                          |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  y  | Year (abs)                     |  Y  |                                |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 */
var formatters$2 = {
  // Year
  y: function y(date, token) {
    // From http://www.unicode.org/reports/tr35/tr35-31/tr35-dates.html#Date_Format_tokens
    // | Year     |     y | yy |   yyy |  yyyy | yyyyy |
    // |----------|-------|----|-------|-------|-------|
    // | AD 1     |     1 | 01 |   001 |  0001 | 00001 |
    // | AD 12    |    12 | 12 |   012 |  0012 | 00012 |
    // | AD 123   |   123 | 23 |   123 |  0123 | 00123 |
    // | AD 1234  |  1234 | 34 |  1234 |  1234 | 01234 |
    // | AD 12345 | 12345 | 45 | 12345 | 12345 | 12345 |

    var signedYear = date.getUTCFullYear();
    // Returns 1 for 1 BC (which is year 0 in JavaScript)
    var year = signedYear > 0 ? signedYear : 1 - signedYear;
    return addLeadingZeros(token === 'yy' ? year % 100 : year, token.length);
  },
  // Month
  M: function M(date, token) {
    var month = date.getUTCMonth();
    return token === 'M' ? String(month + 1) : addLeadingZeros(month + 1, 2);
  },
  // Day of the month
  d: function d(date, token) {
    return addLeadingZeros(date.getUTCDate(), token.length);
  },
  // AM or PM
  a: function a(date, token) {
    var dayPeriodEnumValue = date.getUTCHours() / 12 >= 1 ? 'pm' : 'am';
    switch (token) {
      case 'a':
      case 'aa':
        return dayPeriodEnumValue.toUpperCase();
      case 'aaa':
        return dayPeriodEnumValue;
      case 'aaaaa':
        return dayPeriodEnumValue[0];
      case 'aaaa':
      default:
        return dayPeriodEnumValue === 'am' ? 'a.m.' : 'p.m.';
    }
  },
  // Hour [1-12]
  h: function h(date, token) {
    return addLeadingZeros(date.getUTCHours() % 12 || 12, token.length);
  },
  // Hour [0-23]
  H: function H(date, token) {
    return addLeadingZeros(date.getUTCHours(), token.length);
  },
  // Minute
  m: function m(date, token) {
    return addLeadingZeros(date.getUTCMinutes(), token.length);
  },
  // Second
  s: function s(date, token) {
    return addLeadingZeros(date.getUTCSeconds(), token.length);
  },
  // Fraction of second
  S: function S(date, token) {
    var numberOfDigits = token.length;
    var milliseconds = date.getUTCMilliseconds();
    var fractionalSeconds = Math.floor(milliseconds * Math.pow(10, numberOfDigits - 3));
    return addLeadingZeros(fractionalSeconds, token.length);
  }
};
var formatters$3 = formatters$2;

var dayPeriodEnum = {
  am: 'am',
  pm: 'pm',
  midnight: 'midnight',
  noon: 'noon',
  morning: 'morning',
  afternoon: 'afternoon',
  evening: 'evening',
  night: 'night'
};
/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* | Milliseconds in day            |
 * |  b  | AM, PM, noon, midnight         |  B  | Flexible day period            |
 * |  c  | Stand-alone local day of week  |  C* | Localized hour w/ day period   |
 * |  d  | Day of month                   |  D  | Day of year                    |
 * |  e  | Local day of week              |  E  | Day of week                    |
 * |  f  |                                |  F* | Day of week in month           |
 * |  g* | Modified Julian day            |  G  | Era                            |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  i! | ISO day of week                |  I! | ISO week of year               |
 * |  j* | Localized hour w/ day period   |  J* | Localized hour w/o day period  |
 * |  k  | Hour [1-24]                    |  K  | Hour [0-11]                    |
 * |  l* | (deprecated)                   |  L  | Stand-alone month              |
 * |  m  | Minute                         |  M  | Month                          |
 * |  n  |                                |  N  |                                |
 * |  o! | Ordinal number modifier        |  O  | Timezone (GMT)                 |
 * |  p! | Long localized time            |  P! | Long localized date            |
 * |  q  | Stand-alone quarter            |  Q  | Quarter                        |
 * |  r* | Related Gregorian year         |  R! | ISO week-numbering year        |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  t! | Seconds timestamp              |  T! | Milliseconds timestamp         |
 * |  u  | Extended year                  |  U* | Cyclic year                    |
 * |  v* | Timezone (generic non-locat.)  |  V* | Timezone (location)            |
 * |  w  | Local week of year             |  W* | Week of month                  |
 * |  x  | Timezone (ISO-8601 w/o Z)      |  X  | Timezone (ISO-8601)            |
 * |  y  | Year (abs)                     |  Y  | Local week-numbering year      |
 * |  z  | Timezone (specific non-locat.) |  Z* | Timezone (aliases)             |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 *
 * Letters marked by ! are non-standard, but implemented by date-fns:
 * - `o` modifies the previous token to turn it into an ordinal (see `format` docs)
 * - `i` is ISO day of week. For `i` and `ii` is returns numeric ISO week days,
 *   i.e. 7 for Sunday, 1 for Monday, etc.
 * - `I` is ISO week of year, as opposed to `w` which is local week of year.
 * - `R` is ISO week-numbering year, as opposed to `Y` which is local week-numbering year.
 *   `R` is supposed to be used in conjunction with `I` and `i`
 *   for universal ISO week-numbering date, whereas
 *   `Y` is supposed to be used in conjunction with `w` and `e`
 *   for week-numbering date specific to the locale.
 * - `P` is long localized date format
 * - `p` is long localized time format
 */

var formatters = {
  // Era
  G: function G(date, token, localize) {
    var era = date.getUTCFullYear() > 0 ? 1 : 0;
    switch (token) {
      // AD, BC
      case 'G':
      case 'GG':
      case 'GGG':
        return localize.era(era, {
          width: 'abbreviated'
        });
      // A, B
      case 'GGGGG':
        return localize.era(era, {
          width: 'narrow'
        });
      // Anno Domini, Before Christ
      case 'GGGG':
      default:
        return localize.era(era, {
          width: 'wide'
        });
    }
  },
  // Year
  y: function y(date, token, localize) {
    // Ordinal number
    if (token === 'yo') {
      var signedYear = date.getUTCFullYear();
      // Returns 1 for 1 BC (which is year 0 in JavaScript)
      var year = signedYear > 0 ? signedYear : 1 - signedYear;
      return localize.ordinalNumber(year, {
        unit: 'year'
      });
    }
    return formatters$3.y(date, token);
  },
  // Local week-numbering year
  Y: function Y(date, token, localize, options) {
    var signedWeekYear = getUTCWeekYear(date, options);
    // Returns 1 for 1 BC (which is year 0 in JavaScript)
    var weekYear = signedWeekYear > 0 ? signedWeekYear : 1 - signedWeekYear;

    // Two digit year
    if (token === 'YY') {
      var twoDigitYear = weekYear % 100;
      return addLeadingZeros(twoDigitYear, 2);
    }

    // Ordinal number
    if (token === 'Yo') {
      return localize.ordinalNumber(weekYear, {
        unit: 'year'
      });
    }

    // Padding
    return addLeadingZeros(weekYear, token.length);
  },
  // ISO week-numbering year
  R: function R(date, token) {
    var isoWeekYear = getUTCISOWeekYear(date);

    // Padding
    return addLeadingZeros(isoWeekYear, token.length);
  },
  // Extended year. This is a single number designating the year of this calendar system.
  // The main difference between `y` and `u` localizers are B.C. years:
  // | Year | `y` | `u` |
  // |------|-----|-----|
  // | AC 1 |   1 |   1 |
  // | BC 1 |   1 |   0 |
  // | BC 2 |   2 |  -1 |
  // Also `yy` always returns the last two digits of a year,
  // while `uu` pads single digit years to 2 characters and returns other years unchanged.
  u: function u(date, token) {
    var year = date.getUTCFullYear();
    return addLeadingZeros(year, token.length);
  },
  // Quarter
  Q: function Q(date, token, localize) {
    var quarter = Math.ceil((date.getUTCMonth() + 1) / 3);
    switch (token) {
      // 1, 2, 3, 4
      case 'Q':
        return String(quarter);
      // 01, 02, 03, 04
      case 'QQ':
        return addLeadingZeros(quarter, 2);
      // 1st, 2nd, 3rd, 4th
      case 'Qo':
        return localize.ordinalNumber(quarter, {
          unit: 'quarter'
        });
      // Q1, Q2, Q3, Q4
      case 'QQQ':
        return localize.quarter(quarter, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // 1, 2, 3, 4 (narrow quarter; could be not numerical)
      case 'QQQQQ':
        return localize.quarter(quarter, {
          width: 'narrow',
          context: 'formatting'
        });
      // 1st quarter, 2nd quarter, ...
      case 'QQQQ':
      default:
        return localize.quarter(quarter, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Stand-alone quarter
  q: function q(date, token, localize) {
    var quarter = Math.ceil((date.getUTCMonth() + 1) / 3);
    switch (token) {
      // 1, 2, 3, 4
      case 'q':
        return String(quarter);
      // 01, 02, 03, 04
      case 'qq':
        return addLeadingZeros(quarter, 2);
      // 1st, 2nd, 3rd, 4th
      case 'qo':
        return localize.ordinalNumber(quarter, {
          unit: 'quarter'
        });
      // Q1, Q2, Q3, Q4
      case 'qqq':
        return localize.quarter(quarter, {
          width: 'abbreviated',
          context: 'standalone'
        });
      // 1, 2, 3, 4 (narrow quarter; could be not numerical)
      case 'qqqqq':
        return localize.quarter(quarter, {
          width: 'narrow',
          context: 'standalone'
        });
      // 1st quarter, 2nd quarter, ...
      case 'qqqq':
      default:
        return localize.quarter(quarter, {
          width: 'wide',
          context: 'standalone'
        });
    }
  },
  // Month
  M: function M(date, token, localize) {
    var month = date.getUTCMonth();
    switch (token) {
      case 'M':
      case 'MM':
        return formatters$3.M(date, token);
      // 1st, 2nd, ..., 12th
      case 'Mo':
        return localize.ordinalNumber(month + 1, {
          unit: 'month'
        });
      // Jan, Feb, ..., Dec
      case 'MMM':
        return localize.month(month, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // J, F, ..., D
      case 'MMMMM':
        return localize.month(month, {
          width: 'narrow',
          context: 'formatting'
        });
      // January, February, ..., December
      case 'MMMM':
      default:
        return localize.month(month, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Stand-alone month
  L: function L(date, token, localize) {
    var month = date.getUTCMonth();
    switch (token) {
      // 1, 2, ..., 12
      case 'L':
        return String(month + 1);
      // 01, 02, ..., 12
      case 'LL':
        return addLeadingZeros(month + 1, 2);
      // 1st, 2nd, ..., 12th
      case 'Lo':
        return localize.ordinalNumber(month + 1, {
          unit: 'month'
        });
      // Jan, Feb, ..., Dec
      case 'LLL':
        return localize.month(month, {
          width: 'abbreviated',
          context: 'standalone'
        });
      // J, F, ..., D
      case 'LLLLL':
        return localize.month(month, {
          width: 'narrow',
          context: 'standalone'
        });
      // January, February, ..., December
      case 'LLLL':
      default:
        return localize.month(month, {
          width: 'wide',
          context: 'standalone'
        });
    }
  },
  // Local week of year
  w: function w(date, token, localize, options) {
    var week = getUTCWeek(date, options);
    if (token === 'wo') {
      return localize.ordinalNumber(week, {
        unit: 'week'
      });
    }
    return addLeadingZeros(week, token.length);
  },
  // ISO week of year
  I: function I(date, token, localize) {
    var isoWeek = getUTCISOWeek(date);
    if (token === 'Io') {
      return localize.ordinalNumber(isoWeek, {
        unit: 'week'
      });
    }
    return addLeadingZeros(isoWeek, token.length);
  },
  // Day of the month
  d: function d(date, token, localize) {
    if (token === 'do') {
      return localize.ordinalNumber(date.getUTCDate(), {
        unit: 'date'
      });
    }
    return formatters$3.d(date, token);
  },
  // Day of year
  D: function D(date, token, localize) {
    var dayOfYear = getUTCDayOfYear(date);
    if (token === 'Do') {
      return localize.ordinalNumber(dayOfYear, {
        unit: 'dayOfYear'
      });
    }
    return addLeadingZeros(dayOfYear, token.length);
  },
  // Day of week
  E: function E(date, token, localize) {
    var dayOfWeek = date.getUTCDay();
    switch (token) {
      // Tue
      case 'E':
      case 'EE':
      case 'EEE':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // T
      case 'EEEEE':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'formatting'
        });
      // Tu
      case 'EEEEEE':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'formatting'
        });
      // Tuesday
      case 'EEEE':
      default:
        return localize.day(dayOfWeek, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Local day of week
  e: function e(date, token, localize, options) {
    var dayOfWeek = date.getUTCDay();
    var localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;
    switch (token) {
      // Numerical value (Nth day of week with current locale or weekStartsOn)
      case 'e':
        return String(localDayOfWeek);
      // Padded numerical value
      case 'ee':
        return addLeadingZeros(localDayOfWeek, 2);
      // 1st, 2nd, ..., 7th
      case 'eo':
        return localize.ordinalNumber(localDayOfWeek, {
          unit: 'day'
        });
      case 'eee':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // T
      case 'eeeee':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'formatting'
        });
      // Tu
      case 'eeeeee':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'formatting'
        });
      // Tuesday
      case 'eeee':
      default:
        return localize.day(dayOfWeek, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Stand-alone local day of week
  c: function c(date, token, localize, options) {
    var dayOfWeek = date.getUTCDay();
    var localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;
    switch (token) {
      // Numerical value (same as in `e`)
      case 'c':
        return String(localDayOfWeek);
      // Padded numerical value
      case 'cc':
        return addLeadingZeros(localDayOfWeek, token.length);
      // 1st, 2nd, ..., 7th
      case 'co':
        return localize.ordinalNumber(localDayOfWeek, {
          unit: 'day'
        });
      case 'ccc':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'standalone'
        });
      // T
      case 'ccccc':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'standalone'
        });
      // Tu
      case 'cccccc':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'standalone'
        });
      // Tuesday
      case 'cccc':
      default:
        return localize.day(dayOfWeek, {
          width: 'wide',
          context: 'standalone'
        });
    }
  },
  // ISO day of week
  i: function i(date, token, localize) {
    var dayOfWeek = date.getUTCDay();
    var isoDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;
    switch (token) {
      // 2
      case 'i':
        return String(isoDayOfWeek);
      // 02
      case 'ii':
        return addLeadingZeros(isoDayOfWeek, token.length);
      // 2nd
      case 'io':
        return localize.ordinalNumber(isoDayOfWeek, {
          unit: 'day'
        });
      // Tue
      case 'iii':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // T
      case 'iiiii':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'formatting'
        });
      // Tu
      case 'iiiiii':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'formatting'
        });
      // Tuesday
      case 'iiii':
      default:
        return localize.day(dayOfWeek, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // AM or PM
  a: function a(date, token, localize) {
    var hours = date.getUTCHours();
    var dayPeriodEnumValue = hours / 12 >= 1 ? 'pm' : 'am';
    switch (token) {
      case 'a':
      case 'aa':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        });
      case 'aaa':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        }).toLowerCase();
      case 'aaaaa':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'narrow',
          context: 'formatting'
        });
      case 'aaaa':
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // AM, PM, midnight, noon
  b: function b(date, token, localize) {
    var hours = date.getUTCHours();
    var dayPeriodEnumValue;
    if (hours === 12) {
      dayPeriodEnumValue = dayPeriodEnum.noon;
    } else if (hours === 0) {
      dayPeriodEnumValue = dayPeriodEnum.midnight;
    } else {
      dayPeriodEnumValue = hours / 12 >= 1 ? 'pm' : 'am';
    }
    switch (token) {
      case 'b':
      case 'bb':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        });
      case 'bbb':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        }).toLowerCase();
      case 'bbbbb':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'narrow',
          context: 'formatting'
        });
      case 'bbbb':
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // in the morning, in the afternoon, in the evening, at night
  B: function B(date, token, localize) {
    var hours = date.getUTCHours();
    var dayPeriodEnumValue;
    if (hours >= 17) {
      dayPeriodEnumValue = dayPeriodEnum.evening;
    } else if (hours >= 12) {
      dayPeriodEnumValue = dayPeriodEnum.afternoon;
    } else if (hours >= 4) {
      dayPeriodEnumValue = dayPeriodEnum.morning;
    } else {
      dayPeriodEnumValue = dayPeriodEnum.night;
    }
    switch (token) {
      case 'B':
      case 'BB':
      case 'BBB':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        });
      case 'BBBBB':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'narrow',
          context: 'formatting'
        });
      case 'BBBB':
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Hour [1-12]
  h: function h(date, token, localize) {
    if (token === 'ho') {
      var hours = date.getUTCHours() % 12;
      if (hours === 0) hours = 12;
      return localize.ordinalNumber(hours, {
        unit: 'hour'
      });
    }
    return formatters$3.h(date, token);
  },
  // Hour [0-23]
  H: function H(date, token, localize) {
    if (token === 'Ho') {
      return localize.ordinalNumber(date.getUTCHours(), {
        unit: 'hour'
      });
    }
    return formatters$3.H(date, token);
  },
  // Hour [0-11]
  K: function K(date, token, localize) {
    var hours = date.getUTCHours() % 12;
    if (token === 'Ko') {
      return localize.ordinalNumber(hours, {
        unit: 'hour'
      });
    }
    return addLeadingZeros(hours, token.length);
  },
  // Hour [1-24]
  k: function k(date, token, localize) {
    var hours = date.getUTCHours();
    if (hours === 0) hours = 24;
    if (token === 'ko') {
      return localize.ordinalNumber(hours, {
        unit: 'hour'
      });
    }
    return addLeadingZeros(hours, token.length);
  },
  // Minute
  m: function m(date, token, localize) {
    if (token === 'mo') {
      return localize.ordinalNumber(date.getUTCMinutes(), {
        unit: 'minute'
      });
    }
    return formatters$3.m(date, token);
  },
  // Second
  s: function s(date, token, localize) {
    if (token === 'so') {
      return localize.ordinalNumber(date.getUTCSeconds(), {
        unit: 'second'
      });
    }
    return formatters$3.s(date, token);
  },
  // Fraction of second
  S: function S(date, token) {
    return formatters$3.S(date, token);
  },
  // Timezone (ISO-8601. If offset is 0, output is always `'Z'`)
  X: function X(date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();
    if (timezoneOffset === 0) {
      return 'Z';
    }
    switch (token) {
      // Hours and optional minutes
      case 'X':
        return formatTimezoneWithOptionalMinutes(timezoneOffset);

      // Hours, minutes and optional seconds without `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XX`
      case 'XXXX':
      case 'XX':
        // Hours and minutes without `:` delimiter
        return formatTimezone(timezoneOffset);

      // Hours, minutes and optional seconds with `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XXX`
      case 'XXXXX':
      case 'XXX': // Hours and minutes with `:` delimiter
      default:
        return formatTimezone(timezoneOffset, ':');
    }
  },
  // Timezone (ISO-8601. If offset is 0, output is `'+00:00'` or equivalent)
  x: function x(date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();
    switch (token) {
      // Hours and optional minutes
      case 'x':
        return formatTimezoneWithOptionalMinutes(timezoneOffset);

      // Hours, minutes and optional seconds without `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xx`
      case 'xxxx':
      case 'xx':
        // Hours and minutes without `:` delimiter
        return formatTimezone(timezoneOffset);

      // Hours, minutes and optional seconds with `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xxx`
      case 'xxxxx':
      case 'xxx': // Hours and minutes with `:` delimiter
      default:
        return formatTimezone(timezoneOffset, ':');
    }
  },
  // Timezone (GMT)
  O: function O(date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();
    switch (token) {
      // Short
      case 'O':
      case 'OO':
      case 'OOO':
        return 'GMT' + formatTimezoneShort(timezoneOffset, ':');
      // Long
      case 'OOOO':
      default:
        return 'GMT' + formatTimezone(timezoneOffset, ':');
    }
  },
  // Timezone (specific non-location)
  z: function z(date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();
    switch (token) {
      // Short
      case 'z':
      case 'zz':
      case 'zzz':
        return 'GMT' + formatTimezoneShort(timezoneOffset, ':');
      // Long
      case 'zzzz':
      default:
        return 'GMT' + formatTimezone(timezoneOffset, ':');
    }
  },
  // Seconds timestamp
  t: function t(date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timestamp = Math.floor(originalDate.getTime() / 1000);
    return addLeadingZeros(timestamp, token.length);
  },
  // Milliseconds timestamp
  T: function T(date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timestamp = originalDate.getTime();
    return addLeadingZeros(timestamp, token.length);
  }
};
function formatTimezoneShort(offset, dirtyDelimiter) {
  var sign = offset > 0 ? '-' : '+';
  var absOffset = Math.abs(offset);
  var hours = Math.floor(absOffset / 60);
  var minutes = absOffset % 60;
  if (minutes === 0) {
    return sign + String(hours);
  }
  var delimiter = dirtyDelimiter || '';
  return sign + String(hours) + delimiter + addLeadingZeros(minutes, 2);
}
function formatTimezoneWithOptionalMinutes(offset, dirtyDelimiter) {
  if (offset % 60 === 0) {
    var sign = offset > 0 ? '-' : '+';
    return sign + addLeadingZeros(Math.abs(offset) / 60, 2);
  }
  return formatTimezone(offset, dirtyDelimiter);
}
function formatTimezone(offset, dirtyDelimiter) {
  var delimiter = dirtyDelimiter || '';
  var sign = offset > 0 ? '-' : '+';
  var absOffset = Math.abs(offset);
  var hours = addLeadingZeros(Math.floor(absOffset / 60), 2);
  var minutes = addLeadingZeros(absOffset % 60, 2);
  return sign + hours + delimiter + minutes;
}
var formatters$1 = formatters;

var dateLongFormatter = function dateLongFormatter(pattern, formatLong) {
  switch (pattern) {
    case 'P':
      return formatLong.date({
        width: 'short'
      });
    case 'PP':
      return formatLong.date({
        width: 'medium'
      });
    case 'PPP':
      return formatLong.date({
        width: 'long'
      });
    case 'PPPP':
    default:
      return formatLong.date({
        width: 'full'
      });
  }
};
var timeLongFormatter = function timeLongFormatter(pattern, formatLong) {
  switch (pattern) {
    case 'p':
      return formatLong.time({
        width: 'short'
      });
    case 'pp':
      return formatLong.time({
        width: 'medium'
      });
    case 'ppp':
      return formatLong.time({
        width: 'long'
      });
    case 'pppp':
    default:
      return formatLong.time({
        width: 'full'
      });
  }
};
var dateTimeLongFormatter = function dateTimeLongFormatter(pattern, formatLong) {
  var matchResult = pattern.match(/(P+)(p+)?/) || [];
  var datePattern = matchResult[1];
  var timePattern = matchResult[2];
  if (!timePattern) {
    return dateLongFormatter(pattern, formatLong);
  }
  var dateTimeFormat;
  switch (datePattern) {
    case 'P':
      dateTimeFormat = formatLong.dateTime({
        width: 'short'
      });
      break;
    case 'PP':
      dateTimeFormat = formatLong.dateTime({
        width: 'medium'
      });
      break;
    case 'PPP':
      dateTimeFormat = formatLong.dateTime({
        width: 'long'
      });
      break;
    case 'PPPP':
    default:
      dateTimeFormat = formatLong.dateTime({
        width: 'full'
      });
      break;
  }
  return dateTimeFormat.replace('{{date}}', dateLongFormatter(datePattern, formatLong)).replace('{{time}}', timeLongFormatter(timePattern, formatLong));
};
var longFormatters = {
  p: timeLongFormatter,
  P: dateTimeLongFormatter
};
var longFormatters$1 = longFormatters;

var protectedDayOfYearTokens = ['D', 'DD'];
var protectedWeekYearTokens = ['YY', 'YYYY'];
function isProtectedDayOfYearToken(token) {
  return protectedDayOfYearTokens.indexOf(token) !== -1;
}
function isProtectedWeekYearToken(token) {
  return protectedWeekYearTokens.indexOf(token) !== -1;
}
function throwProtectedError(token, format, input) {
  if (token === 'YYYY') {
    throw new RangeError("Use `yyyy` instead of `YYYY` (in `".concat(format, "`) for formatting years to the input `").concat(input, "`; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md"));
  } else if (token === 'YY') {
    throw new RangeError("Use `yy` instead of `YY` (in `".concat(format, "`) for formatting years to the input `").concat(input, "`; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md"));
  } else if (token === 'D') {
    throw new RangeError("Use `d` instead of `D` (in `".concat(format, "`) for formatting days of the month to the input `").concat(input, "`; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md"));
  } else if (token === 'DD') {
    throw new RangeError("Use `dd` instead of `DD` (in `".concat(format, "`) for formatting days of the month to the input `").concat(input, "`; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md"));
  }
}

var formatDistanceLocale = {
  lessThanXSeconds: {
    one: 'less than a second',
    other: 'less than {{count}} seconds'
  },
  xSeconds: {
    one: '1 second',
    other: '{{count}} seconds'
  },
  halfAMinute: 'half a minute',
  lessThanXMinutes: {
    one: 'less than a minute',
    other: 'less than {{count}} minutes'
  },
  xMinutes: {
    one: '1 minute',
    other: '{{count}} minutes'
  },
  aboutXHours: {
    one: 'about 1 hour',
    other: 'about {{count}} hours'
  },
  xHours: {
    one: '1 hour',
    other: '{{count}} hours'
  },
  xDays: {
    one: '1 day',
    other: '{{count}} days'
  },
  aboutXWeeks: {
    one: 'about 1 week',
    other: 'about {{count}} weeks'
  },
  xWeeks: {
    one: '1 week',
    other: '{{count}} weeks'
  },
  aboutXMonths: {
    one: 'about 1 month',
    other: 'about {{count}} months'
  },
  xMonths: {
    one: '1 month',
    other: '{{count}} months'
  },
  aboutXYears: {
    one: 'about 1 year',
    other: 'about {{count}} years'
  },
  xYears: {
    one: '1 year',
    other: '{{count}} years'
  },
  overXYears: {
    one: 'over 1 year',
    other: 'over {{count}} years'
  },
  almostXYears: {
    one: 'almost 1 year',
    other: 'almost {{count}} years'
  }
};
var formatDistance = function formatDistance(token, count, options) {
  var result;
  var tokenValue = formatDistanceLocale[token];
  if (typeof tokenValue === 'string') {
    result = tokenValue;
  } else if (count === 1) {
    result = tokenValue.one;
  } else {
    result = tokenValue.other.replace('{{count}}', count.toString());
  }
  if (options !== null && options !== void 0 && options.addSuffix) {
    if (options.comparison && options.comparison > 0) {
      return 'in ' + result;
    } else {
      return result + ' ago';
    }
  }
  return result;
};
var formatDistance$1 = formatDistance;

function buildFormatLongFn(args) {
  return function () {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    // TODO: Remove String()
    var width = options.width ? String(options.width) : args.defaultWidth;
    var format = args.formats[width] || args.formats[args.defaultWidth];
    return format;
  };
}

var dateFormats = {
  full: 'EEEE, MMMM do, y',
  long: 'MMMM do, y',
  medium: 'MMM d, y',
  short: 'MM/dd/yyyy'
};
var timeFormats = {
  full: 'h:mm:ss a zzzz',
  long: 'h:mm:ss a z',
  medium: 'h:mm:ss a',
  short: 'h:mm a'
};
var dateTimeFormats = {
  full: "{{date}} 'at' {{time}}",
  long: "{{date}} 'at' {{time}}",
  medium: '{{date}}, {{time}}',
  short: '{{date}}, {{time}}'
};
var formatLong = {
  date: buildFormatLongFn({
    formats: dateFormats,
    defaultWidth: 'full'
  }),
  time: buildFormatLongFn({
    formats: timeFormats,
    defaultWidth: 'full'
  }),
  dateTime: buildFormatLongFn({
    formats: dateTimeFormats,
    defaultWidth: 'full'
  })
};
var formatLong$1 = formatLong;

var formatRelativeLocale = {
  lastWeek: "'last' eeee 'at' p",
  yesterday: "'yesterday at' p",
  today: "'today at' p",
  tomorrow: "'tomorrow at' p",
  nextWeek: "eeee 'at' p",
  other: 'P'
};
var formatRelative = function formatRelative(token, _date, _baseDate, _options) {
  return formatRelativeLocale[token];
};
var formatRelative$1 = formatRelative;

function buildLocalizeFn(args) {
  return function (dirtyIndex, options) {
    var context = options !== null && options !== void 0 && options.context ? String(options.context) : 'standalone';
    var valuesArray;
    if (context === 'formatting' && args.formattingValues) {
      var defaultWidth = args.defaultFormattingWidth || args.defaultWidth;
      var width = options !== null && options !== void 0 && options.width ? String(options.width) : defaultWidth;
      valuesArray = args.formattingValues[width] || args.formattingValues[defaultWidth];
    } else {
      var _defaultWidth = args.defaultWidth;
      var _width = options !== null && options !== void 0 && options.width ? String(options.width) : args.defaultWidth;
      valuesArray = args.values[_width] || args.values[_defaultWidth];
    }
    var index = args.argumentCallback ? args.argumentCallback(dirtyIndex) : dirtyIndex;
    // @ts-ignore: For some reason TypeScript just don't want to match it, no matter how hard we try. I challenge you to try to remove it!
    return valuesArray[index];
  };
}

var eraValues = {
  narrow: ['B', 'A'],
  abbreviated: ['BC', 'AD'],
  wide: ['Before Christ', 'Anno Domini']
};
var quarterValues = {
  narrow: ['1', '2', '3', '4'],
  abbreviated: ['Q1', 'Q2', 'Q3', 'Q4'],
  wide: ['1st quarter', '2nd quarter', '3rd quarter', '4th quarter']
};

// Note: in English, the names of days of the week and months are capitalized.
// If you are making a new locale based on this one, check if the same is true for the language you're working on.
// Generally, formatted dates should look like they are in the middle of a sentence,
// e.g. in Spanish language the weekdays and months should be in the lowercase.
var monthValues = {
  narrow: ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'],
  abbreviated: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  wide: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
};
var dayValues = {
  narrow: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
  short: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  abbreviated: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  wide: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
};
var dayPeriodValues = {
  narrow: {
    am: 'a',
    pm: 'p',
    midnight: 'mi',
    noon: 'n',
    morning: 'morning',
    afternoon: 'afternoon',
    evening: 'evening',
    night: 'night'
  },
  abbreviated: {
    am: 'AM',
    pm: 'PM',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'morning',
    afternoon: 'afternoon',
    evening: 'evening',
    night: 'night'
  },
  wide: {
    am: 'a.m.',
    pm: 'p.m.',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'morning',
    afternoon: 'afternoon',
    evening: 'evening',
    night: 'night'
  }
};
var formattingDayPeriodValues = {
  narrow: {
    am: 'a',
    pm: 'p',
    midnight: 'mi',
    noon: 'n',
    morning: 'in the morning',
    afternoon: 'in the afternoon',
    evening: 'in the evening',
    night: 'at night'
  },
  abbreviated: {
    am: 'AM',
    pm: 'PM',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'in the morning',
    afternoon: 'in the afternoon',
    evening: 'in the evening',
    night: 'at night'
  },
  wide: {
    am: 'a.m.',
    pm: 'p.m.',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'in the morning',
    afternoon: 'in the afternoon',
    evening: 'in the evening',
    night: 'at night'
  }
};
var ordinalNumber = function ordinalNumber(dirtyNumber, _options) {
  var number = Number(dirtyNumber);

  // If ordinal numbers depend on context, for example,
  // if they are different for different grammatical genders,
  // use `options.unit`.
  //
  // `unit` can be 'year', 'quarter', 'month', 'week', 'date', 'dayOfYear',
  // 'day', 'hour', 'minute', 'second'.

  var rem100 = number % 100;
  if (rem100 > 20 || rem100 < 10) {
    switch (rem100 % 10) {
      case 1:
        return number + 'st';
      case 2:
        return number + 'nd';
      case 3:
        return number + 'rd';
    }
  }
  return number + 'th';
};
var localize = {
  ordinalNumber: ordinalNumber,
  era: buildLocalizeFn({
    values: eraValues,
    defaultWidth: 'wide'
  }),
  quarter: buildLocalizeFn({
    values: quarterValues,
    defaultWidth: 'wide',
    argumentCallback: function argumentCallback(quarter) {
      return quarter - 1;
    }
  }),
  month: buildLocalizeFn({
    values: monthValues,
    defaultWidth: 'wide'
  }),
  day: buildLocalizeFn({
    values: dayValues,
    defaultWidth: 'wide'
  }),
  dayPeriod: buildLocalizeFn({
    values: dayPeriodValues,
    defaultWidth: 'wide',
    formattingValues: formattingDayPeriodValues,
    defaultFormattingWidth: 'wide'
  })
};
var localize$1 = localize;

function buildMatchFn(args) {
  return function (string) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var width = options.width;
    var matchPattern = width && args.matchPatterns[width] || args.matchPatterns[args.defaultMatchWidth];
    var matchResult = string.match(matchPattern);
    if (!matchResult) {
      return null;
    }
    var matchedString = matchResult[0];
    var parsePatterns = width && args.parsePatterns[width] || args.parsePatterns[args.defaultParseWidth];
    var key = Array.isArray(parsePatterns) ? findIndex(parsePatterns, function (pattern) {
      return pattern.test(matchedString);
    }) : findKey(parsePatterns, function (pattern) {
      return pattern.test(matchedString);
    });
    var value;
    value = args.valueCallback ? args.valueCallback(key) : key;
    value = options.valueCallback ? options.valueCallback(value) : value;
    var rest = string.slice(matchedString.length);
    return {
      value: value,
      rest: rest
    };
  };
}
function findKey(object, predicate) {
  for (var key in object) {
    if (object.hasOwnProperty(key) && predicate(object[key])) {
      return key;
    }
  }
  return undefined;
}
function findIndex(array, predicate) {
  for (var key = 0; key < array.length; key++) {
    if (predicate(array[key])) {
      return key;
    }
  }
  return undefined;
}

function buildMatchPatternFn(args) {
  return function (string) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var matchResult = string.match(args.matchPattern);
    if (!matchResult) return null;
    var matchedString = matchResult[0];
    var parseResult = string.match(args.parsePattern);
    if (!parseResult) return null;
    var value = args.valueCallback ? args.valueCallback(parseResult[0]) : parseResult[0];
    value = options.valueCallback ? options.valueCallback(value) : value;
    var rest = string.slice(matchedString.length);
    return {
      value: value,
      rest: rest
    };
  };
}

var matchOrdinalNumberPattern = /^(\d+)(th|st|nd|rd)?/i;
var parseOrdinalNumberPattern = /\d+/i;
var matchEraPatterns = {
  narrow: /^(b|a)/i,
  abbreviated: /^(b\.?\s?c\.?|b\.?\s?c\.?\s?e\.?|a\.?\s?d\.?|c\.?\s?e\.?)/i,
  wide: /^(before christ|before common era|anno domini|common era)/i
};
var parseEraPatterns = {
  any: [/^b/i, /^(a|c)/i]
};
var matchQuarterPatterns = {
  narrow: /^[1234]/i,
  abbreviated: /^q[1234]/i,
  wide: /^[1234](th|st|nd|rd)? quarter/i
};
var parseQuarterPatterns = {
  any: [/1/i, /2/i, /3/i, /4/i]
};
var matchMonthPatterns = {
  narrow: /^[jfmasond]/i,
  abbreviated: /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,
  wide: /^(january|february|march|april|may|june|july|august|september|october|november|december)/i
};
var parseMonthPatterns = {
  narrow: [/^j/i, /^f/i, /^m/i, /^a/i, /^m/i, /^j/i, /^j/i, /^a/i, /^s/i, /^o/i, /^n/i, /^d/i],
  any: [/^ja/i, /^f/i, /^mar/i, /^ap/i, /^may/i, /^jun/i, /^jul/i, /^au/i, /^s/i, /^o/i, /^n/i, /^d/i]
};
var matchDayPatterns = {
  narrow: /^[smtwf]/i,
  short: /^(su|mo|tu|we|th|fr|sa)/i,
  abbreviated: /^(sun|mon|tue|wed|thu|fri|sat)/i,
  wide: /^(sunday|monday|tuesday|wednesday|thursday|friday|saturday)/i
};
var parseDayPatterns = {
  narrow: [/^s/i, /^m/i, /^t/i, /^w/i, /^t/i, /^f/i, /^s/i],
  any: [/^su/i, /^m/i, /^tu/i, /^w/i, /^th/i, /^f/i, /^sa/i]
};
var matchDayPeriodPatterns = {
  narrow: /^(a|p|mi|n|(in the|at) (morning|afternoon|evening|night))/i,
  any: /^([ap]\.?\s?m\.?|midnight|noon|(in the|at) (morning|afternoon|evening|night))/i
};
var parseDayPeriodPatterns = {
  any: {
    am: /^a/i,
    pm: /^p/i,
    midnight: /^mi/i,
    noon: /^no/i,
    morning: /morning/i,
    afternoon: /afternoon/i,
    evening: /evening/i,
    night: /night/i
  }
};
var match = {
  ordinalNumber: buildMatchPatternFn({
    matchPattern: matchOrdinalNumberPattern,
    parsePattern: parseOrdinalNumberPattern,
    valueCallback: function valueCallback(value) {
      return parseInt(value, 10);
    }
  }),
  era: buildMatchFn({
    matchPatterns: matchEraPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseEraPatterns,
    defaultParseWidth: 'any'
  }),
  quarter: buildMatchFn({
    matchPatterns: matchQuarterPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseQuarterPatterns,
    defaultParseWidth: 'any',
    valueCallback: function valueCallback(index) {
      return index + 1;
    }
  }),
  month: buildMatchFn({
    matchPatterns: matchMonthPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseMonthPatterns,
    defaultParseWidth: 'any'
  }),
  day: buildMatchFn({
    matchPatterns: matchDayPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseDayPatterns,
    defaultParseWidth: 'any'
  }),
  dayPeriod: buildMatchFn({
    matchPatterns: matchDayPeriodPatterns,
    defaultMatchWidth: 'any',
    parsePatterns: parseDayPeriodPatterns,
    defaultParseWidth: 'any'
  })
};
var match$1 = match;

/**
 * @type {Locale}
 * @category Locales
 * @summary English locale (United States).
 * @language English
 * @iso-639-2 eng
 * @author Sasha Koss [@kossnocorp]{@link https://github.com/kossnocorp}
 * @author Lesha Koss [@leshakoss]{@link https://github.com/leshakoss}
 */
var locale = {
  code: 'en-US',
  formatDistance: formatDistance$1,
  formatLong: formatLong$1,
  formatRelative: formatRelative$1,
  localize: localize$1,
  match: match$1,
  options: {
    weekStartsOn: 0 /* Sunday */,
    firstWeekContainsDate: 1
  }
};
var defaultLocale = locale;

// - [yYQqMLwIdDecihHKkms]o matches any available ordinal number token
//   (one of the certain letters followed by `o`)
// - (\w)\1* matches any sequences of the same letter
// - '' matches two quote characters in a row
// - '(''|[^'])+('|$) matches anything surrounded by two quote characters ('),
//   except a single quote symbol, which ends the sequence.
//   Two quote characters do not end the sequence.
//   If there is no matching single quote
//   then the sequence will continue until the end of the string.
// - . matches any single character unmatched by previous parts of the RegExps
var formattingTokensRegExp = /[yYQqMLwIdDecihHKkms]o|(\w)\1*|''|'(''|[^'])+('|$)|./g;

// This RegExp catches symbols escaped by quotes, and also
// sequences of symbols P, p, and the combinations like `PPPPPPPppppp`
var longFormattingTokensRegExp = /P+p+|P+|p+|''|'(''|[^'])+('|$)|./g;
var escapedStringRegExp = /^'([^]*?)'?$/;
var doubleQuoteRegExp = /''/g;
var unescapedLatinCharacterRegExp = /[a-zA-Z]/;

/**
 * @name format
 * @category Common Helpers
 * @summary Format the date.
 *
 * @description
 * Return the formatted date string in the given format. The result may vary by locale.
 *
 * > ⚠️ Please note that the `format` tokens differ from Moment.js and other libraries.
 * > See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * The characters wrapped between two single quotes characters (') are escaped.
 * Two single quotes in a row, whether inside or outside a quoted sequence, represent a 'real' single quote.
 * (see the last example)
 *
 * Format of the string is based on Unicode Technical Standard #35:
 * https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * with a few additions (see note 7 below the table).
 *
 * Accepted patterns:
 * | Unit                            | Pattern | Result examples                   | Notes |
 * |---------------------------------|---------|-----------------------------------|-------|
 * | Era                             | G..GGG  | AD, BC                            |       |
 * |                                 | GGGG    | Anno Domini, Before Christ        | 2     |
 * |                                 | GGGGG   | A, B                              |       |
 * | Calendar year                   | y       | 44, 1, 1900, 2017                 | 5     |
 * |                                 | yo      | 44th, 1st, 0th, 17th              | 5,7   |
 * |                                 | yy      | 44, 01, 00, 17                    | 5     |
 * |                                 | yyy     | 044, 001, 1900, 2017              | 5     |
 * |                                 | yyyy    | 0044, 0001, 1900, 2017            | 5     |
 * |                                 | yyyyy   | ...                               | 3,5   |
 * | Local week-numbering year       | Y       | 44, 1, 1900, 2017                 | 5     |
 * |                                 | Yo      | 44th, 1st, 1900th, 2017th         | 5,7   |
 * |                                 | YY      | 44, 01, 00, 17                    | 5,8   |
 * |                                 | YYY     | 044, 001, 1900, 2017              | 5     |
 * |                                 | YYYY    | 0044, 0001, 1900, 2017            | 5,8   |
 * |                                 | YYYYY   | ...                               | 3,5   |
 * | ISO week-numbering year         | R       | -43, 0, 1, 1900, 2017             | 5,7   |
 * |                                 | RR      | -43, 00, 01, 1900, 2017           | 5,7   |
 * |                                 | RRR     | -043, 000, 001, 1900, 2017        | 5,7   |
 * |                                 | RRRR    | -0043, 0000, 0001, 1900, 2017     | 5,7   |
 * |                                 | RRRRR   | ...                               | 3,5,7 |
 * | Extended year                   | u       | -43, 0, 1, 1900, 2017             | 5     |
 * |                                 | uu      | -43, 01, 1900, 2017               | 5     |
 * |                                 | uuu     | -043, 001, 1900, 2017             | 5     |
 * |                                 | uuuu    | -0043, 0001, 1900, 2017           | 5     |
 * |                                 | uuuuu   | ...                               | 3,5   |
 * | Quarter (formatting)            | Q       | 1, 2, 3, 4                        |       |
 * |                                 | Qo      | 1st, 2nd, 3rd, 4th                | 7     |
 * |                                 | QQ      | 01, 02, 03, 04                    |       |
 * |                                 | QQQ     | Q1, Q2, Q3, Q4                    |       |
 * |                                 | QQQQ    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 | QQQQQ   | 1, 2, 3, 4                        | 4     |
 * | Quarter (stand-alone)           | q       | 1, 2, 3, 4                        |       |
 * |                                 | qo      | 1st, 2nd, 3rd, 4th                | 7     |
 * |                                 | qq      | 01, 02, 03, 04                    |       |
 * |                                 | qqq     | Q1, Q2, Q3, Q4                    |       |
 * |                                 | qqqq    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 | qqqqq   | 1, 2, 3, 4                        | 4     |
 * | Month (formatting)              | M       | 1, 2, ..., 12                     |       |
 * |                                 | Mo      | 1st, 2nd, ..., 12th               | 7     |
 * |                                 | MM      | 01, 02, ..., 12                   |       |
 * |                                 | MMM     | Jan, Feb, ..., Dec                |       |
 * |                                 | MMMM    | January, February, ..., December  | 2     |
 * |                                 | MMMMM   | J, F, ..., D                      |       |
 * | Month (stand-alone)             | L       | 1, 2, ..., 12                     |       |
 * |                                 | Lo      | 1st, 2nd, ..., 12th               | 7     |
 * |                                 | LL      | 01, 02, ..., 12                   |       |
 * |                                 | LLL     | Jan, Feb, ..., Dec                |       |
 * |                                 | LLLL    | January, February, ..., December  | 2     |
 * |                                 | LLLLL   | J, F, ..., D                      |       |
 * | Local week of year              | w       | 1, 2, ..., 53                     |       |
 * |                                 | wo      | 1st, 2nd, ..., 53th               | 7     |
 * |                                 | ww      | 01, 02, ..., 53                   |       |
 * | ISO week of year                | I       | 1, 2, ..., 53                     | 7     |
 * |                                 | Io      | 1st, 2nd, ..., 53th               | 7     |
 * |                                 | II      | 01, 02, ..., 53                   | 7     |
 * | Day of month                    | d       | 1, 2, ..., 31                     |       |
 * |                                 | do      | 1st, 2nd, ..., 31st               | 7     |
 * |                                 | dd      | 01, 02, ..., 31                   |       |
 * | Day of year                     | D       | 1, 2, ..., 365, 366               | 9     |
 * |                                 | Do      | 1st, 2nd, ..., 365th, 366th       | 7     |
 * |                                 | DD      | 01, 02, ..., 365, 366             | 9     |
 * |                                 | DDD     | 001, 002, ..., 365, 366           |       |
 * |                                 | DDDD    | ...                               | 3     |
 * | Day of week (formatting)        | E..EEE  | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | EEEE    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | EEEEE   | M, T, W, T, F, S, S               |       |
 * |                                 | EEEEEE  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | ISO day of week (formatting)    | i       | 1, 2, 3, ..., 7                   | 7     |
 * |                                 | io      | 1st, 2nd, ..., 7th                | 7     |
 * |                                 | ii      | 01, 02, ..., 07                   | 7     |
 * |                                 | iii     | Mon, Tue, Wed, ..., Sun           | 7     |
 * |                                 | iiii    | Monday, Tuesday, ..., Sunday      | 2,7   |
 * |                                 | iiiii   | M, T, W, T, F, S, S               | 7     |
 * |                                 | iiiiii  | Mo, Tu, We, Th, Fr, Sa, Su        | 7     |
 * | Local day of week (formatting)  | e       | 2, 3, 4, ..., 1                   |       |
 * |                                 | eo      | 2nd, 3rd, ..., 1st                | 7     |
 * |                                 | ee      | 02, 03, ..., 01                   |       |
 * |                                 | eee     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | eeee    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | eeeee   | M, T, W, T, F, S, S               |       |
 * |                                 | eeeeee  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | Local day of week (stand-alone) | c       | 2, 3, 4, ..., 1                   |       |
 * |                                 | co      | 2nd, 3rd, ..., 1st                | 7     |
 * |                                 | cc      | 02, 03, ..., 01                   |       |
 * |                                 | ccc     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | cccc    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | ccccc   | M, T, W, T, F, S, S               |       |
 * |                                 | cccccc  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | AM, PM                          | a..aa   | AM, PM                            |       |
 * |                                 | aaa     | am, pm                            |       |
 * |                                 | aaaa    | a.m., p.m.                        | 2     |
 * |                                 | aaaaa   | a, p                              |       |
 * | AM, PM, noon, midnight          | b..bb   | AM, PM, noon, midnight            |       |
 * |                                 | bbb     | am, pm, noon, midnight            |       |
 * |                                 | bbbb    | a.m., p.m., noon, midnight        | 2     |
 * |                                 | bbbbb   | a, p, n, mi                       |       |
 * | Flexible day period             | B..BBB  | at night, in the morning, ...     |       |
 * |                                 | BBBB    | at night, in the morning, ...     | 2     |
 * |                                 | BBBBB   | at night, in the morning, ...     |       |
 * | Hour [1-12]                     | h       | 1, 2, ..., 11, 12                 |       |
 * |                                 | ho      | 1st, 2nd, ..., 11th, 12th         | 7     |
 * |                                 | hh      | 01, 02, ..., 11, 12               |       |
 * | Hour [0-23]                     | H       | 0, 1, 2, ..., 23                  |       |
 * |                                 | Ho      | 0th, 1st, 2nd, ..., 23rd          | 7     |
 * |                                 | HH      | 00, 01, 02, ..., 23               |       |
 * | Hour [0-11]                     | K       | 1, 2, ..., 11, 0                  |       |
 * |                                 | Ko      | 1st, 2nd, ..., 11th, 0th          | 7     |
 * |                                 | KK      | 01, 02, ..., 11, 00               |       |
 * | Hour [1-24]                     | k       | 24, 1, 2, ..., 23                 |       |
 * |                                 | ko      | 24th, 1st, 2nd, ..., 23rd         | 7     |
 * |                                 | kk      | 24, 01, 02, ..., 23               |       |
 * | Minute                          | m       | 0, 1, ..., 59                     |       |
 * |                                 | mo      | 0th, 1st, ..., 59th               | 7     |
 * |                                 | mm      | 00, 01, ..., 59                   |       |
 * | Second                          | s       | 0, 1, ..., 59                     |       |
 * |                                 | so      | 0th, 1st, ..., 59th               | 7     |
 * |                                 | ss      | 00, 01, ..., 59                   |       |
 * | Fraction of second              | S       | 0, 1, ..., 9                      |       |
 * |                                 | SS      | 00, 01, ..., 99                   |       |
 * |                                 | SSS     | 000, 001, ..., 999                |       |
 * |                                 | SSSS    | ...                               | 3     |
 * | Timezone (ISO-8601 w/ Z)        | X       | -08, +0530, Z                     |       |
 * |                                 | XX      | -0800, +0530, Z                   |       |
 * |                                 | XXX     | -08:00, +05:30, Z                 |       |
 * |                                 | XXXX    | -0800, +0530, Z, +123456          | 2     |
 * |                                 | XXXXX   | -08:00, +05:30, Z, +12:34:56      |       |
 * | Timezone (ISO-8601 w/o Z)       | x       | -08, +0530, +00                   |       |
 * |                                 | xx      | -0800, +0530, +0000               |       |
 * |                                 | xxx     | -08:00, +05:30, +00:00            | 2     |
 * |                                 | xxxx    | -0800, +0530, +0000, +123456      |       |
 * |                                 | xxxxx   | -08:00, +05:30, +00:00, +12:34:56 |       |
 * | Timezone (GMT)                  | O...OOO | GMT-8, GMT+5:30, GMT+0            |       |
 * |                                 | OOOO    | GMT-08:00, GMT+05:30, GMT+00:00   | 2     |
 * | Timezone (specific non-locat.)  | z...zzz | GMT-8, GMT+5:30, GMT+0            | 6     |
 * |                                 | zzzz    | GMT-08:00, GMT+05:30, GMT+00:00   | 2,6   |
 * | Seconds timestamp               | t       | 512969520                         | 7     |
 * |                                 | tt      | ...                               | 3,7   |
 * | Milliseconds timestamp          | T       | 512969520900                      | 7     |
 * |                                 | TT      | ...                               | 3,7   |
 * | Long localized date             | P       | 04/29/1453                        | 7     |
 * |                                 | PP      | Apr 29, 1453                      | 7     |
 * |                                 | PPP     | April 29th, 1453                  | 7     |
 * |                                 | PPPP    | Friday, April 29th, 1453          | 2,7   |
 * | Long localized time             | p       | 12:00 AM                          | 7     |
 * |                                 | pp      | 12:00:00 AM                       | 7     |
 * |                                 | ppp     | 12:00:00 AM GMT+2                 | 7     |
 * |                                 | pppp    | 12:00:00 AM GMT+02:00             | 2,7   |
 * | Combination of date and time    | Pp      | 04/29/1453, 12:00 AM              | 7     |
 * |                                 | PPpp    | Apr 29, 1453, 12:00:00 AM         | 7     |
 * |                                 | PPPppp  | April 29th, 1453 at ...           | 7     |
 * |                                 | PPPPpppp| Friday, April 29th, 1453 at ...   | 2,7   |
 * Notes:
 * 1. "Formatting" units (e.g. formatting quarter) in the default en-US locale
 *    are the same as "stand-alone" units, but are different in some languages.
 *    "Formatting" units are declined according to the rules of the language
 *    in the context of a date. "Stand-alone" units are always nominative singular:
 *
 *    `format(new Date(2017, 10, 6), 'do LLLL', {locale: cs}) //=> '6. listopad'`
 *
 *    `format(new Date(2017, 10, 6), 'do MMMM', {locale: cs}) //=> '6. listopadu'`
 *
 * 2. Any sequence of the identical letters is a pattern, unless it is escaped by
 *    the single quote characters (see below).
 *    If the sequence is longer than listed in table (e.g. `EEEEEEEEEEE`)
 *    the output will be the same as default pattern for this unit, usually
 *    the longest one (in case of ISO weekdays, `EEEE`). Default patterns for units
 *    are marked with "2" in the last column of the table.
 *
 *    `format(new Date(2017, 10, 6), 'MMM') //=> 'Nov'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMM') //=> 'November'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMM') //=> 'N'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMMM') //=> 'November'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMMMM') //=> 'November'`
 *
 * 3. Some patterns could be unlimited length (such as `yyyyyyyy`).
 *    The output will be padded with zeros to match the length of the pattern.
 *
 *    `format(new Date(2017, 10, 6), 'yyyyyyyy') //=> '00002017'`
 *
 * 4. `QQQQQ` and `qqqqq` could be not strictly numerical in some locales.
 *    These tokens represent the shortest form of the quarter.
 *
 * 5. The main difference between `y` and `u` patterns are B.C. years:
 *
 *    | Year | `y` | `u` |
 *    |------|-----|-----|
 *    | AC 1 |   1 |   1 |
 *    | BC 1 |   1 |   0 |
 *    | BC 2 |   2 |  -1 |
 *
 *    Also `yy` always returns the last two digits of a year,
 *    while `uu` pads single digit years to 2 characters and returns other years unchanged:
 *
 *    | Year | `yy` | `uu` |
 *    |------|------|------|
 *    | 1    |   01 |   01 |
 *    | 14   |   14 |   14 |
 *    | 376  |   76 |  376 |
 *    | 1453 |   53 | 1453 |
 *
 *    The same difference is true for local and ISO week-numbering years (`Y` and `R`),
 *    except local week-numbering years are dependent on `options.weekStartsOn`
 *    and `options.firstWeekContainsDate` (compare [getISOWeekYear]{@link https://date-fns.org/docs/getISOWeekYear}
 *    and [getWeekYear]{@link https://date-fns.org/docs/getWeekYear}).
 *
 * 6. Specific non-location timezones are currently unavailable in `date-fns`,
 *    so right now these tokens fall back to GMT timezones.
 *
 * 7. These patterns are not in the Unicode Technical Standard #35:
 *    - `i`: ISO day of week
 *    - `I`: ISO week of year
 *    - `R`: ISO week-numbering year
 *    - `t`: seconds timestamp
 *    - `T`: milliseconds timestamp
 *    - `o`: ordinal number modifier
 *    - `P`: long localized date
 *    - `p`: long localized time
 *
 * 8. `YY` and `YYYY` tokens represent week-numbering years but they are often confused with years.
 *    You should enable `options.useAdditionalWeekYearTokens` to use them. See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * 9. `D` and `DD` tokens represent days of the year but they are often confused with days of the month.
 *    You should enable `options.useAdditionalDayOfYearTokens` to use them. See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * @param {Date|Number} date - the original date
 * @param {String} format - the string of tokens
 * @param {Object} [options] - an object with options.
 * @param {Locale} [options.locale=defaultLocale] - the locale object. See [Locale]{@link https://date-fns.org/docs/Locale}
 * @param {0|1|2|3|4|5|6} [options.weekStartsOn=0] - the index of the first day of the week (0 - Sunday)
 * @param {Number} [options.firstWeekContainsDate=1] - the day of January, which is
 * @param {Boolean} [options.useAdditionalWeekYearTokens=false] - if true, allows usage of the week-numbering year tokens `YY` and `YYYY`;
 *   see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @param {Boolean} [options.useAdditionalDayOfYearTokens=false] - if true, allows usage of the day of year tokens `D` and `DD`;
 *   see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @returns {String} the formatted date string
 * @throws {TypeError} 2 arguments required
 * @throws {RangeError} `date` must not be Invalid Date
 * @throws {RangeError} `options.locale` must contain `localize` property
 * @throws {RangeError} `options.locale` must contain `formatLong` property
 * @throws {RangeError} `options.weekStartsOn` must be between 0 and 6
 * @throws {RangeError} `options.firstWeekContainsDate` must be between 1 and 7
 * @throws {RangeError} use `yyyy` instead of `YYYY` for formatting years using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws {RangeError} use `yy` instead of `YY` for formatting years using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws {RangeError} use `d` instead of `D` for formatting days of the month using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws {RangeError} use `dd` instead of `DD` for formatting days of the month using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws {RangeError} format string contains an unescaped latin alphabet character
 *
 * @example
 * // Represent 11 February 2014 in middle-endian format:
 * const result = format(new Date(2014, 1, 11), 'MM/dd/yyyy')
 * //=> '02/11/2014'
 *
 * @example
 * // Represent 2 July 2014 in Esperanto:
 * import { eoLocale } from 'date-fns/locale/eo'
 * const result = format(new Date(2014, 6, 2), "do 'de' MMMM yyyy", {
 *   locale: eoLocale
 * })
 * //=> '2-a de julio 2014'
 *
 * @example
 * // Escape string by single quote characters:
 * const result = format(new Date(2014, 6, 2, 15), "h 'o''clock'")
 * //=> "3 o'clock"
 */

function format(dirtyDate, dirtyFormatStr, options) {
  var _ref, _options$locale, _ref2, _ref3, _ref4, _options$firstWeekCon, _options$locale2, _options$locale2$opti, _defaultOptions$local, _defaultOptions$local2, _ref5, _ref6, _ref7, _options$weekStartsOn, _options$locale3, _options$locale3$opti, _defaultOptions$local3, _defaultOptions$local4;
  requiredArgs(2, arguments);
  var formatStr = String(dirtyFormatStr);
  var defaultOptions = getDefaultOptions();
  var locale = (_ref = (_options$locale = options === null || options === void 0 ? void 0 : options.locale) !== null && _options$locale !== void 0 ? _options$locale : defaultOptions.locale) !== null && _ref !== void 0 ? _ref : defaultLocale;
  var firstWeekContainsDate = toInteger((_ref2 = (_ref3 = (_ref4 = (_options$firstWeekCon = options === null || options === void 0 ? void 0 : options.firstWeekContainsDate) !== null && _options$firstWeekCon !== void 0 ? _options$firstWeekCon : options === null || options === void 0 ? void 0 : (_options$locale2 = options.locale) === null || _options$locale2 === void 0 ? void 0 : (_options$locale2$opti = _options$locale2.options) === null || _options$locale2$opti === void 0 ? void 0 : _options$locale2$opti.firstWeekContainsDate) !== null && _ref4 !== void 0 ? _ref4 : defaultOptions.firstWeekContainsDate) !== null && _ref3 !== void 0 ? _ref3 : (_defaultOptions$local = defaultOptions.locale) === null || _defaultOptions$local === void 0 ? void 0 : (_defaultOptions$local2 = _defaultOptions$local.options) === null || _defaultOptions$local2 === void 0 ? void 0 : _defaultOptions$local2.firstWeekContainsDate) !== null && _ref2 !== void 0 ? _ref2 : 1);

  // Test if weekStartsOn is between 1 and 7 _and_ is not NaN
  if (!(firstWeekContainsDate >= 1 && firstWeekContainsDate <= 7)) {
    throw new RangeError('firstWeekContainsDate must be between 1 and 7 inclusively');
  }
  var weekStartsOn = toInteger((_ref5 = (_ref6 = (_ref7 = (_options$weekStartsOn = options === null || options === void 0 ? void 0 : options.weekStartsOn) !== null && _options$weekStartsOn !== void 0 ? _options$weekStartsOn : options === null || options === void 0 ? void 0 : (_options$locale3 = options.locale) === null || _options$locale3 === void 0 ? void 0 : (_options$locale3$opti = _options$locale3.options) === null || _options$locale3$opti === void 0 ? void 0 : _options$locale3$opti.weekStartsOn) !== null && _ref7 !== void 0 ? _ref7 : defaultOptions.weekStartsOn) !== null && _ref6 !== void 0 ? _ref6 : (_defaultOptions$local3 = defaultOptions.locale) === null || _defaultOptions$local3 === void 0 ? void 0 : (_defaultOptions$local4 = _defaultOptions$local3.options) === null || _defaultOptions$local4 === void 0 ? void 0 : _defaultOptions$local4.weekStartsOn) !== null && _ref5 !== void 0 ? _ref5 : 0);

  // Test if weekStartsOn is between 0 and 6 _and_ is not NaN
  if (!(weekStartsOn >= 0 && weekStartsOn <= 6)) {
    throw new RangeError('weekStartsOn must be between 0 and 6 inclusively');
  }
  if (!locale.localize) {
    throw new RangeError('locale must contain localize property');
  }
  if (!locale.formatLong) {
    throw new RangeError('locale must contain formatLong property');
  }
  var originalDate = toDate(dirtyDate);
  if (!isValid(originalDate)) {
    throw new RangeError('Invalid time value');
  }

  // Convert the date in system timezone to the same date in UTC+00:00 timezone.
  // This ensures that when UTC functions will be implemented, locales will be compatible with them.
  // See an issue about UTC functions: https://github.com/date-fns/date-fns/issues/376
  var timezoneOffset = getTimezoneOffsetInMilliseconds(originalDate);
  var utcDate = subMilliseconds(originalDate, timezoneOffset);
  var formatterOptions = {
    firstWeekContainsDate: firstWeekContainsDate,
    weekStartsOn: weekStartsOn,
    locale: locale,
    _originalDate: originalDate
  };
  var result = formatStr.match(longFormattingTokensRegExp).map(function (substring) {
    var firstCharacter = substring[0];
    if (firstCharacter === 'p' || firstCharacter === 'P') {
      var longFormatter = longFormatters$1[firstCharacter];
      return longFormatter(substring, locale.formatLong);
    }
    return substring;
  }).join('').match(formattingTokensRegExp).map(function (substring) {
    // Replace two single quote characters with one single quote character
    if (substring === "''") {
      return "'";
    }
    var firstCharacter = substring[0];
    if (firstCharacter === "'") {
      return cleanEscapedString(substring);
    }
    var formatter = formatters$1[firstCharacter];
    if (formatter) {
      if (!(options !== null && options !== void 0 && options.useAdditionalWeekYearTokens) && isProtectedWeekYearToken(substring)) {
        throwProtectedError(substring, dirtyFormatStr, String(dirtyDate));
      }
      if (!(options !== null && options !== void 0 && options.useAdditionalDayOfYearTokens) && isProtectedDayOfYearToken(substring)) {
        throwProtectedError(substring, dirtyFormatStr, String(dirtyDate));
      }
      return formatter(utcDate, substring, locale.localize, formatterOptions);
    }
    if (firstCharacter.match(unescapedLatinCharacterRegExp)) {
      throw new RangeError('Format string contains an unescaped latin alphabet character `' + firstCharacter + '`');
    }
    return substring;
  }).join('');
  return result;
}
function cleanEscapedString(input) {
  var matched = input.match(escapedStringRegExp);
  if (!matched) {
    return input;
  }
  return matched[1].replace(doubleQuoteRegExp, "'");
}

const CFormFeedback = defineComponent({
    name: 'CFormFeedback',
    props: {
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'div',
        },
        /**
         * Method called immediately after the `value` prop changes.
         */
        invalid: Boolean,
        /**
         * If your form layout allows it, you can display validation feedback in a styled tooltip.
         */
        tooltip: Boolean,
        /**
         * Set component validation state to valid.
         */
        valid: Boolean,
    },
    setup(props, { slots }) {
        return () => h$1(props.component, {
            class: [
                {
                    [`invalid-${props.tooltip ? 'tooltip' : 'feedback'}`]: props.invalid,
                    [`valid-${props.tooltip ? 'tooltip' : 'feedback'}`]: props.valid,
                },
            ],
        }, slots.default && slots.default());
    },
});

const CFormControlValidation = defineComponent({
    name: 'CFormControlValidation',
    inheritAttrs: false,
    props: {
        /**
         * @ignore
         */
        describedby: String,
        /**
         * Provide valuable, actionable feedback.
         *
         * @since 4.3.0
         */
        feedback: String,
        /**
         * Provide valuable, actionable feedback.
         *
         * @since 4.3.0
         */
        feedbackInvalid: String,
        /**
         * Provide valuable, actionable invalid feedback when using standard HTML form validation which applied two CSS pseudo-classes, `:invalid` and `:valid`.
         *
         * @since 4.3.0
         */
        feedbackValid: String,
        /**
         * Set component validation state to invalid.
         */
        invalid: Boolean,
        /**
         * Display validation feedback in a styled tooltip.
         *
         * @since 4.3.0
         */
        tooltipFeedback: Boolean,
        /**
         * Set component validation state to valid.
         */
        valid: Boolean,
    },
    setup(props, { slots }) {
        return () => [
            props.feedback &&
                (props.valid || props.invalid) &&
                h$1(CFormFeedback, {
                    ...(props.invalid && { id: props.describedby }),
                    invalid: props.invalid,
                    tooltip: props.tooltipFeedback,
                    valid: props.valid,
                }, {
                    default: () => (slots.feedback && slots.feedback()) || props.feedback,
                }),
            (props.feedbackInvalid || slots.feedbackInvalid) &&
                h$1(CFormFeedback, {
                    id: props.describedby,
                    invalid: true,
                    tooltip: props.tooltipFeedback,
                }, {
                    default: () => (slots.feedbackInvalid && slots.feedbackInvalid()) || props.feedbackInvalid,
                }),
            (props.feedbackValid || slots.feedbackValid) &&
                h$1(CFormFeedback, {
                    tooltip: props.tooltipFeedback,
                    valid: true,
                }, {
                    default: () => (slots.feedbackValid && slots.feedbackValid()) || props.feedbackValid,
                }),
        ];
    },
});

const CFormFloating = defineComponent({
    name: 'CFormFloating',
    setup(_, { slots }) {
        return () => h$1('div', {
            class: 'form-floating',
        }, slots.default && slots.default());
    },
});

const CFormLabel = defineComponent({
    name: 'CFormLabel',
    props: {
        /**
         * A string of all className you want to be applied to the component, and override standard className value.
         */
        customClassName: [Array, String],
    },
    setup(props, { slots }) {
        return () => h$1('label', {
            class: props.customClassName ?? 'form-label',
        }, slots.default && slots.default());
    },
});

const CFormText = defineComponent({
    name: 'CFormText',
    props: {
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'div',
        },
    },
    setup(props, { slots }) {
        return () => h$1(props.component, { class: 'form-text' }, slots.default && slots.default());
    },
});

const CFormControlWrapper = defineComponent({
    name: 'CFormControlWrapper',
    inheritAttrs: false,
    props: {
        ...CFormControlValidation.props,
        /**
         * Provide valuable, actionable valid feedback when using standard HTML form validation which applied two CSS pseudo-classes, `:invalid` and `:valid`.
         *
         * @since 4.3.0
         */
        floatingLabel: String,
        /**
         * @ignore
         */
        id: String,
        /**
         * Add a caption for a component.
         *
         * @since 4.3.0
         */
        label: String,
        /**
         * Add helper text to the component.
         *
         * @since 4.3.0
         */
        text: String,
    },
    setup(props, { slots }) {
        const formControlValidation = () => h$1(CFormControlValidation, {
            describedby: props.describedby,
            feedback: props.feedback,
            feedbackInvalid: props.feedbackInvalid,
            feedbackValid: props.feedbackValid,
            floatingLabel: props.floatingLabel,
            invalid: props.invalid,
            tooltipFeedback: props.tooltipFeedback,
            valid: props.valid,
        }, {
            ...(slots.feedback && { feedback: () => slots.feedback && slots.feedback() }),
            ...(slots.feedbackInvalid && {
                feedbackInvalid: () => slots.feedbackInvalid && slots.feedbackInvalid(),
            }),
            ...(slots.feedbackValid && {
                feedbackValid: () => slots.feedbackInvalid && slots.feedbackInvalid(),
            }),
        });
        return () => props.floatingLabel
            ? h$1(CFormFloating, () => [
                slots.default && slots.default(),
                h$1(CFormLabel, {
                    for: props.id,
                }, {
                    default: () => (slots.label && slots.label()) || props.label || props.floatingLabel,
                }),
                (props.text || slots.text) &&
                    h$1(CFormText, {
                        id: props.describedby,
                    }, {
                        default: () => (slots.text && slots.text()) || props.text,
                    }),
                formControlValidation(),
            ])
            : [
                (props.label || slots.label) &&
                    h$1(CFormLabel, {
                        for: props.id,
                    }, {
                        default: () => (slots.label && slots.label()) || props.label,
                    }),
                slots.default && slots.default(),
                (props.text || slots.text) &&
                    h$1(CFormText, {
                        id: props.describedby,
                    }, {
                        default: () => (slots.text && slots.text()) || props.text,
                    }),
                formControlValidation(),
            ];
    },
});

var top = 'top';
var bottom = 'bottom';
var right = 'right';
var left = 'left';
var auto = 'auto';
var basePlacements = [top, bottom, right, left];
var start = 'start';
var end = 'end';
var clippingParents = 'clippingParents';
var viewport = 'viewport';
var popper = 'popper';
var reference = 'reference';
var variationPlacements = /*#__PURE__*/basePlacements.reduce(function (acc, placement) {
  return acc.concat([placement + "-" + start, placement + "-" + end]);
}, []);
var placements = /*#__PURE__*/[].concat(basePlacements, [auto]).reduce(function (acc, placement) {
  return acc.concat([placement, placement + "-" + start, placement + "-" + end]);
}, []); // modifiers that need to read the DOM

var beforeRead = 'beforeRead';
var read = 'read';
var afterRead = 'afterRead'; // pure-logic modifiers

var beforeMain = 'beforeMain';
var main = 'main';
var afterMain = 'afterMain'; // modifier with the purpose to write to the DOM (or write into a framework state)

var beforeWrite = 'beforeWrite';
var write = 'write';
var afterWrite = 'afterWrite';
var modifierPhases = [beforeRead, read, afterRead, beforeMain, main, afterMain, beforeWrite, write, afterWrite];

function getNodeName(element) {
  return element ? (element.nodeName || '').toLowerCase() : null;
}

function getWindow(node) {
  if (node == null) {
    return window;
  }

  if (node.toString() !== '[object Window]') {
    var ownerDocument = node.ownerDocument;
    return ownerDocument ? ownerDocument.defaultView || window : window;
  }

  return node;
}

function isElement(node) {
  var OwnElement = getWindow(node).Element;
  return node instanceof OwnElement || node instanceof Element;
}

function isHTMLElement(node) {
  var OwnElement = getWindow(node).HTMLElement;
  return node instanceof OwnElement || node instanceof HTMLElement;
}

function isShadowRoot(node) {
  // IE 11 has no ShadowRoot
  if (typeof ShadowRoot === 'undefined') {
    return false;
  }

  var OwnElement = getWindow(node).ShadowRoot;
  return node instanceof OwnElement || node instanceof ShadowRoot;
}

// and applies them to the HTMLElements such as popper and arrow

function applyStyles(_ref) {
  var state = _ref.state;
  Object.keys(state.elements).forEach(function (name) {
    var style = state.styles[name] || {};
    var attributes = state.attributes[name] || {};
    var element = state.elements[name]; // arrow is optional + virtual elements

    if (!isHTMLElement(element) || !getNodeName(element)) {
      return;
    } // Flow doesn't support to extend this property, but it's the most
    // effective way to apply styles to an HTMLElement
    // $FlowFixMe[cannot-write]


    Object.assign(element.style, style);
    Object.keys(attributes).forEach(function (name) {
      var value = attributes[name];

      if (value === false) {
        element.removeAttribute(name);
      } else {
        element.setAttribute(name, value === true ? '' : value);
      }
    });
  });
}

function effect$2(_ref2) {
  var state = _ref2.state;
  var initialStyles = {
    popper: {
      position: state.options.strategy,
      left: '0',
      top: '0',
      margin: '0'
    },
    arrow: {
      position: 'absolute'
    },
    reference: {}
  };
  Object.assign(state.elements.popper.style, initialStyles.popper);
  state.styles = initialStyles;

  if (state.elements.arrow) {
    Object.assign(state.elements.arrow.style, initialStyles.arrow);
  }

  return function () {
    Object.keys(state.elements).forEach(function (name) {
      var element = state.elements[name];
      var attributes = state.attributes[name] || {};
      var styleProperties = Object.keys(state.styles.hasOwnProperty(name) ? state.styles[name] : initialStyles[name]); // Set all values to an empty string to unset them

      var style = styleProperties.reduce(function (style, property) {
        style[property] = '';
        return style;
      }, {}); // arrow is optional + virtual elements

      if (!isHTMLElement(element) || !getNodeName(element)) {
        return;
      }

      Object.assign(element.style, style);
      Object.keys(attributes).forEach(function (attribute) {
        element.removeAttribute(attribute);
      });
    });
  };
} // eslint-disable-next-line import/no-unused-modules


var applyStyles$1 = {
  name: 'applyStyles',
  enabled: true,
  phase: 'write',
  fn: applyStyles,
  effect: effect$2,
  requires: ['computeStyles']
};

function getBasePlacement(placement) {
  return placement.split('-')[0];
}

var max = Math.max;
var min = Math.min;
var round = Math.round;

function getUAString() {
  var uaData = navigator.userAgentData;

  if (uaData != null && uaData.brands && Array.isArray(uaData.brands)) {
    return uaData.brands.map(function (item) {
      return item.brand + "/" + item.version;
    }).join(' ');
  }

  return navigator.userAgent;
}

function isLayoutViewport() {
  return !/^((?!chrome|android).)*safari/i.test(getUAString());
}

function getBoundingClientRect(element, includeScale, isFixedStrategy) {
  if (includeScale === void 0) {
    includeScale = false;
  }

  if (isFixedStrategy === void 0) {
    isFixedStrategy = false;
  }

  var clientRect = element.getBoundingClientRect();
  var scaleX = 1;
  var scaleY = 1;

  if (includeScale && isHTMLElement(element)) {
    scaleX = element.offsetWidth > 0 ? round(clientRect.width) / element.offsetWidth || 1 : 1;
    scaleY = element.offsetHeight > 0 ? round(clientRect.height) / element.offsetHeight || 1 : 1;
  }

  var _ref = isElement(element) ? getWindow(element) : window,
      visualViewport = _ref.visualViewport;

  var addVisualOffsets = !isLayoutViewport() && isFixedStrategy;
  var x = (clientRect.left + (addVisualOffsets && visualViewport ? visualViewport.offsetLeft : 0)) / scaleX;
  var y = (clientRect.top + (addVisualOffsets && visualViewport ? visualViewport.offsetTop : 0)) / scaleY;
  var width = clientRect.width / scaleX;
  var height = clientRect.height / scaleY;
  return {
    width: width,
    height: height,
    top: y,
    right: x + width,
    bottom: y + height,
    left: x,
    x: x,
    y: y
  };
}

// means it doesn't take into account transforms.

function getLayoutRect(element) {
  var clientRect = getBoundingClientRect(element); // Use the clientRect sizes if it's not been transformed.
  // Fixes https://github.com/popperjs/popper-core/issues/1223

  var width = element.offsetWidth;
  var height = element.offsetHeight;

  if (Math.abs(clientRect.width - width) <= 1) {
    width = clientRect.width;
  }

  if (Math.abs(clientRect.height - height) <= 1) {
    height = clientRect.height;
  }

  return {
    x: element.offsetLeft,
    y: element.offsetTop,
    width: width,
    height: height
  };
}

function contains(parent, child) {
  var rootNode = child.getRootNode && child.getRootNode(); // First, attempt with faster native method

  if (parent.contains(child)) {
    return true;
  } // then fallback to custom implementation with Shadow DOM support
  else if (rootNode && isShadowRoot(rootNode)) {
      var next = child;

      do {
        if (next && parent.isSameNode(next)) {
          return true;
        } // $FlowFixMe[prop-missing]: need a better way to handle this...


        next = next.parentNode || next.host;
      } while (next);
    } // Give up, the result is false


  return false;
}

function getComputedStyle$1(element) {
  return getWindow(element).getComputedStyle(element);
}

function isTableElement(element) {
  return ['table', 'td', 'th'].indexOf(getNodeName(element)) >= 0;
}

function getDocumentElement(element) {
  // $FlowFixMe[incompatible-return]: assume body is always available
  return ((isElement(element) ? element.ownerDocument : // $FlowFixMe[prop-missing]
  element.document) || window.document).documentElement;
}

function getParentNode(element) {
  if (getNodeName(element) === 'html') {
    return element;
  }

  return (// this is a quicker (but less type safe) way to save quite some bytes from the bundle
    // $FlowFixMe[incompatible-return]
    // $FlowFixMe[prop-missing]
    element.assignedSlot || // step into the shadow DOM of the parent of a slotted node
    element.parentNode || ( // DOM Element detected
    isShadowRoot(element) ? element.host : null) || // ShadowRoot detected
    // $FlowFixMe[incompatible-call]: HTMLElement is a Node
    getDocumentElement(element) // fallback

  );
}

function getTrueOffsetParent(element) {
  if (!isHTMLElement(element) || // https://github.com/popperjs/popper-core/issues/837
  getComputedStyle$1(element).position === 'fixed') {
    return null;
  }

  return element.offsetParent;
} // `.offsetParent` reports `null` for fixed elements, while absolute elements
// return the containing block


function getContainingBlock(element) {
  var isFirefox = /firefox/i.test(getUAString());
  var isIE = /Trident/i.test(getUAString());

  if (isIE && isHTMLElement(element)) {
    // In IE 9, 10 and 11 fixed elements containing block is always established by the viewport
    var elementCss = getComputedStyle$1(element);

    if (elementCss.position === 'fixed') {
      return null;
    }
  }

  var currentNode = getParentNode(element);

  if (isShadowRoot(currentNode)) {
    currentNode = currentNode.host;
  }

  while (isHTMLElement(currentNode) && ['html', 'body'].indexOf(getNodeName(currentNode)) < 0) {
    var css = getComputedStyle$1(currentNode); // This is non-exhaustive but covers the most common CSS properties that
    // create a containing block.
    // https://developer.mozilla.org/en-US/docs/Web/CSS/Containing_block#identifying_the_containing_block

    if (css.transform !== 'none' || css.perspective !== 'none' || css.contain === 'paint' || ['transform', 'perspective'].indexOf(css.willChange) !== -1 || isFirefox && css.willChange === 'filter' || isFirefox && css.filter && css.filter !== 'none') {
      return currentNode;
    } else {
      currentNode = currentNode.parentNode;
    }
  }

  return null;
} // Gets the closest ancestor positioned element. Handles some edge cases,
// such as table ancestors and cross browser bugs.


function getOffsetParent(element) {
  var window = getWindow(element);
  var offsetParent = getTrueOffsetParent(element);

  while (offsetParent && isTableElement(offsetParent) && getComputedStyle$1(offsetParent).position === 'static') {
    offsetParent = getTrueOffsetParent(offsetParent);
  }

  if (offsetParent && (getNodeName(offsetParent) === 'html' || getNodeName(offsetParent) === 'body' && getComputedStyle$1(offsetParent).position === 'static')) {
    return window;
  }

  return offsetParent || getContainingBlock(element) || window;
}

function getMainAxisFromPlacement(placement) {
  return ['top', 'bottom'].indexOf(placement) >= 0 ? 'x' : 'y';
}

function within(min$1, value, max$1) {
  return max(min$1, min(value, max$1));
}
function withinMaxClamp(min, value, max) {
  var v = within(min, value, max);
  return v > max ? max : v;
}

function getFreshSideObject() {
  return {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0
  };
}

function mergePaddingObject(paddingObject) {
  return Object.assign({}, getFreshSideObject(), paddingObject);
}

function expandToHashMap(value, keys) {
  return keys.reduce(function (hashMap, key) {
    hashMap[key] = value;
    return hashMap;
  }, {});
}

var toPaddingObject = function toPaddingObject(padding, state) {
  padding = typeof padding === 'function' ? padding(Object.assign({}, state.rects, {
    placement: state.placement
  })) : padding;
  return mergePaddingObject(typeof padding !== 'number' ? padding : expandToHashMap(padding, basePlacements));
};

function arrow(_ref) {
  var _state$modifiersData$;

  var state = _ref.state,
      name = _ref.name,
      options = _ref.options;
  var arrowElement = state.elements.arrow;
  var popperOffsets = state.modifiersData.popperOffsets;
  var basePlacement = getBasePlacement(state.placement);
  var axis = getMainAxisFromPlacement(basePlacement);
  var isVertical = [left, right].indexOf(basePlacement) >= 0;
  var len = isVertical ? 'height' : 'width';

  if (!arrowElement || !popperOffsets) {
    return;
  }

  var paddingObject = toPaddingObject(options.padding, state);
  var arrowRect = getLayoutRect(arrowElement);
  var minProp = axis === 'y' ? top : left;
  var maxProp = axis === 'y' ? bottom : right;
  var endDiff = state.rects.reference[len] + state.rects.reference[axis] - popperOffsets[axis] - state.rects.popper[len];
  var startDiff = popperOffsets[axis] - state.rects.reference[axis];
  var arrowOffsetParent = getOffsetParent(arrowElement);
  var clientSize = arrowOffsetParent ? axis === 'y' ? arrowOffsetParent.clientHeight || 0 : arrowOffsetParent.clientWidth || 0 : 0;
  var centerToReference = endDiff / 2 - startDiff / 2; // Make sure the arrow doesn't overflow the popper if the center point is
  // outside of the popper bounds

  var min = paddingObject[minProp];
  var max = clientSize - arrowRect[len] - paddingObject[maxProp];
  var center = clientSize / 2 - arrowRect[len] / 2 + centerToReference;
  var offset = within(min, center, max); // Prevents breaking syntax highlighting...

  var axisProp = axis;
  state.modifiersData[name] = (_state$modifiersData$ = {}, _state$modifiersData$[axisProp] = offset, _state$modifiersData$.centerOffset = offset - center, _state$modifiersData$);
}

function effect$1(_ref2) {
  var state = _ref2.state,
      options = _ref2.options;
  var _options$element = options.element,
      arrowElement = _options$element === void 0 ? '[data-popper-arrow]' : _options$element;

  if (arrowElement == null) {
    return;
  } // CSS selector


  if (typeof arrowElement === 'string') {
    arrowElement = state.elements.popper.querySelector(arrowElement);

    if (!arrowElement) {
      return;
    }
  }

  if (!contains(state.elements.popper, arrowElement)) {
    return;
  }

  state.elements.arrow = arrowElement;
} // eslint-disable-next-line import/no-unused-modules


var arrow$1 = {
  name: 'arrow',
  enabled: true,
  phase: 'main',
  fn: arrow,
  effect: effect$1,
  requires: ['popperOffsets'],
  requiresIfExists: ['preventOverflow']
};

function getVariation(placement) {
  return placement.split('-')[1];
}

var unsetSides = {
  top: 'auto',
  right: 'auto',
  bottom: 'auto',
  left: 'auto'
}; // Round the offsets to the nearest suitable subpixel based on the DPR.
// Zooming can change the DPR, but it seems to report a value that will
// cleanly divide the values into the appropriate subpixels.

function roundOffsetsByDPR(_ref, win) {
  var x = _ref.x,
      y = _ref.y;
  var dpr = win.devicePixelRatio || 1;
  return {
    x: round(x * dpr) / dpr || 0,
    y: round(y * dpr) / dpr || 0
  };
}

function mapToStyles(_ref2) {
  var _Object$assign2;

  var popper = _ref2.popper,
      popperRect = _ref2.popperRect,
      placement = _ref2.placement,
      variation = _ref2.variation,
      offsets = _ref2.offsets,
      position = _ref2.position,
      gpuAcceleration = _ref2.gpuAcceleration,
      adaptive = _ref2.adaptive,
      roundOffsets = _ref2.roundOffsets,
      isFixed = _ref2.isFixed;
  var _offsets$x = offsets.x,
      x = _offsets$x === void 0 ? 0 : _offsets$x,
      _offsets$y = offsets.y,
      y = _offsets$y === void 0 ? 0 : _offsets$y;

  var _ref3 = typeof roundOffsets === 'function' ? roundOffsets({
    x: x,
    y: y
  }) : {
    x: x,
    y: y
  };

  x = _ref3.x;
  y = _ref3.y;
  var hasX = offsets.hasOwnProperty('x');
  var hasY = offsets.hasOwnProperty('y');
  var sideX = left;
  var sideY = top;
  var win = window;

  if (adaptive) {
    var offsetParent = getOffsetParent(popper);
    var heightProp = 'clientHeight';
    var widthProp = 'clientWidth';

    if (offsetParent === getWindow(popper)) {
      offsetParent = getDocumentElement(popper);

      if (getComputedStyle$1(offsetParent).position !== 'static' && position === 'absolute') {
        heightProp = 'scrollHeight';
        widthProp = 'scrollWidth';
      }
    } // $FlowFixMe[incompatible-cast]: force type refinement, we compare offsetParent with window above, but Flow doesn't detect it


    offsetParent = offsetParent;

    if (placement === top || (placement === left || placement === right) && variation === end) {
      sideY = bottom;
      var offsetY = isFixed && offsetParent === win && win.visualViewport ? win.visualViewport.height : // $FlowFixMe[prop-missing]
      offsetParent[heightProp];
      y -= offsetY - popperRect.height;
      y *= gpuAcceleration ? 1 : -1;
    }

    if (placement === left || (placement === top || placement === bottom) && variation === end) {
      sideX = right;
      var offsetX = isFixed && offsetParent === win && win.visualViewport ? win.visualViewport.width : // $FlowFixMe[prop-missing]
      offsetParent[widthProp];
      x -= offsetX - popperRect.width;
      x *= gpuAcceleration ? 1 : -1;
    }
  }

  var commonStyles = Object.assign({
    position: position
  }, adaptive && unsetSides);

  var _ref4 = roundOffsets === true ? roundOffsetsByDPR({
    x: x,
    y: y
  }, getWindow(popper)) : {
    x: x,
    y: y
  };

  x = _ref4.x;
  y = _ref4.y;

  if (gpuAcceleration) {
    var _Object$assign;

    return Object.assign({}, commonStyles, (_Object$assign = {}, _Object$assign[sideY] = hasY ? '0' : '', _Object$assign[sideX] = hasX ? '0' : '', _Object$assign.transform = (win.devicePixelRatio || 1) <= 1 ? "translate(" + x + "px, " + y + "px)" : "translate3d(" + x + "px, " + y + "px, 0)", _Object$assign));
  }

  return Object.assign({}, commonStyles, (_Object$assign2 = {}, _Object$assign2[sideY] = hasY ? y + "px" : '', _Object$assign2[sideX] = hasX ? x + "px" : '', _Object$assign2.transform = '', _Object$assign2));
}

function computeStyles(_ref5) {
  var state = _ref5.state,
      options = _ref5.options;
  var _options$gpuAccelerat = options.gpuAcceleration,
      gpuAcceleration = _options$gpuAccelerat === void 0 ? true : _options$gpuAccelerat,
      _options$adaptive = options.adaptive,
      adaptive = _options$adaptive === void 0 ? true : _options$adaptive,
      _options$roundOffsets = options.roundOffsets,
      roundOffsets = _options$roundOffsets === void 0 ? true : _options$roundOffsets;
  var commonStyles = {
    placement: getBasePlacement(state.placement),
    variation: getVariation(state.placement),
    popper: state.elements.popper,
    popperRect: state.rects.popper,
    gpuAcceleration: gpuAcceleration,
    isFixed: state.options.strategy === 'fixed'
  };

  if (state.modifiersData.popperOffsets != null) {
    state.styles.popper = Object.assign({}, state.styles.popper, mapToStyles(Object.assign({}, commonStyles, {
      offsets: state.modifiersData.popperOffsets,
      position: state.options.strategy,
      adaptive: adaptive,
      roundOffsets: roundOffsets
    })));
  }

  if (state.modifiersData.arrow != null) {
    state.styles.arrow = Object.assign({}, state.styles.arrow, mapToStyles(Object.assign({}, commonStyles, {
      offsets: state.modifiersData.arrow,
      position: 'absolute',
      adaptive: false,
      roundOffsets: roundOffsets
    })));
  }

  state.attributes.popper = Object.assign({}, state.attributes.popper, {
    'data-popper-placement': state.placement
  });
} // eslint-disable-next-line import/no-unused-modules


var computeStyles$1 = {
  name: 'computeStyles',
  enabled: true,
  phase: 'beforeWrite',
  fn: computeStyles,
  data: {}
};

var passive = {
  passive: true
};

function effect(_ref) {
  var state = _ref.state,
      instance = _ref.instance,
      options = _ref.options;
  var _options$scroll = options.scroll,
      scroll = _options$scroll === void 0 ? true : _options$scroll,
      _options$resize = options.resize,
      resize = _options$resize === void 0 ? true : _options$resize;
  var window = getWindow(state.elements.popper);
  var scrollParents = [].concat(state.scrollParents.reference, state.scrollParents.popper);

  if (scroll) {
    scrollParents.forEach(function (scrollParent) {
      scrollParent.addEventListener('scroll', instance.update, passive);
    });
  }

  if (resize) {
    window.addEventListener('resize', instance.update, passive);
  }

  return function () {
    if (scroll) {
      scrollParents.forEach(function (scrollParent) {
        scrollParent.removeEventListener('scroll', instance.update, passive);
      });
    }

    if (resize) {
      window.removeEventListener('resize', instance.update, passive);
    }
  };
} // eslint-disable-next-line import/no-unused-modules


var eventListeners = {
  name: 'eventListeners',
  enabled: true,
  phase: 'write',
  fn: function fn() {},
  effect: effect,
  data: {}
};

var hash$1 = {
  left: 'right',
  right: 'left',
  bottom: 'top',
  top: 'bottom'
};
function getOppositePlacement(placement) {
  return placement.replace(/left|right|bottom|top/g, function (matched) {
    return hash$1[matched];
  });
}

var hash = {
  start: 'end',
  end: 'start'
};
function getOppositeVariationPlacement(placement) {
  return placement.replace(/start|end/g, function (matched) {
    return hash[matched];
  });
}

function getWindowScroll(node) {
  var win = getWindow(node);
  var scrollLeft = win.pageXOffset;
  var scrollTop = win.pageYOffset;
  return {
    scrollLeft: scrollLeft,
    scrollTop: scrollTop
  };
}

function getWindowScrollBarX(element) {
  // If <html> has a CSS width greater than the viewport, then this will be
  // incorrect for RTL.
  // Popper 1 is broken in this case and never had a bug report so let's assume
  // it's not an issue. I don't think anyone ever specifies width on <html>
  // anyway.
  // Browsers where the left scrollbar doesn't cause an issue report `0` for
  // this (e.g. Edge 2019, IE11, Safari)
  return getBoundingClientRect(getDocumentElement(element)).left + getWindowScroll(element).scrollLeft;
}

function getViewportRect(element, strategy) {
  var win = getWindow(element);
  var html = getDocumentElement(element);
  var visualViewport = win.visualViewport;
  var width = html.clientWidth;
  var height = html.clientHeight;
  var x = 0;
  var y = 0;

  if (visualViewport) {
    width = visualViewport.width;
    height = visualViewport.height;
    var layoutViewport = isLayoutViewport();

    if (layoutViewport || !layoutViewport && strategy === 'fixed') {
      x = visualViewport.offsetLeft;
      y = visualViewport.offsetTop;
    }
  }

  return {
    width: width,
    height: height,
    x: x + getWindowScrollBarX(element),
    y: y
  };
}

// of the `<html>` and `<body>` rect bounds if horizontally scrollable

function getDocumentRect(element) {
  var _element$ownerDocumen;

  var html = getDocumentElement(element);
  var winScroll = getWindowScroll(element);
  var body = (_element$ownerDocumen = element.ownerDocument) == null ? void 0 : _element$ownerDocumen.body;
  var width = max(html.scrollWidth, html.clientWidth, body ? body.scrollWidth : 0, body ? body.clientWidth : 0);
  var height = max(html.scrollHeight, html.clientHeight, body ? body.scrollHeight : 0, body ? body.clientHeight : 0);
  var x = -winScroll.scrollLeft + getWindowScrollBarX(element);
  var y = -winScroll.scrollTop;

  if (getComputedStyle$1(body || html).direction === 'rtl') {
    x += max(html.clientWidth, body ? body.clientWidth : 0) - width;
  }

  return {
    width: width,
    height: height,
    x: x,
    y: y
  };
}

function isScrollParent(element) {
  // Firefox wants us to check `-x` and `-y` variations as well
  var _getComputedStyle = getComputedStyle$1(element),
      overflow = _getComputedStyle.overflow,
      overflowX = _getComputedStyle.overflowX,
      overflowY = _getComputedStyle.overflowY;

  return /auto|scroll|overlay|hidden/.test(overflow + overflowY + overflowX);
}

function getScrollParent(node) {
  if (['html', 'body', '#document'].indexOf(getNodeName(node)) >= 0) {
    // $FlowFixMe[incompatible-return]: assume body is always available
    return node.ownerDocument.body;
  }

  if (isHTMLElement(node) && isScrollParent(node)) {
    return node;
  }

  return getScrollParent(getParentNode(node));
}

/*
given a DOM element, return the list of all scroll parents, up the list of ancesors
until we get to the top window object. This list is what we attach scroll listeners
to, because if any of these parent elements scroll, we'll need to re-calculate the
reference element's position.
*/

function listScrollParents(element, list) {
  var _element$ownerDocumen;

  if (list === void 0) {
    list = [];
  }

  var scrollParent = getScrollParent(element);
  var isBody = scrollParent === ((_element$ownerDocumen = element.ownerDocument) == null ? void 0 : _element$ownerDocumen.body);
  var win = getWindow(scrollParent);
  var target = isBody ? [win].concat(win.visualViewport || [], isScrollParent(scrollParent) ? scrollParent : []) : scrollParent;
  var updatedList = list.concat(target);
  return isBody ? updatedList : // $FlowFixMe[incompatible-call]: isBody tells us target will be an HTMLElement here
  updatedList.concat(listScrollParents(getParentNode(target)));
}

function rectToClientRect(rect) {
  return Object.assign({}, rect, {
    left: rect.x,
    top: rect.y,
    right: rect.x + rect.width,
    bottom: rect.y + rect.height
  });
}

function getInnerBoundingClientRect(element, strategy) {
  var rect = getBoundingClientRect(element, false, strategy === 'fixed');
  rect.top = rect.top + element.clientTop;
  rect.left = rect.left + element.clientLeft;
  rect.bottom = rect.top + element.clientHeight;
  rect.right = rect.left + element.clientWidth;
  rect.width = element.clientWidth;
  rect.height = element.clientHeight;
  rect.x = rect.left;
  rect.y = rect.top;
  return rect;
}

function getClientRectFromMixedType(element, clippingParent, strategy) {
  return clippingParent === viewport ? rectToClientRect(getViewportRect(element, strategy)) : isElement(clippingParent) ? getInnerBoundingClientRect(clippingParent, strategy) : rectToClientRect(getDocumentRect(getDocumentElement(element)));
} // A "clipping parent" is an overflowable container with the characteristic of
// clipping (or hiding) overflowing elements with a position different from
// `initial`


function getClippingParents(element) {
  var clippingParents = listScrollParents(getParentNode(element));
  var canEscapeClipping = ['absolute', 'fixed'].indexOf(getComputedStyle$1(element).position) >= 0;
  var clipperElement = canEscapeClipping && isHTMLElement(element) ? getOffsetParent(element) : element;

  if (!isElement(clipperElement)) {
    return [];
  } // $FlowFixMe[incompatible-return]: https://github.com/facebook/flow/issues/1414


  return clippingParents.filter(function (clippingParent) {
    return isElement(clippingParent) && contains(clippingParent, clipperElement) && getNodeName(clippingParent) !== 'body';
  });
} // Gets the maximum area that the element is visible in due to any number of
// clipping parents


function getClippingRect(element, boundary, rootBoundary, strategy) {
  var mainClippingParents = boundary === 'clippingParents' ? getClippingParents(element) : [].concat(boundary);
  var clippingParents = [].concat(mainClippingParents, [rootBoundary]);
  var firstClippingParent = clippingParents[0];
  var clippingRect = clippingParents.reduce(function (accRect, clippingParent) {
    var rect = getClientRectFromMixedType(element, clippingParent, strategy);
    accRect.top = max(rect.top, accRect.top);
    accRect.right = min(rect.right, accRect.right);
    accRect.bottom = min(rect.bottom, accRect.bottom);
    accRect.left = max(rect.left, accRect.left);
    return accRect;
  }, getClientRectFromMixedType(element, firstClippingParent, strategy));
  clippingRect.width = clippingRect.right - clippingRect.left;
  clippingRect.height = clippingRect.bottom - clippingRect.top;
  clippingRect.x = clippingRect.left;
  clippingRect.y = clippingRect.top;
  return clippingRect;
}

function computeOffsets(_ref) {
  var reference = _ref.reference,
      element = _ref.element,
      placement = _ref.placement;
  var basePlacement = placement ? getBasePlacement(placement) : null;
  var variation = placement ? getVariation(placement) : null;
  var commonX = reference.x + reference.width / 2 - element.width / 2;
  var commonY = reference.y + reference.height / 2 - element.height / 2;
  var offsets;

  switch (basePlacement) {
    case top:
      offsets = {
        x: commonX,
        y: reference.y - element.height
      };
      break;

    case bottom:
      offsets = {
        x: commonX,
        y: reference.y + reference.height
      };
      break;

    case right:
      offsets = {
        x: reference.x + reference.width,
        y: commonY
      };
      break;

    case left:
      offsets = {
        x: reference.x - element.width,
        y: commonY
      };
      break;

    default:
      offsets = {
        x: reference.x,
        y: reference.y
      };
  }

  var mainAxis = basePlacement ? getMainAxisFromPlacement(basePlacement) : null;

  if (mainAxis != null) {
    var len = mainAxis === 'y' ? 'height' : 'width';

    switch (variation) {
      case start:
        offsets[mainAxis] = offsets[mainAxis] - (reference[len] / 2 - element[len] / 2);
        break;

      case end:
        offsets[mainAxis] = offsets[mainAxis] + (reference[len] / 2 - element[len] / 2);
        break;
    }
  }

  return offsets;
}

function detectOverflow(state, options) {
  if (options === void 0) {
    options = {};
  }

  var _options = options,
      _options$placement = _options.placement,
      placement = _options$placement === void 0 ? state.placement : _options$placement,
      _options$strategy = _options.strategy,
      strategy = _options$strategy === void 0 ? state.strategy : _options$strategy,
      _options$boundary = _options.boundary,
      boundary = _options$boundary === void 0 ? clippingParents : _options$boundary,
      _options$rootBoundary = _options.rootBoundary,
      rootBoundary = _options$rootBoundary === void 0 ? viewport : _options$rootBoundary,
      _options$elementConte = _options.elementContext,
      elementContext = _options$elementConte === void 0 ? popper : _options$elementConte,
      _options$altBoundary = _options.altBoundary,
      altBoundary = _options$altBoundary === void 0 ? false : _options$altBoundary,
      _options$padding = _options.padding,
      padding = _options$padding === void 0 ? 0 : _options$padding;
  var paddingObject = mergePaddingObject(typeof padding !== 'number' ? padding : expandToHashMap(padding, basePlacements));
  var altContext = elementContext === popper ? reference : popper;
  var popperRect = state.rects.popper;
  var element = state.elements[altBoundary ? altContext : elementContext];
  var clippingClientRect = getClippingRect(isElement(element) ? element : element.contextElement || getDocumentElement(state.elements.popper), boundary, rootBoundary, strategy);
  var referenceClientRect = getBoundingClientRect(state.elements.reference);
  var popperOffsets = computeOffsets({
    reference: referenceClientRect,
    element: popperRect,
    strategy: 'absolute',
    placement: placement
  });
  var popperClientRect = rectToClientRect(Object.assign({}, popperRect, popperOffsets));
  var elementClientRect = elementContext === popper ? popperClientRect : referenceClientRect; // positive = overflowing the clipping rect
  // 0 or negative = within the clipping rect

  var overflowOffsets = {
    top: clippingClientRect.top - elementClientRect.top + paddingObject.top,
    bottom: elementClientRect.bottom - clippingClientRect.bottom + paddingObject.bottom,
    left: clippingClientRect.left - elementClientRect.left + paddingObject.left,
    right: elementClientRect.right - clippingClientRect.right + paddingObject.right
  };
  var offsetData = state.modifiersData.offset; // Offsets can be applied only to the popper element

  if (elementContext === popper && offsetData) {
    var offset = offsetData[placement];
    Object.keys(overflowOffsets).forEach(function (key) {
      var multiply = [right, bottom].indexOf(key) >= 0 ? 1 : -1;
      var axis = [top, bottom].indexOf(key) >= 0 ? 'y' : 'x';
      overflowOffsets[key] += offset[axis] * multiply;
    });
  }

  return overflowOffsets;
}

function computeAutoPlacement(state, options) {
  if (options === void 0) {
    options = {};
  }

  var _options = options,
      placement = _options.placement,
      boundary = _options.boundary,
      rootBoundary = _options.rootBoundary,
      padding = _options.padding,
      flipVariations = _options.flipVariations,
      _options$allowedAutoP = _options.allowedAutoPlacements,
      allowedAutoPlacements = _options$allowedAutoP === void 0 ? placements : _options$allowedAutoP;
  var variation = getVariation(placement);
  var placements$1 = variation ? flipVariations ? variationPlacements : variationPlacements.filter(function (placement) {
    return getVariation(placement) === variation;
  }) : basePlacements;
  var allowedPlacements = placements$1.filter(function (placement) {
    return allowedAutoPlacements.indexOf(placement) >= 0;
  });

  if (allowedPlacements.length === 0) {
    allowedPlacements = placements$1;
  } // $FlowFixMe[incompatible-type]: Flow seems to have problems with two array unions...


  var overflows = allowedPlacements.reduce(function (acc, placement) {
    acc[placement] = detectOverflow(state, {
      placement: placement,
      boundary: boundary,
      rootBoundary: rootBoundary,
      padding: padding
    })[getBasePlacement(placement)];
    return acc;
  }, {});
  return Object.keys(overflows).sort(function (a, b) {
    return overflows[a] - overflows[b];
  });
}

function getExpandedFallbackPlacements(placement) {
  if (getBasePlacement(placement) === auto) {
    return [];
  }

  var oppositePlacement = getOppositePlacement(placement);
  return [getOppositeVariationPlacement(placement), oppositePlacement, getOppositeVariationPlacement(oppositePlacement)];
}

function flip(_ref) {
  var state = _ref.state,
      options = _ref.options,
      name = _ref.name;

  if (state.modifiersData[name]._skip) {
    return;
  }

  var _options$mainAxis = options.mainAxis,
      checkMainAxis = _options$mainAxis === void 0 ? true : _options$mainAxis,
      _options$altAxis = options.altAxis,
      checkAltAxis = _options$altAxis === void 0 ? true : _options$altAxis,
      specifiedFallbackPlacements = options.fallbackPlacements,
      padding = options.padding,
      boundary = options.boundary,
      rootBoundary = options.rootBoundary,
      altBoundary = options.altBoundary,
      _options$flipVariatio = options.flipVariations,
      flipVariations = _options$flipVariatio === void 0 ? true : _options$flipVariatio,
      allowedAutoPlacements = options.allowedAutoPlacements;
  var preferredPlacement = state.options.placement;
  var basePlacement = getBasePlacement(preferredPlacement);
  var isBasePlacement = basePlacement === preferredPlacement;
  var fallbackPlacements = specifiedFallbackPlacements || (isBasePlacement || !flipVariations ? [getOppositePlacement(preferredPlacement)] : getExpandedFallbackPlacements(preferredPlacement));
  var placements = [preferredPlacement].concat(fallbackPlacements).reduce(function (acc, placement) {
    return acc.concat(getBasePlacement(placement) === auto ? computeAutoPlacement(state, {
      placement: placement,
      boundary: boundary,
      rootBoundary: rootBoundary,
      padding: padding,
      flipVariations: flipVariations,
      allowedAutoPlacements: allowedAutoPlacements
    }) : placement);
  }, []);
  var referenceRect = state.rects.reference;
  var popperRect = state.rects.popper;
  var checksMap = new Map();
  var makeFallbackChecks = true;
  var firstFittingPlacement = placements[0];

  for (var i = 0; i < placements.length; i++) {
    var placement = placements[i];

    var _basePlacement = getBasePlacement(placement);

    var isStartVariation = getVariation(placement) === start;
    var isVertical = [top, bottom].indexOf(_basePlacement) >= 0;
    var len = isVertical ? 'width' : 'height';
    var overflow = detectOverflow(state, {
      placement: placement,
      boundary: boundary,
      rootBoundary: rootBoundary,
      altBoundary: altBoundary,
      padding: padding
    });
    var mainVariationSide = isVertical ? isStartVariation ? right : left : isStartVariation ? bottom : top;

    if (referenceRect[len] > popperRect[len]) {
      mainVariationSide = getOppositePlacement(mainVariationSide);
    }

    var altVariationSide = getOppositePlacement(mainVariationSide);
    var checks = [];

    if (checkMainAxis) {
      checks.push(overflow[_basePlacement] <= 0);
    }

    if (checkAltAxis) {
      checks.push(overflow[mainVariationSide] <= 0, overflow[altVariationSide] <= 0);
    }

    if (checks.every(function (check) {
      return check;
    })) {
      firstFittingPlacement = placement;
      makeFallbackChecks = false;
      break;
    }

    checksMap.set(placement, checks);
  }

  if (makeFallbackChecks) {
    // `2` may be desired in some cases – research later
    var numberOfChecks = flipVariations ? 3 : 1;

    var _loop = function _loop(_i) {
      var fittingPlacement = placements.find(function (placement) {
        var checks = checksMap.get(placement);

        if (checks) {
          return checks.slice(0, _i).every(function (check) {
            return check;
          });
        }
      });

      if (fittingPlacement) {
        firstFittingPlacement = fittingPlacement;
        return "break";
      }
    };

    for (var _i = numberOfChecks; _i > 0; _i--) {
      var _ret = _loop(_i);

      if (_ret === "break") break;
    }
  }

  if (state.placement !== firstFittingPlacement) {
    state.modifiersData[name]._skip = true;
    state.placement = firstFittingPlacement;
    state.reset = true;
  }
} // eslint-disable-next-line import/no-unused-modules


var flip$1 = {
  name: 'flip',
  enabled: true,
  phase: 'main',
  fn: flip,
  requiresIfExists: ['offset'],
  data: {
    _skip: false
  }
};

function getSideOffsets(overflow, rect, preventedOffsets) {
  if (preventedOffsets === void 0) {
    preventedOffsets = {
      x: 0,
      y: 0
    };
  }

  return {
    top: overflow.top - rect.height - preventedOffsets.y,
    right: overflow.right - rect.width + preventedOffsets.x,
    bottom: overflow.bottom - rect.height + preventedOffsets.y,
    left: overflow.left - rect.width - preventedOffsets.x
  };
}

function isAnySideFullyClipped(overflow) {
  return [top, right, bottom, left].some(function (side) {
    return overflow[side] >= 0;
  });
}

function hide(_ref) {
  var state = _ref.state,
      name = _ref.name;
  var referenceRect = state.rects.reference;
  var popperRect = state.rects.popper;
  var preventedOffsets = state.modifiersData.preventOverflow;
  var referenceOverflow = detectOverflow(state, {
    elementContext: 'reference'
  });
  var popperAltOverflow = detectOverflow(state, {
    altBoundary: true
  });
  var referenceClippingOffsets = getSideOffsets(referenceOverflow, referenceRect);
  var popperEscapeOffsets = getSideOffsets(popperAltOverflow, popperRect, preventedOffsets);
  var isReferenceHidden = isAnySideFullyClipped(referenceClippingOffsets);
  var hasPopperEscaped = isAnySideFullyClipped(popperEscapeOffsets);
  state.modifiersData[name] = {
    referenceClippingOffsets: referenceClippingOffsets,
    popperEscapeOffsets: popperEscapeOffsets,
    isReferenceHidden: isReferenceHidden,
    hasPopperEscaped: hasPopperEscaped
  };
  state.attributes.popper = Object.assign({}, state.attributes.popper, {
    'data-popper-reference-hidden': isReferenceHidden,
    'data-popper-escaped': hasPopperEscaped
  });
} // eslint-disable-next-line import/no-unused-modules


var hide$1 = {
  name: 'hide',
  enabled: true,
  phase: 'main',
  requiresIfExists: ['preventOverflow'],
  fn: hide
};

function distanceAndSkiddingToXY(placement, rects, offset) {
  var basePlacement = getBasePlacement(placement);
  var invertDistance = [left, top].indexOf(basePlacement) >= 0 ? -1 : 1;

  var _ref = typeof offset === 'function' ? offset(Object.assign({}, rects, {
    placement: placement
  })) : offset,
      skidding = _ref[0],
      distance = _ref[1];

  skidding = skidding || 0;
  distance = (distance || 0) * invertDistance;
  return [left, right].indexOf(basePlacement) >= 0 ? {
    x: distance,
    y: skidding
  } : {
    x: skidding,
    y: distance
  };
}

function offset(_ref2) {
  var state = _ref2.state,
      options = _ref2.options,
      name = _ref2.name;
  var _options$offset = options.offset,
      offset = _options$offset === void 0 ? [0, 0] : _options$offset;
  var data = placements.reduce(function (acc, placement) {
    acc[placement] = distanceAndSkiddingToXY(placement, state.rects, offset);
    return acc;
  }, {});
  var _data$state$placement = data[state.placement],
      x = _data$state$placement.x,
      y = _data$state$placement.y;

  if (state.modifiersData.popperOffsets != null) {
    state.modifiersData.popperOffsets.x += x;
    state.modifiersData.popperOffsets.y += y;
  }

  state.modifiersData[name] = data;
} // eslint-disable-next-line import/no-unused-modules


var offset$1 = {
  name: 'offset',
  enabled: true,
  phase: 'main',
  requires: ['popperOffsets'],
  fn: offset
};

function popperOffsets(_ref) {
  var state = _ref.state,
      name = _ref.name;
  // Offsets are the actual position the popper needs to have to be
  // properly positioned near its reference element
  // This is the most basic placement, and will be adjusted by
  // the modifiers in the next step
  state.modifiersData[name] = computeOffsets({
    reference: state.rects.reference,
    element: state.rects.popper,
    strategy: 'absolute',
    placement: state.placement
  });
} // eslint-disable-next-line import/no-unused-modules


var popperOffsets$1 = {
  name: 'popperOffsets',
  enabled: true,
  phase: 'read',
  fn: popperOffsets,
  data: {}
};

function getAltAxis(axis) {
  return axis === 'x' ? 'y' : 'x';
}

function preventOverflow(_ref) {
  var state = _ref.state,
      options = _ref.options,
      name = _ref.name;
  var _options$mainAxis = options.mainAxis,
      checkMainAxis = _options$mainAxis === void 0 ? true : _options$mainAxis,
      _options$altAxis = options.altAxis,
      checkAltAxis = _options$altAxis === void 0 ? false : _options$altAxis,
      boundary = options.boundary,
      rootBoundary = options.rootBoundary,
      altBoundary = options.altBoundary,
      padding = options.padding,
      _options$tether = options.tether,
      tether = _options$tether === void 0 ? true : _options$tether,
      _options$tetherOffset = options.tetherOffset,
      tetherOffset = _options$tetherOffset === void 0 ? 0 : _options$tetherOffset;
  var overflow = detectOverflow(state, {
    boundary: boundary,
    rootBoundary: rootBoundary,
    padding: padding,
    altBoundary: altBoundary
  });
  var basePlacement = getBasePlacement(state.placement);
  var variation = getVariation(state.placement);
  var isBasePlacement = !variation;
  var mainAxis = getMainAxisFromPlacement(basePlacement);
  var altAxis = getAltAxis(mainAxis);
  var popperOffsets = state.modifiersData.popperOffsets;
  var referenceRect = state.rects.reference;
  var popperRect = state.rects.popper;
  var tetherOffsetValue = typeof tetherOffset === 'function' ? tetherOffset(Object.assign({}, state.rects, {
    placement: state.placement
  })) : tetherOffset;
  var normalizedTetherOffsetValue = typeof tetherOffsetValue === 'number' ? {
    mainAxis: tetherOffsetValue,
    altAxis: tetherOffsetValue
  } : Object.assign({
    mainAxis: 0,
    altAxis: 0
  }, tetherOffsetValue);
  var offsetModifierState = state.modifiersData.offset ? state.modifiersData.offset[state.placement] : null;
  var data = {
    x: 0,
    y: 0
  };

  if (!popperOffsets) {
    return;
  }

  if (checkMainAxis) {
    var _offsetModifierState$;

    var mainSide = mainAxis === 'y' ? top : left;
    var altSide = mainAxis === 'y' ? bottom : right;
    var len = mainAxis === 'y' ? 'height' : 'width';
    var offset = popperOffsets[mainAxis];
    var min$1 = offset + overflow[mainSide];
    var max$1 = offset - overflow[altSide];
    var additive = tether ? -popperRect[len] / 2 : 0;
    var minLen = variation === start ? referenceRect[len] : popperRect[len];
    var maxLen = variation === start ? -popperRect[len] : -referenceRect[len]; // We need to include the arrow in the calculation so the arrow doesn't go
    // outside the reference bounds

    var arrowElement = state.elements.arrow;
    var arrowRect = tether && arrowElement ? getLayoutRect(arrowElement) : {
      width: 0,
      height: 0
    };
    var arrowPaddingObject = state.modifiersData['arrow#persistent'] ? state.modifiersData['arrow#persistent'].padding : getFreshSideObject();
    var arrowPaddingMin = arrowPaddingObject[mainSide];
    var arrowPaddingMax = arrowPaddingObject[altSide]; // If the reference length is smaller than the arrow length, we don't want
    // to include its full size in the calculation. If the reference is small
    // and near the edge of a boundary, the popper can overflow even if the
    // reference is not overflowing as well (e.g. virtual elements with no
    // width or height)

    var arrowLen = within(0, referenceRect[len], arrowRect[len]);
    var minOffset = isBasePlacement ? referenceRect[len] / 2 - additive - arrowLen - arrowPaddingMin - normalizedTetherOffsetValue.mainAxis : minLen - arrowLen - arrowPaddingMin - normalizedTetherOffsetValue.mainAxis;
    var maxOffset = isBasePlacement ? -referenceRect[len] / 2 + additive + arrowLen + arrowPaddingMax + normalizedTetherOffsetValue.mainAxis : maxLen + arrowLen + arrowPaddingMax + normalizedTetherOffsetValue.mainAxis;
    var arrowOffsetParent = state.elements.arrow && getOffsetParent(state.elements.arrow);
    var clientOffset = arrowOffsetParent ? mainAxis === 'y' ? arrowOffsetParent.clientTop || 0 : arrowOffsetParent.clientLeft || 0 : 0;
    var offsetModifierValue = (_offsetModifierState$ = offsetModifierState == null ? void 0 : offsetModifierState[mainAxis]) != null ? _offsetModifierState$ : 0;
    var tetherMin = offset + minOffset - offsetModifierValue - clientOffset;
    var tetherMax = offset + maxOffset - offsetModifierValue;
    var preventedOffset = within(tether ? min(min$1, tetherMin) : min$1, offset, tether ? max(max$1, tetherMax) : max$1);
    popperOffsets[mainAxis] = preventedOffset;
    data[mainAxis] = preventedOffset - offset;
  }

  if (checkAltAxis) {
    var _offsetModifierState$2;

    var _mainSide = mainAxis === 'x' ? top : left;

    var _altSide = mainAxis === 'x' ? bottom : right;

    var _offset = popperOffsets[altAxis];

    var _len = altAxis === 'y' ? 'height' : 'width';

    var _min = _offset + overflow[_mainSide];

    var _max = _offset - overflow[_altSide];

    var isOriginSide = [top, left].indexOf(basePlacement) !== -1;

    var _offsetModifierValue = (_offsetModifierState$2 = offsetModifierState == null ? void 0 : offsetModifierState[altAxis]) != null ? _offsetModifierState$2 : 0;

    var _tetherMin = isOriginSide ? _min : _offset - referenceRect[_len] - popperRect[_len] - _offsetModifierValue + normalizedTetherOffsetValue.altAxis;

    var _tetherMax = isOriginSide ? _offset + referenceRect[_len] + popperRect[_len] - _offsetModifierValue - normalizedTetherOffsetValue.altAxis : _max;

    var _preventedOffset = tether && isOriginSide ? withinMaxClamp(_tetherMin, _offset, _tetherMax) : within(tether ? _tetherMin : _min, _offset, tether ? _tetherMax : _max);

    popperOffsets[altAxis] = _preventedOffset;
    data[altAxis] = _preventedOffset - _offset;
  }

  state.modifiersData[name] = data;
} // eslint-disable-next-line import/no-unused-modules


var preventOverflow$1 = {
  name: 'preventOverflow',
  enabled: true,
  phase: 'main',
  fn: preventOverflow,
  requiresIfExists: ['offset']
};

function getHTMLElementScroll(element) {
  return {
    scrollLeft: element.scrollLeft,
    scrollTop: element.scrollTop
  };
}

function getNodeScroll(node) {
  if (node === getWindow(node) || !isHTMLElement(node)) {
    return getWindowScroll(node);
  } else {
    return getHTMLElementScroll(node);
  }
}

function isElementScaled(element) {
  var rect = element.getBoundingClientRect();
  var scaleX = round(rect.width) / element.offsetWidth || 1;
  var scaleY = round(rect.height) / element.offsetHeight || 1;
  return scaleX !== 1 || scaleY !== 1;
} // Returns the composite rect of an element relative to its offsetParent.
// Composite means it takes into account transforms as well as layout.


function getCompositeRect(elementOrVirtualElement, offsetParent, isFixed) {
  if (isFixed === void 0) {
    isFixed = false;
  }

  var isOffsetParentAnElement = isHTMLElement(offsetParent);
  var offsetParentIsScaled = isHTMLElement(offsetParent) && isElementScaled(offsetParent);
  var documentElement = getDocumentElement(offsetParent);
  var rect = getBoundingClientRect(elementOrVirtualElement, offsetParentIsScaled, isFixed);
  var scroll = {
    scrollLeft: 0,
    scrollTop: 0
  };
  var offsets = {
    x: 0,
    y: 0
  };

  if (isOffsetParentAnElement || !isOffsetParentAnElement && !isFixed) {
    if (getNodeName(offsetParent) !== 'body' || // https://github.com/popperjs/popper-core/issues/1078
    isScrollParent(documentElement)) {
      scroll = getNodeScroll(offsetParent);
    }

    if (isHTMLElement(offsetParent)) {
      offsets = getBoundingClientRect(offsetParent, true);
      offsets.x += offsetParent.clientLeft;
      offsets.y += offsetParent.clientTop;
    } else if (documentElement) {
      offsets.x = getWindowScrollBarX(documentElement);
    }
  }

  return {
    x: rect.left + scroll.scrollLeft - offsets.x,
    y: rect.top + scroll.scrollTop - offsets.y,
    width: rect.width,
    height: rect.height
  };
}

function order(modifiers) {
  var map = new Map();
  var visited = new Set();
  var result = [];
  modifiers.forEach(function (modifier) {
    map.set(modifier.name, modifier);
  }); // On visiting object, check for its dependencies and visit them recursively

  function sort(modifier) {
    visited.add(modifier.name);
    var requires = [].concat(modifier.requires || [], modifier.requiresIfExists || []);
    requires.forEach(function (dep) {
      if (!visited.has(dep)) {
        var depModifier = map.get(dep);

        if (depModifier) {
          sort(depModifier);
        }
      }
    });
    result.push(modifier);
  }

  modifiers.forEach(function (modifier) {
    if (!visited.has(modifier.name)) {
      // check for visited object
      sort(modifier);
    }
  });
  return result;
}

function orderModifiers(modifiers) {
  // order based on dependencies
  var orderedModifiers = order(modifiers); // order based on phase

  return modifierPhases.reduce(function (acc, phase) {
    return acc.concat(orderedModifiers.filter(function (modifier) {
      return modifier.phase === phase;
    }));
  }, []);
}

function debounce(fn) {
  var pending;
  return function () {
    if (!pending) {
      pending = new Promise(function (resolve) {
        Promise.resolve().then(function () {
          pending = undefined;
          resolve(fn());
        });
      });
    }

    return pending;
  };
}

function mergeByName(modifiers) {
  var merged = modifiers.reduce(function (merged, current) {
    var existing = merged[current.name];
    merged[current.name] = existing ? Object.assign({}, existing, current, {
      options: Object.assign({}, existing.options, current.options),
      data: Object.assign({}, existing.data, current.data)
    }) : current;
    return merged;
  }, {}); // IE11 does not support Object.values

  return Object.keys(merged).map(function (key) {
    return merged[key];
  });
}

var DEFAULT_OPTIONS = {
  placement: 'bottom',
  modifiers: [],
  strategy: 'absolute'
};

function areValidElements() {
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  return !args.some(function (element) {
    return !(element && typeof element.getBoundingClientRect === 'function');
  });
}

function popperGenerator(generatorOptions) {
  if (generatorOptions === void 0) {
    generatorOptions = {};
  }

  var _generatorOptions = generatorOptions,
      _generatorOptions$def = _generatorOptions.defaultModifiers,
      defaultModifiers = _generatorOptions$def === void 0 ? [] : _generatorOptions$def,
      _generatorOptions$def2 = _generatorOptions.defaultOptions,
      defaultOptions = _generatorOptions$def2 === void 0 ? DEFAULT_OPTIONS : _generatorOptions$def2;
  return function createPopper(reference, popper, options) {
    if (options === void 0) {
      options = defaultOptions;
    }

    var state = {
      placement: 'bottom',
      orderedModifiers: [],
      options: Object.assign({}, DEFAULT_OPTIONS, defaultOptions),
      modifiersData: {},
      elements: {
        reference: reference,
        popper: popper
      },
      attributes: {},
      styles: {}
    };
    var effectCleanupFns = [];
    var isDestroyed = false;
    var instance = {
      state: state,
      setOptions: function setOptions(setOptionsAction) {
        var options = typeof setOptionsAction === 'function' ? setOptionsAction(state.options) : setOptionsAction;
        cleanupModifierEffects();
        state.options = Object.assign({}, defaultOptions, state.options, options);
        state.scrollParents = {
          reference: isElement(reference) ? listScrollParents(reference) : reference.contextElement ? listScrollParents(reference.contextElement) : [],
          popper: listScrollParents(popper)
        }; // Orders the modifiers based on their dependencies and `phase`
        // properties

        var orderedModifiers = orderModifiers(mergeByName([].concat(defaultModifiers, state.options.modifiers))); // Strip out disabled modifiers

        state.orderedModifiers = orderedModifiers.filter(function (m) {
          return m.enabled;
        });
        runModifierEffects();
        return instance.update();
      },
      // Sync update – it will always be executed, even if not necessary. This
      // is useful for low frequency updates where sync behavior simplifies the
      // logic.
      // For high frequency updates (e.g. `resize` and `scroll` events), always
      // prefer the async Popper#update method
      forceUpdate: function forceUpdate() {
        if (isDestroyed) {
          return;
        }

        var _state$elements = state.elements,
            reference = _state$elements.reference,
            popper = _state$elements.popper; // Don't proceed if `reference` or `popper` are not valid elements
        // anymore

        if (!areValidElements(reference, popper)) {
          return;
        } // Store the reference and popper rects to be read by modifiers


        state.rects = {
          reference: getCompositeRect(reference, getOffsetParent(popper), state.options.strategy === 'fixed'),
          popper: getLayoutRect(popper)
        }; // Modifiers have the ability to reset the current update cycle. The
        // most common use case for this is the `flip` modifier changing the
        // placement, which then needs to re-run all the modifiers, because the
        // logic was previously ran for the previous placement and is therefore
        // stale/incorrect

        state.reset = false;
        state.placement = state.options.placement; // On each update cycle, the `modifiersData` property for each modifier
        // is filled with the initial data specified by the modifier. This means
        // it doesn't persist and is fresh on each update.
        // To ensure persistent data, use `${name}#persistent`

        state.orderedModifiers.forEach(function (modifier) {
          return state.modifiersData[modifier.name] = Object.assign({}, modifier.data);
        });

        for (var index = 0; index < state.orderedModifiers.length; index++) {
          if (state.reset === true) {
            state.reset = false;
            index = -1;
            continue;
          }

          var _state$orderedModifie = state.orderedModifiers[index],
              fn = _state$orderedModifie.fn,
              _state$orderedModifie2 = _state$orderedModifie.options,
              _options = _state$orderedModifie2 === void 0 ? {} : _state$orderedModifie2,
              name = _state$orderedModifie.name;

          if (typeof fn === 'function') {
            state = fn({
              state: state,
              options: _options,
              name: name,
              instance: instance
            }) || state;
          }
        }
      },
      // Async and optimistically optimized update – it will not be executed if
      // not necessary (debounced to run at most once-per-tick)
      update: debounce(function () {
        return new Promise(function (resolve) {
          instance.forceUpdate();
          resolve(state);
        });
      }),
      destroy: function destroy() {
        cleanupModifierEffects();
        isDestroyed = true;
      }
    };

    if (!areValidElements(reference, popper)) {
      return instance;
    }

    instance.setOptions(options).then(function (state) {
      if (!isDestroyed && options.onFirstUpdate) {
        options.onFirstUpdate(state);
      }
    }); // Modifiers have the ability to execute arbitrary code before the first
    // update cycle runs. They will be executed in the same order as the update
    // cycle. This is useful when a modifier adds some persistent data that
    // other modifiers need to use, but the modifier is run after the dependent
    // one.

    function runModifierEffects() {
      state.orderedModifiers.forEach(function (_ref) {
        var name = _ref.name,
            _ref$options = _ref.options,
            options = _ref$options === void 0 ? {} : _ref$options,
            effect = _ref.effect;

        if (typeof effect === 'function') {
          var cleanupFn = effect({
            state: state,
            name: name,
            instance: instance,
            options: options
          });

          var noopFn = function noopFn() {};

          effectCleanupFns.push(cleanupFn || noopFn);
        }
      });
    }

    function cleanupModifierEffects() {
      effectCleanupFns.forEach(function (fn) {
        return fn();
      });
      effectCleanupFns = [];
    }

    return instance;
  };
}

var defaultModifiers = [eventListeners, popperOffsets$1, computeStyles$1, applyStyles$1, offset$1, flip$1, preventOverflow$1, arrow$1, hide$1];
var createPopper = /*#__PURE__*/popperGenerator({
  defaultModifiers: defaultModifiers
}); // eslint-disable-next-line import/no-unused-modules

const CPicker = defineComponent({
    name: 'CPicker',
    props: {
        /**
         * Set container type for the component.
         */
        container: {
            type: String,
            default: 'dropdown',
        },
        /**
         * Toggle the disabled state for the component.
         */
        disabled: Boolean,
        /**
         * A string of all className you want applied to the dropdown menu.
         */
        dropdownClassNames: String,
        /**
         * Toggle visibility of footer element or set the content of footer.
         */
        footer: Boolean,
        /**
         * Toggle the visibility of the component.
         */
        visible: Boolean,
    },
    emits: [
        /**
         * Callback fired when the component requests to be hidden.
         */
        'hide',
        /**
         * Callback fired when the component requests to be shown.
         */
        'show',
    ],
    setup(props, { attrs, emit, slots }) {
        const pickerRef = ref();
        const dropdownRef = ref();
        const togglerRef = ref();
        const popper = ref();
        const visible = ref(props.visible);
        watch(() => props.visible, () => {
            visible.value = props.visible;
        });
        watch(visible, () => {
            if (props.container === 'inline') {
                return;
            }
            if (visible.value) {
                emit('show');
                window.addEventListener('mouseup', handleMouseUp);
                window.addEventListener('keyup', handleKeyUp);
                initPopper();
                return;
            }
            emit('hide');
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('keyup', handleKeyUp);
            destroyPopper();
        });
        const initPopper = () => {
            if (togglerRef.value && dropdownRef.value) {
                popper.value = createPopper(togglerRef.value, dropdownRef.value, {
                    placement: isRTL(pickerRef.value) ? 'bottom-end' : 'bottom-start',
                    modifiers: [
                        {
                            name: 'preventOverflow',
                            options: {
                                boundary: 'clippingParents',
                            },
                        },
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 2],
                            },
                        },
                    ],
                });
            }
        };
        const destroyPopper = () => {
            if (popper.value) {
                popper.value.destroy();
            }
            popper.value = undefined;
        };
        const handleKeyUp = (event) => {
            if (event.key === 'Escape') {
                visible.value = false;
            }
        };
        const handleMouseUp = (event) => {
            if (pickerRef.value && pickerRef.value.contains(event.target)) {
                return;
            }
            visible.value = false;
        };
        switch (props.container) {
            case 'inline': {
                return () => h$1('div', { class: 'picker', ref: pickerRef }, slots.default && slots.default());
            }
            default: {
                return () => h$1('div', {
                    class: [
                        'dropdown',
                        'picker',
                        attrs.class,
                        {
                            show: visible.value,
                        },
                    ],
                    ref: pickerRef,
                }, [
                    /**
                     * @slot Location for the toggler element.
                     */
                    slots.toggler &&
                        slots.toggler().map((slot) => cloneVNode(slot, {
                            onClick: () => {
                                if (!props.disabled && !visible.value) {
                                    visible.value = true;
                                }
                            },
                            ref: (el) => {
                                togglerRef.value = el;
                            },
                        })),
                    h$1('div', {
                        class: [
                            'dropdown-menu',
                            {
                                show: visible.value,
                            },
                            props.dropdownClassNames,
                        ],
                        ref: dropdownRef,
                    }, [
                        slots.default && slots.default(),
                        /**
                         * @slot Location for the footer element.
                         */
                        slots.footer && slots.footer(),
                    ]),
                ]);
            }
        }
    },
});

const CPickerPlugin = {
    install: (app) => {
        app.component(CPicker.name, CPicker);
    },
};

const useDebouncedCallback = (callback, delay) => {
    const timeout = ref();
    const debouncedFn = (...args) => {
        const handler = () => {
            clearTimeout(timeout.value);
            callback(...args);
        };
        clearTimeout(timeout.value);
        timeout.value = setTimeout(handler, delay);
    };
    return debouncedFn();
};

const useIsVisible = (el) => {
    const isIntersecting = ref(false);
    const observer = ref();
    onMounted(() => {
        observer.value = new IntersectionObserver(([entry]) => {
            isIntersecting.value = entry.isIntersecting;
        });
        el.value && observer.value.observe(el.value);
    });
    onUnmounted(() => {
        observer.value.disconnect();
    });
    return isIntersecting;
};

const usePopper = () => {
    const _popper = ref();
    const initPopper = (reference, popper, options) => {
        _popper.value = createPopper(reference, popper, options);
    };
    const destroyPopper = () => {
        if (_popper.value) {
            _popper.value.destroy();
        }
        _popper.value = undefined;
    };
    return {
        popper: _popper.value,
        initPopper,
        destroyPopper,
    };
};

const CTimePickerRollCol = defineComponent({
    name: 'CTimePickerRollCol',
    props: {
        elements: {
            type: Array,
            required: true,
        },
        selected: {
            type: [Number, String],
        },
    },
    emits: ['click'],
    setup(props, { emit }) {
        const init = ref(true);
        const colRef = ref();
        const isVisible = useIsVisible(colRef);
        const scrollToSelectedElement = () => {
            const nodeEl = colRef.value?.querySelector('.selected');
            if (isVisible.value && nodeEl && nodeEl instanceof HTMLElement) {
                colRef.value?.scrollTo({
                    top: nodeEl.offsetTop,
                    behavior: init.value ? 'auto' : 'smooth',
                });
            }
        };
        watch(isVisible, () => {
            scrollToSelectedElement();
            if (isVisible.value) {
                init.value = false;
            }
        });
        onUpdated(() => {
            scrollToSelectedElement();
        });
        const handleKeyDown = (event, value) => {
            if (event.code === 'Space' || event.key === 'Enter') {
                event.preventDefault();
                emit('click', value);
            }
        };
        return () => h$1('div', { class: 'time-picker-roll-col', ref: colRef }, props.elements.map((element) => {
            return h$1('div', {
                class: [
                    'time-picker-roll-cell',
                    {
                        selected: element.value === props.selected,
                    },
                ],
                onClick: () => emit('click', element.value),
                onKeydown: (event) => handleKeyDown(event, element.value),
                role: 'button',
                tabindex: 0,
            }, element.label);
        }));
    },
});

const convert12hTo24h = (abbr, hour) => {
    if (abbr === 'am' && hour === 12) {
        return 0;
    }
    if (abbr === 'am') {
        return hour;
    }
    if (abbr === 'pm' && hour === 12) {
        return 12;
    }
    return hour + 12;
};
const convert24hTo12h = (hour) => hour % 12 || 12;
const convertTimeToDate = (time) => time ? (time instanceof Date ? new Date(time) : new Date(`1970-01-01 ${time}`)) : null;
const getAmPm = (date, locale) => {
    if (date.toLocaleTimeString(locale).includes('AM')) {
        return 'am';
    }
    if (date.toLocaleTimeString(locale).includes('PM')) {
        return 'pm';
    }
    return date.getHours() >= 12 ? 'pm' : 'am';
};
const getLocalizedTimePartials = (locale, ampm = 'auto') => {
    const date = new Date();
    const hour12 = ['am', 'AM', 'pm', 'PM'].some((el) => date.toLocaleString(locale).includes(el));
    const listOfHours = Array.from({ length: (ampm === 'auto' && hour12) || ampm === true ? 12 : 24 }, (_, i) => {
        return {
            value: (ampm === 'auto' && hour12) || ampm === true ? i + 1 : i,
            label: ((ampm === 'auto' && hour12) || ampm === true ? i + 1 : i).toLocaleString(locale),
        };
    });
    const listOfMinutesSeconds = Array.from({ length: 60 }, (_, i) => {
        date.setMinutes(i);
        return {
            value: i,
            label: date
                .toLocaleTimeString(locale, {
                minute: '2-digit',
                second: '2-digit',
            })
                .split(/[^A-Za-z0-9\u06F0-\u06F90-9]/)[0],
        };
    });
    return {
        listOfHours,
        listOfMinutes: listOfMinutesSeconds,
        listOfSeconds: listOfMinutesSeconds,
        hour12,
    };
};
const getSelectedHour = (date, locale, ampm = 'auto') => date
    ? (ampm === 'auto' && isAmPm(locale)) || ampm === true
        ? convert24hTo12h(date.getHours())
        : date.getHours()
    : '';
const getSelectedMinutes = (date) => (date ? date.getMinutes() : '');
const getSelectedSeconds = (date) => (date ? date.getSeconds() : '');
const isAmPm = (locale) => ['am', 'AM', 'pm', 'PM'].some((el) => new Date().toLocaleString(locale).includes(el));
const isValidTime = (time) => {
    const d = new Date(`1970-01-01 ${time}`);
    return d instanceof Date && d.getTime();
};

const Color = {
    type: String,
    validator: (value) => {
        // The value must match one of these strings
        return [
            'primary',
            'secondary',
            'success',
            'danger',
            'warning',
            'info',
            'dark',
            'light',
            'link',
            'transparent',
            'primary-gradient',
            'secondary-gradient',
            'success-gradient',
            'danger-gradient',
            'warning-gradient',
            'info-gradient',
            'dark-gradient',
            'light-gradient',
        ].includes(value);
    },
};

const CTimePicker = defineComponent({
    name: 'CTimePicker',
    props: {
        ...CPicker.props,
        /**
         * Set if the component should use the 12/24 hour format. If `true` forces the interface to a 12-hour format. If `false` forces the interface into a 24-hour format. If `auto` the current locale will determine the 12 or 24-hour interface by default locales.
         *
         * @since 4.7.0
         */
        ampm: {
            type: [Boolean, String],
            default: 'auto',
            validator: (value) => {
                if (typeof value == 'string') {
                    return ['auto'].includes(value);
                }
                if (typeof value == 'boolean') {
                    return true;
                }
                return false;
            },
        },
        /**
         * Toggle visibility or set the content of cancel button.
         */
        cancelButton: {
            type: [Boolean, String],
            default: 'Cancel',
        },
        /**
         * Sets the color context of the cancel button to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
         */
        cancelButtonColor: {
            ...Color,
            default: 'primary',
        },
        /**
         * Size the cancel button small or large.
         *
         * @values 'sm', 'lg'
         */
        cancelButtonSize: {
            type: String,
            default: 'sm',
            validator: (value) => {
                return ['sm', 'lg'].includes(value);
            },
        },
        /**
         * Set the cancel button variant to an outlined button or a ghost button.
         *
         * @values 'ghost', 'outline'
         */
        cancelButtonVariant: {
            type: String,
            default: 'ghost',
            validator: (value) => {
                return ['ghost', 'outline'].includes(value);
            },
        },
        /**
         * Toggle visibility of the cleaner button.
         */
        cleaner: {
            type: Boolean,
            default: true,
        },
        /**
         * Toggle visibility or set the content of confirm button.
         */
        confirmButton: {
            type: [Boolean, String],
            default: 'OK',
        },
        /**
         * Sets the color context of the confirm button to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
         */
        confirmButtonColor: {
            ...Color,
            default: 'primary',
        },
        /**
         * Size the confirm button small or large.
         *
         * @values 'sm', 'lg'
         */
        confirmButtonSize: {
            type: String,
            default: 'sm',
            validator: (value) => {
                return ['sm', 'lg'].includes(value);
            },
        },
        /**
         * Set the confirm button variant to an outlined button or a ghost button.
         *
         * @values 'ghost', 'outline'
         */
        confirmButtonVariant: {
            type: String,
            validator: (value) => {
                return ['ghost', 'outline'].includes(value);
            },
        },
        /**
         * Provide valuable, actionable feedback.
         *
         * @since 4.6.0
         */
        feedback: String,
        /**
         * Provide valuable, actionable feedback.
         *
         * @since 4.6.0
         */
        feedbackInvalid: String,
        /**
         * Provide valuable, actionable invalid feedback when using standard HTML form validation which applied two CSS pseudo-classes, `:invalid` and `:valid`.
         *
         * @since 4.6.0
         */
        feedbackValid: String,
        /**
         * The id global attribute defines an identifier (ID) that must be unique in the whole document.
         */
        id: String,
        /**
         * Toggle visibility or set the content of the input indicator.
         */
        indicator: {
            type: Boolean,
            default: true,
        },
        /**
         * Toggle the readonly state for the component.
         */
        inputReadOnly: Boolean,
        /**
         * Set component validation state to invalid.
         *
         * @since 4.6.0
         */
        invalid: {
            type: Boolean,
            default: undefined,
        },
        /**
         * Add a caption for a component.
         *
         * @since 4.6.0
         */
        label: String,
        /**
         * Sets the default locale for components. If not set, it is inherited from the navigator.language.
         */
        locale: {
            type: String,
            defalut: 'default',
        },
        /**
         * Specifies a short hint that is visible in the input.
         */
        placeholder: {
            type: String,
            default: 'Select time',
        },
        /**
         * When present, it specifies that must be filled out before submitting the form.
         *
         * @since 4.9.0
         */
        required: Boolean,
        /**
         * Show seconds.
         *
         * @since 4.7.0
         */
        seconds: {
            type: Boolean,
            default: true,
        },
        /**
         * Size the component small or large.
         *
         * @values 'sm', 'lg'
         */
        size: {
            type: String,
            default: undefined,
            validator: (value) => {
                return ['sm', 'lg'].includes(value);
            },
        },
        /**
         * Add helper text to the component.
         *
         * @since 4.6.0
         */
        text: String,
        /**
         * Initial selected time.
         */
        time: [Date, String],
        /**
         * Display validation feedback in a styled tooltip.
         *
         * @since 4.6.0
         */
        tooltipFeedback: Boolean,
        /**
         * Set component validation state to valid.
         *
         * @since 4.6.0
         */
        valid: {
            type: Boolean,
            default: undefined,
        },
        /**
         * Set the time picker variant to a roll or select.
         *
         * @values 'roll', 'select'
         */
        variant: {
            type: String,
            default: 'roll',
            validator: (value) => {
                return ['roll', 'select'].includes(value);
            },
        },
        /**
         * Toggle the visibility of the component.
         */
        visible: Boolean,
    },
    emits: [
        /**
         * Callback fired when the time changed.
         */
        'change',
        /**
         * Callback fired when the component requests to be hidden.
         */
        'hide',
        /**
         * Callback fired when the component requests to be shown.
         */
        'show',
        /**
         * Callback fired when the time changed.
         *
         * @since 4.7.0
         */
        'update:time',
    ],
    setup(props, { emit, attrs, slots }) {
        const formRef = ref();
        const inputRef = ref();
        const date = ref(convertTimeToDate(props.time));
        const ampm = ref(date.value ? getAmPm(new Date(date.value), props.locale) : 'am');
        const initialDate = ref(null);
        const visible = ref(props.visible);
        const localizedTimePartials = ref({
            listOfHours: [],
            listOfMinutes: [],
            listOfSeconds: [],
            hour12: false,
        });
        const isValid = ref(props.valid ?? (props.invalid === true ? false : undefined));
        watch(() => props.time, () => {
            date.value = convertTimeToDate(props.time);
        });
        watch(() => [props.valid, props.invalid], () => {
            isValid.value = props.valid ?? (props.invalid === true ? false : undefined);
        });
        watch(date, () => {
            localizedTimePartials.value = getLocalizedTimePartials(props.locale, props.ampm);
            if (date.value) {
                ampm.value = getAmPm(new Date(date.value), props.locale);
            }
        }, { immediate: true });
        watch(inputRef, () => {
            if (inputRef.value && inputRef.value.form) {
                formRef.value = inputRef.value.form;
            }
        });
        watch([formRef, date], () => {
            if (formRef.value) {
                formRef.value.addEventListener('submit', (event) => {
                    setTimeout(() => handleFormValidation(event.target));
                });
                handleFormValidation(formRef.value);
            }
        });
        const handleClear = (event) => {
            event.stopPropagation();
            date.value = null;
            emit('change', null);
            emit('update:time', null);
        };
        const handleFormValidation = (form) => {
            if (!form.classList.contains('was-validated')) {
                return;
            }
            if (date.value) {
                isValid.value = true;
                return;
            }
            isValid.value = false;
        };
        const handleTimeChange = (set, value) => {
            const _date = date.value || new Date('1970-01-01');
            if (set === 'toggle') {
                if (value === 'am') {
                    _date.setHours(_date.getHours() - 12);
                }
                if (value === 'pm') {
                    _date.setHours(_date.getHours() + 12);
                }
            }
            if (set === 'hours') {
                if (localizedTimePartials.value && localizedTimePartials.value.hour12) {
                    _date.setHours(convert12hTo24h(ampm.value, Number.parseInt(value)));
                }
                else {
                    _date.setHours(Number.parseInt(value));
                }
            }
            if (set === 'minutes') {
                _date.setMinutes(Number.parseInt(value));
            }
            if (set === 'seconds') {
                _date.setSeconds(Number.parseInt(value));
            }
            date.value = new Date(_date);
            emit('change', _date.toTimeString(), _date.toLocaleTimeString(props.locale), _date);
            emit('update:time', _date.toLocaleTimeString(props.locale));
        };
        const InputGroup = () => h$1('div', {
            class: [
                'input-group',
                'picker-input-group',
                {
                    [`input-group-${props.size}`]: props.size,
                },
            ],
        }, [
            h$1('input', {
                autocomplete: 'off',
                class: 'form-control',
                disabled: props.disabled,
                onInput: (event) => {
                    if (isValidTime(event.target.value)) {
                        date.value = convertTimeToDate(event.target.value);
                    }
                },
                placeholder: props.placeholder,
                readonly: props.inputReadOnly,
                ref: inputRef,
                required: props.required,
                value: date.value
                    ? date.value.toLocaleTimeString(props.locale, {
                        hour12: localizedTimePartials.value && localizedTimePartials.value.hour12,
                        ...(!props.seconds && { timeStyle: 'short' }),
                    })
                    : '',
            }),
            (props.indicator || props.cleaner) &&
                h$1('div', { class: 'input-group-text' }, [
                    props.indicator &&
                        h$1('span', {
                            class: 'picker-input-group-indicator',
                        }, slots.indicator
                            ? slots.indicator()
                            : h$1('span', { class: 'picker-input-group-icon time-picker-input-icon' })),
                    props.cleaner &&
                        h$1('span', {
                            class: 'picker-input-group-cleaner',
                            onClick: (event) => handleClear(event),
                            role: 'button',
                        }, slots.cleaner
                            ? slots.cleaner()
                            : h$1('span', { class: 'picker-input-group-icon time-picker-cleaner-icon' })),
                ]),
        ]);
        const TimePickerSelect = () => [
            h$1('span', { class: 'time-picker-inline-icon' }),
            h$1('select', {
                class: 'form-select',
                disabled: props.disabled,
                onChange: (event) => handleTimeChange('hours', event.target.value),
                ...(date.value && { value: getSelectedHour(date.value, props.locale) }),
            }, localizedTimePartials.value &&
                localizedTimePartials.value.listOfHours.map((option) => h$1('option', {
                    value: option.value.toString(),
                }, option.label))),
            ':',
            h$1('select', {
                class: 'form-select',
                disabled: props.disabled,
                onChange: (event) => handleTimeChange('minutes', event.target.value),
                ...(date.value && { value: getSelectedMinutes(date.value) }),
            }, localizedTimePartials.value &&
                localizedTimePartials.value.listOfMinutes?.map((option) => h$1('option', {
                    value: option.value.toString(),
                }, option.label))),
            props.seconds && ':',
            props.seconds &&
                h$1('select', {
                    class: 'form-select',
                    disabled: props.disabled,
                    onChange: (event) => handleTimeChange('seconds', event.target.value),
                    ...(date.value && { value: getSelectedSeconds(date.value) }),
                }, localizedTimePartials.value &&
                    localizedTimePartials.value.listOfSeconds?.map((option) => h$1('option', {
                        value: option.value.toString(),
                    }, option.label))),
            localizedTimePartials.value &&
                localizedTimePartials.value.hour12 &&
                h$1('select', {
                    class: 'form-select',
                    disabled: props.disabled,
                    onChange: (event) => handleTimeChange('toggle', event.target.value),
                    value: ampm.value,
                }, [
                    h$1('option', {
                        value: 'am',
                    }, 'AM'),
                    h$1('option', {
                        value: 'pm',
                    }, 'PM'),
                ]),
        ];
        const TimePickerRoll = () => [
            h$1(CTimePickerRollCol, {
                elements: localizedTimePartials.value && localizedTimePartials.value.listOfHours,
                onClick: (index) => handleTimeChange('hours', index.toString()),
                selected: getSelectedHour(date.value, props.locale, props.ampm),
            }),
            h$1(CTimePickerRollCol, {
                elements: localizedTimePartials.value && localizedTimePartials.value.listOfMinutes,
                onClick: (index) => handleTimeChange('minutes', index.toString()),
                selected: getSelectedMinutes(date.value),
            }),
            props.seconds &&
                h$1(CTimePickerRollCol, {
                    elements: localizedTimePartials.value && localizedTimePartials.value.listOfSeconds,
                    onClick: (index) => handleTimeChange('seconds', index.toString()),
                    selected: getSelectedSeconds(date.value),
                }),
            localizedTimePartials.value &&
                localizedTimePartials.value.hour12 &&
                h$1(CTimePickerRollCol, {
                    elements: [
                        { value: 'am', label: 'AM' },
                        { value: 'pm', label: 'PM' },
                    ],
                    onClick: (value) => handleTimeChange('toggle', value),
                    selected: ampm.value,
                }),
        ];
        return () => h$1(CFormControlWrapper, {
            describedby: attrs['aria-describedby'],
            feedback: props.feedback,
            feedbackInvalid: props.feedbackInvalid,
            feedbackValid: props.feedbackValid,
            id: props.id,
            invalid: isValid.value === false ? true : false,
            label: props.label,
            text: props.text,
            tooltipFeedback: props.tooltipFeedback,
            valid: isValid.value,
        }, {
            default: () => h$1(CPicker, {
                class: [
                    'time-picker',
                    {
                        disabled: props.disabled,
                        'is-invalid': isValid.value === false ? true : false,
                        'is-valid': isValid.value,
                    },
                ],
                container: props.container,
                disabled: props.disabled,
                dropdownClassNames: 'time-picker-dropdown',
                footer: true,
                onHide: () => {
                    visible.value = false;
                    emit('hide');
                },
                onShow: () => {
                    if (date.value) {
                        initialDate.value = new Date(date.value);
                    }
                    visible.value = true;
                    emit('show');
                },
                visible: visible.value,
            }, {
                ...(slots.cancelButton && {
                    cancelButton: () => slots.cancelButton && slots.cancelButton(),
                }),
                ...(slots.confirmButton && {
                    confirmButton: () => slots.confirmButton && slots.confirmButton(),
                }),
                toggler: () => InputGroup(),
                default: () => h$1('div', {
                    class: [
                        'time-picker-body',
                        {
                            ['time-picker-roll']: props.variant === 'roll',
                        },
                    ],
                }, props.variant === 'select' ? TimePickerSelect() : TimePickerRoll()),
                footer: () => h$1('div', { class: 'picker-footer' }, [
                    props.cancelButton &&
                        h$1(CButton, {
                            color: props.cancelButtonColor,
                            onClick: () => {
                                if (initialDate.value) {
                                    date.value = new Date(initialDate.value);
                                }
                                visible.value = false;
                            },
                            size: props.cancelButtonSize,
                            variant: props.cancelButtonVariant,
                        }, () => props.cancelButton),
                    props.confirmButton &&
                        h$1(CButton, {
                            color: props.confirmButtonColor,
                            onClick: () => {
                                visible.value = false;
                            },
                            size: props.confirmButtonSize,
                            variant: props.confirmButtonVariant,
                        }, () => props.confirmButton),
                ]),
            }),
        });
    },
});

const CTimePickerPlugin = {
    install: (app) => {
        app.component(CTimePicker.name, CTimePicker);
    },
};

const getLocalDateFromString = (string, locale, time) => {
    if (!Number.isNaN(Date.parse(string))) {
        return new Date(Date.parse(string));
    }
    const date = new Date(2013, 11, 31, 17, 19, 22);
    let regex = time ? date.toLocaleString(locale) : date.toLocaleDateString(locale);
    regex = regex
        .replace('2013', '(?<year>[0-9]{2,4})')
        .replace('12', '(?<month>[0-9]{1,2})')
        .replace('31', '(?<day>[0-9]{1,2})');
    if (time) {
        regex = regex
            .replace('5', '(?<hour>[0-9]{1,2})')
            .replace('17', '(?<hour>[0-9]{1,2})')
            .replace('19', '(?<minute>[0-9]{1,2})')
            .replace('22', '(?<second>[0-9]{1,2})')
            .replace('PM', '(?<ampm>[A-Z]{2})');
    }
    const rgx = new RegExp(`${regex}`);
    const partials = string.match(rgx);
    if (partials === null)
        return;
    const newDate = partials.groups &&
        (time
            ? new Date(Number(partials.groups['year']), Number(partials.groups['month']) - 1, Number(partials.groups['day']), partials.groups['ampm']
                ? partials.groups['ampm'] === 'PM'
                    ? Number(partials.groups['hour']) + 12
                    : Number(partials.groups['hour'])
                : Number(partials.groups['hour']), Number(partials.groups['minute']), Number(partials.groups['second']))
            : new Date(Number(partials.groups['year']), Number(partials.groups['month']) - 1, Number(partials.groups['day'])));
    return newDate;
};

const CDateRangePicker = defineComponent({
    name: 'CDateRangePicker',
    props: {
        /**
         * The number of calendars that render on desktop devices.
         */
        calendars: {
            type: Number,
            default: 2,
        },
        /**
         * Default date of the component
         */
        calendarDate: [Date, String],
        /**
         * Toggle visibility or set the content of cancel button.
         */
        cancelButton: {
            type: [Boolean, String],
            default: 'Cancel',
        },
        /**
         * Sets the color context of the cancel button to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
         */
        cancelButtonColor: {
            ...Color,
            default: 'primary',
        },
        /**
         * Size the cancel button small or large.
         *
         * @values 'sm', 'lg'
         */
        cancelButtonSize: {
            type: String,
            default: 'sm',
            validator: (value) => {
                return ['sm', 'lg'].includes(value);
            },
        },
        /**
         * Set the cancel button variant to an outlined button or a ghost button.
         *
         * @values 'ghost', 'outline'
         */
        cancelButtonVariant: {
            type: String,
            default: 'ghost',
            validator: (value) => {
                return ['ghost', 'outline'].includes(value);
            },
        },
        /**
         * Toggle visibility of the cleaner button.
         */
        cleaner: {
            type: Boolean,
            default: true,
        },
        /**
         * If true the dropdown will be immediately closed after submitting the full date.
         *
         * @since 4.7.0
         */
        closeOnSelect: {
            type: Boolean,
            default: true,
        },
        /**
         * Toggle visibility or set the content of confirm button.
         */
        confirmButton: {
            type: [Boolean, String],
            default: 'OK',
        },
        /**
         * Sets the color context of the confirm button to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
         */
        confirmButtonColor: {
            ...Color,
            default: 'primary',
        },
        /**
         * Size the confirm button small or large.
         *
         * @values 'sm', 'lg'
         */
        confirmButtonSize: {
            type: String,
            default: 'sm',
            validator: (value) => {
                return ['sm', 'lg'].includes(value);
            },
        },
        /**
         * Set the confirm button variant to an outlined button or a ghost button.
         *
         * @values 'ghost', 'outline'
         */
        confirmButtonVariant: {
            type: String,
            validator: (value) => {
                return ['ghost', 'outline'].includes(value);
            },
        },
        /**
         * Set the format of day name.
         *
         * @default 'numeric'
         * @since 4.6.0
         */
        dayFormat: {
            type: [Function, String],
            default: 'numeric',
            required: false,
            validator: (value) => {
                if (typeof value === 'string') {
                    return ['numeric', '2-digit'].includes(value);
                }
                if (typeof value === 'function') {
                    return true;
                }
                if (typeof value === 'function') {
                    return true;
                }
                return false;
            },
        },
        /**
         * Toggle the disabled state for the component.
         */
        disabled: Boolean,
        /**
         * Specify the list of dates that cannot be selected.
         */
        disabledDates: Array,
        /**
         * Initial selected to date (range).
         */
        endDate: [Date, String],
        /**
         * Provide valuable, actionable feedback.
         *
         * @since 4.6.0
         */
        feedback: String,
        /**
         * Provide valuable, actionable feedback.
         *
         * @since 4.6.0
         */
        feedbackInvalid: String,
        /**
         * Provide valuable, actionable invalid feedback when using standard HTML form validation which applied two CSS pseudo-classes, `:invalid` and `:valid`.
         *
         * @since 4.6.0
         */
        feedbackValid: String,
        /**
         * Sets the day of start week.
         * - 0 - Sunday,
         * - 1 - Monday,
         * - 2 - Tuesday,
         * - 3 - Wednesday,
         * - 4 - Thursday,
         * - 5 - Friday,
         * - 6 - Saturday,
         */
        firstDayOfWeek: {
            type: Number,
            default: 1,
        },
        /**
         * Set date format.
         * We use date-fns to format dates. Visit https://date-fns.org/v2.28.0/docs/format to check accepted patterns.
         */
        format: String,
        /**
         * Toggle visibility of footer element or set the content of footer.
         */
        footer: Boolean,
        /**
         * The id global attribute defines an identifier (ID) that must be unique in the whole document.
         *
         * The name attributes for input elements are generated based on the `id` property:
         * - {id}-start-date
         * - {id}-end-date
         */
        id: String,
        /**
         * Toggle visibility or set the content of the input indicator.
         */
        indicator: {
            type: Boolean,
            default: true,
        },
        /**
         * Custom function to format the selected date into a string according to a custom format.
         *
         * @since v4.11.0
         */
        inputDateFormat: Function,
        /**
         * Custom function to parse the input value into a valid Date object.
         *
         * @since v4.11.0
         */
        inputDateParse: Function,
        /**
         * Defines the delay (in milliseconds) for the input field's onChange event.
         *
         * @since v4.11.0
         */
        inputOnChangeDelay: {
            type: Number,
            default: 750,
        },
        /**
         * Toggle the readonly state for the component.
         */
        inputReadOnly: Boolean,
        /**
         * Set component validation state to invalid.
         *
         * @since 4.6.0
         */
        invalid: {
            type: Boolean,
            default: undefined,
        },
        /**
         * Add a caption for a component.
         *
         * @since 4.6.0
         */
        label: String,
        /**
         * Sets the default locale for components. If not set, it is inherited from the navigator.language.
         */
        locale: {
            type: String,
            default: 'default',
        },
        /**
         * Max selectable date.
         */
        maxDate: [Date, String],
        /**
         * Min selectable date.
         */
        minDate: [Date, String],
        /**
         * Show arrows navigation.
         */
        navigation: {
            type: Boolean,
            default: true,
        },
        /**
         * Reorder year-month navigation, and render year first.
         *
         * @since 4.6.0
         */
        navYearFirst: Boolean,
        /**
         * Specifies a short hint that is visible in the input.
         */
        placeholder: {
            type: [String, Array],
            default: () => ['Start date', 'End date'],
        },
        /**
         * @ignore
         */
        range: {
            type: Boolean,
            default: true,
        },
        /**
         * Predefined date ranges the user can select from.
         */
        ranges: Object,
        /**
         * When present, it specifies that must be filled out before submitting the form.
         *
         * @since 4.9.0
         */
        required: Boolean,
        /**
         * Toggle select mode between start and end date.
         */
        selectEndDate: Boolean,
        /**
         * Set whether days in adjacent months shown before or after the current month are selectable. This only applies if the `showAdjacementDays` option is set to true.
         *
         * @since 4.9.0
         */
        selectAdjacementDays: Boolean,
        /**
         * Set whether to display dates in adjacent months (non-selectable) at the start and end of the current month.
         *
         * @since 4.9.0
         */
        showAdjacementDays: {
            type: Boolean,
            default: true,
        },
        /**
         * Default icon or character character that separates two dates.
         */
        separator: {
            type: Boolean,
            default: true,
        },
        /**
         * Size the component small or large.
         *
         * @values 'sm', 'lg'
         */
        size: {
            type: String,
            required: false,
            validator: (value) => {
                return ['sm', 'lg'].includes(value);
            },
        },
        /**
         * Initial selected date.
         */
        startDate: [Date, String],
        /**
         * Add helper text to the component.
         *
         * @since 4.6.0
         */
        text: String,
        /**
         * Provide an additional time selection by adding select boxes to choose times.
         */
        timepicker: Boolean,
        /**
         * Toggle visibility or set the content of today button.
         */
        todayButton: {
            type: [Boolean, String],
            default: 'Today',
        },
        /**
         * Sets the color context of the today button to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
         */
        todayButtonColor: {
            ...Color,
            default: 'primary',
        },
        /**
         * Size the today button small or large.
         *
         * @values 'sm', 'lg'
         */
        todayButtonSize: {
            type: String,
            default: 'sm',
            validator: (value) => {
                return ['sm', 'lg'].includes(value);
            },
        },
        /**
         * Set the today button variant to an outlined button or a ghost button.
         *
         * @values 'ghost', 'outline'
         */
        todayButtonVariant: {
            type: String,
            validator: (value) => {
                return ['ghost', 'outline'].includes(value);
            },
        },
        /**
         * Display validation feedback in a styled tooltip.
         *
         * @since 4.6.0
         */
        tooltipFeedback: Boolean,
        /**
         * Set component validation state to valid.
         *
         * @since 4.6.0
         */
        valid: {
            type: Boolean,
            default: undefined,
        },
        /**
         * Toggle the visibility of the component.
         */
        visible: Boolean,
        /**
         * Set length or format of day name.
         *
         * @type  number | 'long' | 'narrow' | 'short'
         */
        weekdayFormat: {
            type: [Function, Number, String],
            default: 2,
            validator: (value) => {
                if (typeof value === 'string') {
                    return ['long', 'narrow', 'short'].includes(value);
                }
                if (typeof value === 'number') {
                    return true;
                }
                if (typeof value === 'function') {
                    return true;
                }
                return false;
            },
        },
    },
    emits: [
        /**
         * Callback fired when the end date changed.
         *
         * @property {Date} date - date object
         * @property {string} formatedDate - formated date
         */
        'end-date-change',
        /**
         * Callback fired when the component requests to be hidden.
         */
        'hide',
        /**
         * Callback fired when the component requests to be shown.
         */
        'show',
        /**
         * Callback fired when the start date changed.
         *
         * @property {Date} date - date object
         * @property {string} formatedDate - formated date
         */
        'start-date-change',
        /**
         * Callback fired when the start date changed.
         *
         * @property {Date | null} date - date object
         * @since 4.7.0
         */
        'update:start-date',
        /**
         * Callback fired when the end date changed.
         *
         * @property {Date | null} date - date object
         * @since 4.7.0
         */
        'update:end-date',
    ],
    setup(props, { slots, attrs, emit }) {
        const inputEndRef = ref();
        const inputStartRef = ref();
        const formRef = ref();
        const visible = ref(props.visible);
        const calendarDate = ref(props.calendarDate
            ? new Date(props.calendarDate)
            : props.startDate
                ? new Date(props.startDate)
                : props.endDate
                    ? new Date(props.endDate)
                    : new Date());
        const inputStartHoverValue = ref(null);
        const inputEndHoverValue = ref(null);
        const startDate = ref(props.startDate ? new Date(props.startDate) : null);
        const endDate = ref(props.endDate ? new Date(props.endDate) : null);
        const initialStartDate = ref(startDate.value ? new Date(startDate.value) : null);
        const initialEndDate = ref(endDate.value ? new Date(endDate.value) : null);
        const maxDate = ref(props.maxDate ? new Date(props.maxDate) : null);
        const minDate = ref(props.minDate ? new Date(props.minDate) : null);
        const selectEndDate = ref(false);
        const isValid = ref(props.valid ?? (props.invalid === true ? false : undefined));
        const isMobile = ref(false);
        onMounted(() => {
            isMobile.value = window.innerWidth < 768;
        });
        watch(() => [props.valid, props.invalid], () => {
            isValid.value = props.valid ?? (props.invalid === true ? false : undefined);
        });
        watch(() => props.startDate, () => {
            calendarDate.value = props.startDate ? new Date(props.startDate) : new Date();
            startDate.value = props.startDate ? new Date(props.startDate) : null;
        });
        watch(() => props.endDate, () => {
            calendarDate.value = props.endDate ? new Date(props.endDate) : new Date();
            endDate.value = props.endDate ? new Date(props.endDate) : null;
        });
        watch(() => props.maxDate, () => {
            maxDate.value = props.maxDate ? new Date(props.maxDate) : null;
        });
        watch(() => props.minDate, () => {
            minDate.value = props.minDate ? new Date(props.minDate) : null;
        });
        watch(inputStartRef, () => {
            if (inputStartRef.value && inputStartRef.value.form) {
                formRef.value = inputStartRef.value.form;
            }
        });
        watch([formRef, startDate, endDate], () => {
            if (formRef.value) {
                formRef.value.addEventListener('submit', (event) => {
                    setTimeout(() => handleFormValidation(event.target));
                });
                handleFormValidation(formRef.value);
            }
        });
        const formatDate = (date) => {
            return props.inputDateFormat
                ? props.inputDateFormat(date)
                : props.format
                    ? format(date, props.format)
                    : props.timepicker
                        ? date.toLocaleString(props.locale)
                        : date.toLocaleDateString(props.locale);
        };
        const setInputValue = (date) => {
            if (date) {
                return formatDate(date);
            }
            return '';
        };
        const handleCalendarCellHover = (date) => {
            if (selectEndDate.value) {
                inputEndHoverValue.value = date;
                return;
            }
            inputStartHoverValue.value = date;
        };
        const handleCalendarDateChange = (date, difference) => {
            if (difference) {
                calendarDate.value = new Date(date.getFullYear(), date.getMonth() - difference, 1);
                return;
            }
            calendarDate.value = date;
        };
        const handleFormValidation = (form) => {
            if (!form.classList.contains('was-validated')) {
                return;
            }
            if ((props.range && startDate.value && endDate.value) || (!props.range && startDate.value)) {
                isValid.value = true;
                return;
            }
            isValid.value = false;
        };
        const handleStartDateChange = (date) => {
            startDate.value = date;
            inputStartHoverValue.value = null;
            if (props.range) {
                selectEndDate.value = true;
            }
            emit('start-date-change', date, date ? formatDate(date) : undefined);
            emit('update:start-date', date);
            if (props.timepicker || props.footer) {
                return;
            }
            if (props.closeOnSelect && !props.range) {
                visible.value = false;
            }
        };
        const handleEndDateChange = (date) => {
            endDate.value = date;
            inputEndHoverValue.value = null;
            if (props.range) {
                selectEndDate.value = false;
            }
            emit('end-date-change', date, date ? formatDate(date) : undefined);
            emit('update:end-date', date);
            if (props.timepicker || props.footer) {
                return;
            }
            if (props.closeOnSelect && startDate.value !== null) {
                visible.value = false;
            }
        };
        const handleClear = (event) => {
            event.stopPropagation();
            startDate.value = null;
            endDate.value = null;
            inputStartHoverValue.value = null;
            inputEndHoverValue.value = null;
            emit('start-date-change', null);
            emit('end-date-change', null);
            emit('update:start-date', null);
            emit('update:end-date', null);
        };
        const handleOnChange = (value, input) => {
            const date = props.inputDateParse
                ? props.inputDateParse(value)
                : getLocalDateFromString(value, props.locale, props.timepicker);
            if (date instanceof Date && date.getTime()) {
                calendarDate.value = date;
                if (input === 'start') {
                    startDate.value = date;
                }
                else {
                    endDate.value = date;
                }
            }
        };
        const InputGroup = () => h$1('div', {
            class: [
                'input-group',
                'picker-input-group',
                {
                    [`input-group-${props.size}`]: props.size,
                },
            ],
        }, [
            h$1('input', {
                autocomplete: 'off',
                class: [
                    'form-control date-picker-input',
                    {
                        hover: inputStartHoverValue.value,
                    },
                ],
                disabled: props.disabled,
                ...(props.id && { name: props.range ? `${props.id}-start-date` : `${props.id}-date` }),
                onClick: () => {
                    selectEndDate.value = false;
                },
                onChange: (event) => handleOnChange(event.target.value, 'start'),
                onInput: (event) => useDebouncedCallback(() => handleOnChange(event.target.value, 'start'), props.inputOnChangeDelay),
                placeholder: Array.isArray(props.placeholder)
                    ? props.placeholder[0]
                    : props.placeholder,
                readonly: props.inputReadOnly || typeof props.format === 'string',
                required: props.required,
                ref: inputStartRef,
                value: inputStartHoverValue.value
                    ? setInputValue(inputStartHoverValue.value)
                    : setInputValue(startDate.value),
            }),
            props.range &&
                props.separator !== false &&
                h$1('span', { class: 'input-group-text' }, slots.separator
                    ? slots.separator()
                    : h$1('span', { class: 'picker-input-group-icon date-picker-arrow-icon' })),
            props.range &&
                h$1('input', {
                    autocomplete: 'off',
                    class: [
                        'form-control date-picker-input',
                        {
                            hover: inputEndHoverValue.value,
                        },
                    ],
                    disabled: props.disabled,
                    ...(props.id && { name: `${props.id}-end-date` }),
                    onClick: () => {
                        selectEndDate.value = true;
                    },
                    onChange: (event) => handleOnChange(event.target.value, 'end'),
                    onInput: (event) => useDebouncedCallback(() => handleOnChange(event.target.value, 'end'), props.inputOnChangeDelay),
                    placeholder: props.placeholder[1],
                    readonly: props.inputReadOnly || typeof props.format === 'string',
                    required: props.required,
                    ref: inputEndRef,
                    value: inputEndHoverValue.value
                        ? setInputValue(inputEndHoverValue.value)
                        : setInputValue(endDate.value),
                }),
            (props.indicator || props.cleaner) &&
                h$1('span', { class: 'input-group-text' }, [
                    props.indicator &&
                        h$1('span', {
                            class: 'picker-input-group-indicator',
                        }, slots.indicator
                            ? slots.indicator()
                            : h$1('span', { class: 'picker-input-group-icon date-picker-input-icon' })),
                    props.cleaner &&
                        h$1('span', {
                            class: 'picker-input-group-cleaner',
                            onClick: (event) => handleClear(event),
                            role: 'button',
                        }, slots.cleaner
                            ? slots.cleaner()
                            : h$1('span', { class: 'picker-input-group-icon date-picker-cleaner-icon' })),
                ]),
        ]);
        return () => h$1(CFormControlWrapper, {
            describedby: attrs['aria-describedby'],
            feedback: props.feedback,
            feedbackInvalid: props.feedbackInvalid,
            feedbackValid: props.feedbackValid,
            id: props.id,
            invalid: isValid.value === false ? true : false,
            label: props.label,
            text: props.text,
            tooltipFeedback: props.tooltipFeedback,
            valid: isValid.value,
        }, {
            default: () => h$1(CPicker, {
                class: [
                    'date-picker',
                    {
                        disabled: props.disabled,
                        'is-invalid': isValid.value === false ? true : false,
                        'is-valid': isValid.value,
                    },
                ],
                disabled: props.disabled,
                dropdownClassNames: 'date-picker-dropdown',
                footer: props.footer || props.timepicker,
                id: props.id,
                onHide: () => {
                    visible.value = false;
                    emit('hide');
                },
                onShow: () => {
                    if (startDate.value) {
                        initialStartDate.value = new Date(startDate.value);
                    }
                    if (endDate.value) {
                        initialEndDate.value = new Date(endDate.value);
                    }
                    visible.value = true;
                    emit('show');
                },
                visible: visible.value,
            }, {
                toggler: () => InputGroup(),
                footer: () => h$1('div', { class: 'picker-footer' }, [
                    props.todayButton &&
                        h$1(CButton, {
                            class: 'me-auto',
                            color: props.todayButtonColor,
                            size: props.todayButtonSize,
                            variant: props.todayButtonVariant,
                            onClick: () => {
                                const date = new Date();
                                startDate.value = date;
                                if (props.range) {
                                    endDate.value = date;
                                }
                                calendarDate.value = date;
                            },
                        }, () => props.todayButton),
                    props.cancelButton &&
                        h$1(CButton, {
                            color: props.cancelButtonColor,
                            onClick: () => {
                                startDate.value = initialStartDate.value;
                                if (props.range) {
                                    endDate.value = initialEndDate.value;
                                }
                                visible.value = false;
                            },
                            size: props.cancelButtonSize,
                            variant: props.cancelButtonVariant,
                        }, () => props.cancelButton),
                    props.confirmButton &&
                        h$1(CButton, {
                            color: props.confirmButtonColor,
                            onClick: () => {
                                visible.value = false;
                            },
                            size: props.confirmButtonSize,
                            variant: props.confirmButtonVariant,
                        }, () => props.confirmButton),
                ]),
                default: () => h$1('div', {
                    class: 'date-picker-body',
                }, [
                    props.ranges &&
                        h$1('div', { class: 'date-picker-ranges' }, Object.keys(props.ranges).map((key) => h$1(CButton, {
                            color: 'secondary',
                            onClick: () => {
                                if (props.ranges) {
                                    startDate.value = props.ranges[key][0];
                                    endDate.value = props.ranges[key][1];
                                }
                            },
                            variant: 'ghost',
                        }, () => key))),
                    h$1('div', { class: 'date-picker-calendars' }, h$1(CCalendar, {
                        calendarDate: new Date(calendarDate.value.getFullYear(), calendarDate.value.getMonth(), 1),
                        calendars: props.calendars,
                        dayFormat: props.dayFormat,
                        disabledDates: props.disabledDates,
                        ...(endDate.value && { endDate: endDate.value }),
                        firstDayOfWeek: props.firstDayOfWeek,
                        locale: props.locale,
                        ...(maxDate.value && { maxDate: maxDate.value }),
                        ...(minDate.value && { minDate: minDate.value }),
                        navYearFirst: props.navYearFirst,
                        navigation: props.navigation,
                        range: props.range,
                        selectEndDate: selectEndDate.value,
                        selectAdjacementDays: props.selectAdjacementDays,
                        showAdjacementDays: props.showAdjacementDays,
                        ...(startDate.value && { startDate: startDate.value }),
                        onCalendarCellHover: (date) => handleCalendarCellHover(date),
                        onCalendarDateChange: (date) => handleCalendarDateChange(date),
                        onStartDateChange: (date) => handleStartDateChange(date),
                        onEndDateChange: (date) => handleEndDateChange(date),
                    }, {
                        /**
                         * @slot Location for next icon.
                         */
                        ...(slots.navNextIcon && {
                            navNextIcon: () => slots.navNextIcon && slots.navNextIcon(),
                        }),
                        /**
                         * @slot Location for next double icon.
                         */
                        ...(slots.navNextDoubleIcon && {
                            navNextDoubleIcon: () => slots.navNextDoubleIcon && slots.navNextDoubleIcon(),
                        }),
                        /**
                         * @slot Location for previous icon.
                         */
                        ...(slots.navPrevIcon && {
                            navPrevIcon: () => slots.navPrevIcon && slots.navPrevIcon(),
                        }),
                        /**
                         * @slot Location for double previous icon.
                         */
                        ...(slots.navPrevDoubleIcon && {
                            navPrevDoubleIcon: () => slots.navPrevDoubleIcon && slots.navPrevDoubleIcon(),
                        }),
                    })),
                    props.timepicker &&
                        h$1('div', { class: 'date-picker-timepickers' }, isMobile.value || (props.range && props.calendars === 1)
                            ? [
                                h$1(CTimePicker, {
                                    container: 'inline',
                                    disabled: startDate.value === null ? true : false,
                                    locale: props.locale,
                                    onChange: (_, __, date) => handleStartDateChange(date),
                                    time: startDate.value,
                                    variant: 'select',
                                }),
                                h$1(CTimePicker, {
                                    container: 'inline',
                                    disabled: endDate.value === null ? true : false,
                                    locale: props.locale,
                                    onChange: (_, __, date) => handleEndDateChange(date),
                                    time: endDate.value,
                                    variant: 'select',
                                }),
                            ]
                            : [...Array(props.calendars)].map((_, index) => h$1(CTimePicker, {
                                container: 'inline',
                                disabled: index === 0
                                    ? startDate.value === null
                                        ? true
                                        : false
                                    : endDate.value === null
                                        ? true
                                        : false,
                                locale: props.locale,
                                onChange: (_, __, date) => index === 0
                                    ? handleStartDateChange(date)
                                    : handleEndDateChange(date),
                                time: index === 0 ? startDate.value : endDate.value,
                                variant: 'select',
                            }))),
                ]),
            }),
        });
    },
});

const CDateRangePickerPlugin = {
    install: (app) => {
        app.component(CDateRangePicker.name, CDateRangePicker);
    },
};

const CDatePicker = defineComponent({
    name: 'CDatePicker',
    props: {
        /**
         * Default date of the component
         */
        calendarDate: {
            type: [Date, String],
        },
        /**
         * Toggle visibility or set the content of cancel button.
         */
        cancelButton: {
            type: [Boolean, String],
            default: 'Cancel',
        },
        /**
         * Sets the color context of the cancel button to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
         */
        cancelButtonColor: {
            ...Color,
            default: 'primary',
        },
        /**
         * Size the cancel button small or large.
         *
         * @values 'sm', 'lg'
         */
        cancelButtonSize: {
            type: String,
            default: 'sm',
            validator: (value) => {
                return ['sm', 'lg'].includes(value);
            },
        },
        /**
         * Set the cancel button variant to an outlined button or a ghost button.
         *
         * @values 'ghost', 'outline'
         */
        cancelButtonVariant: {
            type: String,
            default: 'ghost',
            validator: (value) => {
                return ['ghost', 'outline'].includes(value);
            },
        },
        /**
         * Toggle visibility of the cleaner button.
         */
        cleaner: {
            type: Boolean,
            default: true,
        },
        /**
         * Toggle visibility or set the content of confirm button.
         */
        confirmButton: {
            type: [Boolean, String],
            default: 'OK',
        },
        /**
         * Sets the color context of the confirm button to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
         */
        confirmButtonColor: {
            ...Color,
            default: 'primary',
        },
        /**
         * Size the confirm button small or large.
         *
         * @values 'sm', 'lg'
         */
        confirmButtonSize: {
            type: String,
            default: 'sm',
            validator: (value) => {
                return ['sm', 'lg'].includes(value);
            },
        },
        /**
         * Set the confirm button variant to an outlined button or a ghost button.
         *
         * @values 'ghost', 'outline'
         */
        confirmButtonVariant: {
            type: String,
            validator: (value) => {
                return ['ghost', 'outline'].includes(value);
            },
        },
        /**
         * Set the format of day name.
         *
         * @default 'numeric'
         * @since 4.6.0
         */
        dayFormat: {
            type: [Function, String],
            default: 'numeric',
            required: false,
            validator: (value) => {
                if (typeof value === 'string') {
                    return ['numeric', '2-digit'].includes(value);
                }
                if (typeof value === 'function') {
                    return true;
                }
                if (typeof value === 'function') {
                    return true;
                }
                return false;
            },
        },
        /**
         * Toggle the disabled state for the component.
         */
        disabled: Boolean,
        /**
         * Specify the list of dates that cannot be selected.
         */
        disabledDates: {
            type: Array,
        },
        /**
         * Initial selected date.
         */
        date: {
            type: [Date, String],
            required: false,
        },
        /**
         * Sets the day of start week.
         * - 0 - Sunday,
         * - 1 - Monday,
         * - 2 - Tuesday,
         * - 3 - Wednesday,
         * - 4 - Thursday,
         * - 5 - Friday,
         * - 6 - Saturday,
         */
        firstDayOfWeek: {
            type: Number,
            default: 1,
        },
        /**
         * Set date format.
         * We use date-fns to format dates. Visit https://date-fns.org/v2.28.0/docs/format to check accepted patterns.
         */
        format: String,
        /**
         * Toggle visibility of footer element or set the content of footer.
         */
        footer: Boolean,
        /**
         * The id global attribute defines an identifier (ID) that must be unique in the whole document.
         *
         * The name attribute for the input element is generated based on the `id` property:
         * - {id}-date
         */
        id: String,
        /**
         * Toggle visibility or set the content of the input indicator.
         */
        indicator: {
            type: Boolean,
            default: true,
        },
        /**
         * Toggle the readonly state for the component.
         */
        inputReadOnly: Boolean,
        /**
         * Sets the default locale for components. If not set, it is inherited from the navigator.language.
         */
        locale: {
            type: String,
            default: 'default',
        },
        /**
         * Max selectable date.
         */
        maxDate: {
            type: [Date, String],
        },
        /**
         * Min selectable date.
         */
        minDate: {
            type: [Date, String],
        },
        /**
         * Show arrows navigation.
         */
        navigation: {
            type: Boolean,
            default: true,
        },
        /**
         * Reorder year-month navigation, and render year first.
         *
         * @since 4.6.0
         */
        navYearFirst: Boolean,
        /**
         * Specifies a short hint that is visible in the input.
         */
        placeholder: {
            type: String,
            default: 'Select date',
        },
        /**
         * Set whether days in adjacent months shown before or after the current month are selectable. This only applies if the `showAdjacementDays` option is set to true.
         *
         * @since 4.9.0
         */
        selectAdjacementDays: Boolean,
        /**
         * Set whether to display dates in adjacent months (non-selectable) at the start and end of the current month.
         *
         * @since 4.9.0
         */
        showAdjacementDays: {
            type: Boolean,
            default: true,
        },
        /**
         * Size the component small or large.
         *
         * @values 'sm', 'lg'
         */
        size: {
            type: String,
            required: false,
            validator: (value) => {
                return ['sm', 'lg'].includes(value);
            },
        },
        /**
         * Provide an additional time selection by adding select boxes to choose times.
         */
        timepicker: Boolean,
        /**
         * Set length or format of day name.
         *
         * @type  number | 'long' | 'narrow' | 'short'
         */
        weekdayFormat: {
            type: [Function, Number, String],
            default: 2,
            validator: (value) => {
                if (typeof value === 'string') {
                    return ['long', 'narrow', 'short'].includes(value);
                }
                if (typeof value === 'number') {
                    return true;
                }
                if (typeof value === 'function') {
                    return true;
                }
                return false;
            },
        },
    },
    emits: [
        /**
         * Callback fired when the date changed.
         *
         * @property {Date} date - date object
         * @property {string} formatedDate - formated date
         */
        'date-change',
        /**
         * Callback fired when the date changed.
         *
         * @property {Date | null} date - date object
         * @since 4.7.0
         */
        'update:date',
    ],
    setup(props, { emit }) {
        return () => h$1(CDateRangePicker, {
            calendars: 1,
            onStartDateChange: (date, formatedDate) => {
                emit('date-change', date, formatedDate);
                emit('update:date', date);
            },
            range: false,
            startDate: props.date,
            ...props,
        });
    },
});

const CDatePickerPlugin = {
    install: (app) => {
        app.component(CDatePicker.name, CDatePicker);
    },
};

const getPlacement = (placement, direction, alignment, isRTL) => {
    let _placement = placement;
    if (direction === 'dropup') {
        _placement = isRTL ? 'top-end' : 'top-start';
    }
    if (direction === 'dropup-center') {
        _placement = 'top';
    }
    if (direction === 'dropend') {
        _placement = isRTL ? 'left-start' : 'right-start';
    }
    if (direction === 'dropstart') {
        _placement = isRTL ? 'right-start' : 'left-start';
    }
    if (alignment === 'end') {
        _placement = isRTL ? 'bottom-start' : 'bottom-end';
    }
    return _placement;
};
const CDropdown = defineComponent({
    name: 'CDropdown',
    props: {
        /**
         * Set aligment of dropdown menu.
         *
         * @values { 'start' | 'end' | { xs: 'start' | 'end' } | { sm: 'start' | 'end' } | { md: 'start' | 'end' } | { lg: 'start' | 'end' } | { xl: 'start' | 'end'} | { xxl: 'start' | 'end'} }
         */
        alignment: {
            type: [String, Object],
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            validator: (value) => {
                if (value === 'start' || value === 'end') {
                    return true;
                }
                else {
                    if (value.xs !== undefined && (value.xs === 'start' || value.xs === 'end')) {
                        return true;
                    }
                    if (value.sm !== undefined && (value.sm === 'start' || value.sm === 'end')) {
                        return true;
                    }
                    if (value.md !== undefined && (value.md === 'start' || value.md === 'end')) {
                        return true;
                    }
                    if (value.lg !== undefined && (value.lg === 'start' || value.lg === 'end')) {
                        return true;
                    }
                    if (value.xl !== undefined && (value.xl === 'start' || value.xl === 'end')) {
                        return true;
                    }
                    if (value.xxl !== undefined && (value.xxl === 'start' || value.xxl === 'end')) {
                        return true;
                    }
                    return false;
                }
            },
        },
        /**
         * Configure the auto close behavior of the dropdown:
         * - `true` - the dropdown will be closed by clicking outside or inside the dropdown menu.
         * - `false` - the dropdown will be closed by clicking the toggle button and manually calling hide or toggle method. (Also will not be closed by pressing esc key)
         * - `'inside'` - the dropdown will be closed (only) by clicking inside the dropdown menu.
         * - `'outside'` - the dropdown will be closed (only) by clicking outside the dropdown menu.
         */
        autoClose: {
            type: [Boolean, String],
            default: true,
            validator: (value) => {
                return typeof value === 'boolean' || ['inside', 'outside'].includes(value);
            },
        },
        /**
         * Sets a darker color scheme to match a dark navbar.
         */
        dark: Boolean,
        /**
         * Sets a specified  direction and location of the dropdown menu.
         *
         * @values 'center', 'dropup', 'dropup-center', 'dropend', 'dropstart'
         */
        direction: {
            type: String,
            validator: (value) => {
                return ['center', 'dropup', 'dropup-center', 'dropend', 'dropstart'].includes(value);
            },
        },
        /**
         * Toggle the disabled state for the component.
         */
        disabled: Boolean,
        /**
         * Offset of the dropdown menu relative to its target.
         *
         * @since 4.9.0
         */
        offset: {
            type: Array,
            default: () => [0, 2],
        },
        /**
         * Describes the placement of your component after Popper.js has applied all the modifiers that may have flipped or altered the originally provided placement property.
         *
         * @values 'auto', 'top-end', 'top', 'top-start', 'bottom-end', 'bottom', 'bottom-start', 'right-start', 'right', 'right-end', 'left-start', 'left', 'left-end'
         */
        placement: {
            type: String,
            default: 'bottom-start',
        },
        /**
         * If you want to disable dynamic positioning set this property to `true`.
         */
        popper: {
            type: Boolean,
            default: true,
        },
        /**
         * Sets which event handlers you’d like provided to your toggle prop. You can specify one trigger or an array of them.
         */
        trigger: {
            type: String,
            default: 'click',
        },
        /**
         * Set the dropdown variant to an btn-group, dropdown, input-group, and nav-item.
         *
         * @values 'btn-group', 'dropdown', 'input-group', 'nav-item'
         */
        variant: {
            type: String,
            default: 'btn-group',
            validator: (value) => {
                return ['btn-group', 'dropdown', 'input-group', 'nav-item'].includes(value);
            },
        },
        /**
         * Toggle the visibility of dropdown menu component.
         */
        visible: Boolean,
    },
    emits: [
        /**
         * Callback fired when the component requests to be hidden.
         */
        'hide',
        /**
         * Callback fired when the component requests to be shown.
         */
        'show',
    ],
    setup(props, { slots, emit }) {
        const dropdownToggleRef = ref();
        const dropdownMenuRef = ref();
        const popper = ref(typeof props.alignment === 'object' ? false : props.popper);
        const visible = ref(props.visible);
        const { initPopper, destroyPopper } = usePopper();
        const popperConfig = {
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: props.offset,
                    },
                },
            ],
            placement: getPlacement(props.placement, props.direction, props.alignment, isRTL(dropdownMenuRef.value)),
        };
        watch(() => props.visible, () => {
            visible.value = props.visible;
        });
        watch(visible, () => {
            if (visible.value && dropdownToggleRef.value && dropdownMenuRef.value) {
                popper.value && initPopper(dropdownToggleRef.value, dropdownMenuRef.value, popperConfig);
                window.addEventListener('mouseup', handleMouseUp);
                window.addEventListener('keyup', handleKeyup);
                emit('show');
                return;
            }
            popper.value && destroyPopper();
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('keyup', handleKeyup);
            emit('hide');
        });
        provide('config', {
            alignment: props.alignment,
            dark: props.dark,
            popper: props.popper,
        });
        provide('variant', props.variant);
        provide('visible', visible);
        provide('dropdownToggleRef', dropdownToggleRef);
        provide('dropdownMenuRef', dropdownMenuRef);
        const handleKeyup = (event) => {
            if (props.autoClose === false) {
                return;
            }
            if (event.key === 'Escape') {
                setVisible(false);
            }
        };
        const handleMouseUp = (event) => {
            if (!dropdownToggleRef.value || !dropdownMenuRef.value) {
                return;
            }
            if (dropdownToggleRef.value.contains(event.target)) {
                return;
            }
            if (props.autoClose === true ||
                (props.autoClose === 'inside' &&
                    dropdownMenuRef.value.contains(event.target)) ||
                (props.autoClose === 'outside' &&
                    !dropdownMenuRef.value.contains(event.target))) {
                setVisible(false);
                return;
            }
        };
        const setVisible = (_visible) => {
            if (props.disabled) {
                return;
            }
            if (typeof _visible == 'boolean') {
                visible.value = _visible;
                return;
            }
            if (visible.value === true) {
                visible.value = false;
                return;
            }
            visible.value = true;
        };
        provide('setVisible', setVisible);
        return () => props.variant === 'input-group'
            ? [slots.default && slots.default()]
            : h$1('div', {
                class: [
                    props.variant === 'nav-item' ? 'nav-item dropdown' : props.variant,
                    props.direction === 'center'
                        ? 'dropdown-center'
                        : props.direction === 'dropup-center'
                            ? 'dropup dropup-center'
                            : props.direction,
                ],
            }, slots.default && slots.default());
    },
});

const CDropdownItem = defineComponent({
    name: 'CDropdownItem',
    props: {
        /**
         * Toggle the active state for the component.
         */
        active: Boolean,
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'a',
        },
        /**
         * Toggle the disabled state for the component.
         */
        disabled: Boolean,
        /**
         * The href attribute specifies the URL of the page the link goes to.
         */
        href: String,
    },
    setup(props, { slots }) {
        return () => h$1(CLink, {
            class: 'dropdown-item',
            active: props.active,
            component: props.component,
            disabled: props.disabled,
            href: props.href,
        }, {
            default: () => slots.default && slots.default(),
        });
    },
});

const CDropdownHeader = defineComponent({
    name: 'CDropdownHeader',
    props: {
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'h6',
        },
    },
    setup(props, { slots }) {
        return () => h$1(props.component, {
            class: 'dropdown-header',
        }, slots.default && slots.default());
    },
});

const CDropdownDivider = defineComponent({
    name: 'CDropdownDivider',
    setup() {
        return () => h$1('hr', {
            class: 'dropdown-divider',
        });
    },
});

const CDropdownMenu = defineComponent({
    name: 'CDropdownMenu',
    props: {
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         *
         * @values 'div', 'ul'
         */
        component: {
            type: String,
            default: 'div',
        },
    },
    setup(props, { slots }) {
        const dropdownMenuRef = inject('dropdownMenuRef');
        const config = inject('config'); // eslint-disable-line @typescript-eslint/no-explicit-any
        const visible = inject('visible');
        const { alignment, dark, popper } = config;
        // eslint-disable-next-line @typescript-eslint/ban-types, unicorn/consistent-function-scoping
        const alignmentClassNames = (alignment) => {
            const classNames = [];
            if (typeof alignment === 'object') {
                Object.keys(alignment).map((key) => {
                    classNames.push(`dropdown-menu${key === 'xs' ? '' : `-${key}`}-${alignment[key]}`);
                });
            }
            if (typeof alignment === 'string') {
                classNames.push(`dropdown-menu-${alignment}`);
            }
            return classNames;
        };
        return () => h$1(props.component, {
            class: [
                'dropdown-menu',
                { 'dropdown-menu-dark': dark, show: visible.value },
                alignmentClassNames(alignment),
            ],
            ...((typeof alignment === 'object' || !popper) && {
                'data-coreui-popper': 'static',
            }),
            ref: dropdownMenuRef,
        }, props.component === 'ul'
            ? slots.default && slots.default().map((vnode) => h$1('li', {}, vnode))
            : slots.default && slots.default());
    },
});

const CDropdownToggle = defineComponent({
    name: 'CDropdownToggle',
    props: {
        /**
         * Toggle the active state for the component.
         */
        active: Boolean,
        /**
         * Sets the color context of the component to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
         */
        color: Color$1,
        /**
         * Enables pseudo element caret on toggler.
         */
        caret: {
            type: Boolean,
            default: true,
        },
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'button',
        },
        /**
         * Create a custom toggler which accepts any content.
         */
        custom: Boolean,
        /**
         * Toggle the disabled state for the component.
         */
        disabled: Boolean,
        /**
         * @values 'rounded', 'rounded-top', 'rounded-end', 'rounded-bottom', 'rounded-start', 'rounded-circle', 'rounded-pill', 'rounded-0', 'rounded-1', 'rounded-2', 'rounded-3'
         */
        shape: Shape,
        /**
         * Size the component small or large.
         *
         * @values 'sm', 'lg'
         */
        size: {
            type: String,
            validator: (value) => {
                return ['sm', 'lg'].includes(value);
            },
        },
        /**
         * Similarly, create split button dropdowns with virtually the same markup as single button dropdowns, but with the addition of `.dropdown-toggle-split` className for proper spacing around the dropdown caret.
         */
        split: Boolean,
        /**
         * Sets which event handlers you’d like provided to your toggle prop. You can specify one trigger or an array of them.
         *
         * @type 'hover' | 'focus' | 'click'
         */
        trigger: {
            type: String,
            default: 'click',
        },
        /**
         * Set the button variant to an outlined button or a ghost button.
         *
         * @values 'ghost', 'outline'
         */
        variant: {
            type: String,
            validator: (value) => {
                return ['ghost', 'outline'].includes(value);
            },
        },
    },
    setup(props, { slots }) {
        const togglerRef = ref();
        const dropdownToggleRef = inject('dropdownToggleRef');
        const dropdownVariant = inject('variant');
        const visible = inject('visible');
        const setVisible = inject('setVisible');
        const className = [
            {
                'dropdown-toggle': props.caret,
                'dropdown-toggle-split': props.split,
                active: props.active,
                disabled: props.disabled,
            },
        ];
        const triggers = {
            ...((props.trigger === 'click' || props.trigger.includes('click')) && {
                onClick: () => {
                    if (props.disabled) {
                        return;
                    }
                    setVisible();
                },
            }),
            ...((props.trigger === 'focus' || props.trigger.includes('focus')) && {
                onfocus: () => {
                    if (props.disabled) {
                        return;
                    }
                    setVisible(true);
                },
                onblur: () => {
                    if (props.disabled) {
                        return;
                    }
                    setVisible(false);
                },
            }),
        };
        onMounted(() => {
            if (togglerRef.value) {
                dropdownToggleRef.value = togglerRef.value.$el;
            }
        });
        return () => props.custom
            ? slots.default &&
                slots.default().map((slot) => cloneVNode(slot, {
                    ref: (el) => {
                        togglerRef.value = el;
                    },
                    ...triggers,
                }))
            : dropdownVariant === 'nav-item'
                ? h$1('a', {
                    active: props.active,
                    class: [
                        'nav-link',
                        className,
                        {
                            show: visible.value,
                        },
                    ],
                    disabled: props.disabled,
                    href: '#',
                    ref: dropdownToggleRef,
                    ...triggers,
                }, { default: () => slots.default && slots.default() })
                : h$1(CButton, {
                    class: [
                        className,
                        {
                            show: visible.value,
                        },
                    ],
                    active: props.active,
                    color: props.color,
                    disabled: props.disabled,
                    ref: (el) => {
                        togglerRef.value = el;
                    },
                    shape: props.shape,
                    size: props.size,
                    ...triggers,
                    ...(props.component === 'button' && { type: 'button' }),
                    variant: props.variant,
                }, () => props.split
                    ? h$1('span', { class: 'visually-hidden' }, 'Toggle Dropdown')
                    : slots.default && slots.default());
    },
});

const CDropdownPlugin = {
    install: (app) => {
        app.component(CDropdown.name, CDropdown);
        app.component(CDropdownItem.name, CDropdownItem);
        app.component(CDropdownHeader.name, CDropdownHeader);
        app.component(CDropdownDivider.name, CDropdownDivider);
        app.component(CDropdownMenu.name, CDropdownMenu);
        app.component(CDropdownToggle.name, CDropdownToggle);
    },
};

const CSpinner = defineComponent({
    name: 'CSpinner',
    props: {
        /**
         * Sets the color context of the component to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
         */
        color: {
            type: String,
            validator: (value) => {
                return [
                    'primary',
                    'secondary',
                    'success',
                    'danger',
                    'warning',
                    'info',
                    'dark',
                    'light',
                ].includes(value);
            },
        },
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'div',
        },
        /**
         * Size the component small.
         *
         * @values 'sm'
         */
        size: {
            type: String,
            validator: (value) => {
                return value === 'sm';
            },
        },
        /**
         * Set the button variant to an outlined button or a ghost button.
         *
         * @values 'border', 'grow'
         */
        variant: {
            type: String,
            default: 'border',
            validator: (value) => {
                return ['border', 'grow'].includes(value);
            },
        },
        /**
         * Set visually hidden label for accessibility purposes.
         */
        visuallyHiddenLabel: {
            type: String,
            default: 'Loading...',
        },
    },
    setup(props) {
        return () => h$1(props.component, {
            class: [
                `spinner-${props.variant}`,
                {
                    [`spinner-${props.variant}-${props.size}`]: props.size,
                    [`text-${props.color}`]: props.color,
                },
            ],
            role: 'status',
        }, h$1('span', { class: ['visually-hidden'] }, props.visuallyHiddenLabel));
    },
});

const CSpinnerPlugin = {
    install: (app) => {
        app.component(CSpinner.name, CSpinner);
    },
};

const CElementCover = defineComponent({
    name: 'CElementCover',
    props: {
        /**
         * Array of custom boundaries. Use to create custom cover area (instead of parent element area). Area is defined by four sides: 'top', 'bottom', 'right', 'left'. If side is not defined by any custom boundary it is equal to parent element boundary. Each custom boundary is object with keys:
         * - sides (array) - select boundaries of element to define boundaries. Sides names: 'top', 'bottom', 'right', 'left'.
         * - query (string) - query used to get element which define boundaries. Search will be done only inside parent element, by parent.querySelector(query) function. [docs]
         *
         * @type {sides: string[], query: string}[]
         */
        boundaries: Array,
        /**
         * Opacity of the cover. [docs]
         *
         * @type number
         */
        opacity: {
            type: Number,
            default: 0.4,
        },
    },
    /**
     * Location for custom content.
     *
     * @slot default
     */
    setup(props, { slots }) {
        const elementCoverRef = ref();
        const customBoundaries = ref({});
        const getCustomBoundaries = () => {
            if (!props.boundaries || elementCoverRef === null) {
                return {};
            }
            const parent = elementCoverRef.value.parentElement;
            if (!parent) {
                return {};
            }
            const parentCoords = parent.getBoundingClientRect();
            const customBoundaries = {};
            props.boundaries.forEach(({ sides, query }) => {
                const element = parent.querySelector(query);
                if (!element || !sides) {
                    return;
                }
                const coords = element.getBoundingClientRect();
                sides.forEach((side) => {
                    const sideMargin = Math.abs(coords[side] - parentCoords[side]);
                    customBoundaries[side] = `${sideMargin}px`;
                });
            });
            return customBoundaries;
        };
        const containerCoords = {
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
        };
        const coverStyles = {
            ...containerCoords,
            position: 'absolute',
            'z-index': 2,
            'background-color': `rgba(255,255,255,${props.opacity})`,
        };
        onMounted(() => {
            nextTick(() => {
                customBoundaries.value = getCustomBoundaries();
            });
        });
        return () => h$1('div', {
            style: { ...coverStyles, ...customBoundaries.value },
            ref: elementCoverRef,
        }, h$1('div', {
            style: {
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translateX(-50%) translateY(-50%)',
            },
        }, slots.default
            ? slots.default()
            : h$1(CSpinner, {
                variant: 'grow',
                color: 'primary',
            })));
    },
});

const CElementCoverPlugin = {
    install: (app) => {
        app.component(CElementCover.name, CElementCover);
    },
};

const CFooter = defineComponent({
    name: 'CFooter',
    props: {
        /**
         * Place footer in non-static positions.
         *
         * @values 'fixed', 'sticky'
         */
        position: {
            type: String,
            validator: (value) => {
                return ['fixed', 'sticky'].includes(value);
            },
        },
    },
    setup(props, { slots }) {
        return () => h$1('div', { class: ['footer', { [`footer-${props.position}`]: props.position }] }, slots.default && slots.default());
    },
});

const CFooterPlugin = {
    install: (app) => {
        app.component(CFooter.name, CFooter);
    },
};

const CForm = defineComponent({
    name: 'CForm',
    props: {
        /**
         * Mark a form as validated. If you set it `true`, all validation styles will be applied to the forms component.
         */
        validated: Boolean,
    },
    setup(props, { slots }) {
        return () => h$1('form', { class: [{ ['was-validated']: props.validated }] }, slots.default && slots.default());
    },
});

const CFormCheck = defineComponent({
    name: 'CFormCheck',
    inheritAttrs: false,
    props: {
        /**
         * Create button-like checkboxes and radio buttons.
         *
         * @see http://coreui.io/vue/docs/components/button.html
         */
        button: Object,
        /**
         * Use in conjunction with the v-model directive to specify the value that should be assigned to the bound variable when the checkbox is in the `false` state.
         *
         * @since 4.10.0
         */
        falseValue: String,
        /**
         * Provide valuable, actionable feedback.
         *
         * @since 4.3.0
         */
        feedback: String,
        /**
         * Provide valuable, actionable feedback.
         *
         * @since 4.3.0
         */
        feedbackInvalid: String,
        /**
         * Provide valuable, actionable invalid feedback when using standard HTML form validation which applied two CSS pseudo-classes, `:invalid` and `:valid`.
         *
         * @since 4.3.0
         */
        feedbackValid: String,
        /**
         * Sets hit area to the full area of the component.
         */
        hitArea: {
            type: String,
            validator: (value) => {
                // The value must match one of these strings
                return ['full'].includes(value);
            },
        },
        /**
         * The id global attribute defines an identifier (ID) that must be unique in the whole document.
         */
        id: String,
        /**
         * Input Checkbox indeterminate Property
         */
        indeterminate: Boolean,
        /**
         * Group checkboxes or radios on the same horizontal row by adding.
         */
        inline: Boolean,
        /**
         * Set component validation state to invalid.
         */
        invalid: Boolean,
        /**
         * The element represents a caption for a component.
         */
        label: String,
        /**
         * The default name for a value passed using v-model.
         */
        modelValue: {
            type: [Array, Boolean, String],
            value: undefined,
        },
        /**
         * Put checkboxes or radios on the opposite side.
         *
         * @since 4.8.0
         */
        reverse: Boolean,
        /**
         * Display validation feedback in a styled tooltip.
         *
         * @since 4.3.0
         */
        tooltipFeedback: Boolean,
        /**
         * Use in conjunction with the v-model directive to specify the value that should be assigned to the bound variable when the checkbox is in the `true` state.
         *
         * @since 4.10.0
         */
        trueValue: String,
        /**
         * Specifies the type of component.
         *
         * @values 'checkbox', 'radio'
         */
        type: {
            type: String,
            default: 'checkbox',
        },
        /**
         * Set component validation state to valid.
         */
        valid: Boolean,
        /**
         * The value attribute of component.
         */
        value: String,
    },
    emits: [
        /**
         * Event occurs when the checked value has been changed.
         */
        'change',
        /**
         * Emit the new value whenever there’s a change event.
         */
        'update:modelValue',
    ],
    setup(props, { attrs, emit, slots }) {
        const handleChange = (event) => {
            const target = event.target;
            emit('change', event);
            if (props.falseValue && props.trueValue) {
                emit('update:modelValue', target.checked ? props.trueValue : props.falseValue);
                return;
            }
            if (props.value && Array.isArray(props.modelValue)) {
                if (props.modelValue.includes(props.value)) {
                    emit('update:modelValue', props.modelValue.filter((value) => value !== props.value));
                }
                else {
                    emit('update:modelValue', [...props.modelValue, props.value]);
                }
                return;
            }
            if (props.value === undefined) {
                emit('update:modelValue', target.checked);
                return;
            }
            if (props.value && (props.modelValue === undefined || typeof props.modelValue === 'string')) {
                emit('update:modelValue', target.checked ? props.value : undefined);
            }
        };
        const className = [
            'form-check',
            {
                'form-check-inline': props.inline,
                'form-check-reverse': props.reverse,
                'is-invalid': props.invalid,
                'is-valid': props.valid,
            },
            attrs.class,
        ];
        const inputClassName = [
            props.button ? 'btn-check' : 'form-check-input',
            {
                'is-invalid': props.invalid,
                'is-valid': props.valid,
                'me-2': props.hitArea,
            },
        ];
        const isChecked = computed(() => {
            if (Array.isArray(props.modelValue)) {
                return props.modelValue.includes(props.value);
            }
            if (typeof props.modelValue === 'string') {
                return props.modelValue === props.value;
            }
            return props.modelValue;
        });
        const formControl = () => {
            return h$1('input', {
                ...attrs,
                ...((props.modelValue || props.value) && { checked: isChecked.value }),
                class: inputClassName,
                id: props.id,
                indeterminate: props.indeterminate,
                onChange: (event) => handleChange(event),
                type: props.type,
                value: props.value,
            });
        };
        const formLabel = () => props.button
            ? h$1(CButton, {
                component: 'label',
                ...props.button,
                ...(props.id && { for: props.id }),
            }, {
                default: () => (slots.label && slots.label()) || props.label,
            })
            : h$1(CFormLabel, { class: 'form-check-label', ...(props.id && { for: props.id }) }, {
                default: () => (slots.label && slots.label()) || props.label,
            });
        const formValidation = () => {
            return h$1(CFormControlValidation, {
                describedby: attrs['aria-describedby'],
                feedback: props.feedback,
                feedbackInvalid: props.feedbackInvalid,
                feedbackValid: props.feedbackValid,
                invalid: props.invalid,
                tooltipFeedback: props.tooltipFeedback,
                valid: props.valid,
            });
        };
        return () => props.button
            ? [formControl(), (slots.label || props.label) && formLabel(), formValidation()]
            : props.label
                ? props.hitArea
                    ? [
                        h$1(CFormLabel, {
                            customClassName: className,
                            ...(props.id && { for: props.id }),
                        }, [formControl(), props.label]),
                        formValidation(),
                    ]
                    : h$1('div', {
                        class: className,
                    }, [formControl(), props.label && formLabel(), formValidation()])
                : formControl();
    },
});

const File = typeof window === 'undefined' ? class File extends Object {
} : window.File;
const CFormInput = defineComponent({
    name: 'CFormInput',
    props: {
        /**
         * Toggle the disabled state for the component.
         */
        disabled: Boolean,
        // Inherited Props from CFormControlWrapper
        /**
         * Provide valuable, actionable feedback.
         *
         * @since 4.3.0
         */
        feedback: String,
        /**
         * Provide valuable, actionable feedback.
         *
         * @since 4.3.0
         */
        feedbackInvalid: String,
        /**
         * Provide valuable, actionable invalid feedback when using standard HTML form validation which applied two CSS pseudo-classes, `:invalid` and `:valid`.
         *
         * @since 4.3.0
         */
        feedbackValid: String,
        /**
         * Provide valuable, actionable valid feedback when using standard HTML form validation which applied two CSS pseudo-classes, `:invalid` and `:valid`.
         *
         * @since 4.3.0
         */
        floatingLabel: String,
        /**
         * The id global attribute defines an identifier (ID) that must be unique in the whole document.
         */
        id: String,
        /**
         * Set component validation state to invalid.
         */
        invalid: Boolean,
        /**
         * Add a caption for a component.
         *
         * @since 4.3.0
         */
        label: String,
        /**
         * The default name for a value passed using v-model.
         */
        modelValue: [File, Number, String],
        /**
         * Render the component styled as plain text. Removes the default form field styling and preserve the correct margin and padding. Recommend to use only along side `readonly`.
         */
        plainText: Boolean,
        /**
         * Toggle the readonly state for the component.
         */
        readonly: Boolean,
        /**
         * Size the component small or large.
         *
         * @values 'sm' | 'lg'
         */
        size: {
            type: String,
            validator: (value) => {
                return ['sm', 'lg'].includes(value);
            },
        },
        /**
         * Add helper text to the component.
         *
         * @since 4.3.0
         */
        text: String,
        /**
         * Display validation feedback in a styled tooltip.
         *
         * @since 4.3.0
         */
        tooltipFeedback: Boolean,
        /**
         * Specifies the type of component.
         *
         * @values 'color' | 'file' | 'text' | string
         */
        type: {
            type: String,
            default: 'text',
        },
        /**
         * Set component validation state to valid.
         */
        valid: Boolean,
    },
    emits: [
        /**
         * Event occurs when the element loses focus, after the content has been changed.
         */
        'change',
        /**
         * Event occurs immediately after the value of a component has changed.
         */
        'input',
        /**
         * Emit the new value whenever there’s an input or change event.
         */
        'update:modelValue',
    ],
    setup(props, { attrs, emit, slots }) {
        const handleChange = (event) => {
            const target = event.target;
            emit('change', event);
            emit('update:modelValue', target.value);
        };
        const handleInput = (event) => {
            const target = event.target;
            emit('input', event);
            emit('update:modelValue', target.value);
        };
        return () => h$1(CFormControlWrapper, {
            describedby: attrs['aria-describedby'],
            feedback: props.feedback,
            feedbackInvalid: props.feedbackInvalid,
            feedbackValid: props.feedbackValid,
            floatingLabel: props.floatingLabel,
            id: props.id,
            invalid: props.invalid,
            label: props.label,
            text: props.text,
            tooltipFeedback: props.tooltipFeedback,
            valid: props.valid,
        }, {
            default: () => h$1('input', {
                id: props.id,
                ...attrs,
                class: [
                    props.plainText ? 'form-control-plaintext' : 'form-control',
                    {
                        'form-control-color': props.type === 'color',
                        [`form-control-${props.size}`]: props.size,
                        'is-invalid': props.invalid,
                        'is-valid': props.valid,
                    },
                    attrs.class,
                ],
                disabled: props.disabled,
                onChange: (event) => handleChange(event),
                onInput: (event) => handleInput(event),
                readonly: props.readonly,
                type: props.type,
                ...((props.modelValue || props.modelValue === 0) && { value: props.modelValue }),
            }, slots.default && slots.default()),
            ...(slots.feedback && { feedback: () => slots.feedback && slots.feedback() }),
            ...(slots.feedbackInvalid && {
                feedbackInvalid: () => slots.feedbackInvalid && slots.feedbackInvalid(),
            }),
            ...(slots.feedbackValid && {
                feedbackValid: () => slots.feedbackInvalid && slots.feedbackInvalid(),
            }),
            ...(slots.label && { label: () => slots.label && slots.label() }),
            ...(slots.text && { text: () => slots.text && slots.text() }),
        });
    },
});

const CFormRange = defineComponent({
    name: 'CFormRange',
    props: {
        /**
         * Toggle the disabled state for the component.
         */
        disabled: Boolean,
        /**
         * Add a caption for a component.
         *
         * @since 4.3.0
         */
        label: String,
        /**
         * Specifies the maximum value for the component.
         */
        max: Number,
        /**
         * Specifies the minimum value for the component.
         */
        min: Number,
        /**
         * The default name for a value passed using v-model.
         */
        modelValue: String,
        /**
         * Toggle the readonly state for the component.
         */
        readonly: Boolean,
        /**
         * Specifies the interval between legal numbers in the component.
         */
        steps: Number,
        /**
         * The `value` attribute of component.
         *
         * @controllable onChange
         * */
        value: Number,
    },
    emits: [
        /**
         * Event occurs when the value has been changed.
         */
        'change',
        /**
         * Emit the new value whenever there’s a change event.
         */
        'update:modelValue',
    ],
    setup(props, { attrs, emit, slots }) {
        const handleChange = (event) => {
            const target = event.target;
            emit('change', event);
            emit('update:modelValue', target.value);
        };
        return () => [
            props.label &&
                h$1(CFormLabel, {
                    for: attrs.id,
                }, {
                    default: () => (slots.label && slots.label()) || props.label,
                }),
            h$1('input', {
                ...attrs,
                class: 'form-range',
                disabled: props.disabled,
                max: props.max,
                min: props.min,
                onChange: (event) => handleChange(event),
                readonly: props.readonly,
                steps: props.steps,
                type: 'range',
                value: props.modelValue,
            }, slots.default && slots.default()),
        ];
    },
});

const CFormSelect = defineComponent({
    name: 'CFormSelect',
    props: {
        /**
         * Provide valuable, actionable feedback.
         *
         * @since 4.3.0
         */
        feedback: String,
        /**
         * Provide valuable, actionable feedback.
         *
         * @since 4.3.0
         */
        feedbackInvalid: String,
        /**
         * Provide valuable, actionable invalid feedback when using standard HTML form validation which applied two CSS pseudo-classes, `:invalid` and `:valid`.
         *
         * @since 4.3.0
         */
        feedbackValid: String,
        /**
         * Provide valuable, actionable valid feedback when using standard HTML form validation which applied two CSS pseudo-classes, `:invalid` and `:valid`.
         *
         * @since 4.3.0
         */
        floatingLabel: String,
        /**
         * Specifies the number of visible options in a drop-down list.
         */
        htmlSize: Number,
        /**
         * The id global attribute defines an identifier (ID) that must be unique in the whole document.
         */
        id: String,
        /**
         * Set component validation state to invalid.
         */
        invalid: Boolean,
        /**
         * Add a caption for a component.
         *
         * @since 4.3.0
         */
        label: String,
        /**
         * The default name for a value passed using v-model.
         */
        modelValue: {
            type: [String, Array],
        },
        multiple: Boolean,
        /**
         * Options list of the select component. Available keys: `label`, `value`, `disabled`.
         * Examples:
         * - `:options="[{ value: 'js', label: 'JavaScript' }, { value: 'html', label: 'HTML', disabled: true }]"`
         * - `:options="['js', 'html']"`
         */
        options: Array,
        /**
         * Size the component small or large.
         *
         * @values 'sm' | 'lg'
         */
        size: {
            type: String,
            validator: (value) => {
                return ['sm', 'lg'].includes(value);
            },
        },
        /**
         * Add helper text to the component.
         *
         * @since 4.3.0
         */
        text: String,
        /**
         * Display validation feedback in a styled tooltip.
         *
         * @since 4.3.0
         */
        tooltipFeedback: Boolean,
        /**
         * Set component validation state to valid.
         */
        valid: Boolean,
    },
    emits: [
        /**
         * Event occurs when when a user changes the selected option of a `<select>` element.
         */
        'change',
        /**
         * Emit the new value whenever there’s a change event.
         */
        'update:modelValue',
    ],
    setup(props, { attrs, emit, slots }) {
        const handleChange = (event) => {
            const target = event.target;
            const selected = Array.from(target.options)
                .filter((option) => option.selected)
                .map((option) => option.value);
            emit('change', event);
            emit('update:modelValue', target.multiple ? selected : selected[0]);
        };
        return () => h$1(CFormControlWrapper, {
            describedby: attrs['aria-describedby'],
            feedback: props.feedback,
            feedbackInvalid: props.feedbackInvalid,
            feedbackValid: props.feedbackValid,
            floatingLabel: props.floatingLabel,
            id: props.id,
            invalid: props.invalid,
            label: props.label,
            text: props.text,
            tooltipFeedback: props.tooltipFeedback,
            valid: props.valid,
        }, {
            default: () => h$1('select', {
                id: props.id,
                ...attrs,
                class: [
                    'form-select',
                    {
                        [`form-select-${props.size}`]: props.size,
                        'is-invalid': props.invalid,
                        'is-valid': props.valid,
                    },
                    attrs.class,
                ],
                multiple: props.multiple,
                onChange: (event) => handleChange(event),
                size: props.htmlSize,
                ...(props.modelValue && !props.multiple && { value: props.modelValue }),
            }, props.options
                ? props.options.map((option) => {
                    return h$1('option', {
                        ...(typeof option === 'object' && {
                            ...(option.disabled && { disabled: option.disabled }),
                            ...(option.selected && { selected: option.selected }),
                            ...(option.value !== undefined && {
                                value: option.value,
                                ...(props.modelValue &&
                                    props.multiple &&
                                    props.modelValue.includes(option.value) && { selected: true }),
                            }),
                        }),
                    }, typeof option === 'string' ? option : option.label);
                })
                : slots.default && slots.default()),
            ...(slots.feedback && { feedback: () => slots.feedback && slots.feedback() }),
            ...(slots.feedbackInvalid && {
                feedbackInvalid: () => slots.feedbackInvalid && slots.feedbackInvalid(),
            }),
            ...(slots.feedbackValid && {
                feedbackValid: () => slots.feedbackInvalid && slots.feedbackInvalid(),
            }),
            ...(slots.label && { label: () => slots.label && slots.label() }),
            ...(slots.text && { text: () => slots.text && slots.text() }),
        });
    },
});

const CFormSwitch = defineComponent({
    name: 'CFormSwitch',
    inheritAttrs: false,
    props: {
        /**
         * The id global attribute defines an identifier (ID) that must be unique in the whole document
         */
        id: String,
        /**
         * Set component validation state to invalid.
         */
        invalid: Boolean,
        /**
         * The element represents a caption for a component.
         */
        label: String,
        /**
         * The default name for a value passed using v-model.
         */
        modelValue: [Boolean, String],
        /**
         * Put checkboxes or radios on the opposite side.
         *
         * @since 4.8.0
         */
        reverse: Boolean,
        /**
         * Size the component large or extra large. Works only with `switch`.
         *
         * @values 'lg' | 'xl'
         */
        size: {
            type: String,
            validator: (value) => {
                return ['lg', 'xl'].includes(value);
            },
        },
        /**
         * Specifies the type of component.
         *
         * @values 'checkbox', 'radio'
         */
        type: {
            type: String,
            default: 'checkbox',
        },
        /**
         * Set component validation state to valid.
         */
        valid: Boolean,
    },
    emits: [
        /**
         * Event occurs when the checked value has been changed.
         */
        'change',
        /**
         * Emit the new value whenever there’s a change event.
         */
        'update:modelValue',
    ],
    setup(props, { attrs, emit }) {
        const handleChange = (event) => {
            const target = event.target;
            emit('change', event);
            emit('update:modelValue', target.checked);
        };
        return () => h$1('div', {
            class: [
                'form-check form-switch',
                {
                    'form-check-reverse': props.reverse,
                    [`form-switch-${props.size}`]: props.size,
                    'is-invalid': props.invalid,
                    'is-valid': props.valid,
                },
            ],
        }, [
            h$1('input', {
                ...attrs,
                ...(props.modelValue && { checked: props.modelValue }),
                class: [
                    'form-check-input',
                    {
                        'is-invalid': props.invalid,
                        'is-valid': props.valid,
                    },
                ],
                id: props.id,
                onChange: (event) => handleChange(event),
                type: props.type,
            }),
            props.label &&
                h$1(CFormLabel, {
                    ...(props.id && { for: props.id }),
                    class: 'form-check-label',
                }, {
                    default: () => props.label,
                }),
        ]);
    },
});

const CFormTextarea = defineComponent({
    name: 'CFormTextarea',
    props: {
        /**
         * Toggle the disabled state for the component.
         */
        disabled: Boolean,
        /**
         * Provide valuable, actionable feedback.
         *
         * @since 4.3.0
         */
        feedback: String,
        /**
         * Provide valuable, actionable feedback.
         *
         * @since 4.3.0
         */
        feedbackInvalid: String,
        /**
         * Provide valuable, actionable invalid feedback when using standard HTML form validation which applied two CSS pseudo-classes, `:invalid` and `:valid`.
         *
         * @since 4.3.0
         */
        feedbackValid: String,
        /**
         * Provide valuable, actionable valid feedback when using standard HTML form validation which applied two CSS pseudo-classes, `:invalid` and `:valid`.
         *
         * @since 4.3.0
         */
        floatingLabel: String,
        /**
         * The id global attribute defines an identifier (ID) that must be unique in the whole document.
         */
        id: String,
        /**
         * Set component validation state to invalid.
         */
        invalid: Boolean,
        /**
         * Add a caption for a component.
         *
         * @since 4.3.0
         */
        label: String,
        /**
         * The default name for a value passed using v-model.
         */
        modelValue: String,
        /**
         * Render the component styled as plain text. Removes the default form field styling and preserve the correct margin and padding. Recommend to use only along side `readonly`.
         */
        plainText: Boolean,
        /**
         * Toggle the readonly state for the component.
         */
        readonly: Boolean,
        /**
         * Add helper text to the component.
         *
         * @since 4.3.0
         */
        text: String,
        /**
         * Display validation feedback in a styled tooltip.
         *
         * @since 4.3.0
         */
        tooltipFeedback: Boolean,
        /**
         * Set component validation state to valid.
         */
        valid: Boolean,
    },
    emits: [
        /**
         * Event occurs when the element loses focus, after the content has been changed.
         */
        'change',
        /**
         * Event occurs immediately after the value of a component has changed.
         */
        'input',
        /**
         * Emit the new value whenever there’s an input or change event.
         */
        'update:modelValue',
    ],
    setup(props, { attrs, emit, slots }) {
        const handleChange = (event) => {
            const target = event.target;
            emit('change', event);
            emit('update:modelValue', target.value);
        };
        const handleInput = (event) => {
            const target = event.target;
            emit('input', event);
            emit('update:modelValue', target.value);
        };
        return () => h$1(CFormControlWrapper, {
            describedby: attrs['aria-describedby'],
            feedback: props.feedback,
            feedbackInvalid: props.feedbackInvalid,
            feedbackValid: props.feedbackValid,
            floatingLabel: props.floatingLabel,
            id: props.id,
            invalid: props.invalid,
            label: props.label,
            text: props.text,
            tooltipFeedback: props.tooltipFeedback,
            valid: props.valid,
        }, {
            default: () => h$1('textarea', {
                id: props.id,
                ...attrs,
                disabled: props.disabled,
                readonly: props.readonly,
                class: [
                    props.plainText ? 'form-control-plaintext' : 'form-control',
                    {
                        'is-invalid': props.invalid,
                        'is-valid': props.valid,
                    },
                ],
                onChange: (event) => handleChange(event),
                onInput: (event) => handleInput(event),
                ...(props.modelValue && { value: props.modelValue }),
            }, slots.default && slots.default()),
            ...(slots.feedback && { feedback: () => slots.feedback && slots.feedback() }),
            ...(slots.feedbackInvalid && {
                feedbackInvalid: () => slots.feedbackInvalid && slots.feedbackInvalid(),
            }),
            ...(slots.feedbackValid && {
                feedbackValid: () => slots.feedbackInvalid && slots.feedbackInvalid(),
            }),
            ...(slots.label && { label: () => slots.label && slots.label() }),
            ...(slots.text && { text: () => slots.text && slots.text() }),
        });
    },
});

const CInputGroup = defineComponent({
    name: 'CInputGroup',
    props: {
        /**
         * Size the component small or large.
         *
         * @values 'sm', 'lg'
         */
        size: {
            type: String,
            validator: (value) => {
                return ['sm', 'lg'].includes(value);
            },
        },
    },
    setup(props, { slots }) {
        return () => h$1('div', {
            class: [
                'input-group',
                {
                    [`input-group-${props.size}`]: props.size,
                },
            ],
        }, slots.default && slots.default());
    },
});

const CInputGroupText = defineComponent({
    name: 'CInputGroupText',
    props: {
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'span',
        },
    },
    setup(props, { slots }) {
        return () => h$1(props.component, { class: 'input-group-text' }, slots.default && slots.default());
    },
});

const CFormPlugin = {
    install: (app) => {
        app.component(CForm.name, CForm);
        app.component(CFormCheck.name, CFormCheck);
        // app.component(CFormControl.name, CFormControl)
        app.component(CFormFeedback.name, CFormFeedback);
        app.component(CFormFloating.name, CFormFloating);
        app.component(CFormInput.name, CFormInput);
        app.component(CFormLabel.name, CFormLabel);
        app.component(CFormRange.name, CFormRange);
        app.component(CFormSelect.name, CFormSelect);
        app.component(CFormSwitch.name, CFormSwitch);
        app.component(CFormText.name, CFormText);
        app.component(CFormTextarea.name, CFormTextarea);
        app.component(CInputGroup.name, CInputGroup);
        app.component(CInputGroupText.name, CInputGroupText);
    },
};

const BREAKPOINTS$4 = [
    'xxl',
    'xl',
    'lg',
    'md',
    'sm',
    'xs',
];
const CCol = defineComponent({
    name: 'CCol',
    props: {
        /**
         * The number of columns/offset/order on extra small devices (<576px).
         *
         * @values 'auto' | number | string | boolean | { span: 'auto' | number | string | boolean } | { offset: number | string } | { order: 'first' | 'last' | number | string }
         */
        xs: {
            type: [Boolean, Number, String, Object],
        },
        /**
         * The number of columns/offset/order on small devices (<768px).
         *
         * @values 'auto' | number | string | boolean | { span: 'auto' | number | string | boolean } | { offset: number | string } | { order: 'first' | 'last' | number | string }
         */
        sm: {
            type: [Boolean, Number, String, Object],
        },
        /**
         * The number of columns/offset/order on medium devices (<992px).
         *
         * @values 'auto' | number | string | boolean | { span: 'auto' | number | string | boolean } | { offset: number | string } | { order: 'first' | 'last' | number | string }
         */
        md: {
            type: [Boolean, Number, String, Object],
        },
        /**
         * The number of columns/offset/order on large devices (<1200px).
         *
         * @values 'auto' | number | string | boolean | { span: 'auto' | number | string | boolean } | { offset: number | string } | { order: 'first' | 'last' | number | string }
         */
        lg: {
            type: [Boolean, Number, String, Object],
        },
        /**
         * The number of columns/offset/order on X-Large devices (<1400px).
         *
         * @values 'auto' | number | string | boolean | { span: 'auto' | number | string | boolean } | { offset: number | string } | { order: 'first' | 'last' | number | string }
         */
        xl: {
            type: [Boolean, Number, String, Object],
        },
        /**
         * The number of columns/offset/order on XX-Large devices (≥1400px).
         *
         * @values 'auto' | number | string | boolean | { span: 'auto' | number | string | boolean } | { offset: number | string } | { order: 'first' | 'last' | number | string }
         */
        xxl: {
            type: [Boolean, Number, String, Object],
        },
    },
    setup(props, { slots }) {
        const repsonsiveClassNames = [];
        BREAKPOINTS$4.forEach((bp) => {
            const breakpoint = props[bp];
            const infix = bp === 'xs' ? '' : `-${bp}`;
            if (breakpoint) {
                if (typeof breakpoint === 'number' || typeof breakpoint === 'string') {
                    repsonsiveClassNames.push(`col${infix}-${breakpoint}`);
                }
                if (typeof breakpoint === 'boolean') {
                    repsonsiveClassNames.push(`col${infix}`);
                }
            }
            if (breakpoint && typeof breakpoint === 'object') {
                if (typeof breakpoint.span === 'number' || typeof breakpoint.span === 'string') {
                    repsonsiveClassNames.push(`col${infix}-${breakpoint.span}`);
                }
                if (typeof breakpoint.span === 'boolean') {
                    repsonsiveClassNames.push(`col${infix}`);
                }
                if (typeof breakpoint.order === 'number' || typeof breakpoint.order === 'string') {
                    repsonsiveClassNames.push(`order${infix}-${breakpoint.order}`);
                }
                if (typeof breakpoint.offset === 'number') {
                    repsonsiveClassNames.push(`offset${infix}-${breakpoint.offset}`);
                }
            }
        });
        return () => h$1('div', {
            class: [repsonsiveClassNames.length > 0 ? repsonsiveClassNames : 'col'],
        }, slots.default && slots.default());
    },
});

const BREAKPOINTS$3 = [
    'xxl',
    'xl',
    'lg',
    'md',
    'sm',
    'fluid',
];
const CContainer = defineComponent({
    name: 'CContainer',
    props: {
        /**
         * Set container 100% wide until small breakpoint.
         */
        sm: Boolean,
        /**
         * Set container 100% wide until medium breakpoint.
         */
        md: Boolean,
        /**
         * Set container 100% wide until large breakpoint.
         */
        lg: Boolean,
        /**
         * Set container 100% wide until X-large breakpoint.
         */
        xl: Boolean,
        /**
         * Set container 100% wide until XX-large breakpoint.
         */
        xxl: Boolean,
        /**
         * Set container 100% wide, spanning the entire width of the viewport.
         */
        fluid: Boolean,
    },
    setup(props, { slots }) {
        const repsonsiveClassNames = [];
        BREAKPOINTS$3.forEach((bp) => {
            const breakpoint = props[bp];
            breakpoint && repsonsiveClassNames.push(`container-${bp}`);
        });
        return () => h$1('div', {
            class: [repsonsiveClassNames.length > 0 ? repsonsiveClassNames : 'container'],
        }, slots.default && slots.default());
    },
});

const BREAKPOINTS$2 = [
    'xxl',
    'xl',
    'lg',
    'md',
    'sm',
    'xs',
];
const CRow = defineComponent({
    name: 'CRow',
    props: {
        /**
         * The number of columns/offset/order on extra small devices (<576px).
         *
         * @values { cols: 'auto' | number | string } | { gutter: number | string } | { gutterX: number | string } | { gutterY: number | string }
         */
        xs: Object,
        /**
         * The number of columns/offset/order on small devices (<768px).
         *
         * @values { cols: 'auto' | number | string } | { gutter: number | string } | { gutterX: number | string } | { gutterY: number | string }
         */
        sm: Object,
        /**
         * The number of columns/offset/order on medium devices (<992px).
         *
         * @values { cols: 'auto' | number | string } | { gutter: number | string } | { gutterX: number | string } | { gutterY: number | string }
         */
        md: Object,
        /**
         * The number of columns/offset/order on large devices (<1200px).
         *
         * @values { cols: 'auto' | number | string } | { gutter: number | string } | { gutterX: number | string } | { gutterY: number | string }
         */
        lg: Object,
        /**
         * The number of columns/offset/order on X-Large devices (<1400px).
         *
         * @values { cols: 'auto' | number | string } | { gutter: number | string } | { gutterX: number | string } | { gutterY: number | string }
         */
        xl: Object,
        /**
         * The number of columns/offset/order on XX-Large devices (≥1400px).
         *
         * @values { cols: 'auto' | number | string } | { gutter: number | string } | { gutterX: number | string } | { gutterY: number | string }
         */
        xxl: Object,
    },
    setup(props, { slots }) {
        const repsonsiveClassNames = [];
        BREAKPOINTS$2.forEach((bp) => {
            const breakpoint = props[bp];
            const infix = bp === 'xs' ? '' : `-${bp}`;
            if (typeof breakpoint === 'object') {
                if (breakpoint.cols) {
                    repsonsiveClassNames.push(`row-cols${infix}-${breakpoint.cols}`);
                }
                if (typeof breakpoint.gutter === 'number') {
                    repsonsiveClassNames.push(`g${infix}-${breakpoint.gutter}`);
                }
                if (typeof breakpoint.gutterX === 'number') {
                    repsonsiveClassNames.push(`gx${infix}-${breakpoint.gutterX}`);
                }
                if (typeof breakpoint.gutterY === 'number') {
                    repsonsiveClassNames.push(`gy${infix}-${breakpoint.gutterY}`);
                }
            }
        });
        return () => h$1('div', {
            class: ['row', repsonsiveClassNames],
        }, slots.default && slots.default());
    },
});

const CGridPlugin = {
    install: (app) => {
        app.component(CCol.name, CCol);
        app.component(CContainer.name, CContainer);
        app.component(CRow.name, CRow);
    },
};

const CHeader = defineComponent({
    name: 'CHeader',
    props: {
        /**
         * Defines optional container wrapping children elements.
         *
         * @values boolean, 'sm', 'md', 'lg', 'xl', 'xxl', 'fluid'
         */
        container: {
            type: [Boolean, String],
            validator: (value) => {
                return (typeof value === 'boolean' || ['sm', 'md', 'lg', 'xl', 'xxl', 'fluid'].includes(value));
            },
        },
        /**
         * Place header in non-static positions.
         *
         * @values 'fixed', 'sticky'
         */
        position: {
            type: String,
            validator: (value) => {
                return ['fixed', 'sticky'].includes(value);
            },
        },
    },
    setup(props, { slots }) {
        return () => h$1('div', { class: ['header', { [`header-${props.position}`]: props.position }] }, props.container
            ? h$1('div', { class: `container${props.container === true ? '' : '-' + props.container}` }, slots.default && slots.default())
            : slots.default && slots.default());
    },
});

const CHeaderBrand = defineComponent({
    name: 'CHeaderBrand',
    props: {
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'a',
        },
    },
    setup(props, { slots }) {
        return () => h$1(props.component, { class: 'header-brand' }, slots.default && slots.default());
    },
});

const CHeaderDivider = defineComponent({
    name: 'CHeaderDivider',
    setup(_, { slots }) {
        return () => h$1('div', { class: 'header-divider' }, slots.default && slots.default());
    },
});

const CHeaderNav = defineComponent({
    name: 'CHeaderNav',
    props: {
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'ul',
        },
    },
    setup(props, { slots }) {
        return () => h$1(props.component, {
            class: 'header-nav',
            role: 'navigation',
        }, slots.default && slots.default());
    },
});

const CHeaderText = defineComponent({
    name: 'CHeaderText',
    setup(_, { slots }) {
        return () => h$1('span', { class: 'header-text' }, slots.default && slots.default());
    },
});

const CHeaderToggler = defineComponent({
    name: 'CHeaderToggler',
    setup(_, { slots }) {
        return () => h$1('button', {
            class: 'header-toggler',
            type: 'button',
            'aria-label': 'Toggle navigation',
        }, slots.default ? slots.default() : h$1('span', { class: ['header-toggler-icon'] }));
    },
});

const CHeaderPlugin = {
    install: (app) => {
        app.component(CHeader.name, CHeader);
        app.component(CHeaderBrand.name, CHeaderBrand);
        app.component(CHeaderDivider.name, CHeaderDivider);
        app.component(CHeaderNav.name, CHeaderNav);
        app.component(CHeaderText.name, CHeaderText);
        app.component(CHeaderToggler.name, CHeaderToggler);
    },
};

const CImage = defineComponent({
    name: 'CImage',
    props: {
        /**
         * Set the horizontal aligment.
         *
         * @values 'start', 'center', 'end'
         */
        align: {
            type: String,
            validator: (value) => {
                return ['start', 'center', 'end'].includes(value);
            },
        },
        /**
         * Make image responsive.
         */
        fluid: Boolean,
        /**
         * Make image rounded.
         */
        rounded: Boolean,
        /**
         * Give an image a rounded 1px border appearance.
         */
        thumbnail: Boolean,
    },
    setup(props) {
        return () => h$1('img', {
            class: [
                {
                    [`float-${props.align}`]: props.align && (props.align === 'start' || props.align === 'end'),
                    'd-block mx-auto': props.align && props.align === 'center',
                    'img-fluid': props.fluid,
                    rounded: props.rounded,
                    'img-thumbnail': props.thumbnail,
                },
            ],
        });
    },
});

const CImagePlugin = {
    install: (app) => {
        app.component(CImage.name, CImage);
    },
};

const CListGroup = defineComponent({
    name: 'CListGroup',
    props: {
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'ul',
        },
        /**
         * Remove some borders and rounded corners to render list group items edge-to-edge in a parent component (e.g., `<CCard>`)
         */
        flush: Boolean,
        /**
         * Specify a layout type.
         *
         * @values 'horizontal', 'horizontal-sm', 'horizontal-md', 'horizontal-lg', 'horizontal-xl', 'horizontal-xxl',
         */
        layout: {
            type: String,
            validator: (value) => {
                return [
                    'horizontal',
                    'horizontal-sm',
                    'horizontal-md',
                    'horizontal-lg',
                    'horizontal-xl',
                    'horizontal-xxl',
                ].includes(value);
            },
        },
    },
    setup(props, { slots }) {
        return () => h$1(props.component, {
            class: [
                'list-group',
                {
                    'list-group-flush': props.flush,
                    [`list-group-${props.layout}`]: props.layout,
                },
            ],
        }, slots.default && slots.default());
    },
});

const CListGroupItem = defineComponent({
    name: 'CListGroupItem',
    props: {
        /**
         * Toggle the active state for the component.
         */
        active: Boolean,
        /**
         * Sets the color context of the component to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light', string
         */
        color: Color$1,
        /**
         * Toggle the disabled state for the component.
         */
        disabled: Boolean,
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'li',
        },
    },
    setup(props, { slots }) {
        return () => h$1(props.component, {
            class: [
                'list-group-item',
                {
                    [`list-group-item-${props.color}`]: props.color,
                    'list-group-item-action': props.component === 'a' || props.component === 'button',
                    [`active`]: props.active,
                    [`disabled`]: props.disabled,
                },
            ],
            ...((props.component === 'a' || props.component === 'button') && {
                active: props.active,
                disabled: props.disabled,
            }),
            ...(props.active && { 'aria-current': true }),
            ...(props.disabled && { 'aria-disabled': true }),
        }, slots.default && slots.default());
    },
});

const CListGroupPlugin = {
    install: (app) => {
        app.component(CListGroup.name, CListGroup);
        app.component(CListGroupItem.name, CListGroupItem);
    },
};

const CLoadingButton = defineComponent({
    name: 'CLoadingButton',
    props: {
        /**
         * Makes button disabled when loading.
         */
        disabledOnLoading: Boolean,
        /**
         * Loading state (set to true to start animation).
         */
        loading: {
            type: Boolean,
            default: false,
            required: false,
        },
        /**
         * Sets type of spinner.
         *
         * @values 'border', 'grow'
         * @default 'border'
         */
        spinnerType: {
            type: String,
            default: 'border',
            required: false,
            validator: (value) => {
                return ['border', 'grow'].includes(value);
            },
        },
        /**
         * Automatically starts loading animation and stops after a determined amount of milliseconds.
         */
        timeout: {
            type: Number,
            default: undefined,
            required: false,
        },
        ...CButton.props,
    },
    emits: [
        /**
         * Event called when the user clicks on a component.
         */
        'click',
    ],
    setup(props, { emit, slots }) {
        const loading = ref(props.loading);
        watch(() => props.loading, () => {
            loading.value = props.loading;
        });
        const handleOnClick = () => {
            emit('click');
            if (props.timeout) {
                loading.value = true;
                setTimeout(() => {
                    loading.value = false;
                }, props.timeout);
            }
        };
        return () => h$1(CButton, {
            ...props,
            class: ['btn-loading', { ['is-loading']: loading.value }],
            ...(props.disabledOnLoading && loading.value && { disabled: true }),
            onClick: () => handleOnClick(),
        }, {
            default: () => [
                h$1(CSpinner, { class: 'btn-loading-spinner', size: 'sm', variant: props.spinnerType }),
                slots.default && slots.default(),
            ],
        });
    },
});

const CLoadingButtonPlugin = {
    install: (app) => {
        app.component(CLoadingButton.name, CLoadingButton);
    },
};

const CModal = defineComponent({
    name: 'CModal',
    inheritAttrs: false,
    props: {
        /**
         * Align the modal in the center or top of the screen.
         *
         * @values 'top', 'center'
         */
        alignment: {
            default: 'top',
            validator: (value) => {
                return ['top', 'center'].includes(value);
            },
        },
        /**
         * Apply a backdrop on body while offcanvas is open.
         *
         * @values boolean | 'static'
         */
        backdrop: {
            type: [Boolean, String],
            default: true,
            validator: (value) => {
                if (typeof value == 'string') {
                    return ['static'].includes(value);
                }
                if (typeof value == 'boolean') {
                    return true;
                }
                return false;
            },
        },
        /**
         * A string of all className you want applied to the modal content component.
         */
        contentClassName: String,
        /**
         * Set modal to covers the entire user viewport
         *
         * @values boolean, 'sm', 'md', 'lg', 'xl', 'xxl'
         */
        fullscreen: {
            type: [Boolean, String],
            validator: (value) => {
                if (typeof value == 'string') {
                    return ['sm', 'md', 'lg', 'xl', 'xxl'].includes(value);
                }
                if (typeof value == 'boolean') {
                    return true;
                }
                return false;
            },
        },
        /**
         * Closes the modal when escape key is pressed.
         */
        keyboard: {
            type: Boolean,
            default: true,
        },
        /**
         * Create a scrollable modal that allows scrolling the modal body.
         */
        scrollable: Boolean,
        /**
         * Size the component small, large, or extra large.
         *
         * @values 'sm', 'lg', 'xl'
         */
        size: {
            type: String,
            validator: (value) => {
                return ['sm', 'lg', 'xl'].includes(value);
            },
        },
        /**
         * Remove animation to create modal that simply appear rather than fade in to view.
         */
        transition: {
            type: Boolean,
            default: true,
        },
        /**
         * By default the component is unmounted after close animation, if you want to keep the component mounted set this property to false.
         */
        unmountOnClose: {
            type: Boolean,
            default: true,
        },
        /**
         * Toggle the visibility of alert component.
         */
        visible: Boolean,
    },
    emits: [
        /**
         * Callback fired when the component requests to be closed.
         */
        'close',
        /**
         * Callback fired when the component requests to be closed.
         */
        'close-prevented',
        /**
         * Callback fired when the modal is shown, its backdrop is static and a click outside the modal or an escape key press is performed with the keyboard option set to false.
         */
        'show',
    ],
    setup(props, { slots, attrs, emit }) {
        const modalRef = ref();
        const modalContentRef = ref();
        const visible = ref(props.visible);
        watch(() => props.visible, () => {
            visible.value = props.visible;
        });
        const handleEnter = (el, done) => {
            executeAfterTransition(() => done(), el);
            document.body.classList.add('modal-open');
            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = '0px';
            el.style.display = 'block';
            setTimeout(() => {
                el.classList.add('show');
            }, 1);
            emit('show');
        };
        const handleAfterEnter = () => {
            window.addEventListener('mousedown', handleMouseDown);
            window.addEventListener('keyup', handleKeyUp);
        };
        // eslint-disable-next-line unicorn/consistent-function-scoping
        const handleLeave = (el, done) => {
            executeAfterTransition(() => done(), el);
            document.body.classList.remove('modal-open');
            document.body.style.removeProperty('overflow');
            document.body.style.removeProperty('padding-right');
            if (document.body.className === '') {
                document.body.removeAttribute('class');
            }
            el.classList.remove('show');
        };
        const handleAfterLeave = (el) => {
            window.removeEventListener('mousedown', handleMouseDown);
            window.removeEventListener('keyup', handleKeyUp);
            el.style.display = 'none';
        };
        const handleDismiss = () => {
            emit('close');
            visible.value = false;
        };
        const handleKeyUp = (event) => {
            if (modalContentRef.value && !modalContentRef.value.contains(event.target)) {
                if (props.backdrop !== 'static' && event.key === 'Escape' && props.keyboard) {
                    handleDismiss();
                }
                if (props.backdrop === 'static') {
                    modalRef.value.classList.add('modal-static');
                    emit('close-prevented');
                    setTimeout(() => {
                        modalRef.value.classList.remove('modal-static');
                    }, 300);
                }
            }
        };
        const handleMouseDown = (event) => {
            window.addEventListener('mouseup', () => handleMouseUp(event), { once: true });
        };
        const handleMouseUp = (event) => {
            if (modalContentRef.value && !modalContentRef.value.contains(event.target)) {
                if (props.backdrop !== 'static') {
                    handleDismiss();
                }
                if (props.backdrop === 'static') {
                    modalRef.value.classList.add('modal-static');
                    setTimeout(() => {
                        modalRef.value.classList.remove('modal-static');
                    }, 300);
                }
            }
        };
        provide('handleDismiss', handleDismiss);
        const modal = () => h$1('div', {
            class: [
                'modal',
                {
                    ['fade']: props.transition,
                },
                attrs.class,
            ],
            ref: modalRef,
        }, h$1('div', {
            class: [
                'modal-dialog',
                {
                    'modal-dialog-centered': props.alignment === 'center',
                    [`modal-fullscreen-${props.fullscreen}-down`]: props.fullscreen && typeof props.fullscreen === 'string',
                    'modal-fullscreen': props.fullscreen && typeof props.fullscreen === 'boolean',
                    ['modal-dialog-scrollable']: props.scrollable,
                    [`modal-${props.size}`]: props.size,
                },
            ],
            role: 'dialog',
        }, h$1('div', { class: ['modal-content', props.contentClassName], ref: modalContentRef }, slots.default && slots.default())));
        return () => [
            h$1(Transition, {
                css: false,
                onEnter: (el, done) => handleEnter(el, done),
                onAfterEnter: () => handleAfterEnter(),
                onLeave: (el, done) => handleLeave(el, done),
                onAfterLeave: (el) => handleAfterLeave(el),
            }, () => props.unmountOnClose
                ? visible.value && modal()
                : withDirectives(modal(), [[vShow, visible.value]])),
            props.backdrop &&
                h$1(CBackdrop, {
                    class: 'modal-backdrop',
                    visible: visible.value,
                }),
        ];
    },
});

const CModalBody = defineComponent({
    name: 'CModalBody',
    setup(_, { slots }) {
        return () => h$1('div', { class: 'modal-body' }, slots.default && slots.default());
    },
});

const CModalFooter = defineComponent({
    name: 'CModalFooter',
    setup(_, { slots }) {
        return () => h$1('div', { class: 'modal-footer' }, slots.default && slots.default());
    },
});

const CModalHeader = defineComponent({
    name: 'CModalHeader',
    props: {
        /**
         * Add a close button component to the header.
         */
        closeButton: {
            type: Boolean,
            default: true,
        },
    },
    setup(props, { slots }) {
        const handleDismiss = inject('handleDismiss');
        return () => h$1('span', { class: 'modal-header' }, [
            slots.default && slots.default(),
            props.closeButton && h$1(CCloseButton, { onClick: () => handleDismiss() }, ''),
        ]);
    },
});

const CModalTitle = defineComponent({
    name: 'CModalTitle',
    props: {
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'h5',
        },
    },
    setup(props, { slots }) {
        return () => h$1(props.component, { class: 'modal-title' }, slots.default && slots.default());
    },
});

const CModalPlugin = {
    install: (app) => {
        app.component(CModal.name, CModal);
        app.component(CModalBody.name, CModalBody);
        app.component(CModalFooter.name, CModalFooter);
        app.component(CModalHeader.name, CModalHeader);
        app.component(CModalTitle.name, CModalTitle);
    },
};

const CMultiSelectNativeSelect = defineComponent({
    name: 'CMultiSelectGroupOption',
    props: {
        id: String,
        multiple: {
            type: Boolean,
            default: true,
        },
        options: {
            type: Array,
            default: () => [],
        },
        required: Boolean,
        value: [Number, String, Array],
    },
    emits: ['change'],
    setup(props, { emit }) {
        const nativeSelectRef = inject('nativeSelectRef');
        const createNativeOptions = (options) => {
            return options.map((option) => {
                return option.options
                    ? h$1('optgroup', { label: option.label }, createNativeOptions(option.options))
                    : h$1('option', { disabled: option.disabled, value: option.value });
            });
        };
        const handleChange = (event) => {
            const target = event.target;
            emit('change', Number(target.value));
        };
        return () => h$1('select', {
            className: 'multi-select-new',
            ...(props.id && { id: `${props.id}-multi-select` }),
            ...(props.id && { name: `${props.id}-multi-select` }),
            multiple: props.multiple,
            tabIndex: '-1',
            style: { display: 'none' },
            required: props.required,
            value: props.value,
            ref: nativeSelectRef,
            onChange: handleChange,
        }, props.options && createNativeOptions(props.options));
    },
});

const CVirtualScroller = defineComponent({
    name: 'CVirtualScroller',
    props: {
        /**
         * Amount of visible items
         */
        visibleItems: {
            type: Number,
            default: 10,
        },
    },
    setup(props, { slots }) {
        const virtualScrollRef = ref();
        const virtualScrollContentRef = ref();
        const currentItemIndex = ref(1);
        const itemHeight = ref(0);
        const itemsNumber = ref(0);
        const viewportPadding = ref(0);
        const buffer = computed(() => Math.floor(props.visibleItems / 2));
        const maxHeight = computed(() => itemsNumber.value * itemHeight.value + 2 * viewportPadding.value);
        const viewportHeight = computed(() => Math.min(props.visibleItems, itemsNumber.value) * itemHeight.value +
            2 * viewportPadding.value);
        onMounted(() => {
            if (virtualScrollRef.value) {
                viewportPadding.value = Number.parseFloat(getComputedStyle(virtualScrollRef.value).paddingTop);
                // It's necessary to calculate heights of items
                virtualScrollRef.value.dispatchEvent(new CustomEvent('scroll'));
            }
        });
        const handleScroll = (scrollTop) => {
            currentItemIndex.value =
                itemHeight.value && Math.max(Math.ceil(scrollTop / itemHeight.value), 1);
        };
        return () => {
            const children = slots.default
                ? Array.isArray(slots.default()[0].children)
                    ? slots.default()[0].children
                    : slots.default()
                : [];
            itemsNumber.value = children && children.length > 0 ? children.length : 0;
            return h$1('div', {
                class: ['virtual-scroller'],
                onScroll: (event) => handleScroll(event.target.scrollTop),
                style: {
                    height: `${viewportHeight.value}px`,
                    overflowY: 'auto',
                },
                ref: virtualScrollRef,
            }, h$1('div', {
                class: 'virtual-scroller-content',
                style: {
                    height: `${maxHeight.value}px`,
                },
                ref: virtualScrollContentRef,
            }, children.map((slot, index) => index + 1 > Math.max(currentItemIndex.value - buffer.value, 0) &&
                index + 1 <= currentItemIndex.value + props.visibleItems + buffer.value &&
                cloneVNode(slot, {
                    class: [
                        {
                            'virtual-scroller-item-preload': index + 1 > currentItemIndex.value + props.visibleItems ||
                                index + 1 < currentItemIndex.value,
                        },
                    ],
                    style: {
                        ...(currentItemIndex.value > buffer.value && {
                            transform: `translateY(${(currentItemIndex.value - buffer.value) * itemHeight.value}px)`,
                        }),
                    },
                    ref: (node) => {
                        if (itemHeight.value === 0 && node && node.offsetHeight) {
                            itemHeight.value =
                                node.offsetHeight +
                                    Number.parseFloat(getComputedStyle(node).marginTop) +
                                    Number.parseFloat(getComputedStyle(node).marginBottom);
                        }
                    },
                }))));
        };
    },
});

const CVirtualScrollerPlugin = {
    install: (app) => {
        app.component(CVirtualScroller.name, CVirtualScroller);
    },
};

const createOption = (text, options) => {
    const value = text.toLowerCase().replace(/\s/g, '-');
    let uniqueValue = value;
    let i = 1;
    while (options.some((option) => String(option.value) === uniqueValue)) {
        uniqueValue = `${value}-${i}`;
        i++;
    }
    return [
        {
            value: uniqueValue,
            text,
            custom: true,
        },
    ];
};
const filterOptionsList = (search, _options) => {
    if (search.length > 0 && _options) {
        const optionsList = [];
        for (const option of _options) {
            const options = option.options &&
                option.options.filter((option) => option.text && option.text.toLowerCase().includes(search.toLowerCase()));
            if ((option.text && option.text.toLowerCase().includes(search.toLowerCase())) ||
                (options && options.length > 0)) {
                optionsList.push(Object.assign({}, option, options && options.length > 0 && { options }));
            }
        }
        return optionsList;
    }
    return _options;
};
const flattenOptionsArray = (options, keepGroupLabel) => {
    const optionsList = [];
    for (const option of options) {
        if (Array.isArray(option.options)) {
            const { options, ...rest } = option;
            if (keepGroupLabel) {
                optionsList.push(rest);
            }
            optionsList.push(...options);
        }
        else {
            optionsList.push(option);
        }
    }
    return optionsList;
};
const getNextSibling = (elem, selector) => {
    // Get the next sibling element
    let sibling = elem.nextElementSibling;
    // If there's no selector, return the first sibling
    if (!selector)
        return sibling;
    // If the sibling matches our selector, use it
    // If not, jump to the next sibling and continue the loop
    while (sibling) {
        if (sibling.matches(selector))
            return sibling;
        sibling = sibling.nextElementSibling;
    }
    return;
};
const getPreviousSibling = (elem, selector) => {
    // Get the next sibling element
    let sibling = elem.previousElementSibling;
    // If there's no selector, return the first sibling
    if (!selector)
        return sibling;
    // If the sibling matches our selector, use it
    // If not, jump to the next sibling and continue the loop
    while (sibling) {
        if (sibling.matches(selector))
            return sibling;
        sibling = sibling.previousElementSibling;
    }
    return;
};
const selectOptions = (options, selected, deselected) => {
    let _selected = [...selected, ...options];
    if (deselected) {
        _selected = _selected.filter((selectedOption) => !deselected.some((deselectedOption) => deselectedOption.value === selectedOption.value));
    }
    const deduplicated = [];
    for (const option of _selected) {
        if (!deduplicated.some((obj) => obj.value === option.value)) {
            deduplicated.push(option);
        }
    }
    return deduplicated;
};

const CMultiSelectOptions = defineComponent({
    name: 'CMultiSelectOptions',
    props: {
        loading: Boolean,
        options: {
            type: Array,
            default: () => [],
        },
        optionsMaxHeight: {
            type: [Number, String],
            default: 'auto',
        },
        optionsStyle: {
            type: String,
            default: 'checkbox',
            validator: (value) => {
                return ['checkbox', 'text'].includes(value);
            },
        },
        scopedSlots: Object,
        searchNoResultsLabel: {
            type: String,
            default: 'no items',
        },
        selected: {
            type: Array,
            default: () => [],
        },
        virtualScroller: Boolean,
        visibleItems: {
            type: Number,
            default: 10,
        },
    },
    emits: ['optionClick'],
    setup(props, { emit }) {
        const handleKeyDown = (event, option) => {
            if (event.code === 'Space' || event.key === 'Enter') {
                event.preventDefault();
                handleOptionClick && handleOptionClick(option);
                return;
            }
            if (event.key === 'Down' || event.key === 'ArrowDown') {
                event.preventDefault();
                const target = event.target;
                const next = getNextSibling(target, '.form-multi-select-option');
                next && next.focus();
            }
            if (event.key === 'Up' || event.key === 'ArrowUp') {
                event.preventDefault();
                const target = event.target;
                const prev = getPreviousSibling(target, '.form-multi-select-option');
                prev && prev.focus();
            }
        };
        const handleOptionClick = (option) => {
            emit('optionClick', option);
        };
        // TODO: find solution how to remove any
        const createOptions = (options) => options.length > 0
            ? options.map((option) => 'value' in option
                ? h$1('div', {
                    class: [
                        'form-multi-select-option',
                        {
                            'form-multi-select-option-with-checkbox': props.optionsStyle === 'checkbox',
                            'form-multi-selected': props.selected.some((_option) => _option.value === option.value),
                            disabled: option.disabled,
                        },
                    ],
                    onClick: () => handleOptionClick(option),
                    onKeydown: (event) => handleKeyDown(event, option),
                    tabindex: 0,
                }, props.scopedSlots && props.scopedSlots['options']
                    ? h$1(props.scopedSlots['options'], { option: option })
                    : option.text)
                : [
                    h$1('div', { class: 'form-multi-select-optgroup-label' }, props.scopedSlots && props.scopedSlots['options-groups']
                        ? h$1(props.scopedSlots['options-groups'], { option: option })
                        : option.label),
                ])
            : h$1('div', { class: 'form-multi-select-options-empty' }, props.searchNoResultsLabel);
        return () => [
            props.virtualScroller
                ? h$1(CVirtualScroller, {
                    class: 'form-multi-select-options',
                    visibleItems: props.visibleItems,
                }, {
                    default: () => createOptions(props.options),
                })
                : h$1('div', {
                    class: 'form-multi-select-options',
                    ...(props.optionsMaxHeight !== 'auto' && {
                        style: { maxHeight: props.optionsMaxHeight, overflow: 'scroll' },
                    }),
                }, createOptions(props.options)),
            props.loading && h$1(CElementCover),
        ];
    },
});

const CMultiSelectSelection = defineComponent({
    name: 'CMultiSelectSelection',
    props: {
        multiple: {
            type: Boolean,
            default: true,
        },
        placeholder: String,
        search: [Boolean, String],
        selected: {
            type: Array,
            default: () => [],
        },
        selectionType: {
            type: String,
            default: 'tags',
            validator: (value) => {
                return ['counter', 'tags', 'text'].includes(value);
            },
        },
        selectionTypeCounterText: {
            type: String,
            default: 'item(s) selected',
        },
    },
    emits: ['remove'],
    setup(props, { emit, slots }) {
        const handleRemove = (option) => {
            emit('remove', option);
        };
        return () => h$1('div', {
            class: 'form-multi-select-selection',
        }, [
            props.multiple &&
                props.selectionType === 'counter' &&
                !props.search &&
                props.selected.length === 0 &&
                props.placeholder,
            props.multiple &&
                props.selectionType === 'counter' &&
                !props.search &&
                props.selected.length > 0 &&
                `${props.selected.length} ${props.selectionTypeCounterText}`,
            props.multiple &&
                props.selectionType === 'tags' &&
                props.selected.map((option) => {
                    if (props.selectionType === 'tags') {
                        return h$1('span', { class: 'form-multi-select-tag' }, [
                            option.text,
                            !option.disabled &&
                                h$1('button', {
                                    class: 'form-multi-select-tag-delete close',
                                    ariaLabel: 'Close',
                                    onClick: () => handleRemove(option),
                                }, h$1('span', { ariaHidden: 'true' }, 'x')),
                        ]);
                    }
                    return;
                }),
            props.multiple &&
                props.selectionType === 'text' &&
                props.selected.map((option, index) => h$1('span', `${option.text}${index === props.selected.length - 1 ? '' : ','}\xA0`)),
            !props.multiple && !props.search && props.selected.map((option) => option.text)[0],
            slots.default && slots.default(),
        ]);
    },
});

const CMultiSelect = defineComponent({
    name: 'CMultiSelect',
    props: {
        /**
         * Allow users to create options if they are not in the list of options.
         *
         * @since 4.9.0
         */
        allowCreateOptions: Boolean,
        /**
         * Enables selection cleaner element.
         *
         * @default true
         */
        cleaner: {
            type: Boolean,
            default: true,
        },
        /**
         * Clear current search on selecting an item.
         *
         * @since 4.9.0
         */
        clearSearchOnSelect: Boolean,
        /**
         * Toggle the disabled state for the component.
         */
        disabled: Boolean,
        /**
         * Provide valuable, actionable feedback.
         *
         * @since 4.6.0
         */
        feedback: String,
        /**
         * Provide valuable, actionable feedback.
         *
         * @since 4.6.0
         */
        feedbackInvalid: String,
        /**
         * Provide valuable, actionable invalid feedback when using standard HTML form validation which applied two CSS pseudo-classes, `:invalid` and `:valid`.
         *
         * @since 4.6.0
         */
        feedbackValid: String,
        /**
         * The id global attribute defines an identifier (ID) that must be unique in the whole document.
         */
        id: String,
        /**
         * Set component validation state to invalid.
         *
         * @since 4.6.0
         */
        invalid: Boolean,
        /**
         * Add a caption for a component.
         *
         * @since 4.6.0
         */
        label: String,
        /**
         * When set, the options list will have a loading style: loading spinner and reduced opacity.
         *
         * @since 4.9.0
         */
        loading: Boolean,
        /**
         * It specifies that multiple options can be selected at once.
         *
         * @default true
         */
        multiple: {
            type: Boolean,
            default: true,
        },
        /**
         * List of option elements.
         */
        options: {
            type: Array,
            default: () => [],
        },
        /**
         * Sets maxHeight of options list.
         *
         * @default 'auto'
         */
        optionsMaxHeight: {
            type: [Number, String],
            default: 'auto',
        },
        /**
         * Sets option style.
         *
         * @values 'checkbox', 'text'
         * @default 'checkbox'
         */
        optionsStyle: {
            type: String,
            default: 'checkbox',
            validator: (value) => {
                return ['checkbox', 'text'].includes(value);
            },
        },
        /**
         * Specifies a short hint that is visible in the search input.
         *
         * @default 'Select...''
         */
        placeholder: {
            type: String,
            default: 'Select...',
        },
        /**
         * When it is present, it indicates that the user must choose a value before submitting the form.
         */
        required: Boolean,
        /**
         * Enables search input element.
         */
        search: {
            type: [Boolean, String],
            default: true,
            validator: (value) => {
                if (typeof value == 'string') {
                    return ['external'].includes(value);
                }
                if (typeof value == 'boolean') {
                    return true;
                }
                return false;
            },
        },
        /**
         * Sets the label for no results when filtering.
         */
        searchNoResultsLabel: {
            type: String,
            default: 'no items',
        },
        /**
         * Enables select all button.
         *
         * @default true
         */
        selectAll: {
            type: Boolean,
            default: true,
        },
        /**
         * Sets the select all button label.
         *
         * @default 'Select all options'
         */
        selectAllLabel: {
            type: String,
            default: 'Select all options',
        },
        /**
         * Sets the selection style.
         *
         * @values 'counter', 'tags', 'text'
         * @default 'tags'
         */
        selectionType: {
            type: String,
            default: 'tags',
            validator: (value) => {
                return ['counter', 'tags', 'text'].includes(value);
            },
        },
        /**
         * Sets the counter selection label.
         *
         * @default 'item(s) selected'
         */
        selectionTypeCounterText: {
            type: String,
            default: 'item(s) selected',
        },
        /**
         * Size the component small or large.
         *
         * @values 'sm', 'lg'
         */
        size: {
            type: String,
            validator: (value) => {
                return ['sm', 'lg'].includes(value);
            },
        },
        /**
         * Add helper text to the component.
         *
         * @since 4.6.0
         */
        text: String,
        /**
         * Display validation feedback in a styled tooltip.
         *
         * @since 4.6.0
         */
        tooltipFeedback: Boolean,
        /**
         * Set component validation state to valid.
         *
         * @since 4.6.0
         */
        valid: Boolean,
        /**
         * Enable virtual scroller for the options list.
         *
         * @since 4.8.0
         */
        virtualScroller: Boolean,
        /**
         * Toggle the visibility of multi select dropdown.
         *
         * @default false
         */
        visible: Boolean,
        /**
         *
         * Amount of visible items when virtualScroller is set to `true`.
         *
         * @since 4.8.0
         */
        visibleItems: {
            type: Number,
            default: 10,
        },
    },
    emits: [
        /**
         * Execute a function when a user changes the selected option. [docs]
         */
        'change',
        /**
         * Execute a function when the filter value changed.
         *
         * @since 4.7.0
         */
        'filterChange',
        /**
         * The callback is fired when the Multi Select component requests to be hidden.
         */
        'hide',
        /**
         * The callback is fired when the Multi Select component requests to be shown.
         */
        'show',
    ],
    setup(props, { attrs, emit, slots }) {
        const multiSelectRef = ref();
        const dropdownRef = ref();
        const nativeSelectRef = ref();
        const togglerRef = ref();
        const searchRef = ref();
        const options = ref(props.options);
        const popper = ref();
        const searchValue = ref('');
        const selected = ref([]);
        const userOptions = ref([]);
        const visible = ref(props.visible);
        provide('nativeSelectRef', nativeSelectRef);
        const filteredOptions = computed(() => flattenOptionsArray(props.search === 'external'
            ? [...options.value, ...filterOptionsList(searchValue.value, userOptions.value)]
            : filterOptionsList(searchValue.value, [...options.value, ...userOptions.value]), true));
        const flattenedOptions = computed(() => flattenOptionsArray(props.options));
        const userOption = computed(() => {
            if (props.allowCreateOptions &&
                filteredOptions.value.some((option) => option.text && option.text.toLowerCase() === searchValue.value.toLowerCase())) {
                return false;
            }
            return searchRef.value && createOption(String(searchValue.value), flattenedOptions.value);
        });
        watch(() => props.options, (newValue, oldValue) => {
            if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
                options.value = newValue;
                const _selected = flattenedOptions.value.filter((option) => option.selected === true);
                const deselected = flattenedOptions.value.filter((option) => option.selected === false);
                if (_selected) {
                    selected.value = selectOptions(_selected, selected.value, deselected);
                }
            }
        }, { immediate: true });
        watch(selected, () => {
            nativeSelectRef.value &&
                nativeSelectRef.value.dispatchEvent(new Event('change', { bubbles: true }));
            if (popper.value) {
                popper.value.update();
            }
        });
        watch(visible, () => {
            if (visible.value) {
                emit('show');
                window.addEventListener('mouseup', handleMouseUp);
                window.addEventListener('keyup', handleKeyUp);
                initPopper();
                // TODO: find better solution
                setTimeout(() => {
                    searchRef.value && searchRef.value.focus();
                }, 100);
                return;
            }
            emit('hide');
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('keyup', handleKeyUp);
            destroyPopper();
        });
        onBeforeUnmount(() => {
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('keyup', handleKeyUp);
        });
        const initPopper = () => {
            if (togglerRef.value && dropdownRef.value) {
                popper.value = createPopper(togglerRef.value, dropdownRef.value, {
                    placement: isRTL() ? 'bottom-end' : 'bottom-start',
                    modifiers: [
                        {
                            name: 'preventOverflow',
                            options: {
                                boundary: 'clippingParents',
                            },
                        },
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 2],
                            },
                        },
                    ],
                });
            }
        };
        const destroyPopper = () => {
            if (popper.value) {
                popper.value.destroy();
            }
            popper.value = undefined;
        };
        const handleKeyUp = (event) => {
            if (event.key === 'Escape') {
                visible.value = false;
            }
        };
        const handleMouseUp = (event) => {
            if (multiSelectRef.value && multiSelectRef.value.contains(event.target)) {
                return;
            }
            visible.value = false;
        };
        const handleSearchChange = (event) => {
            const target = event.target;
            searchValue.value = target.value.toLowerCase();
            emit('filterChange', target.value);
        };
        const handleSearchKeyDown = (event) => {
            if (event.key === 'Enter' && searchValue.value && props.allowCreateOptions) {
                event.preventDefault();
                if (!userOption.value) {
                    selected.value = [
                        ...selected.value,
                        filteredOptions.value.find((option) => String(option.text).toLowerCase() === searchValue.value.toLowerCase()),
                    ];
                }
                if (userOption.value) {
                    selected.value = [...selected.value, ...userOption.value];
                    userOptions.value = [...userOptions.value, ...userOption.value];
                }
                searchValue.value = '';
                if (searchRef.value) {
                    searchRef.value.value = '';
                }
                return;
            }
            if (searchValue.value.length > 0) {
                return;
            }
            if (event.key === 'Backspace' || event.key === 'Delete') {
                const last = selected.value.filter((option) => !option.disabled).pop();
                if (last) {
                    selected.value = selected.value.filter((option) => option.value !== last.value);
                }
            }
        };
        const handleOptionClick = (option) => {
            if (!props.multiple) {
                selected.value = [{ value: option.value, text: option.text }];
                visible.value = false;
                if (searchRef.value) {
                    searchRef.value.value = '';
                }
                return;
            }
            if (option.custom && !userOptions.value.some((_option) => _option.value === option.value)) {
                userOptions.value = [...userOptions.value, option];
            }
            if (props.clearSearchOnSelect || option.custom) {
                searchValue.value = '';
                if (searchRef.value) {
                    searchRef.value.value = '';
                    searchRef.value.focus();
                }
            }
            if (selected.value.some((_option) => _option.value === option.value)) {
                selected.value = selected.value.filter((_option) => _option.value !== option.value);
            }
            else {
                selected.value = [
                    ...selected.value,
                    { value: option.value, text: option.text },
                ];
            }
        };
        const handleSelectAll = () => {
            selected.value = selectOptions([
                ...flattenedOptions.value.filter((option) => !option.disabled),
                ...userOptions.value,
            ], selected.value);
        };
        const handleDeselectAll = () => {
            selected.value = selected.value.filter((option) => option.disabled);
        };
        return () => [
            h$1(CMultiSelectNativeSelect, {
                id: props.id,
                multiple: props.multiple,
                options: selected.value,
                required: props.required,
                value: props.multiple
                    ? selected.value.map((option) => option.value.toString())
                    : selected.value.map((option) => option.value)[0],
                onChange: () => emit('change', selected.value),
            }),
            h$1(CFormControlWrapper, {
                describedby: attrs['aria-describedby'],
                feedback: props.feedback,
                feedbackInvalid: props.feedbackInvalid,
                feedbackValid: props.feedbackValid,
                id: props.id,
                invalid: props.invalid,
                label: props.label,
                text: props.text,
                tooltipFeedback: props.tooltipFeedback,
                valid: props.valid,
            }, {
                default: () => h$1('div', {
                    class: [
                        'dropdown',
                        'picker',
                        'form-multi-select',
                        {
                            'form-multi-select-with-cleaner': props.cleaner,
                            disabled: props.disabled,
                            [`form-multi-select-${props.size}`]: props.size,
                            'form-multi-select-selection-tags': props.multiple && props.selectionType === 'tags',
                            show: visible.value,
                            'is-invalid': props.invalid,
                            'is-valid': props.valid,
                        },
                    ],
                    'aria-expanded': visible.value,
                    id: props.id,
                    ref: multiSelectRef,
                }, {
                    default: () => [
                        h$1('div', {
                            class: 'form-multi-select-input-group',
                            onClick: () => {
                                visible.value = true;
                            },
                            ref: togglerRef,
                        }, {
                            default: () => [
                                h$1(CMultiSelectSelection, {
                                    multiple: props.multiple,
                                    placeholder: props.placeholder,
                                    onRemove: (option) => !props.disabled && handleOptionClick(option),
                                    search: props.search,
                                    selected: selected.value,
                                    selectionType: props.selectionType,
                                    selectionTypeCounterText: props.selectionTypeCounterText,
                                }),
                                props.multiple &&
                                    props.cleaner &&
                                    selected.value.length > 0 &&
                                    !props.disabled &&
                                    h$1('button', {
                                        type: 'button',
                                        class: 'form-multi-select-selection-cleaner',
                                        onClick: () => handleDeselectAll(),
                                    }),
                                props.search &&
                                    h$1('input', {
                                        type: 'text',
                                        class: 'form-multi-select-search',
                                        disabled: props.disabled,
                                        autocomplete: 'off',
                                        onInput: (event) => handleSearchChange(event),
                                        onKeydown: (event) => handleSearchKeyDown(event),
                                        ...(selected.value.length === 0 && {
                                            placeholder: props.placeholder,
                                        }),
                                        ...(selected.value.length > 0 &&
                                            props.selectionType === 'counter' && {
                                            placeholder: `${selected.value.length} ${props.selectionTypeCounterText}`,
                                        }),
                                        ...(selected.value.length > 0 &&
                                            !props.multiple && {
                                            placeholder: selected.value.map((option) => option.text)[0],
                                        }),
                                        ...(props.multiple &&
                                            selected.value.length > 0 &&
                                            props.selectionType !== 'counter' && {
                                            size: searchValue.value.length + 2,
                                        }),
                                        ref: searchRef,
                                    }),
                            ],
                        }),
                        h$1('div', {
                            class: [
                                'dropdown-menu',
                                {
                                    show: visible.value,
                                },
                            ],
                            role: 'menu',
                            ref: dropdownRef,
                        }, {
                            default: () => [
                                props.multiple &&
                                    props.selectAll &&
                                    h$1('button', {
                                        class: 'form-multi-select-all',
                                        onClick: () => handleSelectAll(),
                                        type: 'button',
                                    }, props.selectAllLabel),
                                h$1(CMultiSelectOptions, {
                                    loading: props.loading,
                                    onOptionClick: (option) => handleOptionClick(option),
                                    options: filteredOptions.value.length === 0 && props.allowCreateOptions
                                        ? userOption.value || []
                                        : filteredOptions.value,
                                    optionsMaxHeight: props.optionsMaxHeight,
                                    optionsStyle: props.optionsStyle,
                                    scopedSlots: slots,
                                    searchNoResultsLabel: props.searchNoResultsLabel,
                                    selected: selected.value,
                                    virtualScroller: props.virtualScroller,
                                    visibleItems: props.visibleItems,
                                }),
                            ],
                        }),
                    ],
                }),
            }),
        ];
    },
});

const CMultiSelectPlugin = {
    install: (app) => {
        app.component(CMultiSelect.name, CMultiSelect);
    },
};

const CNav = defineComponent({
    name: 'CNav',
    props: {
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'ul',
        },
        /**
         * Specify a layout type for component.
         *
         * @values 'fill', 'justified'
         */
        layout: {
            type: String,
            validator: (value) => {
                return ['fill', 'justified'].includes(value);
            },
        },
        /**
         * Set the nav variant to tabs or pills.
         *
         * @values 'tabs', 'pills', 'underline'
         */
        variant: {
            type: String,
            validator: (value) => {
                return ['tabs', 'pills', 'underline'].includes(value);
            },
        },
    },
    setup(props, { slots }) {
        return () => h$1(props.component, {
            class: [
                'nav',
                {
                    [`nav-${props.layout}`]: props.layout,
                    [`nav-${props.variant}`]: props.variant,
                },
            ],
            role: 'navigation',
        }, slots.default && slots.default());
    },
});

const CNavGroup = defineComponent({
    name: 'CNavGroup',
    props: {
        /**
         * Make nav group more compact by cutting all `padding` in half.
         */
        compact: Boolean,
        /**
         * Show nav group items.
         */
        visible: Boolean,
    },
    emits: ['visible-change'],
    setup(props, { slots, emit }) {
        const visible = ref();
        const navGroupRef = ref();
        const visibleGroup = ref();
        const handleVisibleChange = (visible, index) => {
            if (visible) {
                visibleGroup.value = index;
            }
            else {
                if (visibleGroup.value === index) {
                    visibleGroup.value = 0;
                }
            }
        };
        const isVisible = (index) => Boolean(visibleGroup.value === index);
        onMounted(() => {
            visible.value = props.visible;
            props.visible && navGroupRef.value.classList.add('show');
            emit('visible-change', visible.value);
        });
        watch(() => props.visible, () => {
            visible.value = props.visible;
            if (visible.value === false) {
                visibleGroup.value = undefined;
            }
        });
        watch(visible, () => {
            emit('visible-change', visible.value);
        });
        const handleTogglerClick = () => {
            visible.value = !visible.value;
            emit('visible-change', visible.value);
        };
        const handleBeforeEnter = (el) => {
            el.style.height = '0px';
            navGroupRef.value.classList.add('show');
        };
        // eslint-disable-next-line unicorn/consistent-function-scoping
        const handleEnter = (el, done) => {
            executeAfterTransition(() => done(), el);
            el.style.height = `${el.scrollHeight}px`;
        };
        // eslint-disable-next-line unicorn/consistent-function-scoping
        const handleAfterEnter = (el) => {
            el.style.height = 'auto';
        };
        // eslint-disable-next-line unicorn/consistent-function-scoping
        const handleBeforeLeave = (el) => {
            el.style.height = `${el.scrollHeight}px`;
        };
        // eslint-disable-next-line unicorn/consistent-function-scoping
        const handleLeave = (el, done) => {
            executeAfterTransition(() => done(), el);
            setTimeout(() => {
                el.style.height = '0px';
            }, 1);
        };
        const handleAfterLeave = () => {
            navGroupRef.value.classList.remove('show');
        };
        return () => h$1('li', {
            class: 'nav-group',
            ref: navGroupRef,
        }, [
            slots.togglerContent &&
                h$1('a', {
                    class: ['nav-link', 'nav-group-toggle'],
                    onClick: handleTogglerClick,
                }, slots.togglerContent && slots.togglerContent()),
            h$1(Transition, {
                css: false,
                onBeforeEnter: (el) => handleBeforeEnter(el),
                onEnter: (el, done) => handleEnter(el, done),
                onAfterEnter: (el) => handleAfterEnter(el),
                onBeforeLeave: (el) => handleBeforeLeave(el),
                onLeave: (el, done) => handleLeave(el, done),
                onAfterLeave: () => handleAfterLeave(),
            }, {
                default: () => visible.value &&
                    h$1('ul', {
                        class: [
                            'nav-group-items',
                            {
                                compact: props.compact,
                            },
                        ],
                    }, slots.default &&
                        slots.default().map((vnode, index) => {
                            // @ts-expect-error name is defined in component
                            if (vnode.type.name === 'CNavGroup') {
                                return h$1(vnode, {
                                    onVisibleChange: (visible) => handleVisibleChange(visible, index + 1),
                                    ...(visibleGroup.value && { visible: isVisible(index + 1) }),
                                });
                            }
                            return vnode;
                        })),
            }),
        ]);
    },
});

const CNavGroupItems = defineComponent({
    name: 'CNavGroupItems',
    setup(_, { slots }) {
        return () => h$1('ul', { class: 'nav-group-items' }, slots.default && slots.default());
    },
});

const CNavLink = defineComponent({
    name: 'CNavLink',
    props: {
        /**
         * Toggle the active state for the component.
         */
        active: Boolean,
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'a',
        },
        /**
         * Toggle the disabled state for the component.
         */
        disabled: Boolean,
        /**
         * @ignore
         */
        href: String,
    },
    setup(props, { slots }) {
        return () => h$1(CLink, {
            class: 'nav-link',
            active: props.active,
            component: props.component,
            disabled: props.disabled,
            href: props.href,
        }, {
            default: () => slots.default && slots.default(),
        });
    },
});

const CNavItem = defineComponent({
    name: 'CNavItem',
    props: {
        ...CNavLink.props,
    },
    setup(props, { slots }) {
        return () => h$1('li', {
            class: 'nav-item',
        }, props.href
            ? h$1(CNavLink, {
                active: props.active,
                component: props.component,
                disabled: props.disabled,
                href: props.href,
            }, {
                default: () => slots.default && slots.default(),
            })
            : slots.default && slots.default());
    },
});

const CNavTitle = defineComponent({
    name: 'CNavTitle',
    setup(_, { slots }) {
        return () => h$1('li', { class: 'nav-title' }, slots.default && slots.default());
    },
});

const CNavPlugin = {
    install: (app) => {
        app.component(CNav.name, CNav);
        app.component(CNavGroup.name, CNavGroup);
        app.component(CNavGroupItems.name, CNavGroupItems);
        app.component(CNavItem.name, CNavItem);
        app.component(CNavLink.name, CNavLink);
        app.component(CNavTitle.name, CNavTitle);
    },
};

const CNavbar = defineComponent({
    name: 'CNavbar',
    props: {
        /**
         * Sets the color context of the component to one of CoreUI’s themed colors.
         *
         * @values  'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light', string
         */
        color: Color$1,
        /**
         * Sets if the color of text should be colored for a light or dark dark background.
         *
         * @values 'dark', 'light'
         */
        colorScheme: {
            type: String,
            validator: (value) => {
                return ['dark', 'light'].includes(value);
            },
        },
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'nav',
        },
        /**
         * Defines optional container wrapping children elements.
         *
         * @values boolean, 'sm', 'md', 'lg', 'xl', 'xxl', 'fluid'
         */
        container: {
            type: [Boolean, String],
            validator: (value) => {
                return (typeof value === 'boolean' || ['sm', 'md', 'lg', 'xl', 'xxl', 'fluid'].includes(value));
            },
        },
        /**
         * Defines the responsive breakpoint to determine when content collapses.
         *
         * @values boolean, 'sm', 'md', 'lg', 'xl', 'xxl'
         */
        expand: {
            type: [Boolean, String],
            validator: (value) => {
                return typeof value === 'boolean' || ['sm', 'md', 'lg', 'xl', 'xxl'].includes(value);
            },
        },
        /**
         * Place component in non-static positions.
         *
         * @values 'fixed-top', 'fixed-bottom', 'sticky-top'
         */
        placement: {
            type: String,
            validator: (value) => {
                return ['fixed-top', 'fixed-bottom', 'sticky-top'].includes(value);
            },
        },
    },
    setup(props, { slots }) {
        return () => h$1(props.component, {
            class: [
                'navbar',
                {
                    [`bg-${props.color}`]: props.color,
                    [`navbar-${props.colorScheme}`]: props.colorScheme,
                    [typeof props.expand === 'boolean'
                        ? 'navbar-expand'
                        : `navbar-expand-${props.expand}`]: props.expand,
                },
                props.placement,
            ],
        }, props.container
            ? h$1('div', { class: [`container${props.container === true ? '' : '-' + props.container}`] }, slots.default && slots.default())
            : slots.default && slots.default());
    },
});

const CNavbarBrand = defineComponent({
    name: 'CNavbarBrand',
    props: {
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         *
         */
        component: {
            type: String,
            default: 'a',
        },
        /**
         * The href attribute specifies the URL of the page the link goes to.
         */
        href: String,
    },
    setup(props, { slots }) {
        return () => h$1(props.component ?? (props.href ? 'a' : 'span'), {
            class: 'navbar-brand',
            href: props.href,
        }, slots.default && slots.default());
    },
});

const CNavbarNav = defineComponent({
    name: 'CNavbarNav',
    props: {
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'ul',
        },
    },
    setup(props, { slots }) {
        return () => h$1(props.component, {
            class: 'navbar-nav',
            role: 'navigation',
        }, slots.default && slots.default());
    },
});

const CNavbarText = defineComponent({
    name: 'CNavbarText',
    setup(_, { slots }) {
        return () => h$1('span', { class: 'navbar-text' }, slots.default && slots.default());
    },
});

const CNavbarToggler = defineComponent({
    name: 'CNavbarToggler',
    setup(_, { slots }) {
        return () => h$1('button', {
            class: 'navbar-toggler',
        }, slots.default ? slots.default() : h$1('span', { class: ['navbar-toggler-icon'] }));
    },
});

const CNavbarPlugin = {
    install: (app) => {
        app.component(CNavbar.name, CNavbar);
        app.component(CNavbarBrand.name, CNavbarBrand);
        app.component(CNavbarNav.name, CNavbarNav);
        app.component(CNavbarText.name, CNavbarText);
        app.component(CNavbarToggler.name, CNavbarToggler);
    },
};

const COffcanvas = defineComponent({
    name: 'COffcanvas',
    inheritAttrs: false,
    props: {
        /**
         * Apply a backdrop on body while offcanvas is open.
         *
         * @values boolean | 'static'
         */
        backdrop: {
            type: [Boolean, String],
            default: true,
            validator: (value) => {
                if (typeof value === 'string') {
                    return ['static'].includes(value);
                }
                if (typeof value === 'boolean') {
                    return true;
                }
                return false;
            },
        },
        /**
         * Closes the offcanvas when escape key is pressed.
         */
        keyboard: {
            type: Boolean,
            default: true,
        },
        /**
         * Components placement, there’s no default placement.
         *
         * @values 'start', 'end', 'top', 'bottom'
         */
        placement: {
            type: String,
            require: true,
            validator: (value) => {
                return ['start', 'end', 'top', 'bottom'].includes(value);
            },
        },
        /**
         * Responsive offcanvas property hide content outside the viewport from a specified breakpoint and down.
         *
         * @values boolean | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'
         * @since 4.7.0
         */
        responsive: {
            type: [Boolean, String],
            default: true,
            validator: (value) => {
                if (typeof value === 'string') {
                    return ['sm', 'md', 'lg', 'xl', 'xxl'].includes(value);
                }
                if (typeof value === 'boolean') {
                    return true;
                }
                return false;
            },
        },
        /**
         * Allow body scrolling while offcanvas is open
         */
        scroll: {
            type: Boolean,
            default: false,
        },
        /**
         * Toggle the visibility of offcanvas component.
         */
        visible: {
            type: Boolean,
            default: false,
        },
    },
    emits: [
        /**
         * Callback fired when the component requests to be hidden.
         */
        'hide',
        /**
         * Callback fired when the component requests to be shown.
         */
        'show',
    ],
    setup(props, { attrs, emit, slots }) {
        const offcanvasRef = ref();
        const visible = ref(props.visible);
        watch(() => props.visible, () => {
            visible.value = props.visible;
        });
        watch(visible, () => {
            if (visible.value && !props.scroll) {
                document.body.style.overflow = 'hidden';
                document.body.style.paddingRight = '0px';
                return;
            }
            if (!props.scroll) {
                document.body.style.removeProperty('overflow');
                document.body.style.removeProperty('padding-right');
            }
        });
        const handleEnter = (el, done) => {
            emit('show');
            executeAfterTransition(() => done(), el);
            setTimeout(() => {
                el.classList.add('show');
            }, 1);
        };
        const handleAfterEnter = () => {
            offcanvasRef.value.focus();
        };
        // eslint-disable-next-line unicorn/consistent-function-scoping
        const handleLeave = (el, done) => {
            executeAfterTransition(() => done(), el);
            el.classList.add('hiding');
        };
        // eslint-disable-next-line unicorn/consistent-function-scoping
        const handleAfterLeave = (el) => {
            el.classList.remove('show', 'hiding');
        };
        const handleDismiss = () => {
            visible.value = false;
            emit('hide');
        };
        const handleBackdropDismiss = () => {
            if (props.backdrop !== 'static') {
                handleDismiss();
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && props.keyboard) {
                handleDismiss();
            }
        };
        return () => [
            h$1(Transition, {
                css: false,
                onEnter: (el, done) => handleEnter(el, done),
                onAfterEnter: () => handleAfterEnter(),
                onLeave: (el, done) => handleLeave(el, done),
                onAfterLeave: (el) => handleAfterLeave(el),
            }, () => withDirectives(h$1('div', {
                ...attrs,
                class: [
                    {
                        [`offcanvas${typeof props.responsive === 'boolean' ? '' : '-' + props.responsive}`]: props.responsive,
                        [`offcanvas-${props.placement}`]: props.placement,
                    },
                    attrs.class,
                ],
                onKeydown: (event) => handleKeyDown(event),
                ref: offcanvasRef,
                role: 'dialog',
                tabindex: -1,
            }, slots.default && slots.default()), [[vVisible, props.visible]])),
            props.backdrop &&
                h$1(CBackdrop, {
                    class: 'offcanvas-backdrop',
                    onClick: handleBackdropDismiss,
                    visible: visible.value,
                }),
        ];
    },
});

const COffcanvasBody = defineComponent({
    name: 'COffcanvasBody',
    setup(_, { slots }) {
        return () => h$1('div', { class: 'offcanvas-body' }, slots.default && slots.default());
    },
});

const COffcanvasHeader = defineComponent({
    name: 'COffcanvasHeader',
    setup(_, { slots }) {
        return () => h$1('div', { class: 'offcanvas-header' }, slots.default && slots.default());
    },
});

const COffcanvasTitle = defineComponent({
    name: 'COffcanvasTitle',
    props: {
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'h5',
        },
    },
    setup(props, { slots }) {
        return () => h$1(props.component, { class: 'offcanvas-title' }, slots.default && slots.default());
    },
});

const COffcanvasPlugin = {
    install: (app) => {
        app.component(COffcanvas.name, COffcanvas);
        app.component(COffcanvasBody.name, COffcanvasBody);
        app.component(COffcanvasHeader.name, COffcanvasHeader);
        app.component(COffcanvasTitle.name, COffcanvasTitle);
    },
};

const CPagination = defineComponent({
    name: 'CPagination',
    props: {
        /**
         * Set the alignment of pagination components.
         *
         * @values 'start', 'center', 'end'
         */
        align: {
            type: String,
            validator: (value) => {
                return ['start', 'center', 'end'].includes(value);
            },
        },
        /**
         * Size the component small or large.
         *
         * @values 'sm', 'lg'
         */
        size: {
            type: String,
            validator: (value) => {
                return ['sm', 'lg'].includes(value);
            },
        },
    },
    setup(props, { slots }) {
        return () => h$1('nav', {}, h$1('ul', {
            class: [
                'pagination',
                {
                    [`justify-content-${props.align}`]: props.align,
                    [`pagination-${props.size}`]: props.size,
                },
            ],
        }, slots.default && slots.default()));
    },
});

const CPaginationItem = defineComponent({
    name: 'CPaginationItem',
    props: {
        /**
         * Toggle the active state for the component.
         */
        active: Boolean,
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: String,
        /**
         * Toggle the disabled state for the component.
         */
        disabled: Boolean,
        /**
         * The href attribute specifies the URL of the page the link goes to.
         */
        href: String,
    },
    setup(props, { slots }) {
        return () => {
            const component = props.component ?? (props.active ? 'span' : 'a');
            return h$1('li', {
                class: [
                    'page-item',
                    {
                        ['active']: props.active,
                        ['disabled']: props.disabled,
                    },
                ],
                ...(props.active && { active: props.active, 'aria-current': 'page' }),
            }, component === 'a'
                ? h$1(CLink, {
                    class: ['page-link'],
                    component: component,
                    href: props.href,
                }, {
                    default: () => slots.default && slots.default(),
                })
                : h$1(component, { class: ['page-link'] }, slots.default && slots.default()));
        };
    },
});

const CPaginationPlugin = {
    install: (app) => {
        app.component(CPagination.name, CPagination);
        app.component(CPaginationItem.name, CPaginationItem);
    },
};

const BREAKPOINTS$1 = [
    'xxl',
    'xl',
    'lg',
    'md',
    'sm',
    'xs',
];
const CPlaceholder = defineComponent({
    name: 'CPlaceholder',
    props: {
        /**
         * Set animation type to better convey the perception of something being actively loaded.
         *
         * @values 'glow', 'wave'
         */
        animation: {
            type: String,
            validator: (value) => {
                return ['glow', 'wave'].includes(value);
            },
        },
        /**
         * Sets the color context of the component to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
         */
        color: Color$1,
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: {
            type: String,
            default: 'span',
        },
        /**
         * Size the component extra small, small, or large.
         *
         * @values 'xs', 'sm', 'lg'
         */
        size: {
            type: String,
            validator: (value) => {
                return ['xs', 'sm', 'lg'].includes(value);
            },
        },
        /**
         * The number of columns on extra small devices (<576px).
         */
        xs: Number,
        /**
         * The number of columns on small devices (<768px).
         */
        sm: Number,
        /**
         * The number of columns on medium devices (<992px).
         */
        md: Number,
        /**
         * The number of columns on large devices (<1200px).
         */
        lg: Number,
        /**
         * The number of columns on X-Large devices (<1400px).
         */
        xl: Number,
        /**
         * The number of columns on XX-Large devices (≥1400px).
         */
        xxl: Number,
    },
    setup(props, { slots }) {
        const repsonsiveClassNames = [];
        BREAKPOINTS$1.forEach((bp) => {
            const breakpoint = props[bp];
            const infix = bp === 'xs' ? '' : `-${bp}`;
            if (typeof breakpoint === 'number') {
                repsonsiveClassNames.push(`col${infix}-${breakpoint}`);
            }
            if (typeof breakpoint === 'boolean') {
                repsonsiveClassNames.push(`col${infix}`);
            }
        });
        return () => h$1(props.component, {
            class: [
                props.animation ? `placeholder-${props.animation}` : 'placeholder',
                {
                    [`bg-${props.color}`]: props.color,
                    [`placeholder-${props.size}`]: props.size,
                },
                repsonsiveClassNames,
            ],
        }, slots.default && slots.default());
    },
});

const CPlaceholderPlugin = {
    install: (app) => {
        app.component(CPlaceholder.name, CPlaceholder);
    },
};

const CProgressBar = defineComponent({
    name: 'CProgressBar',
    props: {
        /**
         * Use to animate the stripes right to left via CSS3 animations.
         */
        animated: Boolean,
        /**
         * Sets the color context of the component to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
         */
        color: Color$1,
        /**
         * The percent to progress the ProgressBar.
         */
        value: {
            type: Number,
            default: 0,
        },
        /**
         * Set the progress bar variant to optional striped.
         *
         * @values 'striped'
         */
        variant: {
            type: String,
            validator: (value) => {
                return value === 'striped';
            },
        },
    },
    setup(props, { slots }) {
        return () => h$1('div', {
            class: [
                'progress-bar',
                `bg-${props.color}`,
                {
                    [`progress-bar-${props.variant}`]: props.variant,
                    ['progress-bar-animated']: props.animated,
                },
            ],
            role: 'progressbar',
            style: `width: ${props.value}%`,
            'aria-valuenow': props.value,
            'aria-valuemin': '0',
            'aria-valuemax': '100',
        }, slots.default && slots.default());
    },
});

const CProgress = defineComponent({
    name: 'CProgress',
    props: {
        /**
         * Sets the height of the component. If you set that value the inner `<CProgressBar>` will automatically resize accordingly.
         */
        height: Number,
        /**
         * Makes progress bar thinner.
         */
        thin: Boolean,
        /**
         * Change the default color to white.
         */
        white: Boolean,
        ...CProgressBar.props,
    },
    setup(props, { slots }) {
        return () => h$1('div', {
            class: [
                'progress',
                {
                    'progress-thin': props.thin,
                    'progress-white': props.white,
                },
            ],
            ...(props.height, { style: `height: ${props.height}px` }),
        }, props.value
            ? h$1(CProgressBar, {
                value: props.value,
                animated: props.animated,
                color: props.color,
                variant: props.variant,
            }, slots.default && slots.default())
            : slots.default && slots.default());
    },
});

const CProgressPlugin = {
    install: (app) => {
        app.component(CProgress.name, CProgress);
        app.component(CProgressBar.name, CProgressBar);
    },
};

const CPopover = defineComponent({
    name: 'CPopover',
    props: {
        /**
         * Apply a CSS fade transition to the popover.
         *
         * @since 4.9.0
         */
        animation: {
            type: Boolean,
            default: true,
        },
        /**
         * Content for your component. If you want to pass non-string value please use dedicated slot `<template #content>...</template>`
         */
        content: String,
        /**
         * The delay for displaying and hiding the popover (in milliseconds). When a numerical value is provided, the delay applies to both the hide and show actions. The object structure for specifying the delay is as follows: delay: `{ 'show': 500, 'hide': 100 }`.
         *
         * @since 4.9.0
         */
        delay: {
            type: [Number, Object],
            default: 0,
        },
        /**
         * Specify the desired order of fallback placements by providing a list of placements as an array. The placements should be prioritized based on preference.
         *
         * @since 4.9.0
         */
        fallbackPlacements: {
            type: [String, Array],
            default: () => ['top', 'right', 'bottom', 'left'],
            validator: (value) => {
                if (typeof value === 'string') {
                    return ['top', 'right', 'bottom', 'left'].includes(value);
                }
                if (Array.isArray(value)) {
                    return value.every((e) => ['top', 'right', 'bottom', 'left'].includes(e));
                }
                return false;
            },
        },
        /**
         * Offset of the popover relative to its target.
         */
        offset: {
            type: Array,
            default: () => [0, 8],
        },
        /**
         * Describes the placement of your component after Popper.js has applied all the modifiers that may have flipped or altered the originally provided placement property.
         */
        placement: {
            type: String,
            default: 'top',
            validator: (value) => {
                return ['top', 'right', 'bottom', 'left'].includes(value);
            },
        },
        /**
         * Title for your component. If you want to pass non-string value please use dedicated slot `<template #title>...</template>`
         */
        title: String,
        /**
         * Sets which event handlers you’d like provided to your toggle prop. You can specify one trigger or an array of them.
         *
         * @values 'click', 'focus', 'hover'
         */
        trigger: {
            type: [String, Array],
            default: 'click',
            validator: (value) => {
                if (typeof value === 'string') {
                    return ['click', 'focus', 'hover'].includes(value);
                }
                if (Array.isArray(value)) {
                    return value.every((e) => ['click', 'focus', 'hover'].includes(e));
                }
                return false;
            },
        },
        /**
         * Toggle the visibility of popover component.
         */
        visible: Boolean,
    },
    emits: [
        /**
         * Callback fired when the component requests to be hidden.
         */
        'hide',
        /**
         * Callback fired when the component requests to be shown.
         */
        'show',
    ],
    setup(props, { attrs, slots, emit }) {
        const togglerRef = ref();
        const popoverRef = ref();
        const visible = ref(props.visible);
        const { initPopper, destroyPopper } = usePopper();
        const delay = typeof props.delay === 'number' ? { show: props.delay, hide: props.delay } : props.delay;
        const popperConfig = {
            modifiers: [
                {
                    name: 'arrow',
                    options: {
                        element: '.popover-arrow',
                    },
                },
                {
                    name: 'flip',
                    options: {
                        fallbackPlacements: props.fallbackPlacements,
                    },
                },
                {
                    name: 'offset',
                    options: {
                        offset: props.offset,
                    },
                },
            ],
            placement: getRTLPlacement(props.placement, togglerRef.value),
        };
        const handleEnter = (el, done) => {
            emit('show');
            initPopper(togglerRef.value, popoverRef.value, popperConfig);
            el.classList.add('show');
            executeAfterTransition(() => done(), el);
        };
        const handleLeave = (el, done) => {
            emit('hide');
            el.classList.remove('show');
            executeAfterTransition(() => {
                done();
                destroyPopper();
            }, el);
        };
        const toggleVisible = (event, _visible) => {
            togglerRef.value = event.target;
            if (_visible) {
                setTimeout(() => {
                    visible.value = true;
                }, delay.show);
                return;
            }
            setTimeout(() => {
                visible.value = false;
            }, delay.hide);
        };
        return () => [
            h$1(Teleport, {
                to: 'body',
            }, h$1(Transition, {
                onEnter: (el, done) => handleEnter(el, done),
                onLeave: (el, done) => handleLeave(el, done),
            }, () => visible.value &&
                h$1('div', {
                    class: [
                        'popover',
                        'bs-popover-auto',
                        {
                            fade: props.animation,
                        },
                    ],
                    ref: popoverRef,
                    role: 'tooltip',
                    ...attrs,
                }, [
                    h$1('div', { class: 'popover-arrow' }),
                    (props.title || slots.title) &&
                        h$1('div', { class: 'popover-header' }, {
                            default: () => (slots.title && slots.title()) || props.title,
                        }),
                    (props.content || slots.content) &&
                        h$1('div', { class: 'popover-body' }, {
                            default: () => (slots.content && slots.content()) || props.content,
                        }),
                ]))),
            slots.toggler &&
                slots.toggler({
                    on: {
                        click: (event) => props.trigger.includes('click') && toggleVisible(event, !visible.value),
                        blur: (event) => props.trigger.includes('focus') && toggleVisible(event, false),
                        focus: (event) => props.trigger.includes('focus') && toggleVisible(event, true),
                        mouseenter: (event) => props.trigger.includes('hover') && toggleVisible(event, true),
                        mouseleave: (event) => props.trigger.includes('hover') && toggleVisible(event, false),
                    },
                }),
        ];
    },
});

const CPopoverPlugin = {
    install: (app) => {
        app.component(CPopover.name, CPopover);
    },
};

const isOnMobile = (element) => {
    if (!element) {
        return;
    }
    return Boolean(getComputedStyle(element).getPropertyValue('--cui-is-mobile'));
};
const CSidebar = defineComponent({
    name: 'CSidebar',
    props: {
        /**
         * Sets if the color of text should be colored for a light or dark dark background.
         *
         * @values 'dark', light'
         */
        colorScheme: {
            type: String,
            default: undefined,
            validator: (value) => {
                return ['dark', 'light'].includes(value);
            },
        },
        /**
         * Make sidebar narrow.
         */
        narrow: Boolean,
        /**
         * Set sidebar to overlaid variant.
         */
        overlaid: Boolean,
        /**
         * Components placement, there’s no default placement.
         * @values 'start', 'end'
         */
        placement: {
            type: String,
            default: undefined,
            validator: (value) => {
                return ['start', 'end'].includes(value);
            },
        },
        /**
         * Place sidebar in non-static positions.
         */
        position: {
            type: String,
            validator: (value) => {
                return ['fixed'].includes(value);
            },
        },
        /**
         * Size the component small, large, or extra large.
         */
        size: {
            type: String,
            validator: (value) => {
                return ['sm', 'lg', 'xl'].includes(value);
            },
        },
        /**
         * Expand narrowed sidebar on hover.
         */
        unfoldable: Boolean,
        /**
         * Toggle the visibility of sidebar component.
         */
        visible: Boolean,
    },
    emits: [
        /**
         * Callback fired when the component requests to be hidden.
         */
        'hide',
        /**
         * Callback fired when the component requests to be shown.
         */
        'show',
        /**
         * Event emitted after visibility of component changed.
         */
        'visible-change',
    ],
    setup(props, { attrs, slots, emit }) {
        const mobile = ref();
        const inViewport = ref();
        const sidebarRef = ref();
        const visible = ref(props.visible);
        watch(inViewport, () => {
            emit('visible-change', inViewport.value);
            inViewport.value ? emit('show') : emit('hide');
        });
        watch(() => props.visible, () => (visible.value = props.visible));
        watch(mobile, () => {
            if (mobile.value && visible.value)
                visible.value = false;
        });
        onMounted(() => {
            mobile.value = isOnMobile(sidebarRef.value);
            inViewport.value = isInViewport(sidebarRef.value);
            window.addEventListener('resize', handleResize);
            window.addEventListener('mouseup', handleClickOutside);
            window.addEventListener('keyup', handleKeyup);
            sidebarRef.value.addEventListener('mouseup', handleOnClick);
            sidebarRef.value.addEventListener('transitionend', () => {
                inViewport.value = isInViewport(sidebarRef.value);
            });
        });
        onBeforeUnmount(() => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('mouseup', handleClickOutside);
            window.removeEventListener('keyup', handleKeyup);
            sidebarRef.value.removeEventListener('mouseup', handleOnClick);
            // eslint-disable-next-line unicorn/no-invalid-remove-event-listener
            sidebarRef.value.removeEventListener('transitionend', () => {
                inViewport.value = isInViewport(sidebarRef.value);
            });
        });
        const handleHide = () => {
            visible.value = false;
            emit('visible-change', false);
        };
        const handleResize = () => {
            mobile.value = isOnMobile(sidebarRef.value);
            inViewport.value = isInViewport(sidebarRef.value);
        };
        const handleKeyup = (event) => {
            if (mobile.value && !sidebarRef.value.contains(event.target)) {
                handleHide();
            }
        };
        const handleClickOutside = (event) => {
            if (mobile.value && !sidebarRef.value.contains(event.target)) {
                handleHide();
            }
        };
        const handleOnClick = (event) => {
            const target = event.target;
            target &&
                target.classList.contains('nav-link') &&
                !target.classList.contains('nav-group-toggle') &&
                mobile.value &&
                handleHide();
        };
        return () => [
            h$1('div', {
                class: [
                    'sidebar',
                    {
                        [`sidebar-${props.colorScheme}`]: props.colorScheme,
                        'sidebar-narrow': props.narrow,
                        'sidebar-overlaid': props.overlaid,
                        [`sidebar-${props.placement}`]: props.placement,
                        [`sidebar-${props.position}`]: props.position,
                        [`sidebar-${props.size}`]: props.size,
                        'sidebar-narrow-unfoldable': props.unfoldable,
                        show: visible.value === true && mobile.value,
                        hide: visible.value === false && !mobile.value,
                    },
                    attrs.class,
                ],
                ref: sidebarRef,
            }, slots.default && slots.default()),
            mobile.value &&
                h$1(CBackdrop, {
                    class: 'sidebar-backdrop d-none',
                    visible: props.visible,
                    onClick: () => handleHide(),
                }),
        ];
    },
});

const CSidebarBrand = defineComponent({
    name: 'CSidebarBrand',
    setup(_, { slots }) {
        return () => h$1('div', { class: 'sidebar-brand' }, slots.default && slots.default());
    },
});

const CSidebarFooter = defineComponent({
    name: 'CSidebarFooter',
    setup(_, { slots }) {
        return () => h$1('div', { class: 'sidebar-footer' }, slots.default && slots.default());
    },
});

const CSidebarHeader = defineComponent({
    name: 'CSidebarHeader',
    setup(_, { slots }) {
        return () => h$1('div', { class: 'sidebar-header' }, slots.default && slots.default());
    },
});

const CSidebarNav = defineComponent({
    name: 'CSidebarNav',
    setup(_, { slots }) {
        const visibleGroup = ref();
        const handleVisibleChange = (visible, index) => {
            if (visible) {
                visibleGroup.value = index;
            }
            else {
                if (visibleGroup.value === index) {
                    visibleGroup.value = 0;
                }
            }
        };
        const isVisible = (index) => Boolean(visibleGroup.value === index);
        return () => h$1('ul', {
            class: 'sidebar-nav',
        }, slots.default &&
            slots.default().map((vnode, index) => {
                // @ts-expect-error name is defined in component
                if (vnode.type.name === 'CNavGroup') {
                    return h$1(vnode, {
                        onVisibleChange: (visible) => handleVisibleChange(visible, index + 1),
                        ...(visibleGroup.value && { visible: isVisible(index + 1) }),
                    });
                }
                return vnode;
            }));
    },
});

const CSidebarToggler = defineComponent({
    name: 'CSidebarToggler',
    setup(_, { slots }) {
        return () => h$1('button', { class: 'sidebar-toggler' }, slots.default && slots.default());
    },
});

const CSidebarPlugin = {
    install: (app) => {
        app.component(CSidebar.name, CSidebar);
        app.component(CSidebarBrand.name, CSidebarBrand);
        app.component(CSidebarFooter.name, CSidebarFooter);
        app.component(CSidebarHeader.name, CSidebarHeader);
        app.component(CSidebarNav.name, CSidebarNav);
        app.component(CSidebarToggler.name, CSidebarToggler);
    },
};

const CSmartPagination = defineComponent({
    name: 'CSmartPagination',
    props: {
        /**
         * Horizontall align
         *
         * @default 'start'
         */
        align: {
            type: String,
            default: 'start',
            require: false,
            validator: (value) => {
                return ['start', 'center', 'end'].includes(value);
            },
        },
        /**
         * Current page number
         *
         * @default 1
         */
        activePage: {
            type: Number,
            default: 1,
            require: false,
        },
        /**
         * Show/hide arrows
         *
         * @default true
         */
        arrows: {
            type: Boolean,
            default: true,
            require: false,
        },
        /**
         * Show/hide dots
         *
         * @default true
         */
        dots: {
            type: Boolean,
            default: true,
            require: false,
        },
        /**
         * Show double arrows buttons
         *
         * @default true
         */
        doubleArrows: {
            type: Boolean,
            default: true,
            require: false,
        },
        /**
         * The content of 'firstButton' button
         *
         * @default '&laquo;'
         */
        firstButton: {
            type: String,
            default: '&laquo;',
            require: false,
        },
        /**
         * The content of 'lastButton' button
         *
         * @default '&raquo;'
         */
        lastButton: {
            type: String,
            default: '&raquo;',
            require: false,
        },
        /**
         * Maximum items number
         *
         * @default 5
         */
        limit: {
            type: Number,
            default: 5,
            require: false,
        },
        /**
         * The content of 'nextButton' button
         *
         * @default '&rsaquo;'
         */
        nextButton: {
            type: String,
            default: '&rsaquo;',
            require: false,
        },
        /**
         * Number of pages
         */
        pages: {
            type: Number,
            default: 1000,
            require: true,
        },
        /**
         * The content of 'previousButton' button
         *
         * @default '&lsaquo;'
         */
        previousButton: {
            type: String,
            default: '&lsaquo;',
            require: false,
        },
        /**
         * Size of pagination, valid values: 'sm', 'lg'
         */
        size: {
            type: String,
            default: undefined,
            required: false,
            validator: (value) => {
                return ['sm', 'lg'].includes(value);
            },
        },
    },
    emits: [
        /**
         * On active page change callback.
         */
        'activePageChange',
    ],
    setup(props, { emit }) {
        const activePage = ref(props.activePage);
        const limit = ref(props.limit);
        const pages = ref(props.pages);
        watch(() => props.activePage, () => {
            activePage.value = props.activePage;
        });
        watch(() => props.limit, () => {
            limit.value = props.limit;
        });
        watch(() => props.pages, () => {
            pages.value = props.pages;
        });
        const showDots = computed(() => {
            return props.dots && limit.value > 4 && limit.value < pages.value;
        });
        const maxPrevItems = computed(() => {
            return Math.floor((limit.value - 1) / 2);
        });
        const maxNextItems = computed(() => {
            return Math.ceil((limit.value - 1) / 2);
        });
        const beforeDots = computed(() => {
            return showDots.value && activePage.value > maxPrevItems.value + 1;
        });
        const afterDots = computed(() => {
            return showDots.value && activePage.value < pages.value - maxNextItems.value;
        });
        const computedLimit = computed(() => {
            return limit.value - (afterDots.value ? 1 : 0) - (beforeDots.value ? 1 : 0);
        });
        const range = computed(() => {
            return activePage.value + maxNextItems.value;
        });
        const lastItem = computed(() => {
            return range.value >= pages.value ? pages.value : range.value - (afterDots.value ? 1 : 0);
        });
        const itemsAmount = computed(() => {
            return pages.value < computedLimit.value ? pages.value : computedLimit.value;
        });
        const items = computed(() => {
            if (activePage.value - maxPrevItems.value <= 1) {
                return Array.from({
                    length: itemsAmount.value,
                }, (_v, i) => i + 1);
            }
            else {
                return Array.from({
                    length: itemsAmount.value,
                }, (_v, i) => {
                    return lastItem.value - i;
                }).reverse();
            }
        });
        const setPage = (number) => {
            if (number !== activePage.value) {
                activePage.value = number;
                emit('activePageChange', number);
            }
        };
        return () => h$1(CPagination, {
            align: props.align,
            'aria-label': 'pagination',
            size: props.size,
        }, {
            default: () => [
                props.doubleArrows &&
                    h$1(CPaginationItem, {
                        onClick: () => {
                            activePage.value !== 1 && setPage(1);
                        },
                        'aria-label': 'Go to first page',
                        ...(activePage.value === 1 && {
                            'aria-disabled': true,
                            disabled: true,
                        }),
                    }, {
                        default: () => typeof props.firstButton === 'string'
                            ? h$1('span', {
                                innerHTML: props.firstButton,
                            })
                            : props.firstButton,
                    }),
                props.arrows &&
                    h$1(CPaginationItem, {
                        onClick: () => {
                            activePage.value !== 1 && setPage(activePage.value - 1);
                        },
                        'aria-label': 'Go to previous page',
                        ...(activePage.value === 1 && {
                            'aria-disabled': true,
                            disabled: true,
                        }),
                    }, {
                        default: () => typeof props.previousButton === 'string'
                            ? h$1('span', {
                                innerHTML: props.previousButton,
                            })
                            : props.previousButton,
                    }),
                beforeDots.value &&
                    h$1(CPaginationItem, {
                        role: 'separator',
                        disabled: true,
                    }, {
                        default: () => '...',
                    }),
                items.value.map((i) => {
                    return h$1(CPaginationItem, {
                        onClick: () => setPage(i),
                        'aria-label': activePage.value === i ? `Current page ${i}` : `Go to page ${i}`,
                        active: activePage.value === i,
                    }, {
                        default: () => i,
                    });
                }),
                afterDots.value &&
                    h$1(CPaginationItem, {
                        role: 'separator',
                        disabled: true,
                    }, {
                        default: () => '...',
                    }),
                props.arrows &&
                    h$1(CPaginationItem, {
                        onClick: () => {
                            activePage.value !== pages.value && setPage(activePage.value + 1);
                        },
                        'aria-label': 'Go to next page',
                        ...(activePage.value === pages.value && {
                            'aria-disabled': true,
                            disabled: true,
                        }),
                    }, {
                        default: () => typeof props.nextButton === 'string'
                            ? h$1('span', {
                                innerHTML: props.nextButton,
                            })
                            : props.nextButton,
                    }),
                props.doubleArrows &&
                    h$1(CPaginationItem, {
                        onClick: () => {
                            activePage.value !== pages.value && setPage(pages.value);
                        },
                        'aria-label': 'Go to last page',
                        ...(activePage.value === pages.value && {
                            'aria-disabled': true,
                            disabled: true,
                        }),
                    }, {
                        default: () => typeof props.lastButton === 'string'
                            ? h$1('span', {
                                innerHTML: props.lastButton,
                            })
                            : props.lastButton,
                    }),
            ],
        });
    },
});

const CSmartPaginationPlugin = {
    install: (app) => {
        app.component(CSmartPagination.name, CSmartPagination);
    },
};

var cilArrowBottom = ["512 512", "<polygon fill='var(--ci-primary-color, currentColor)' points='367.997 338.75 271.999 434.747 271.999 17.503 239.999 17.503 239.999 434.745 144.003 338.75 121.376 361.377 256 496 390.624 361.377 367.997 338.75' class='ci-primary'/>"];

var cilArrowTop = ["512 512", "<polygon fill='var(--ci-primary-color, currentColor)' points='390.624 150.625 256 16 121.376 150.625 144.004 173.252 240.001 77.254 240.001 495.236 272.001 495.236 272.001 77.257 367.996 173.252 390.624 150.625' class='ci-primary'/>"];

var cilFilterX = ["512 512", "<polygon fill='var(--ci-primary-color, currentColor)' points='40 16 40 53.828 109.024 136 150.815 136 76.896 48 459.51 48 304 242.388 304 401.373 241.373 464 240 464 240 368 208 368 208 496 254.627 496 336 414.627 336 253.612 496 53.612 496 16 40 16' class='ci-primary'/><polygon fill='var(--ci-primary-color, currentColor)' points='166.403 248.225 226.864 187.763 204.237 165.135 143.775 225.597 83.313 165.135 60.687 187.763 121.148 248.225 60.687 308.687 83.313 331.314 143.775 270.852 204.237 331.314 226.864 308.687 166.403 248.225' class='ci-primary'/>"];

var cilSwapVertical = ["512 512", "<polygon fill='var(--ci-primary-color, currentColor)' points='384 433.373 384 160 352 160 352 434.51 282.177 364.687 259.55 387.313 367.432 495.196 475.313 387.313 452.687 364.687 384 433.373' class='ci-primary'/><polygon fill='var(--ci-primary-color, currentColor)' points='159.432 17.372 51.55 125.255 74.177 147.882 144 78.059 144 352 176 352 176 79.195 244.687 147.882 267.313 125.255 159.432 17.372' class='ci-primary'/>"];

const CIcon = defineComponent({
    name: 'CIcon',
    props: {
        /**
         * Use `:icon="..."` instead of
         *
         * @deprecated since version 3.0
         */
        content: {
            type: [String, Array],
            default: undefined,
            required: false,
        },
        /**
         * Use for replacing default CIcon component classes. Prop is overriding the 'size' prop.
         */
        customClassName: {
            type: [String, Array, Object],
            default: undefined,
            required: false,
        },
        /**
         * Name of the icon placed in React object or SVG content.
         */
        icon: {
            type: [String, Array],
            default: undefined,
            required: false,
        },
        /**
         * Use `icon="..."` instead of
         *
         * @deprecated since version 3.0
         */
        name: {
            type: String,
            default: undefined,
            required: false,
        },
        /**
         * Size of the icon. Available sizes: 'sm', 'lg', 'xl', 'xxl', '3xl...9xl', 'custom', 'custom-size'.
         */
        size: {
            type: String,
            default: undefined,
            required: false,
            validator: (value) => {
                return [
                    'custom',
                    'custom-size',
                    'sm',
                    'lg',
                    'xl',
                    'xxl',
                    '3xl',
                    '4xl',
                    '5xl',
                    '6xl',
                    '7xl',
                    '8xl',
                    '9xl',
                ].includes(value);
            },
        },
        /**
         * Title tag content.
         */
        title: {
            type: String,
            default: undefined,
            required: false,
        },
        /**
         * If defined component will be rendered using 'use' tag.
         */
        use: {
            type: String,
            default: undefined,
            required: false,
        },
    },
    setup(props, { attrs }) {
        const icons = inject('icons');
        const _icon = props.icon || props.content || props.name;
        const toCamelCase = (str) => {
            return str
                .replace(/([-_][a-z0-9])/gi, ($1) => {
                return $1.toUpperCase();
            })
                .replace(/-/gi, '');
        };
        const iconName = computed(() => _icon && typeof _icon === 'string' ? (_icon.includes('-') ? toCamelCase(_icon) : _icon) : '');
        const titleCode = props.title ? `<title>${props.title}</title>` : 'undefined';
        const code = computed(() => Array.isArray(_icon)
            ? _icon
            : typeof _icon === 'string' && iconName.value && icons[iconName.value]
                ? icons[iconName.value]
                : 'undefined');
        const iconCode = Array.isArray(code.value) ? code.value[1] || code.value[0] : code.value;
        const scale = Array.isArray(code.value) && code.value.length > 1 ? code.value[0] : '64 64';
        const viewBox = attrs.viewBox || `0 0 ${scale}`;
        const size = () => {
            const addCustom = !props.size && (attrs.width || attrs.height);
            return props.size === 'custom' || addCustom ? 'custom-size' : props.size;
        };
        const classNames = (() => {
            return [props.customClassName || ['icon', { [`icon-${size()}`]: size() }], attrs.class];
        })();
        return () => props.use
            ? h$1('svg', {
                ...attrs,
                xmlns: 'http://www.w3.org/2000/svg',
                class: classNames,
                role: 'img',
            }, h$1('use', { href: props.use }))
            : h$1('svg', {
                ...attrs,
                xmlns: 'http://www.w3.org/2000/svg',
                class: classNames,
                viewBox: viewBox,
                innerHTML: `${titleCode}${iconCode}`,
                role: 'img',
            });
    },
});

const CTableBody = defineComponent({
    name: 'CTableBody',
    props: {
        /**
         * Sets the color context of the component to one of CoreUI’s themed colors.
         *
         * @values  'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light', string
         */
        color: Color$1,
    },
    setup(props, { slots }) {
        return () => h$1('tbody', {
            class: [
                {
                    [`table-${props.color}`]: props.color,
                },
            ],
        }, slots.default && slots.default());
    },
});

const CTableCaption = defineComponent({
    name: 'CTableCaption',
    setup(_, { slots }) {
        return () => h$1('caption', {}, slots.default && slots.default());
    },
});

const CTableDataCell = defineComponent({
    name: 'CTableDataCell',
    props: {
        /**
         * Highlight a table row or cell.
         */
        active: Boolean,
        /**
         * Set the vertical aligment.
         *
         * @values 'bottom', 'middle', 'top'
         */
        align: {
            type: String,
            validator: (value) => {
                return ['bottom', 'middle', 'top'].includes(value);
            },
        },
        /**
         * Sets the color context of the component to one of CoreUI’s themed colors.
         *
         * @values  'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light', string
         */
        color: Color$1,
        /**
         * @ignore
         */
        scope: String,
    },
    setup(props, { slots }) {
        return () => h$1(props.scope ? 'th' : 'td', {
            class: [
                {
                    [`align-${props.align}`]: props.align,
                    'table-active': props.active,
                    [`table-${props.color}`]: props.color,
                },
            ],
            ...(props.scope && { scope: props.scope }),
        }, slots.default && slots.default());
    },
});

const CTableFoot = defineComponent({
    name: 'CTableFoot',
    props: {
        /**
         * Sets the color context of the component to one of CoreUI’s themed colors.
         *
         * @values  'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light', string
         */
        color: Color$1,
    },
    setup(props, { slots }) {
        return () => h$1('tfoot', {
            class: [
                {
                    [`table-${props.color}`]: props.color,
                },
            ],
        }, slots.default && slots.default());
    },
});

const CTableHead = defineComponent({
    name: 'CTableHead',
    props: {
        /**
         * Sets the color context of the component to one of CoreUI’s themed colors.
         *
         * @values  'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light', string
         */
        color: Color$1,
    },
    setup(props, { slots }) {
        return () => h$1('thead', {
            class: [
                {
                    [`table-${props.color}`]: props.color,
                },
            ],
        }, slots.default && slots.default());
    },
});

const CTableHeaderCell = defineComponent({
    name: 'CTableHeaderCell',
    props: {
        /**
         * Sets the color context of the component to one of CoreUI’s themed colors.
         *
         * @values  'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light', string
         */
        color: Color$1,
    },
    setup(props, { slots }) {
        return () => h$1('th', {
            class: [
                {
                    [`table-${props.color}`]: props.color,
                },
            ],
        }, slots.default && slots.default());
    },
});

const CTableRow = defineComponent({
    name: 'CTableRow',
    props: {
        /**
         * Highlight a table row or cell..
         */
        active: Boolean,
        /**
         * Set the vertical aligment.
         *
         * @values 'bottom', 'middle', 'top'
         */
        align: {
            type: String,
            validator: (value) => {
                return ['bottom', 'middle', 'top'].includes(value);
            },
        },
        /**
         * Sets the color context of the component to one of CoreUI’s themed colors.
         *
         * @values  'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light', string
         */
        color: Color$1,
    },
    setup(props, { slots }) {
        return () => h$1('tr', {
            class: [
                {
                    [`align-${props.align}`]: props.align,
                    'table-active': props.active,
                    [`table-${props.color}`]: props.color,
                },
            ],
        }, slots.default && slots.default());
    },
});

const pretifyName$1 = (name) => {
    return name
        .replace(/[-_.]/g, ' ')
        .replace(/ +/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};
const getColumnLabel$1 = (column) => typeof column === 'object' ? column.label ?? pretifyName$1(column.key) : pretifyName$1(column);
const getColumnNames$1 = (columns, items) => columns
    ? columns.map((column) => {
        return typeof column === 'object' ? column.key : column;
    })
    : items && getColumnNamesFromItems$1(items);
const getColumnNamesFromItems$1 = (items) => Object.keys(items[0] || {}).filter((el) => el.charAt(0) !== '_');

const CTable = defineComponent({
    name: 'CTable',
    props: {
        /**
         * Set the vertical aligment.
         *
         * @values 'bottom', 'middle', 'top'
         */
        align: {
            type: String,
            validator: (value) => {
                return ['bottom', 'middle', 'top'].includes(value);
            },
        },
        /**
         * Sets the border color of the component to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
         */
        borderColor: Color$1,
        /**
         * Add borders on all sides of the table and cells.
         */
        bordered: Boolean,
        /**
         * Remove borders on all sides of the table and cells.
         */
        borderless: Boolean,
        /**
         * Put the `<caption>` on the top of the table.
         *
         * @values 'top' | string
         */
        caption: String,
        /**
         * Set the text of the table caption and the caption on the top of the table.
         *
         * @since 4.5.0
         */
        captionTop: String,
        /**
         * Prop for table columns configuration. If prop is not defined, table will display columns based on the first item keys, omitting keys that begins with underscore (e.g. '_props')
         *
         * In columns prop each array item represents one column. Item might be specified in two ways:
         * String: each item define column name equal to item value.
         * Object: item is object with following keys available as column configuration:
         * - key (required)(String) - define column name equal to item key.
         * - label (String) - define visible label of column. If not defined, label will be generated automatically based on column name, by converting kebab-case and snake_case to individual words and capitalization of each word.
         * - _props (Object) - adds classes to all cels in column, ex. _props: { scope: 'col', className: 'custom-class' },
         * - _style (Object) - adds styles to the column header (useful for defining widths)
         *
         * @since 4.5.0
         */
        columns: {
            type: Array,
        },
        /**
         * Sets the color context of the component to one of CoreUI’s themed colors.
         *
         * @values  'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light', string
         */
        color: Color$1,
        /**
         * Array of objects or strings, where each element represents one cell in the table footer.
         *
         * Example items:
         * ['FooterCell', 'FooterCell', 'FooterCell']
         * or
         * [{ label: 'FooterCell', _props: { color: 'success' }, ...]
         *
         * @since 4.5.0
         */
        footer: {
            type: Array,
        },
        /**
         * Enable a hover state on table rows within a `<CTableBody>`.
         */
        hover: Boolean,
        /**
         * Array of objects, where each object represents one item - row in table. Additionally, you can add style classes to each row by passing them by '_props' key and to single cell by '_cellProps'.
         *
         * Example item:
         * { name: 'John' , age: 12, _props: { color: 'success' }, _cellProps: { age: { className: 'fw-bold'}}}
         *
         * @since 4.5.0
         */
        items: {
            type: Array,
        },
        responsive: {
            type: [Boolean, String],
            validator: (value) => {
                if (typeof value == 'string') {
                    return ['sm', 'md', 'lg', 'xl', 'xxl'].includes(value);
                }
                if (typeof value == 'boolean') {
                    return true;
                }
                return false;
            },
        },
        /**
         * Make table more compact by cutting all cell `padding` in half.
         */
        small: Boolean,
        /**
         * Add zebra-striping to any table row within the `<CTableBody>`.
         */
        striped: Boolean,
        /**
         * Add zebra-striping to any table column.
         *
         * @since 4.4.0
         */
        stripedColumns: Boolean,
        /**
         * Properties that will be passed to the table footer component.
         *
         * Properties to [CTableFoot](#ctablefoot) component.
         * @since 4.5.0
         */
        tableFootProps: {
            type: Object,
        },
        /**
         * Properties that will be passed to the table head component.
         *
         *  Properties to [CTableHead](#ctablehead) component.
         * @since 4.5.0
         */
        tableHeadProps: {
            type: Object,
        },
    },
    setup(props, { slots, attrs }) {
        const columnNames = computed(() => getColumnNames$1(props.columns, props.items));
        //   props.columns
        //     ? props.columns.map((column: Column | string) => {
        //         if (typeof column === 'object') return column.key
        //         else return column
        //       })
        //     : Object.keys((props.items && props.items[0]) || {}).filter((el) => el.charAt(0) !== '_'),
        // )
        const table = () => h$1('table', {
            class: [
                'table',
                {
                    [`align-${props.align}`]: props.align,
                    [`caption-top`]: props.captionTop || props.caption === 'top',
                    [`border-${props.borderColor}`]: props.borderColor,
                    'table-bordered': props.bordered,
                    'table-borderless': props.borderless,
                    [`table-${props.color}`]: props.color,
                    'table-hover': props.hover,
                    'table-sm': props.small,
                    'table-striped': props.striped,
                    'table-striped-columns': props.stripedColumns,
                },
                attrs.class,
            ],
        }, {
            default: () => [
                ((props.caption && props.caption !== 'top') || props.captionTop) &&
                    h$1(CTableCaption, {}, {
                        default: () => props.caption || props.captionTop,
                    }),
                props.columns &&
                    h$1(CTableHead, {
                        ...props.tableHeadProps,
                    }, {
                        default: () => h$1(CTableRow, {}, {
                            default: () => [
                                props.columns &&
                                    props.columns.map((column) => h$1(CTableHeaderCell, {
                                        ...(typeof column === 'object' &&
                                            column._props && { ...column._props }),
                                        ...(typeof column === 'object' &&
                                            column._style && { style: { ...column._style } }),
                                    }, {
                                        default: () => getColumnLabel$1(column),
                                    })),
                            ],
                        }),
                    }),
                props.items &&
                    h$1(CTableBody, {}, {
                        default: () => [
                            props.items &&
                                props.items.map((item) => h$1(CTableRow, {
                                    ...(item._props && { ...item._props }),
                                }, {
                                    default: () => [
                                        columnNames.value &&
                                            columnNames.value.map((colName) => item[colName] !== undefined &&
                                                h$1(CTableDataCell, {
                                                    ...(item._cellProps &&
                                                        item._cellProps['all'] && { ...item._cellProps['all'] }),
                                                    ...(item._cellProps &&
                                                        item._cellProps[colName] && {
                                                        ...item._cellProps[colName],
                                                    }),
                                                }, {
                                                    default: () => item[colName],
                                                })),
                                    ],
                                })),
                        ],
                    }),
                slots.default && slots.default(),
                props.footer &&
                    h$1(CTableFoot, {
                        ...props.tableFootProps,
                    }, {
                        default: () => h$1(CTableRow, {}, {
                            default: () => [
                                props.footer &&
                                    props.footer.map((item) => h$1(CTableDataCell, {
                                        ...(typeof item === 'object' &&
                                            item._props && { ...item._props }),
                                    }, {
                                        default: () => (typeof item === 'object' ? item.label : item),
                                    })),
                            ],
                        }),
                    }),
            ],
        });
        return () => [
            props.responsive
                ? h$1('div', {
                    class: typeof props.responsive === 'boolean'
                        ? 'table-responsive'
                        : `table-responsive-${props.responsive}`,
                }, table())
                : table(),
        ];
    },
});

const CTablePlugin = {
    install: (app) => {
        app.component(CTable.name, CTable);
        app.component(CTableBody.name, CTableBody);
        app.component(CTableCaption.name, CTableCaption);
        app.component(CTableDataCell.name, CTableDataCell);
        app.component(CTableFoot.name, CTableFoot);
        app.component(CTableHead.name, CTableHead);
        app.component(CTableHeaderCell.name, CTableHeaderCell);
        app.component(CTableRow.name, CTableRow);
    },
};

const filterColumns = (items, columnFilter, columnFilterState, itemsDataColumns) => {
    if (columnFilter && typeof columnFilter === 'object' && columnFilter.external) {
        return items;
    }
    Object.entries(columnFilterState).forEach(([key, value]) => {
        if (value instanceof Function) {
            items = items.filter((item) => value(item[key]));
            return;
        }
        const columnFilter = String(value).toLowerCase();
        if (columnFilter && itemsDataColumns.includes(key)) {
            items = items.filter((item) => {
                return String(item[key]).toLowerCase().includes(columnFilter);
            });
        }
    });
    return items;
};
const filterTable = (items, tableFilter, tableFilterState, itemsDataColumns) => {
    if (!tableFilterState ||
        (tableFilter && typeof tableFilter === 'object' && tableFilter.external)) {
        return items;
    }
    const filter = tableFilterState.toLowerCase();
    const valueContainFilter = (val) => String(val).toLowerCase().includes(filter);
    items = items.filter((item) => {
        return !!itemsDataColumns.find((key) => valueContainFilter(item[key]));
    });
    return items;
};
const getClickedColumnName = (target, columnNames) => {
    const closest = target.closest('tr');
    const children = closest ? Array.from(closest.children) : [];
    const clickedCell = children.filter((child) => child.contains(target))[0];
    return columnNames[children.indexOf(clickedCell)];
};
const getColumnKey = (column) => typeof column === 'object' ? column.key : column;
const getColumnLabel = (column) => typeof column === 'object'
    ? column.label !== undefined
        ? column.label
        : pretifyName(column.key)
    : pretifyName(column);
const getColumnNames = (columns, items) => {
    if (columns) {
        const _columns = [];
        for (const column of columns) {
            if (typeof column === 'object' && column.children) {
                _columns.push(...getColumnNames(column.children, []));
                continue;
            }
            typeof column === 'object' ? _columns.push(column.key) : _columns.push(column);
        }
        return _columns;
    }
    return getColumnNamesFromItems(items);
};
const getColumns = (_columns) => {
    const columns = [];
    for (const column of _columns) {
        if (typeof column === 'object' && column.group && column.children) {
            columns.push(...getColumns(column.children));
            continue;
        }
        if (typeof column === 'object' && column.children) {
            columns.push(...getColumns(column.children));
        }
        columns.push(column);
    }
    return columns;
};
const countColumns = (columns, counter = 0) => {
    let _counter = counter;
    for (const column of columns) {
        if (!column.children) {
            _counter++;
        }
        if (column.children) {
            _counter = countColumns(column.children, _counter);
        }
    }
    return _counter;
};
const getColumnGroups = (columns) => {
    const groups = [];
    const traverseColumns = (column, deep = 0, colSpan = 0) => {
        const groups = [];
        if (column.children) {
            for (const _column of column.children) {
                if (!_column.group) {
                    colSpan++;
                }
                groups.push(...traverseColumns(_column, deep + 1, colSpan));
            }
        }
        if (typeof column === 'object' && column.group) {
            const { children, group, ...rest } = column;
            groups.push({
                deep: deep,
                label: group,
                ...(children && { colspan: countColumns(children) }),
                ...rest,
            });
        }
        return groups;
    };
    if (columns) {
        for (const column of columns) {
            if (typeof column === 'object' && column.group) {
                const objects = traverseColumns(column);
                if (objects) {
                    for (const object of objects) {
                        const { deep, ...rest } = object;
                        if (deep === undefined) {
                            continue;
                        }
                        for (let i = 0; i < deep; i++) {
                            if (groups[i]) {
                                continue;
                            }
                            groups.push([]);
                        }
                        if (groups[deep]) {
                            groups[deep].push(rest);
                        }
                        else {
                            groups.push([rest]);
                        }
                    }
                }
            }
        }
    }
    return groups;
};
const getColumnNamesFromItems = (items) => Object.keys(items[0] || {}).filter((el) => el.charAt(0) !== '_');
const getColumnSorterState = (key, sorterState) => {
    if (sorterState && sorterState.column === key) {
        if (sorterState.state) {
            return sorterState.state;
        }
        return 0;
    }
    return 0;
};
const getColumnValues = (items, key) => {
    return items.map((item) => item[key]);
};
const getTableDataCellProps = (item, colName) => {
    const props = item._cellProps && {
        ...(item._cellProps['all'] && { ...item._cellProps['all'] }),
        ...(item._cellProps[colName] && { ...item._cellProps[colName] }),
    };
    return props;
};
const getTableHeaderCellProps = (column) => {
    if (typeof column === 'object' && column._props) {
        return column._props;
    }
    return {};
};
const getTableHeaderCellStyles = (column, columnSorter) => {
    const style = {};
    if (columnSorter &&
        (typeof column !== 'object' ||
            (typeof column === 'object' && (column.sorter === undefined || column.sorter)))) {
        style['cursor'] = 'pointer';
    }
    if (typeof column === 'object' && column._style) {
        return { ...style, ...column._style };
    }
    return style;
};
const isSortable = (i, columns, columnSorter, itemsDataColumns, columnNames) => {
    const isDataColumn = itemsDataColumns.includes(columnNames[i]);
    let column;
    if (columns)
        column = columns[i];
    return (columnSorter &&
        (!columns ||
            typeof column !== 'object' ||
            (typeof column === 'object' && (column.sorter === undefined || column.sorter))) &&
        isDataColumn);
};
const pretifyName = (name) => {
    return name
        .replace(/[-_.]/g, ' ')
        .replace(/ +/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};
const sortItems = (columnSorter, items, itemsDataColumns, sorterState) => {
    const column = sorterState.column;
    if (!column ||
        !itemsDataColumns.includes(column) ||
        (columnSorter && typeof columnSorter === 'object' && columnSorter.external)) {
        return items;
    }
    const flip = sorterState.state === 'asc' ? 1 : sorterState.state === 'desc' ? -1 : 0;
    const sorted = items.slice().sort((item, item2) => {
        const value = item[column];
        const value2 = item2[column];
        const a = typeof value === 'number' ? value : String(value).toLowerCase();
        const b = typeof value2 === 'number' ? value2 : String(value2).toLowerCase();
        return a > b ? 1 * flip : b > a ? -1 * flip : 0;
    });
    return sorted;
};

const CSmartTableBody = defineComponent({
    name: 'CSmartTableBody',
    props: {
        clickableRows: Boolean,
        columnNames: {
            type: Array,
            default: () => [],
            require: true,
        },
        currentItems: {
            type: Array,
            default: () => [],
        },
        firstItemOnActivePageIndex: {
            type: Number,
            require: true,
            default: 0,
        },
        noItemsLabel: String,
        scopedSlots: Object,
        selectable: Boolean,
        selected: Array,
    },
    emits: ['rowChecked', 'rowClick'],
    setup(props, { emit }) {
        const colspan = props.selectable
            ? props.columnNames.length + 1
            : props.columnNames.length;
        return () => h$1(CTableBody, {
            ...(props.clickableRows && { style: 'cursor:pointer;' }),
        }, {
            default: () => props.currentItems.length > 0
                ? props.currentItems.map((item, trIndex) => [
                    h$1(CTableRow, {
                        ...(item._props && { ...item._props }),
                        ...(props.clickableRows && { tabindex: 0 }),
                        onClick: (event) => {
                            emit('rowClick', item, trIndex + props.firstItemOnActivePageIndex, getClickedColumnName(event.target, props.columnNames), event);
                        },
                    }, {
                        default: () => [
                            props.selectable &&
                                h$1(CTableDataCell, {}, () => h$1(CFormCheck, {
                                    checked: props.selected &&
                                        isObjectInArray(props.selected, item, [
                                            '_cellProps',
                                            '_props',
                                            '_selected',
                                        ]),
                                    onChange: (event) => {
                                        emit('rowChecked', item, event.target.checked);
                                    },
                                })),
                            props.columnNames.map((colName) => props.scopedSlots &&
                                props.scopedSlots[colName] &&
                                typeof props.scopedSlots[colName] === 'function'
                                ? h$1(props.scopedSlots[colName], { item: item })
                                : typeof item[colName] !== 'undefined' &&
                                    h$1(CTableDataCell, {
                                        ...getTableDataCellProps(item, colName),
                                    }, {
                                        default: () => String(item[colName]),
                                    })),
                        ],
                    }),
                    props.scopedSlots &&
                        props.scopedSlots['details'] && [
                        h$1(CTableRow, {
                            colspan: props.selectable
                                ? props.columnNames.length + 1
                                : props.columnNames.length,
                            class: 'p-0',
                            style: { 'border-bottom-width': '0' },
                            tabindex: '-1',
                        }),
                        h$1(CTableRow, {
                            class: 'p-0',
                            key: `details${trIndex}`,
                            onClick: (event) => {
                                emit('rowClick', item, trIndex + props.firstItemOnActivePageIndex, getClickedColumnName(event.target, props.columnNames), true);
                            },
                        }, {
                            default: () => h$1(CTableDataCell, {
                                colspan: props.selectable
                                    ? props.columnNames.length + 1
                                    : props.columnNames.length,
                                class: 'p-0',
                                style: { border: 0 },
                            }, {
                                default: () => props.scopedSlots &&
                                    props.scopedSlots['details'] &&
                                    h$1(props.scopedSlots['details'], {
                                        item: item,
                                    }),
                            }),
                        }),
                    ],
                ])
                : h$1(CTableRow, {}, {
                    default: () => h$1(CTableDataCell, { colspan: colspan }, {
                        default: () => props.noItemsLabel,
                    }),
                }),
        });
    },
});

const CSmartTableHead = defineComponent({
    name: 'CSmartTableHead',
    props: {
        columnFilter: [Boolean, Object],
        columnFilterValue: Object,
        columnSorter: [Boolean, Object],
        component: {
            type: String,
            default: 'head',
        },
        columns: {
            type: Array,
            default: () => [],
        },
        items: {
            type: Array,
            default: () => [],
        },
        selectable: Boolean,
        selectAll: [Boolean, Object],
        selectedAll: [Boolean, String],
        showGroups: {
            type: Boolean,
            default: true,
        },
        sorterState: Object,
    },
    emits: ['customFilterChange', 'filterInput', 'filterChange', 'selectAllChecked', 'sortClick'],
    setup(props, { slots, emit }) {
        const columns = computed(() => getColumns(props.columns));
        const groups = computed(() => getColumnGroups(props.columns));
        const columnSorterIcon = (column) => {
            if (getColumnSorterState(getColumnKey(column), props.sorterState) === 0) {
                return h$1('span', { class: 'opacity-25 float-end me-1' }, slots.sortingIcon && slots.sortingIcon());
            }
            if (getColumnSorterState(getColumnKey(column), props.sorterState) === 'asc') {
                return h$1('span', { class: 'float-end me-1' }, slots.sortingIconAscending && slots.sortingIconAscending());
            }
            if (getColumnSorterState(getColumnKey(column), props.sorterState) === 'desc') {
                return h$1('span', { class: 'float-end me-1' }, slots.sortingIconDescending && slots.sortingIconDescending());
            }
            return;
        };
        return () => h$1(props.component === 'head' ? CTableHead : CTableFoot, {}, {
            default: () => [
                props.showGroups &&
                    groups.value &&
                    groups.value.length > 0 &&
                    groups.value.map((row) => [
                        h$1(CTableRow, {}, () => [
                            props.selectable && h$1(CTableHeaderCell),
                            row.map((cell) => h$1(CTableHeaderCell, {
                                colspan: cell.colspan,
                                ...getTableHeaderCellProps(cell),
                            }, () => cell.label)),
                        ]),
                    ]),
                h$1(CTableRow, {}, {
                    default: () => [
                        props.selectable &&
                            h$1(CTableHeaderCell, {}, () => h$1(CFormCheck, {
                                checked: typeof props.selectedAll === 'boolean' ? props.selectedAll : false,
                                indeterminate: props.selectedAll === 'indeterminate' ? true : false,
                                onChange: () => {
                                    emit('selectAllChecked');
                                },
                            })),
                        columns.value.map((column, index) => h$1(CTableHeaderCell, {
                            ...getTableHeaderCellProps(column),
                            onClick: () => {
                                emit('sortClick', getColumnKey(column), index);
                            },
                            style: getTableHeaderCellStyles(column, props.columnSorter),
                        }, {
                            default: () => [
                                h$1('div', {
                                    class: 'd-inline',
                                }, getColumnLabel(column)),
                                props.columnSorter &&
                                    (typeof column === 'object'
                                        ? typeof column.sorter === 'undefined'
                                            ? true
                                            : column.sorter
                                        : true) &&
                                    columnSorterIcon(column),
                            ],
                        })),
                    ],
                }),
                props.columnFilter &&
                    h$1(CTableRow, {}, {
                        default: () => [
                            props.selectable && h$1(CTableHeaderCell),
                            columns.value.map((column) => h$1(CTableHeaderCell, {
                                ...getTableHeaderCellProps(column),
                            }, {
                                default: () => (typeof column === 'object'
                                    ? column.filter === undefined
                                        ? true
                                        : column.filter
                                    : true)
                                    ? typeof column !== 'string' && typeof column.filter === 'function'
                                        ? column.filter(getColumnValues(props.items, getColumnKey(column)), (value) => {
                                            emit('customFilterChange', getColumnKey(column), value);
                                        })
                                        : h$1(CFormInput, {
                                            size: 'sm',
                                            onInput: (event) => {
                                                emit('filterInput', getColumnKey(column), event.target.value);
                                            },
                                            onChange: (event) => {
                                                emit('filterChange', getColumnKey(column), event.target.value);
                                            },
                                            'aria-label': `column name: '${getColumnLabel(column)}' filter input`,
                                            ...(props.columnFilterValue &&
                                                props.columnFilterValue[getColumnKey(column)] && {
                                                value: props.columnFilterValue[getColumnKey(column)],
                                            }),
                                        })
                                    : '',
                            })),
                        ],
                    }),
            ],
        });
    },
});

const CSmartTable = defineComponent({
    name: 'CSmartTable',
    props: {
        /**
         * Sets active page. If 'pagination' prop is enabled, activePage is set only initially.
         */
        activePage: {
            type: Number,
            default: 1,
        },
        /**
         * When set, displays table cleaner above table, next to the table filter (or in place of table filter if `tableFilter` prop is not set)
         * Cleaner resets `tableFilterValue`, `columnFilterValue`, `sorterValue`. If clean is possible it is clickable (`tabIndex="0"` `role="button"`, `color="danger"`), otherwise it is not clickable and transparent. Cleaner can be customized through the `cleanerIcon` slot.
         *
         */
        cleaner: Boolean,
        /**
         * Style table items as clickable.
         */
        clickableRows: Boolean,
        /**
         * When set, displays additional filter row between table header and items, allowing filtering by specific column.
         * Column filter can be customized, by passing prop as object with additional options as keys. Available options:
         * - external (Boolean) - Disables automatic filtering inside component.
         * - lazy (Boolean) - Set to true to trigger filter updates only on change event.
         */
        columnFilter: {
            type: [Boolean, Object],
        },
        /**
         * Value of table filter. To set pass object where keys are column names and values are filter strings e.g.:
         * { user: 'John', age: 12 }
         */
        columnFilterValue: {
            type: Object,
        },
        /**
         * Prop for table columns configuration. If prop is not defined, table will display columns based on the first item keys, omitting keys that begins with underscore (e.g. '_props')
         *
         * In columns prop each array item represents one column. Item might be specified in two ways:
         * String: each item define column name equal to item value.
         * Object: item is object with following keys available as column configuration:
         * - key (required)(String) - define column name equal to item key.
         * - filter (Boolean) - removes filter from column when set to false.
         * - label (String) - define visible label of column. If not defined, label will be generated automatically based on column name, by converting kebab-case and snake_case to individual words and capitalization of each word.
         * - sorter (Boolean) - disables sorting of the column when set to false
         * - [_props](https://coreui.io/vue/docs/components/table.html#ctableheadercell) (String/Array/Object) - add props to `CTableHeaderCell`.
         * - _style (String/Array/Object) - adds styles to the column header (useful for defining widths)
         */
        columns: {
            type: Array,
        },
        /**
         * Enables table sorting by column value. Sorting will be performed corectly only if values in column are of one type: string (case insensitive) or number.
         *
         * Sorter can be customized, by passing prop as object with additional options as keys. Available options:
         * - external (Boolean) - Disables automatic sorting inside component.
         * - resetable (Boolean) - If set to true clicking on sorter have three states: ascending, descending and null. That means that third click on sorter will reset sorting, and restore table to original order.
         */
        columnSorter: {
            type: [Boolean, Object],
        },
        /**
         * If `true` Displays table footer, which mirrors table header. (without column filter).
         * Or Array of objects or strings, where each element represents one cell in the table footer.
         *
         * Example items:
         * `['FooterCell', 'FooterCell', 'FooterCell']`
         * or
         * `[{ label: 'FooterCell', _props: { color: 'success' }, ...]`
         */
        footer: {
            type: [Boolean, Array],
        },
        /**
         * Set to false to remove table header.
         */
        header: {
            type: Boolean,
            default: true,
        },
        /**
         * Array of objects, where each object represents one item - row in table. Additionally, you can customize each row by passing them by [_props](http://coreui.io/vue/docs/components/table.html#ctablerow) key and single cell by [_cellProps](http://coreui.io/vue/docs/components/table.html#ctabledatacell).
         *
         * Examples:
         * - `_props: { color: 'primary', align: 'middle'}`
         * - `_cellProps: { all: { class: 'fw-semibold'}, 'name': { color: 'info' }}`
         */
        items: {
            type: Array,
            default: () => [],
        },
        /**
         * The total number of items. Use if you pass a portion of data from an external source to let know component what is the total number of items.
         *
         * @since 4.8.0
         */
        itemsNumber: Number,
        /**
         * Number of items per site, when pagination is enabled.
         */
        itemsPerPage: {
            type: Number,
            default: 10,
        },
        /**
         * Label for items per page selector.
         */
        itemsPerPageLabel: {
            type: String,
            default: 'Items per page:',
        },
        /**
         * Items per page selector options.
         */
        itemsPerPageOptions: {
            type: Array,
            default: () => [5, 10, 20, 50],
        },
        /**
         * Adds select element over table, which is used for control items per page in pagination. If you want to customize this element, pass object with optional values:
         * - label (String) - replaces default label text
         * - values (Array) - custom array of pagination values
         * - external (Boolean) - disables automatic 'itemsPerPage' change (use to change pages externaly by 'pagination-change' event).
         */
        itemsPerPageSelect: {
            type: [Boolean, Object],
        },
        /**
         * When set, table will have loading style: loading spinner and reduced opacity. When 'small' prop is enabled spinner will be also smaller.
         */
        loading: Boolean,
        /**
         * ReactNode or string for passing custom noItemsLabel texts.
         */
        noItemsLabel: {
            type: String,
            default: 'No items found',
        },
        /**
         * Enables default pagination. Set to true for default setup or pass an object with additional CPagination props. Default pagination will always have the computed number of pages that cannot be changed. The number of pages is generated based on the number of passed items and 'itemsPerPage' prop. If this restriction is an obstacle, you can make external CPagination instead.
         */
        pagination: {
            type: [Boolean, Object],
        },
        /**
         * Properties to [CSmartPagination](https://coreui.io/vue/docs/components/smart-pagination#csmartpagination) component.
         */
        paginationProps: Object,
        /**
         * Add checkboxes to make table rows selectable.
         */
        selectable: Boolean,
        /**
         * Enables select all checkbox displayed in the header of the table.
         *
         * Can be customized, by passing prop as object with additional options as keys. Available options:
         * - external (Boolean) - Disables automatic selection inside the component.
         *
         * @since 4.8.0
         */
        selectAll: {
            type: [Boolean, Object],
        },
        /**
         * Array of selected objects, where each object represents one item - row in table.
         *
         * Example item: `{ name: 'John' , age: 12 }`
         *
         * @since 4.8.0
         */
        selected: {
            type: Array,
            default: () => [],
        },
        /**
         * State of the sorter. Name key is column name, direction can be 'asc' or 'desc'. eg.:
         * { column: 'status', state: 'asc' }
         */
        sorterValue: {
            type: Object,
        },
        /**
         * Properties to [CTableBody](https://coreui.io/vue/docs/components/table/#ctablebody) component.
         */
        tableBodyProps: Object,
        /**
         * Properties to [CTableFoot](https://coreui.io/vue/docs/components/table/#ctablefoot) component.
         */
        tableFootProps: Object,
        /**
         * When set, displays table filter above table, allowing filtering by specific column.
         *
         * Column filter can be customized, by passing prop as object with additional options as keys. Available options:
         * - external (Boolean) - Disables automatic filtering inside component.
         * - lazy (Boolean) - Set to true to trigger filter updates only on change event.
         */
        tableFilter: {
            type: [Boolean, Object],
        },
        /**
         * The element represents a caption for a component.
         */
        tableFilterLabel: {
            type: String,
            default: 'Filter:',
        },
        /**
         * Specifies a short hint that is visible in the search input.
         */
        tableFilterPlaceholder: {
            type: String,
            default: 'type string...',
        },
        /**
         * Value of table filter.
         */
        tableFilterValue: String,
        /**
         * Properties to [CTableHead](https://coreui.io/vue/docs/components/table/#ctablehead) component.
         */
        tableHeadProps: Object,
        /**
         * Properties to [CTable](https://coreui.io/vue/docs/components/table/#ctable) component.
         */
        tableProps: Object,
    },
    emits: [
        /**
         * Page change callback.
         *
         * @property {number} page - active page number
         */
        'activePageChange',
        /**
         * Column filter change callback.
         *
         * @property {object} ColumnFilterValue {[key: string]: string | number}
         */
        'columnFilterChange',
        /**
         * Filtered items change callback.
         *
         * @property {array} items
         */
        'filteredItemsChange',
        /**
         * Pagination change callback.
         *
         * @property {number} itemsPerPageNumber - items per page number
         */
        'itemsPerPageChange',
        /**
         * Row click callback.
         *
         * @property {object} item
         * @property {number} index
         * @property {string} columnName
         * @property {event} event
         */
        'rowClick',
        /**
         * Select all callback.
         *
         * @since 4.8.0
         */
        'selectAll',
        /**
         * Selected items change callback.
         *
         * @property {array} items
         */
        'selectedItemsChange',
        /**
         * Sorter value change callback.
         *
         * @property {object} SorterValue { column?: string, state?: number | string}
         */
        'sorterChange',
        /**
         * Table filter change callback.
         *
         * @property {string} tableFilterValue
         */
        'tableFilterChange',
    ],
    setup(props, { emit, slots }) {
        const activePage = ref(props.activePage);
        const columnFilterState = ref(props.columnFilterValue ?? {});
        const items = ref(props.items.map((item, index) => {
            return { ...item, _id: index };
        }));
        const itemsNumber = ref(props.itemsNumber);
        // eslint-disable-next-line unicorn/explicit-length-check
        const itemsPerPage = ref(props.itemsPerPage || items.value.length);
        const selected = ref([]);
        const selectedAll = ref();
        const sorterState = ref(props.sorterValue || {});
        const tableFilterState = ref(props.tableFilterValue ?? '');
        watch(() => props.activePage, () => {
            activePage.value = props.activePage;
        });
        watch(() => props.columnFilterValue, () => {
            if (props.columnFilterValue) {
                columnFilterState.value = props.columnFilterValue;
            }
        });
        watch(() => props.items, () => {
            if (props.items &&
                props.items.length < itemsPerPage.value * activePage.value - itemsPerPage.value) {
                activePage.value = 1;
            }
            props.items.forEach((item) => {
                if (item._selected) {
                    const _item = { ...item };
                    delete _item['_cellProps'];
                    delete _item['_props'];
                    delete _item['_selected'];
                    selected.value = [...selected.value, _item];
                }
            });
            if (Array.isArray(props.items)) {
                items.value = props.items;
                // eslint-disable-next-line unicorn/explicit-length-check
                itemsNumber.value = props.itemsNumber || props.items.length;
            }
        }, {
            immediate: true,
        });
        watch(() => props.itemsNumber, () => {
            itemsNumber.value = props.itemsNumber;
        });
        watch(() => props.itemsPerPage, () => {
            itemsPerPage.value = props.itemsPerPage;
        });
        watch(() => props.selected, () => {
            selected.value = props.selected;
        });
        watch(() => props.sorterValue, () => {
            if (props.sorterValue) {
                sorterState.value = props.sorterValue;
            }
        });
        watch(itemsPerPage, () => {
            if (props.itemsPerPage !== itemsPerPage.value) {
                activePage.value = 1; // TODO: set proper page after _itemsPerPage update
            }
            emit('itemsPerPageChange', itemsPerPage.value);
        });
        watch([selected, itemsNumber], () => {
            if (props.selectable) {
                emit('selectedItemsChange', selected);
                if (selected.value.length === itemsNumber.value) {
                    selectedAll.value = true;
                    return;
                }
                if (selected.value.length === 0) {
                    selectedAll.value = false;
                    return;
                }
                if (selected.value.length > 0 && selected.value.length !== itemsNumber.value) {
                    selectedAll.value = 'indeterminate';
                }
            }
        }, {
            immediate: true,
        });
        onMounted(() => {
            if (items.value &&
                items.value.length < itemsPerPage.value * activePage.value - itemsPerPage.value) {
                activePage.value = 1;
            }
        });
        const handleSorterChange = (column, index) => {
            if (!isSortable(index, props.columns, props.columnSorter, itemsDataColumns.value, columnNames.value)) {
                return;
            }
            //if column changed or sort was descending change asc to true
            const state = sorterState.value;
            if (state.column === column) {
                if (state.state === 0) {
                    state.state = 'asc';
                }
                else if (state.state === 'asc') {
                    state.state = 'desc';
                }
                else {
                    if (typeof props.columnSorter === 'object' && !props.columnSorter.resetable) {
                        state.state = 'asc';
                    }
                    else {
                        state.state = 0;
                    }
                }
            }
            else {
                state.column = column;
                state.state = 'asc';
            }
            sorterState.value.column = state.column;
            sorterState.value.state = state.state;
            emit('sorterChange', sorterState.value);
        };
        const handleActivePageChange = (page) => {
            activePage.value = page;
            emit('activePageChange', page);
        };
        const handleItemsPerPageChange = (event) => {
            if (typeof props.itemsPerPageSelect !== 'object' ||
                (typeof props.itemsPerPageSelect === 'object' && !props.itemsPerPageSelect.external)) {
                itemsPerPage.value = Number(event.target.value);
            }
        };
        const handleRowChecked = (item, value) => {
            if (value && !isObjectInArray(selected.value, item, ['_cellProps', '_props', '_selected'])) {
                selected.value = [...selected.value, item];
                return;
            }
            selected.value = selected.value.filter((_item) => !isObjectInArray([_item], item, ['_cellProps', '_props', '_selected']));
        };
        const handleSelectAllChecked = () => {
            if (selectedAll.value === true) {
                selected.value = [];
                return;
            }
            emit('selectAll');
            if (props.selectAll && typeof props.selectAll === 'object' && props.selectAll.external) {
                return;
            }
            selected.value = items.value.map((item) => {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { _cellProps, _props, _selected, ...rest } = item;
                return rest;
            });
        };
        const handleColumnFilterChange = (colName, value, type) => {
            const _isLazy = props.columnFilter &&
                typeof props.columnFilter === 'object' &&
                props.columnFilter.lazy === true;
            if ((_isLazy && type === 'input') || (!_isLazy && type === 'change')) {
                return;
            }
            activePage.value = 1;
            columnFilterState.value = { ...columnFilterState.value, [`${colName}`]: value };
            emit('columnFilterChange', columnFilterState.value);
        };
        const handleTableFilterChange = (value, type) => {
            const _isLazy = props.columnFilter &&
                typeof props.columnFilter === 'object' &&
                props.columnFilter.lazy === true;
            if ((_isLazy && type === 'input') || (!_isLazy && type === 'change')) {
                return;
            }
            activePage.value = 1;
            tableFilterState.value = value;
            emit('tableFilterChange', tableFilterState.value);
        };
        const handleClean = () => {
            tableFilterState.value = '';
            columnFilterState.value = {};
            sorterState.value = {};
        };
        const columnNames = computed(() => getColumnNames(props.columns, items.value));
        const itemsDataColumns = computed(() => columnNames.value.filter((name) => getColumnNamesFromItems(items.value).includes(name)));
        const filteredColumns = computed(() => filterColumns(items.value, props.columnFilter, columnFilterState.value, itemsDataColumns.value));
        const filteredTable = computed(() => filterTable(filteredColumns.value, props.tableFilter, tableFilterState.value, itemsDataColumns.value));
        const sortedItems = computed(() => sortItems(props.columnSorter, filteredTable.value, itemsDataColumns.value, sorterState.value));
        watch(sortedItems, () => {
            emit('filteredItemsChange', sortedItems.value);
        });
        const numberOfPages = computed(() => itemsPerPage.value ? Math.ceil(sortedItems.value.length / itemsPerPage.value) : 1);
        const firstItemOnActivePageIndex = computed(() => activePage.value ? (activePage.value - 1) * itemsPerPage.value : 0);
        const currentItems = computed(() => activePage.value
            ? sortedItems.value.slice(firstItemOnActivePageIndex.value, firstItemOnActivePageIndex.value + itemsPerPage.value)
            : sortedItems.value);
        return () => h$1('div', {}, [
            (props.tableFilter || props.cleaner) &&
                h$1('div', {
                    class: 'row my-2 mx-0',
                }, [
                    props.tableFilter &&
                        h$1('div', {
                            class: 'col-auto p-0',
                        }, props.tableFilter &&
                            h$1('div', {
                                class: 'row mb-2',
                            }, {
                                default: () => [
                                    h$1(CFormLabel, {
                                        class: 'col-sm-auto col-form-label',
                                    }, {
                                        default: () => props.tableFilterLabel,
                                    }),
                                    h$1('div', {
                                        class: 'col-sm-auto',
                                    }, h$1(CFormInput, {
                                        onInput: (e) => {
                                            handleTableFilterChange(e.target.value, 'input');
                                        },
                                        onChange: (e) => {
                                            handleTableFilterChange(e.target.value, 'change');
                                        },
                                        placeholder: props.tableFilterPlaceholder,
                                        value: tableFilterState.value,
                                    })),
                                ],
                            })),
                    props.cleaner &&
                        h$1('div', {
                            class: 'col-auto p-0',
                        }, h$1('button', {
                            type: 'button',
                            class: 'btn btn-transparent',
                            ...(!(tableFilterState.value ||
                                sorterState.value.column ||
                                Object.values(columnFilterState.value).join('')) && { disabled: true, tabIndex: -1 }),
                            onClick: () => handleClean(),
                            onKeydown: (event) => {
                                if (event.key === 'Enter')
                                    handleClean();
                            },
                        }, slots.cleanerIcon
                            ? slots.cleanerIcon()
                            : h$1(CIcon, { width: '18', content: cilFilterX }))),
                ]),
            h$1('div', {
                class: 'position-relative',
            }, {
                default: () => [
                    h$1(CTable, {
                        ...props.tableProps,
                    }, {
                        default: () => [
                            props.header &&
                                h$1(CSmartTableHead, {
                                    component: 'head',
                                    ...props.tableHeadProps,
                                    columnFilter: props.columnFilter,
                                    columnFilterValue: columnFilterState.value,
                                    columns: props.columns ?? columnNames.value,
                                    columnSorter: props.columnSorter,
                                    items: items.value,
                                    selectable: props.selectable,
                                    selectAll: props.selectAll,
                                    selectedAll: selectedAll.value,
                                    sorterState: sorterState.value,
                                    onCustomFilterChange: (key, value) => handleColumnFilterChange(key, value),
                                    onFilterInput: (key, value) => handleColumnFilterChange(key, value, 'input'),
                                    onFilterChange: (key, value) => handleColumnFilterChange(key, value, 'change'),
                                    onSelectAllChecked: () => handleSelectAllChecked(),
                                    onSortClick: (key, index) => handleSorterChange(key, index),
                                }, {
                                    // @slot Sorter icon when items are unsorted.
                                    sortingIcon: () => slots.sortingIcon
                                        ? slots.sortingIcon()
                                        : h$1('svg', {
                                            xmlns: 'http://www.w3.org/2000/svg',
                                            class: 'icon',
                                            viewBox: '0 0 512 512',
                                            role: 'img',
                                            innerHTML: cilSwapVertical[1],
                                        }),
                                    // @slot Sorter icon when items are sorted ascending.
                                    sortingIconAscending: () => slots.sortingIconAscending
                                        ? slots.sortingIconAscending()
                                        : h$1('svg', {
                                            xmlns: 'http://www.w3.org/2000/svg',
                                            class: 'icon',
                                            viewBox: '0 0 512 512',
                                            role: 'img',
                                            innerHTML: cilArrowBottom[1],
                                        }),
                                    // @slot  Sorter icon when items are sorted descending.
                                    sortingIconDescending: () => slots.sortingIconDescending
                                        ? slots.sortingIconDescending()
                                        : h$1('svg', {
                                            xmlns: 'http://www.w3.org/2000/svg',
                                            class: 'icon',
                                            viewBox: '0 0 512 512',
                                            role: 'img',
                                            innerHTML: cilArrowTop[1],
                                        }),
                                }),
                            h$1(CSmartTableBody, {
                                clickableRows: props.clickableRows,
                                columnNames: columnNames.value,
                                currentItems: currentItems.value,
                                firstItemOnActivePageIndex: firstItemOnActivePageIndex.value,
                                noItemsLabel: props.noItemsLabel,
                                onRowChecked: (item, value) => handleRowChecked(item, value),
                                onRowClick: (item, index, columnName, event) => props.clickableRows && emit('rowClick', item, index, columnName, event),
                                scopedSlots: slots,
                                selectable: props.selectable,
                                selected: selected.value,
                                ...props.tableBodyProps,
                            }),
                            typeof props.footer === 'boolean' &&
                                props.footer &&
                                h$1(CSmartTableHead, {
                                    component: 'footer',
                                    ...props.tableFootProps,
                                    columnFilter: false,
                                    columnSorter: false,
                                    columns: props.columns ? props.columns : columnNames.value,
                                    selectable: props.selectable,
                                    selectAll: props.selectAll,
                                    selectedAll: selectedAll.value,
                                    showGroups: false,
                                    onSelectAllChecked: () => handleSelectAllChecked(),
                                }),
                            Array.isArray(props.footer) &&
                                h$1(CTableFoot, {
                                    ...props.tableFootProps,
                                }, {
                                    default: () => h$1(CTableRow, {}, {
                                        default: () => [
                                            Array.isArray(props.footer) &&
                                                props.footer.map((item) => h$1(CTableDataCell, {
                                                    ...(typeof item === 'object' &&
                                                        item._props && { ...item._props }),
                                                }, {
                                                    default: () => typeof item === 'object' ? item.label : item,
                                                })),
                                        ],
                                    }),
                                }),
                        ],
                    }),
                    props.loading &&
                        h$1(CElementCover, {
                            boundaries: [
                                { sides: ['top'], query: 'tbody' },
                                { sides: ['bottom'], query: 'tbody' },
                            ],
                        }, {
                            // @slot elementCover.
                            ...(slots.elementCover && {
                                default: () => slots.elementCover && slots.elementCover(),
                            }),
                        }),
                ],
            }),
            (props.pagination || props.itemsPerPageSelect) &&
                h$1('div', {
                    class: 'row',
                }, [
                    h$1('div', {
                        class: 'col',
                    }, (props.pagination && numberOfPages.value > 1) || props.paginationProps
                        ? h$1(CSmartPagination, {
                            pages: numberOfPages.value,
                            activePage: activePage.value,
                            ...props.paginationProps,
                            onActivePageChange: (page) => typeof props.pagination === 'object' && props.pagination.external
                                ? emit('activePageChange', page)
                                : handleActivePageChange(page),
                        })
                        : ''),
                    props.itemsPerPageSelect &&
                        h$1('div', {
                            class: 'col-auto ms-auto',
                        }, h$1('div', {
                            class: 'row',
                        }, {
                            default: () => [
                                h$1(CFormLabel, {
                                    class: 'col-auto col-form-label',
                                }, {
                                    default: () => props.itemsPerPageLabel,
                                }),
                                h$1('div', {
                                    class: 'col-auto',
                                }, h$1(CFormSelect, {
                                    value: itemsPerPage.value,
                                    onChange: handleItemsPerPageChange,
                                }, {
                                    default: () => props.itemsPerPageOptions &&
                                        props.itemsPerPageOptions.map((number, index) => {
                                            return h$1('option', {
                                                value: number,
                                                key: index,
                                            }, number);
                                        }),
                                })),
                            ],
                        })),
                ]),
        ]);
    },
});

const CSmartTablePlugin = {
    install: (app) => {
        app.component(CSmartTable.name, CSmartTable);
    },
};

const CTabContent = defineComponent({
    name: 'CTabContent',
    setup(_, { slots }) {
        return () => h$1('div', { class: 'tab-content' }, slots.default && slots.default());
    },
});

const CTabPane = defineComponent({
    name: 'CTabPane',
    props: {
        /**
         * Toggle the visibility of component.
         */
        visible: {
            type: Boolean,
            default: false,
        },
    },
    emits: [
        /**
         * Callback fired when the component requests to be hidden.
         */
        'hide',
        /**
         * Callback fired when the component requests to be shown.
         */
        'show',
    ],
    setup(props, { slots, emit }) {
        const tabPaneRef = ref();
        const firstRender = ref(true);
        const handleEnter = (el, done) => {
            firstRender.value = false;
            emit('show');
            setTimeout(() => {
                executeAfterTransition(() => done(), el);
                el.classList.add('show');
            }, 1);
        };
        const handleLeave = (el, done) => {
            firstRender.value = false;
            emit('hide');
            el.classList.remove('show');
            executeAfterTransition(() => done(), el);
        };
        return () => h$1(Transition, {
            onEnter: (el, done) => handleEnter(el, done),
            onLeave: (el, done) => handleLeave(el, done),
        }, () => withDirectives(h$1('div', {
            class: [
                'tab-pane',
                'fade',
                {
                    active: props.visible,
                    show: firstRender.value && props.visible,
                },
            ],
            ref: tabPaneRef,
        }, slots.default && slots.default()), [[vShow, props.visible]]));
    },
});

const CTabsPlugin = {
    install: (app) => {
        app.component(CTabContent.name, CTabContent);
        app.component(CTabPane.name, CTabPane);
    },
};

const CToast = defineComponent({
    name: 'CToast',
    props: {
        /**
         * Auto hide the toast.
         */
        autohide: {
            type: Boolean,
            default: true,
        },
        /**
         * Sets the color context of the component to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light', string
         */
        color: Color$1,
        /**
         * Delay hiding the toast (ms).
         */
        delay: {
            type: Number,
            default: 5000,
        },
        /**
         * Optionally add a close button to component and allow it to self dismiss.
         */
        dismissible: {
            type: Boolean,
            default: true,
        },
        /**
         * index of the component.
         */
        index: Number,
        /**
         * Title node for your component.
         */
        title: String,
        /**
         * Toggle the visibility of component.
         */
        visible: Boolean,
    },
    emits: [
        /**
         * Callback fired when the component requests to be closed.
         */
        'close',
        /**
         * Callback fired when the component requests to be shown.
         */
        'show',
    ],
    setup(props, { slots, emit }) {
        const timeout = ref(0);
        const visible = ref();
        const updateVisible = (_visible) => {
            visible.value = _visible;
        };
        provide('updateVisible', updateVisible);
        onMounted(() => {
            if (props.visible) {
                visible.value = props.visible;
            }
            if (props.autohide) {
                clearTimeout(timeout.value);
                timeout.value = window.setTimeout(() => {
                    visible.value = false;
                    emit('close');
                }, props.delay);
            }
        });
        return () => h$1(Transition, {
            appear: true,
            enterFromClass: '',
            enterActiveClass: 'show showing',
            enterToClass: 'show',
            leaveFromClass: 'show',
            leaveActiveClass: 'show showing',
            leaveToClass: 'show',
            onAfterEnter: (el) => {
                el.classList.add('show');
                props.index ? emit('show', props.index) : emit('show');
            },
            onAfterLeave: () => {
                props.index ? emit('close', props.index) : emit('close');
            },
        }, {
            default: () => visible.value &&
                h$1('div', {
                    class: [
                        'toast fade',
                        {
                            [`bg-${props.color}`]: props.color,
                        },
                    ],
                    'aria-live': 'assertive',
                    'aria-atomic': true,
                    role: 'alert',
                }, slots.default && slots.default()),
        });
    },
});

const CToastBody = defineComponent({
    name: 'CToastBody',
    setup(_, { slots }) {
        return () => h$1('div', { class: 'toast-body' }, slots.default && slots.default());
    },
});

const CToastClose = defineComponent({
    name: 'CToastClose',
    props: {
        /**
         * Component used for the root node. Either a string to use a HTML element or a component.
         */
        component: String,
        ...CCloseButton.props,
    },
    emits: [
        /**
         * Event called before the dissmiss animation has started.
         */
        'close',
    ],
    setup(props, { slots, emit }) {
        // eslint-disable-next-line no-unused-vars
        const updateVisible = inject('updateVisible');
        const handleClose = () => {
            emit('close');
            updateVisible(false);
        };
        return () => props.component
            ? h$1(props.component, {
                onClick: () => {
                    handleClose();
                },
            }, () => slots.default && slots.default())
            : h$1(CCloseButton, {
                ...props,
                onClick: () => {
                    handleClose();
                },
            });
    },
});

const CToaster = defineComponent({
    name: 'CToaster',
    props: {
        /**
         * Describes the placement of component.
         *
         * @values 'top-start', 'top', 'top-end', 'middle-start', 'middle', 'middle-end', 'bottom-start', 'bottom', 'bottom-end'
         */
        placement: {
            type: String,
            validator: (value) => {
                return [
                    'top-start',
                    'top-center',
                    'top-end',
                    'middle-start',
                    'middle-center',
                    'middle-end',
                    'bottom-start',
                    'bottom-center',
                    'bottom-end',
                ].includes(value);
            },
        },
    },
    setup(props, { slots }) {
        return () => h$1('div', {
            class: [
                'toaster toast-container p-3',
                {
                    'position-fixed': props.placement,
                    'top-0': props.placement && props.placement.includes('top'),
                    'top-50 translate-middle-y': props.placement && props.placement.includes('middle'),
                    'bottom-0': props.placement && props.placement.includes('bottom'),
                    'start-0': props.placement && props.placement.includes('start'),
                    'start-50 translate-middle-x': props.placement && props.placement.includes('center'),
                    'end-0': props.placement && props.placement.includes('end'),
                },
            ],
        }, slots.default && slots.default());
    },
});

const CToastHeader = defineComponent({
    name: 'CToastHeader',
    props: {
        /**
         * Automatically add a close button to the header.
         */
        closeButton: Boolean,
    },
    emits: [
        /**
         * Event called after clicking the close button.
         */
        'close',
    ],
    setup(props, { slots, emit }) {
        return () => h$1('div', { class: 'toast-header' }, [
            slots.default && slots.default(),
            props.closeButton &&
                h$1(CToastClose, {
                    onClose: () => emit('close'),
                }),
        ]);
    },
});

const CToastPlugin = {
    install: (app) => {
        app.component(CToast.name, CToast);
        app.component(CToastBody.name, CToastBody);
        app.component(CToastClose.name, CToastClose);
        app.component(CToaster.name, CToaster);
        app.component(CToastHeader.name, CToastHeader);
    },
};

const CTooltip = defineComponent({
    name: 'CTooltip',
    props: {
        /**
         * Apply a CSS fade transition to the tooltip.
         *
         * @since 4.9.0
         */
        animation: {
            type: Boolean,
            default: true,
        },
        /**
         * Content for your component. If you want to pass non-string value please use dedicated slot `<template #content>...</template>`
         */
        content: String,
        /**
         * The delay for displaying and hiding the popover (in milliseconds). When a numerical value is provided, the delay applies to both the hide and show actions. The object structure for specifying the delay is as follows: delay: `{ 'show': 500, 'hide': 100 }`.
         *
         * @since 4.9.0
         */
        delay: {
            type: [Number, Object],
            default: 0,
        },
        /**
         * Specify the desired order of fallback placements by providing a list of placements as an array. The placements should be prioritized based on preference.
         *
         * @since 4.9.0
         */
        fallbackPlacements: {
            type: [String, Array],
            default: () => ['top', 'right', 'bottom', 'left'],
            validator: (value) => {
                if (typeof value === 'string') {
                    return ['top', 'right', 'bottom', 'left'].includes(value);
                }
                if (Array.isArray(value)) {
                    return value.every((e) => ['top', 'right', 'bottom', 'left'].includes(e));
                }
                return false;
            },
        },
        /**
         * Offset of the tooltip relative to its target.
         */
        offset: {
            type: Array,
            default: () => [0, 6],
        },
        /**
         * Describes the placement of your component after Popper.js has applied all the modifiers that may have flipped or altered the originally provided placement property.
         */
        placement: {
            type: String,
            default: 'top',
            validator: (value) => {
                return ['top', 'right', 'bottom', 'left'].includes(value);
            },
        },
        /**
         * Sets which event handlers you’d like provided to your toggle prop. You can specify one trigger or an array of them.
         *
         * @values 'click', 'focus', 'hover'
         */
        trigger: {
            type: [String, Array],
            default: () => ['hover', 'focus'],
            validator: (value) => {
                if (typeof value === 'string') {
                    return ['click', 'focus', 'hover'].includes(value);
                }
                if (Array.isArray(value)) {
                    return value.every((e) => ['click', 'focus', 'hover'].includes(e));
                }
                return false;
            },
        },
        /**
         * Toggle the visibility of tooltip component.
         */
        visible: Boolean,
    },
    emits: [
        /**
         * Callback fired when the component requests to be hidden.
         */
        'hide',
        /**
         * Callback fired when the component requests to be shown.
         */
        'show',
    ],
    setup(props, { attrs, slots, emit }) {
        const togglerRef = ref();
        const tooltipRef = ref();
        const visible = ref(props.visible);
        const { initPopper, destroyPopper } = usePopper();
        const delay = typeof props.delay === 'number' ? { show: props.delay, hide: props.delay } : props.delay;
        const popperConfig = {
            modifiers: [
                {
                    name: 'arrow',
                    options: {
                        element: '.tooltip-arrow',
                    },
                },
                {
                    name: 'flip',
                    options: {
                        fallbackPlacements: props.fallbackPlacements,
                    },
                },
                {
                    name: 'offset',
                    options: {
                        offset: props.offset,
                    },
                },
            ],
            placement: getRTLPlacement(props.placement, togglerRef.value),
        };
        const handleEnter = (el, done) => {
            emit('show');
            initPopper(togglerRef.value, tooltipRef.value, popperConfig);
            el.classList.add('show');
            executeAfterTransition(() => done(), el);
        };
        const handleLeave = (el, done) => {
            emit('hide');
            el.classList.remove('show');
            executeAfterTransition(() => {
                done();
                destroyPopper();
            }, el);
        };
        const toggleVisible = (event, _visible) => {
            togglerRef.value = event.target;
            if (_visible) {
                setTimeout(() => {
                    visible.value = true;
                }, delay.show);
                return;
            }
            setTimeout(() => {
                visible.value = false;
            }, delay.hide);
        };
        return () => [
            h$1(Teleport, {
                to: 'body',
            }, h$1(Transition, {
                onEnter: (el, done) => handleEnter(el, done),
                onLeave: (el, done) => handleLeave(el, done),
            }, () => visible.value &&
                h$1('div', {
                    class: [
                        'tooltip',
                        'bs-tooltip-auto',
                        {
                            fade: props.animation,
                        },
                    ],
                    ref: tooltipRef,
                    role: 'tooltip',
                    ...attrs,
                }, [
                    h$1('div', { class: 'tooltip-arrow' }),
                    (props.content || slots.content) &&
                        h$1('div', { class: 'tooltip-inner' }, {
                            default: () => (slots.content && slots.content()) || props.content,
                        }),
                ]))),
            slots.toggler &&
                slots.toggler({
                    on: {
                        click: (event) => props.trigger.includes('click') && toggleVisible(event, !visible.value),
                        blur: (event) => props.trigger.includes('focus') && toggleVisible(event, false),
                        focus: (event) => props.trigger.includes('focus') && toggleVisible(event, true),
                        mouseenter: (event) => props.trigger.includes('hover') && toggleVisible(event, true),
                        mouseleave: (event) => props.trigger.includes('hover') && toggleVisible(event, false),
                    },
                }),
        ];
    },
});

const CTooltipPlugin = {
    install: (app) => {
        app.component(CTooltip.name, CTooltip);
    },
};

const CWidgetStatsA = defineComponent({
    name: 'CWidgetStatsA',
    props: {
        color: String,
        /**
         * Title for your component. If you want to pass non-string value please use dedicated slot `<template #title>...</template>`
         */
        title: String,
        /**
         * Value for your component. If you want to pass non-string or non-number value please use dedicated slot `<template #value>...</template>`
         */
        value: {
            type: [Number, String],
            default: 0,
        },
    },
    /**
     * Location for action component, ex. `<CDropdown>`.
     *
     * @slot action
     */
    /**
     * Location for chart component.
     *
     * @slot chart
     */
    setup(props, { slots }) {
        return () => h$1(CCard, {
            class: [
                { [`bg-${props.color}`]: props.color, 'text-high-emphasis-inverse': props.color },
            ],
        }, () => [
            h$1(CCardBody, {
                class: 'pb-0 d-flex justify-content-between align-items-start',
            }, () => [
                h$1('div', {}, [
                    (props.value || slots.value) &&
                        h$1('div', { class: 'fs-4 fw-semibold' }, {
                            default: () => (slots.value && slots.value()) || props.value,
                        }),
                    (props.title || slots.title) &&
                        h$1('div', {}, {
                            default: () => (slots.title && slots.title()) || props.title,
                        }),
                ]),
                /**
                 * @slot Location for action component, ex. `<CDropdown>`.
                 */
                slots.action && slots.action(),
            ]),
            /**
             * @slot Location for chart component.
             */
            slots.chart && slots.chart(),
            slots.default && slots.default(),
        ]);
    },
});

/*!
 * is-plain-object <https://github.com/jonschlinkert/is-plain-object>
 *
 * Copyright (c) 2014-2017, Jon Schlinkert.
 * Released under the MIT License.
 */

function isObject(o) {
  return Object.prototype.toString.call(o) === '[object Object]';
}

function isPlainObject(o) {
  var ctor,prot;

  if (isObject(o) === false) return false;

  // If has modified constructor
  ctor = o.constructor;
  if (ctor === undefined) return true;

  // If has modified prototype
  prot = ctor.prototype;
  if (isObject(prot) === false) return false;

  // If constructor does not have an Object-specific method
  if (prot.hasOwnProperty('isPrototypeOf') === false) {
    return false;
  }

  // Most likely a plain Object
  return true;
}

function t(){return t=Object.assign?Object.assign.bind():function(e){for(var t=1;t<arguments.length;t++){var n=arguments[t];for(var r in n)Object.prototype.hasOwnProperty.call(n,r)&&(e[r]=n[r]);}return e},t.apply(this,arguments)}function n(e,t){if(null==e)return {};var n,r,i={},o=Object.keys(e);for(r=0;r<o.length;r++)t.indexOf(n=o[r])>=0||(i[n]=e[n]);return i}const r={silent:!1,logLevel:"warn"},i=["validator"],o=Object.prototype,a=o.toString,s=o.hasOwnProperty,u=/^\s*function (\w+)/;function l(e){var t;const n=null!==(t=null==e?void 0:e.type)&&void 0!==t?t:e;if(n){const e=n.toString().match(u);return e?e[1]:""}return ""}const c=isPlainObject,f=e=>e;let d=f;if("production"!==process.env.NODE_ENV){const e="undefined"!=typeof console;d=e?function(e,t=r.logLevel){!1===r.silent&&console[t](`[VueTypes warn]: ${e}`);}:f;}const p=(e,t)=>s.call(e,t),y=Number.isInteger||function(e){return "number"==typeof e&&isFinite(e)&&Math.floor(e)===e},v=Array.isArray||function(e){return "[object Array]"===a.call(e)},h=e=>"[object Function]"===a.call(e),b=(e,t)=>c(e)&&p(e,"_vueTypes_name")&&(!t||e._vueTypes_name===t),g=e=>c(e)&&(p(e,"type")||["_vueTypes_name","validator","default","required"].some(t=>p(e,t)));function O(e,t){return Object.defineProperty(e.bind(t),"__original",{value:e})}function m(e,t,n=!1){let r,i=!0,o="";r=c(e)?e:{type:e};const a=b(r)?r._vueTypes_name+" - ":"";if(g(r)&&null!==r.type){if(void 0===r.type||!0===r.type)return i;if(!r.required&&null==t)return i;v(r.type)?(i=r.type.some(e=>!0===m(e,t,!0)),o=r.type.map(e=>l(e)).join(" or ")):(o=l(r),i="Array"===o?v(t):"Object"===o?c(t):"String"===o||"Number"===o||"Boolean"===o||"Function"===o?function(e){if(null==e)return "";const t=e.constructor.toString().match(u);return t?t[1].replace(/^Async/,""):""}(t)===o:t instanceof r.type);}if(!i){const e=`${a}value "${t}" should be of type "${o}"`;return !1===n?(d(e),!1):e}if(p(r,"validator")&&h(r.validator)){const e=d,o=[];if(d=e=>{o.push(e);},i=r.validator(t),d=e,!i){const e=(o.length>1?"* ":"")+o.join("\n* ");return o.length=0,!1===n?(d(e),i):e}}return i}function j(e,t){const n=Object.defineProperties(t,{_vueTypes_name:{value:e,writable:!0},isRequired:{get(){return this.required=!0,this}},def:{value(e){return void 0===e?this.type===Boolean||Array.isArray(this.type)&&this.type.includes(Boolean)?void(this.default=void 0):(p(this,"default")&&delete this.default,this):h(e)||!0===m(this,e,!0)?(this.default=v(e)?()=>[...e]:c(e)?()=>Object.assign({},e):e,this):(d(`${this._vueTypes_name} - invalid default value: "${e}"`),this)}}}),{validator:r}=n;return h(r)&&(n.validator=O(r,n)),n}function _(e,t){const n=j(e,t);return Object.defineProperty(n,"validate",{value(e){return h(this.validator)&&d(`${this._vueTypes_name} - calling .validate() will overwrite the current custom validator function. Validator info:\n${JSON.stringify(this)}`),this.validator=O(e,this),this}})}function T(e,t,r){const o=function(e){const t={};return Object.getOwnPropertyNames(e).forEach(n=>{t[n]=Object.getOwnPropertyDescriptor(e,n);}),Object.defineProperties({},t)}(t);if(o._vueTypes_name=e,!c(r))return o;const{validator:a}=r,s=n(r,i);if(h(a)){let{validator:e}=o;e&&(e=null!==(l=(u=e).__original)&&void 0!==l?l:u),o.validator=O(e?function(t){return e.call(this,t)&&a.call(this,t)}:a,o);}var u,l;return Object.assign(o,s)}function $(e){return e.replace(/^(?!\s*$)/gm,"  ")}const w=()=>_("any",{}),x=()=>_("function",{type:Function}),P=()=>_("boolean",{type:Boolean}),A=()=>_("string",{type:String}),E=()=>_("number",{type:Number}),S=()=>_("array",{type:Array}),N=()=>_("object",{type:Object}),V=()=>j("integer",{type:Number,validator(e){const t=y(e);return !1===t&&d(`integer - "${e}" is not an integer`),t}}),q=()=>j("symbol",{validator(e){const t="symbol"==typeof e;return !1===t&&d(`symbol - invalid value "${e}"`),t}}),k=()=>Object.defineProperty({type:null,validator(e){const t=null===e;return !1===t&&d("nullable - value should be null"),t}},"_vueTypes_name",{value:"nullable"});function D(e,t="custom validation failed"){if("function"!=typeof e)throw new TypeError("[VueTypes error]: You must provide a function as argument");return j(e.name||"<<anonymous function>>",{type:null,validator(n){const r=e(n);return r||d(`${this._vueTypes_name} - ${t}`),r}})}function L(e){if(!v(e))throw new TypeError("[VueTypes error]: You must provide an array as argument.");const t=`oneOf - value should be one of "${e.map(e=>"symbol"==typeof e?e.toString():e).join('", "')}".`,n={validator(n){const r=-1!==e.indexOf(n);return r||d(t),r}};if(-1===e.indexOf(null)){const t=e.reduce((e,t)=>{if(null!=t){const n=t.constructor;-1===e.indexOf(n)&&e.push(n);}return e},[]);t.length>0&&(n.type=t);}return j("oneOf",n)}function B(e){if(!v(e))throw new TypeError("[VueTypes error]: You must provide an array as argument");let t=!1,n=!1,r=[];for(let i=0;i<e.length;i+=1){const o=e[i];if(g(o)){if(h(o.validator)&&(t=!0),b(o,"oneOf")&&o.type){r=r.concat(o.type);continue}if(b(o,"nullable")){n=!0;continue}if(!0===o.type||!o.type){d('oneOfType - invalid usage of "true" and "null" as types.');continue}r=r.concat(o.type);}else r.push(o);}r=r.filter((e,t)=>r.indexOf(e)===t);const i=!1===n&&r.length>0?r:null;return j("oneOfType",t?{type:i,validator(t){const n=[],r=e.some(e=>{const r=m(e,t,!0);return "string"==typeof r&&n.push(r),!0===r});return r||d(`oneOfType - provided value does not match any of the ${n.length} passed-in validators:\n${$(n.join("\n"))}`),r}}:{type:i})}function F(e){return j("arrayOf",{type:Array,validator(t){let n="";const r=t.every(t=>(n=m(e,t,!0),!0===n));return r||d(`arrayOf - value validation error:\n${$(n)}`),r}})}function Y(e){return j("instanceOf",{type:e})}function I(e){return j("objectOf",{type:Object,validator(t){let n="";const r=Object.keys(t).every(r=>(n=m(e,t[r],!0),!0===n));return r||d(`objectOf - value validation error:\n${$(n)}`),r}})}function J(e){const t=Object.keys(e),n=t.filter(t=>{var n;return !(null===(n=e[t])||void 0===n||!n.required)}),r=j("shape",{type:Object,validator(r){if(!c(r))return !1;const i=Object.keys(r);if(n.length>0&&n.some(e=>-1===i.indexOf(e))){const e=n.filter(e=>-1===i.indexOf(e));return d(1===e.length?`shape - required property "${e[0]}" is not defined.`:`shape - required properties "${e.join('", "')}" are not defined.`),!1}return i.every(n=>{if(-1===t.indexOf(n))return !0===this._vueTypes_isLoose||(d(`shape - shape definition does not include a "${n}" property. Allowed keys: "${t.join('", "')}".`),!1);const i=m(e[n],r[n],!0);return "string"==typeof i&&d(`shape - "${n}" property validation error:\n ${$(i)}`),!0===i})}});return Object.defineProperty(r,"_vueTypes_isLoose",{writable:!0,value:!1}),Object.defineProperty(r,"loose",{get(){return this._vueTypes_isLoose=!0,this}}),r}const M=["name","validate","getter"],R=/*#__PURE__*/(()=>{var e;return (e=class{static get any(){return w()}static get func(){return x().def(this.defaults.func)}static get bool(){return void 0===this.defaults.bool?P():P().def(this.defaults.bool)}static get string(){return A().def(this.defaults.string)}static get number(){return E().def(this.defaults.number)}static get array(){return S().def(this.defaults.array)}static get object(){return N().def(this.defaults.object)}static get integer(){return V().def(this.defaults.integer)}static get symbol(){return q()}static get nullable(){return k()}static extend(e){if(d("VueTypes.extend is deprecated. Use the ES6+ method instead. See https://dwightjack.github.io/vue-types/advanced/extending-vue-types.html#extending-namespaced-validators-in-es6 for details."),v(e))return e.forEach(e=>this.extend(e)),this;const{name:t,validate:r=!1,getter:i=!1}=e,o=n(e,M);if(p(this,t))throw new TypeError(`[VueTypes error]: Type "${t}" already defined`);const{type:a}=o;if(b(a))return delete o.type,Object.defineProperty(this,t,i?{get:()=>T(t,a,o)}:{value(...e){const n=T(t,a,o);return n.validator&&(n.validator=n.validator.bind(n,...e)),n}});let s;return s=i?{get(){const e=Object.assign({},o);return r?_(t,e):j(t,e)},enumerable:!0}:{value(...e){const n=Object.assign({},o);let i;return i=r?_(t,n):j(t,n),n.validator&&(i.validator=n.validator.bind(i,...e)),i},enumerable:!0},Object.defineProperty(this,t,s)}}).defaults={},e.sensibleDefaults=void 0,e.config=r,e.custom=D,e.oneOf=L,e.instanceOf=Y,e.oneOfType=B,e.arrayOf=F,e.objectOf=I,e.shape=J,e.utils={validate:(e,t)=>!0===m(t,e,!0),toType:(e,t,n=!1)=>n?_(e,t):j(e,t)},e})();function U(e={func:()=>{},bool:!0,string:"",number:0,array:()=>[],object:()=>({}),integer:0}){var n;return (n=class extends R{static get sensibleDefaults(){return t({},this.defaults)}static set sensibleDefaults(n){this.defaults=!1!==n?t({},!0!==n?n:e):{};}}).defaults=t({},e),n}class z extends(U()){}

const CWidgetStatsB = defineComponent({
    name: 'CWidgetStatsB',
    props: {
        /**
         * Sets the color context of the component to one of CoreUI’s themed colors
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
         */
        color: Color$1,
        /**
         * Colors have been inverted from their default dark shade.
         */
        inverse: Boolean,
        progress: J({
            /**
             * Sets the color context of the progress bar to one of CoreUI’s themed colors
             *
             * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
             */
            color: Color$1,
            /**
             * The percent to progress the ProgressBar (out of 100).
             */
            value: {
                type: Number,
                default: 0,
            },
        }),
        /**
         * Helper text for your component. If you want to pass non-string value please use dedicated slot `<template #text>...</template>`
         */
        text: String,
        /**
         * Title for your component. If you want to pass non-string value please use dedicated slot `<template #title>...</template>`
         */
        title: String,
        /**
         * Value for your component. If you want to pass non-string or non-number value please use dedicated slot `<template #value>...</template>`
         */
        value: {
            type: [Number, String],
            default: 0,
        },
    },
    setup(props, { slots }) {
        return () => h$1(CCard, {
            class: [
                {
                    'text-high-emphasis-inverse': props.inverse,
                },
            ],
            color: props.color,
        }, () => h$1(CCardBody, {
            class: 'card-body',
        }, () => [
            (props.value || slots.value) &&
                h$1('div', {
                    class: 'fs-4 fw-semibold',
                }, {
                    default: () => (slots.value && slots.value()) || props.value,
                }),
            (props.title || slots.title) &&
                h$1('div', {}, {
                    default: () => (slots.title && slots.title()) || props.title,
                }),
            h$1(CProgress, {
                class: 'my-2',
                ...(props.progress && props.progress.color && { color: props.progress.color }),
                height: 4,
                ...(props.progress && props.progress.value && { value: props.progress.value }),
                white: props.inverse,
            }),
            (props.text || slots.text) &&
                h$1('small', {
                    class: [
                        props.inverse ? 'text-medium-emphasis-inverse' : 'text-medium-emphasis',
                    ],
                }, {
                    default: () => (slots.text && slots.text()) || props.text,
                }),
        ]));
    },
});

const CWidgetStatsC = defineComponent({
    name: 'CWidgetStatsC',
    props: {
        /**
         * Sets the color context of the component to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
         */
        color: Color$1,
        /**
         * Colors have been inverted from their default dark shade.
         */
        inverse: Boolean,
        progress: J({
            /**
             * Sets the color context of the progress bar to one of CoreUI’s themed colors
             *
             * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
             */
            color: Color$1,
            /**
             * The percent to progress the ProgressBar (out of 100).
             */
            value: {
                type: Number,
                default: 0,
            },
        }),
        /**
         * Title for your component. If you want to pass non-string value please use dedicated slot `<template #title>...</template>`
         */
        title: String,
        /**
         * Value for your component. If you want to pass non-string or non-number value please use dedicated slot `<template #value>...</template>`
         */
        value: {
            type: [Number, String],
            default: 0,
        },
    },
    /**
     * Location for icon component.
     *
     * @slot icon
     */
    setup(props, { slots }) {
        return () => h$1(CCard, {
            class: [
                {
                    ['text-white']: props.inverse,
                },
            ],
            color: props.color,
        }, () => h$1(CCardBody, {
            class: 'card-body',
        }, () => [
            slots.icon &&
                h$1('div', {
                    class: [
                        'text-end mb-4',
                        props.inverse ? 'text-medium-emphasis-inverse' : 'text-medium-emphasis',
                    ],
                }, slots.icon && slots.icon()),
            (props.value || slots.value) &&
                h$1('div', {
                    class: 'fs-4 fw-semibold',
                }, {
                    default: () => (slots.value && slots.value()) || props.value,
                }),
            (props.title || slots.title) &&
                h$1('div', {
                    class: [
                        'text-uppercase fw-semibold small',
                        props.inverse ? 'text-medium-emphasis-inverse' : 'text-medium-emphasis',
                    ],
                }, {
                    default: () => (slots.title && slots.title()) || props.title,
                }),
            h$1(CProgress, {
                class: 'my-2',
                ...(props.progress && props.progress.color && { color: props.progress.color }),
                height: 4,
                ...(props.progress && props.progress.value && { value: props.progress.value }),
                white: props.inverse,
            }),
        ]));
    },
});

const CWidgetStatsD = defineComponent({
    name: 'CWidgetStatsD',
    props: {
        /**
         * Sets the color context of the component to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
         */
        color: Color$1,
        /**
         * Values and titles for your component.
         */
        values: {
            type: Array,
            default: () => [],
        },
    },
    /**
     * Location for icon component, ex. `<CDropdown>`.
     *
     * @slot icon
     */
    /**
     * Location for chart component.
     *
     * @slot chart
     */
    setup(props, { slots }) {
        return () => h$1(CCard, {}, {
            default: () => [
                h$1(CCardHeader, {
                    class: [
                        'position-relative d-flex justify-content-center align-items-center',
                        {
                            [`bg-${props.color}`]: props.color,
                        },
                    ],
                }, () => [slots.icon && slots.icon(), slots.chart && slots.chart()]),
                h$1(CCardBody, {
                    class: 'row text-center',
                }, {
                    default: () => props.values &&
                        props.values.map((value, index) => [
                            index % 2 !== 0 && h$1('div', { class: 'vr' }),
                            h$1(CCol, {}, {
                                default: () => [
                                    h$1(CCol, { class: 'fs-5 fw-semibold' }, () => value.value),
                                    h$1(CCol, { class: 'text-uppercase text-medium-emphasis small' }, () => value.title),
                                ],
                            }),
                        ]),
                }),
            ],
        });
    },
});

const CWidgetStatsE = defineComponent({
    name: 'CWidgetStatsE',
    props: {
        /**
         * Title for your component. If you want to pass non-string value please use dedicated slot `<template #title>...</template>`
         */
        title: String,
        /**
         * Value for your component. If you want to pass non-string or non-number value please use dedicated slot `<template #value>...</template>`
         */
        value: {
            type: [Number, String],
            default: 0,
        },
    },
    /**
     * Location for chart component.
     *
     * @slot chart
     */
    setup(props, { slots }) {
        return () => h$1(CCard, {}, () => h$1(CCardBody, {
            class: 'text-center',
        }, () => [
            (props.title || slots.title) &&
                h$1('div', {
                    class: 'text-muted small text-uppercase font-weight-bold',
                }, {
                    default: () => (slots.title && slots.title()) || props.title,
                }),
            (props.value || slots.value) &&
                h$1('div', {
                    class: 'h2 py-3',
                }, {
                    default: () => (slots.value && slots.value()) || props.value,
                }),
            slots.chart && slots.chart(),
            slots.default && slots.default(),
        ]));
    },
});

const CWidgetStatsF = defineComponent({
    name: 'CWidgetStatsF',
    props: {
        /**
         * Sets the color context of the component to one of CoreUI’s themed colors.
         *
         * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
         */
        color: Color$1,
        /**
         * Set padding of your component.
         */
        padding: {
            type: Boolean,
            default: true,
        },
        /**
         * Title for your component. If you want to pass non-string value please use dedicated slot `<template #title>...</template>`
         */
        title: String,
        /**
         * Helper text for your component. If you want to pass non-string value please use dedicated slot `<template #text>...</template>`
         */
        text: String,
        /**
         * Value for your component. If you want to pass non-string or non-number value please use dedicated slot `<template #value>...</template>`
         */
        value: {
            type: [Number, String],
            default: 0,
        },
    },
    /**
     * Location for icon component.
     *
     * @slot icon
     */
    setup(props, { slots }) {
        return () => h$1(CCard, {}, {
            default: () => [
                h$1(CCardBody, {
                    class: ['d-flex align-items-center', props.padding === false && 'p-0'],
                }, () => [
                    h$1('div', {
                        class: [
                            'me-3',
                            'text-white',
                            `bg-${props.color}`,
                            props.padding ? 'p-3' : 'p-4',
                        ],
                    }, (slots.default && slots.default()) || (slots.icon && slots.icon())),
                    h$1('div', {}, [
                        (props.value || slots.value) &&
                            h$1('div', {
                                class: [`fs-6 fw-semibold text-${props.color}`],
                            }, {
                                default: () => (slots.value && slots.value()) || props.value,
                            }),
                        (props.title || slots.title) &&
                            h$1('div', {
                                class: 'text-medium-emphasis text-uppercase fw-semibold small',
                            }, {
                                default: () => (slots.title && slots.title()) || props.title,
                            }),
                    ]),
                ]),
                slots.footer && h$1(CCardFooter, {}, () => slots.footer && slots.footer()),
            ],
        });
    },
});

const CWidgetsStatsPlugin = {
    install: (app) => {
        app.component(CWidgetStatsA.name, CWidgetStatsA);
        app.component(CWidgetStatsB.name, CWidgetStatsB);
        app.component(CWidgetStatsC.name, CWidgetStatsC);
        app.component(CWidgetStatsD.name, CWidgetStatsD);
        app.component(CWidgetStatsE.name, CWidgetStatsE);
        app.component(CWidgetStatsF.name, CWidgetStatsF);
    },
};

var Components = /*#__PURE__*/Object.freeze({
    __proto__: null,
    CAccordion: CAccordion,
    CAccordionBody: CAccordionBody,
    CAccordionButton: CAccordionButton,
    CAccordionHeader: CAccordionHeader,
    CAccordionItem: CAccordionItem,
    CAccordionPlugin: CAccordionPlugin,
    CAlert: CAlert,
    CAlertHeading: CAlertHeading,
    CAlertLink: CAlertLink,
    CAlertPlugin: CAlertPlugin,
    CAvatar: CAvatar,
    CAvatarPlugin: CAvatarPlugin,
    CBackdrop: CBackdrop,
    CBackdropPlugin: CBackdropPlugin,
    CBadge: CBadge,
    CBadgePlugin: CBadgePlugin,
    CBreadcrumb: CBreadcrumb,
    CBreadcrumbItem: CBreadcrumbItem,
    CBreadcrumbPlugin: CBreadcrumbPlugin,
    CButton: CButton,
    CButtonGroup: CButtonGroup,
    CButtonGroupPlugin: CButtonGroupPlugin,
    CButtonPlugin: CButtonPlugin,
    CButtonToolbar: CButtonToolbar,
    CCLinkPlugin: CCLinkPlugin,
    CCalendar: CCalendar,
    CCalendarPlugin: CCalendarPlugin,
    CCallout: CCallout,
    CCalloutPlugin: CCalloutPlugin,
    CCard: CCard,
    CCardBody: CCardBody,
    CCardFooter: CCardFooter,
    CCardGroup: CCardGroup,
    CCardHeader: CCardHeader,
    CCardImage: CCardImage,
    CCardImageOverlay: CCardImageOverlay,
    CCardLink: CCardLink,
    CCardPlugin: CCardPlugin,
    CCardSubtitle: CCardSubtitle,
    CCardText: CCardText,
    CCardTitle: CCardTitle,
    CCarousel: CCarousel,
    CCarouselCaption: CCarouselCaption,
    CCarouselItem: CCarouselItem,
    CCarouselPlugin: CCarouselPlugin,
    CCloseButton: CCloseButton,
    CCloseButtonPlugin: CCloseButtonPlugin,
    CCol: CCol,
    CCollapse: CCollapse,
    CCollapsePlugin: CCollapsePlugin,
    CContainer: CContainer,
    CDatePicker: CDatePicker,
    CDatePickerPlugin: CDatePickerPlugin,
    CDateRangePicker: CDateRangePicker,
    CDateRangePickerPlugin: CDateRangePickerPlugin,
    CDropdown: CDropdown,
    CDropdownDivider: CDropdownDivider,
    CDropdownHeader: CDropdownHeader,
    CDropdownItem: CDropdownItem,
    CDropdownMenu: CDropdownMenu,
    CDropdownPlugin: CDropdownPlugin,
    CDropdownToggle: CDropdownToggle,
    CElementCover: CElementCover,
    CElementCoverPlugin: CElementCoverPlugin,
    CFooter: CFooter,
    CFooterPlugin: CFooterPlugin,
    CForm: CForm,
    CFormCheck: CFormCheck,
    CFormFeedback: CFormFeedback,
    CFormFloating: CFormFloating,
    CFormInput: CFormInput,
    CFormLabel: CFormLabel,
    CFormPlugin: CFormPlugin,
    CFormRange: CFormRange,
    CFormSelect: CFormSelect,
    CFormSwitch: CFormSwitch,
    CFormText: CFormText,
    CFormTextarea: CFormTextarea,
    CGridPlugin: CGridPlugin,
    CHeader: CHeader,
    CHeaderBrand: CHeaderBrand,
    CHeaderDivider: CHeaderDivider,
    CHeaderNav: CHeaderNav,
    CHeaderPlugin: CHeaderPlugin,
    CHeaderText: CHeaderText,
    CHeaderToggler: CHeaderToggler,
    CImage: CImage,
    CImagePlugin: CImagePlugin,
    CInputGroup: CInputGroup,
    CInputGroupText: CInputGroupText,
    CLink: CLink,
    CListGroup: CListGroup,
    CListGroupItem: CListGroupItem,
    CListGroupPlugin: CListGroupPlugin,
    CLoadingButton: CLoadingButton,
    CLoadingButtonPlugin: CLoadingButtonPlugin,
    CModal: CModal,
    CModalBody: CModalBody,
    CModalFooter: CModalFooter,
    CModalHeader: CModalHeader,
    CModalPlugin: CModalPlugin,
    CModalTitle: CModalTitle,
    CMultiSelect: CMultiSelect,
    CMultiSelectPlugin: CMultiSelectPlugin,
    CNav: CNav,
    CNavGroup: CNavGroup,
    CNavGroupItems: CNavGroupItems,
    CNavItem: CNavItem,
    CNavLink: CNavLink,
    CNavPlugin: CNavPlugin,
    CNavTitle: CNavTitle,
    CNavbar: CNavbar,
    CNavbarBrand: CNavbarBrand,
    CNavbarNav: CNavbarNav,
    CNavbarPlugin: CNavbarPlugin,
    CNavbarText: CNavbarText,
    CNavbarToggler: CNavbarToggler,
    COffcanvas: COffcanvas,
    COffcanvasBody: COffcanvasBody,
    COffcanvasHeader: COffcanvasHeader,
    COffcanvasPlugin: COffcanvasPlugin,
    COffcanvasTitle: COffcanvasTitle,
    CPagination: CPagination,
    CPaginationItem: CPaginationItem,
    CPaginationPlugin: CPaginationPlugin,
    CPicker: CPicker,
    CPickerPlugin: CPickerPlugin,
    CPlaceholder: CPlaceholder,
    CPlaceholderPlugin: CPlaceholderPlugin,
    CPopover: CPopover,
    CPopoverPlugin: CPopoverPlugin,
    CProgress: CProgress,
    CProgressBar: CProgressBar,
    CProgressPlugin: CProgressPlugin,
    CRow: CRow,
    CSidebar: CSidebar,
    CSidebarBrand: CSidebarBrand,
    CSidebarFooter: CSidebarFooter,
    CSidebarHeader: CSidebarHeader,
    CSidebarNav: CSidebarNav,
    CSidebarPlugin: CSidebarPlugin,
    CSidebarToggler: CSidebarToggler,
    CSmartPagination: CSmartPagination,
    CSmartPaginationPlugin: CSmartPaginationPlugin,
    CSmartTable: CSmartTable,
    CSmartTablePlugin: CSmartTablePlugin,
    CSpinner: CSpinner,
    CSpinnerPlugin: CSpinnerPlugin,
    CTabContent: CTabContent,
    CTabPane: CTabPane,
    CTable: CTable,
    CTableBody: CTableBody,
    CTableCaption: CTableCaption,
    CTableDataCell: CTableDataCell,
    CTableFoot: CTableFoot,
    CTableHead: CTableHead,
    CTableHeaderCell: CTableHeaderCell,
    CTablePlugin: CTablePlugin,
    CTableRow: CTableRow,
    CTabsPlugin: CTabsPlugin,
    CTimePicker: CTimePicker,
    CTimePickerPlugin: CTimePickerPlugin,
    CToast: CToast,
    CToastBody: CToastBody,
    CToastClose: CToastClose,
    CToastHeader: CToastHeader,
    CToastPlugin: CToastPlugin,
    CToaster: CToaster,
    CTooltip: CTooltip,
    CTooltipPlugin: CTooltipPlugin,
    CVirtualScroller: CVirtualScroller,
    CVirtualScrollerPlugin: CVirtualScrollerPlugin,
    CWidgetStatsA: CWidgetStatsA,
    CWidgetStatsB: CWidgetStatsB,
    CWidgetStatsC: CWidgetStatsC,
    CWidgetStatsD: CWidgetStatsD,
    CWidgetStatsE: CWidgetStatsE,
    CWidgetStatsF: CWidgetStatsF,
    CWidgetsStatsPlugin: CWidgetsStatsPlugin
});

const BREAKPOINTS = [
    'xxl',
    'xl',
    'lg',
    'md',
    'sm',
    'xs',
];
var vCPlaceholder = {
    name: 'c-placeholder',
    mounted(el, binding) {
        const value = binding.value;
        el.classList.add(value.animation ? `placeholder-${value.animation}` : 'placeholder');
        BREAKPOINTS.forEach((bp) => {
            const breakpoint = value[bp];
            const infix = bp === 'xs' ? '' : `-${bp}`;
            if (typeof breakpoint === 'number') {
                el.classList.add(`col${infix}-${breakpoint}`);
            }
            if (typeof breakpoint === 'boolean') {
                el.classList.add(`col${infix}`);
            }
        });
    },
};

const createPopoverElement = (id, header, content) => {
    const popover = document.createElement('div');
    popover.id = id;
    popover.classList.add('popover', 'bs-popover-auto', 'fade');
    popover.setAttribute('role', 'popover');
    popover.innerHTML = `<div class="popover-arrow" data-popper-arrow></div>
     <div class="popover-header">${header}</div>
     <div class="popover-body" id="">${content}</div>`;
    return popover;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const addPopoverElement = (popover, el, popperOptions) => {
    document.body.appendChild(popover);
    createPopper(el, popover, popperOptions);
    setTimeout(() => {
        popover.classList.add('show');
    }, 1);
};
const removePopoverElement = (popover) => {
    popover.classList.remove('show');
    setTimeout(() => {
        popover.remove();
    }, 300);
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const togglePopoverElement = (popover, el, popperOptions) => {
    const popperElement = document.getElementById(popover.id);
    if (popperElement && popperElement.classList.contains('show')) {
        removePopoverElement(popover);
        return;
    }
    addPopoverElement(popover, el, popperOptions);
};
var vCPopover = {
    name: 'c-popover',
    uid: '',
    mounted(el, binding) {
        const value = binding.value;
        const content = typeof value === 'string' ? value : value.content ?? '';
        const header = value.header ?? '';
        const trigger = value.trigger ?? 'click';
        // Popper Config
        const offset = value.offset ?? [0, 8];
        const placement = value.placement ?? 'top';
        const popperOptions = {
            placement: placement,
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: offset,
                    },
                },
            ],
        };
        const popoverUID = getUID('popover');
        binding.arg = popoverUID;
        const popover = createPopoverElement(popoverUID, header, content);
        trigger.includes('click') &&
            el.addEventListener('click', () => {
                togglePopoverElement(popover, el, popperOptions);
            });
        if (trigger.includes('focus')) {
            el.addEventListener('focus', () => {
                addPopoverElement(popover, el, popperOptions);
            });
            el.addEventListener('blur', () => {
                removePopoverElement(popover);
            });
        }
        if (trigger.includes('hover')) {
            el.addEventListener('mouseenter', () => {
                addPopoverElement(popover, el, popperOptions);
            });
            el.addEventListener('mouseleave', () => {
                removePopoverElement(popover);
            });
        }
    },
    unmounted(_el, binding) {
        const popover = binding.arg && document.getElementById(binding.arg);
        popover && popover.remove();
    },
};

const createTooltipElement = (id, content) => {
    const tooltip = document.createElement('div');
    tooltip.id = id;
    tooltip.classList.add('tooltip', 'bs-tooltip-auto', 'fade');
    tooltip.setAttribute('role', 'tooltip');
    tooltip.innerHTML = `<div class="tooltip-arrow" data-popper-arrow></div>
     <div class="tooltip-inner" id="">${content}</div>`;
    return tooltip;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const addTooltipElement = (tooltip, el, popperOptions) => {
    document.body.appendChild(tooltip);
    createPopper(el, tooltip, popperOptions);
    setTimeout(() => {
        tooltip.classList.add('show');
    }, 1);
};
const removeTooltipElement = (tooltip) => {
    tooltip.classList.remove('show');
    setTimeout(() => {
        tooltip.remove();
    }, 300);
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const toggleTooltipElement = (tooltip, el, popperOptions) => {
    const popperElement = document.getElementById(tooltip.id);
    if (popperElement && popperElement.classList.contains('show')) {
        removeTooltipElement(tooltip);
        return;
    }
    addTooltipElement(tooltip, el, popperOptions);
};
var vCTooltip = {
    name: 'c-tooltip',
    mounted(el, binding) {
        const value = binding.value;
        const content = typeof value === 'string' ? value : value.content ?? '';
        const trigger = value.trigger ?? 'hover';
        // Popper Config
        const offset = value.offset ?? [0, 6];
        const placement = value.placement ?? 'top';
        const popperOptions = {
            placement: placement,
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: offset,
                    },
                },
            ],
        };
        const tooltipUID = getUID('tooltip');
        binding.arg = tooltipUID;
        const tooltip = createTooltipElement(tooltipUID, content);
        trigger.includes('click') &&
            el.addEventListener('click', () => {
                toggleTooltipElement(tooltip, el, popperOptions);
            });
        if (trigger.includes('focus')) {
            el.addEventListener('focus', () => {
                addTooltipElement(tooltip, el, popperOptions);
            });
            el.addEventListener('blur', () => {
                removeTooltipElement(tooltip);
            });
        }
        if (trigger.includes('hover')) {
            el.addEventListener('mouseenter', () => {
                addTooltipElement(tooltip, el, popperOptions);
            });
            el.addEventListener('mouseleave', () => {
                removeTooltipElement(tooltip);
            });
        }
    },
    beforeUnmount(_el, binding) {
        const tooltip = binding.arg && document.getElementById(binding.arg);
        tooltip && tooltip.remove();
    },
};

var Directives = /*#__PURE__*/Object.freeze({
    __proto__: null,
    vcplaceholder: vCPlaceholder,
    vcpopover: vCPopover,
    vctooltip: vCTooltip
});

const CoreuiVue = {
    install: (app) => {
        for (const key in Components) {
            app.component(key, Components[key]);
        }
        for (const key in Directives) {
            app.directive(Directives[key]['name'], Directives[key]);
        }
    },
};

export { CAccordion, CAccordionBody, CAccordionButton, CAccordionHeader, CAccordionItem, CAccordionPlugin, CAlert, CAlertHeading, CAlertLink, CAlertPlugin, CAvatar, CAvatarPlugin, CBackdrop, CBackdropPlugin, CBadge, CBadgePlugin, CBreadcrumb, CBreadcrumbItem, CBreadcrumbPlugin, CButton, CButtonGroup, CButtonGroupPlugin, CButtonPlugin, CButtonToolbar, CCLinkPlugin, CCalendar, CCalendarPlugin, CCallout, CCalloutPlugin, CCard, CCardBody, CCardFooter, CCardGroup, CCardHeader, CCardImage, CCardImageOverlay, CCardLink, CCardPlugin, CCardSubtitle, CCardText, CCardTitle, CCarousel, CCarouselCaption, CCarouselItem, CCarouselPlugin, CCloseButton, CCloseButtonPlugin, CCol, CCollapse, CCollapsePlugin, CContainer, CDatePicker, CDatePickerPlugin, CDateRangePicker, CDateRangePickerPlugin, CDropdown, CDropdownDivider, CDropdownHeader, CDropdownItem, CDropdownMenu, CDropdownPlugin, CDropdownToggle, CElementCover, CElementCoverPlugin, CFooter, CFooterPlugin, CForm, CFormCheck, CFormFeedback, CFormFloating, CFormInput, CFormLabel, CFormPlugin, CFormRange, CFormSelect, CFormSwitch, CFormText, CFormTextarea, CGridPlugin, CHeader, CHeaderBrand, CHeaderDivider, CHeaderNav, CHeaderPlugin, CHeaderText, CHeaderToggler, CImage, CImagePlugin, CInputGroup, CInputGroupText, CLink, CListGroup, CListGroupItem, CListGroupPlugin, CLoadingButton, CLoadingButtonPlugin, CModal, CModalBody, CModalFooter, CModalHeader, CModalPlugin, CModalTitle, CMultiSelect, CMultiSelectPlugin, CNav, CNavGroup, CNavGroupItems, CNavItem, CNavLink, CNavPlugin, CNavTitle, CNavbar, CNavbarBrand, CNavbarNav, CNavbarPlugin, CNavbarText, CNavbarToggler, COffcanvas, COffcanvasBody, COffcanvasHeader, COffcanvasPlugin, COffcanvasTitle, CPagination, CPaginationItem, CPaginationPlugin, CPicker, CPickerPlugin, CPlaceholder, CPlaceholderPlugin, CPopover, CPopoverPlugin, CProgress, CProgressBar, CProgressPlugin, CRow, CSidebar, CSidebarBrand, CSidebarFooter, CSidebarHeader, CSidebarNav, CSidebarPlugin, CSidebarToggler, CSmartPagination, CSmartPaginationPlugin, CSmartTable, CSmartTablePlugin, CSpinner, CSpinnerPlugin, CTabContent, CTabPane, CTable, CTableBody, CTableCaption, CTableDataCell, CTableFoot, CTableHead, CTableHeaderCell, CTablePlugin, CTableRow, CTabsPlugin, CTimePicker, CTimePickerPlugin, CToast, CToastBody, CToastClose, CToastHeader, CToastPlugin, CToaster, CTooltip, CTooltipPlugin, CVirtualScroller, CVirtualScrollerPlugin, CWidgetStatsA, CWidgetStatsB, CWidgetStatsC, CWidgetStatsD, CWidgetStatsE, CWidgetStatsF, CWidgetsStatsPlugin, CoreuiVue as default, useDebouncedCallback, useIsVisible, usePopper, vCPlaceholder as vcplaceholder, vCPopover as vcpopover, vCTooltip as vctooltip };
//# sourceMappingURL=index.es.js.map
